// ===== Packages =====

import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
    useCallback,
    useLayoutEffect,
}                                           from 'react';
import { useDropzone }                      from 'react-dropzone';
import { ReactSVG }                         from 'react-svg';
// eslint-disable-next-line import/no-extraneous-dependencies
// eslint-disable-next-line import/no-extraneous-dependencies
import moment                               from 'moment-timezone';
import { v4 as uuidv4 }                     from 'uuid';
import {
    fetchSignInMethodsForEmail,
}                                           from 'firebase/auth';
import {
    getDownloadURL,
    getStorage,
    ref,
    StorageError,
    StorageReference,
    UploadTaskSnapshot,
}                                           from 'firebase/storage';
import {
    Editor,
    Descendant,
    Transforms,
    Range,
}                                           from 'slate';
import { ReactEditor }                      from 'slate-react';
import {
    Resizable,
    ResizeCallbackData,
}                                           from 'react-resizable';
import {
    useMatch,
    useParams,
    useNavigate,
    useLocation,
    useSearchParams,
}                                           from 'react-router-dom';
import { createPortal }                     from 'react-dom';
import ShortUniqueId                        from 'short-unique-id';
import mime                                 from 'mime';

// ===== Firebase =====

import fire                             from '../../fire';

// ===== Components =====

import { Transition }                       from '../Transition';
import {
    PostEditor,
    AnnotationEditor,
}                                           from '../Editor/editors';
import {
    Annotation,
    AnnotationBucket,
    UploadingMediaItem,
    Button,
    OptionsMenu,
}                                           from '../Editor/helpers';
import PostAccessDialog                     from '../PostAccessDialog';
import PostPublishingDialog                 from '../PostPublishingDialog';
import PlaybackSpeedButtonLabel             from '../PlaybackSpeedButtonLabel';
import ReaderContentsTableOutliner          from '../ReaderContentsTableOutliner';
import Tooltip                              from '../Tooltip';
import Modal                                from '../Modal';

// ===== Hooks =====

import {
    useTimeout,
    useTaskQueue,
    useLatest,
    useMediaQuality,
}                                           from '../../hooks';

// ===== Enums =====

import {
    PAGE_ROUTE,
    EDITOR_CONTEXT_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    MEDIA_TYPE,
    USER_ACTION_TYPE,
    BUTTON_TYPE,
    STORAGE_ERROR_CODE,
    TOOLTIP_TYPE,
    STORAGE_ENTITY,
    READER_PARAMS_TYPE,
    ADVENTURE_CHARACTER,
    READ_DURATION_TYPE,
    BOOK_TYPE,
    STAGED_CART_ITEM_CART_OPERATION,
    STORE_ITEM_TYPE,
    SALE_NEGOTIATION_ITEM_STATUS_TYPE,
    AUTHENTICATION_TYPE,
    AUTHENTICATION_ERROR_CODE,
    EMAIL_CONVERSION_CALLABLE_ERROR_CODE,
    ANNOTATION_TARGET_SCOPE,
    POST_ACCESS_ITEM_SCOPE,
    POST_COLLAB_DOC_CHECKPOINT_REASON,
    POST_CONTENT_SCOPE,
    POST_OUTLINE_MUTATION_TYPE,
    POST_RESPONSE_TIER,
    STICKY_CTA_NUDGE_SCENARIO,
    STICKY_CTA_NUDGE_TARGET,
    READER_PROGRESS_SOURCE,
    READER_RESUME_SOURCE,
    READER_RESUME_LOG_EVENT,
    PREVIEW_AUDIO_END_PROMPT_ACCESS_TYPE,
    FAILURE_TELEMETRY_TYPE,
}                                           from '../../enums';
import { ZOOM_LEVEL }                       from '../Editor/elements/enums';

// ===== Services =====

import {
    playAudio,
    updatePageTitle,
    findParentNodeWithClass,
    recordUserAction,
    formatNumber,
    setColorLightness,
    detectTouchDevice,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    getMediaStorageBucket,
    uploadToCloudStorage,
    getStorageErrorMessage,
    stripeGetStoreItems,
    formatReleaseTimestamp,
    formatAudioTimestamp as formatAudioMiniPlayerTimestamp,
    roundToNDecimals,
    getNextAudioPlaybackSpeed as getNextAudioMiniPlayerPlaybackSpeed,
    getPost,
    createPost,
    addPostAuthor,
    updatePost,
    recordPostView,
    updatePostOutline,
    getPostContentsTable,
    getPostZoomLevelCount,
    getPostAuthorItems,
    createAnnotation,
    getAnnotations,
    getAnnotationById,
    reattachDetachedAnnotation as reattachDetachedAnnotationService,
    updateUser,
    createMedia,
    createSaleNegotiationRequest,
    getUserBookPurchases,
    authenticateWithProvider,
    authenticateWithEmailConversion,
    validateEmail,
    deepEqual,
    mapKeyDiff,
    isTestMode,
    getPostEngagementItems,
    subscribeToPostEngagement,
    subscribeToPostCollabDoc,
    subscribeToPostEngagementItems,
    subscribeToAnnotationEngagementItems,
    subscribeToLatestPostUpdateEvent,
    buildPostScopeKey,
    applySerializedScopeAccessMetadataToPost,
    getAnnotationFreshnessTimestamp,
    isAnnotationComment,
    resolvePostContentTarget,
    resolveReaderContentsProgress,
    resolvePostPathFromScopeIds,
    resolveInternalPostLinkTarget,
    exportPostCollaborativeDoc,
    replacePostCollaborativeDoc,
    getEmailSignInMethodsByEmail,
    getUserUndeletedAnnotationCount,
    areAnnotationDictationAssetsEqual,
} from '../../services';
import buildStoreItemsByProduct                        from '../../services/buildStoreItemsByProduct';
import inferNameFromEmail                               from '../../services/inferNameFromEmail';
import {
    mapSignInMethodToAuthenticationType,
}                                                       from '../../services/authProviderHelpers';
import resolveDraftStatusUpdatedAt                      from '../../services/resolveDraftStatusUpdatedAt';
import resolvePreferredUserName                         from '../../services/resolvePreferredUserName';
import resolvePreviewAudioEndPrompt, {
    resolvePreviewAudioEndPromptScope,
}                                                       from '../../services/resolvePreviewAudioEndPrompt';
import useLazyAudioRef                                 from '../../services/useLazyAudioRef';

// ===== Interfaces =====

import {
    ICoord,
    IReadPostItem,
    IReadPostChapterItem,
    IReadPostSectionItem,
    IDimension,
    IMediaItem,
    IAnnotationItem,
    IAnnotationItemDetached,
    IAnnotationEngagementItem,
    IAnnotationQuote,
    IGetPostResult,
    IPartialUserItem,
    IPostCollabDocItem,
    IPostEngagementItem,
    IPostUpdateEventItem,
    ISnackbarItem,
    IContentsChapterItem,
    IPostPathEvent,
    IPostPathExpandChapterItem,
    IZoomLevelItem,
    ICartItem,
    IBookAboutBlurbParagraph,
    IStagedCartItem,
    IStoreItem,
    IPostAccessItem,
    IReaderChapterProgressItem,
    IPostContentTarget,
    ICollaborativeCursorData,
    IUserBookPurchaseItem,
    IReadOnlyAudioNotePlaybackState,
    ISerializedScopeAccessMetadata,
    IInternalPostLinkTarget,
    IReadOnlyRevealTargetResolution,
}                                           from '../../interfaces';

// ===== Constants =====

import {
    FADE_IN_TRANSITION_DURATION,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_OFFSET_DURATION,
    FADE_IN_STAGGER_TRANSITION_DURATION,
    FADE_IN_STAGGER_DEFAULT_STYLE,
    FADE_IN_STAGGER_TRANSITION_STYLES,
    DEFAULT_AUDIO_VOLUME,
    DETAIL_VIEW_Z_INDEX,
    READER_VIEW_OVERLAY_Z_INDEX,
    HANDLEBAR_TRANSITION_DURATION,
    HOVER_TARGET_CLASSNAME,
    DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH,
    DETAIL_VIEW_HANDLEBAR_WIDTH,
    DETAIL_VIEW_HANDLEBAR_HEIGHT,
    DETAIL_VIEW_TRANSITION_DURATION,
    DETAIL_SHEET_ENTER_TRANSITION_DURATION,
    DETAIL_SHEET_EXIT_TRANSITION_DURATION,
    DETAIL_SHEET_CLASSNAME,
    DEFAULT_ANNOTATION_EDITOR_HEIGHT,
    SLATE_PARAGRAPH_CLASSNAME,
    ANNOTATION_DECORATION_PREFIX,
    BOOK_POST_ID,
    BODY_FONT_SIZE,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    EXTENDED_SNACKBAR_VISIBLE_DURATION,
    USER_PROFILE_AVATAR_LENGTH_MULTIPLIER,
    DEFAULT_POST_TITLE,
    POST_TITLE_MARGIN_TOP,
    POST_TITLE_MARGIN_BOTTOM,
    POST_SUBTITLE_MARGIN,
    POST_DETAIL_TEXT_MARGIN_LEFT,
    POST_THUMBNAIL_MAX_WIDTH_MEDIUM,
    POST_THUMBNAIL_MAX_WIDTH_SMALL,
    CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_Y_OFFSET_SMALL_VIEWPORT,
    BUTTON_CONTAINER_LIGHTNESS_VALUE,
    POST_BANNER_LEFT_CONTAINER_WIDTH,
    POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT,
    POST_TITLE_ENTER_DURATION,
    POST_SUBTITLE_ENTER_DURATION,
    POST_THUMBNAIL_ENTER_DURATION,
    POST_METADATA_ENTER_DURATION,
    CONTENTS_TABLE_MARGIN,
    POST_BANNER_HEIGHT,
    STICKY_PURCHASE_CTA_MAX_WIDTH,
    SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME,
    CONTENTS_TABLE_TOP_EXPANDED,
    MILLISECONDS_IN_A_MINUTE,
    MILLISECONDS_IN_AN_HOUR,
    MILLISECONDS_IN_A_SECOND,
    ANNOTATION_EDITOR_FONT_MULTIPLIER_LARGE,
    NAVIGATION_CART_BUTTON_ID,
    DEFAULT_EDITOR_COLOR,
    MODAL_SHEET_CLOSE_BUTTON_COLOR,
    MODAL_SHEET_CLOSE_BUTTON_LENGTH,
    BUTTON_EXPANSION_DURATION,
    SALES_EMAIL_ADDRESS,
    WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    AUDIO_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    AUDIO_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER,
    READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER,
    SIGN_IN_ANNOTATION_CREATE_MESSAGE,
    POST_EDITOR_MAX_WIDTH,
    PAGE_LOGO_WIDTH,
    DEFAULT_FONT_SIZE,
    CENTS_IN_A_DOLLAR,
    SUPPORT_EMAIL_ADDRESS,
    CONTENTS_TABLE_WIDTH_CONTRACTED,
    POST_AUTHOR_THRESHOLD,
    POST_PUBLISHED_THRESHOLD,
    POST_VIEWS_THRESHOLD,
    POST_ANNOTATIONS_THRESHOLD,
    PURCHASE_BOOK_BUTTON_THRESHOLD,
    POST_BANNER_PURCHASE_ACTION_GAP,
    POST_VIEW_RECORD_RETRY,
    POST_PAYWALL_OVERHANG_HEIGHT,
    POST_SUPER_SMALL_VIEWPORT_THRESHOLD,
    CURSOR_Z_INDEX,
}                                           from '../../constants/generalConstants';
import {
    isLockedUntilIndefinite,
}                                           from '../../constants/postAccessConstants';
import MEDIA_QUERY_SIZE                     from '../../constants/mediaQuerySizes';
import CURSOR_SIGN                          from '../../constants/cursorSigns';
import KEYCODE                              from '../../constants/keycodes';
import {
    INTERNAL_POST_LINK_CONSOLE_LOG_STORAGE_KEY,
    READER_NUDGE_CONSOLE_LOG_STORAGE_KEY,
    READER_RESUME_CONSOLE_LOG_STORAGE_KEY,
    READER_RESUME_RUNTIME_LOG_KEY,
    shouldEnableInternalPostLinkConsoleLogs,
    shouldEnableReaderNudgeConsoleLogs,
    shouldEnableReaderResumeConsoleLogs,
}                                           from '../../constants/debugFlags';

// ===== Sounds =====

import ObjectGrasp                          from '../../sounds/object_grasp.mp3';
import ObjectThump                          from '../../sounds/object_thump.mp3';
import InputClick                           from '../../sounds/button_click.mp3';
import SwooshIn                             from '../../sounds/swoosh_in.mp3';
import SwooshOut                            from '../../sounds/swoosh_out.mp3';
import TooltipEnter                         from '../../sounds/create.mp3';
import ButtonHover                          from '../../sounds/button_hover.mp3';
import Success                              from '../../sounds/message_success.mp3';
import ToggleSnap                           from '../../sounds/snap.mp3';

// ===== Images =====

import CrossIcon                            from '../../images/editor/cross.svg';
import UploadIcon                           from '../../images/editor/upload.svg';
import VerascopeLogo                        from '../../images/verascope-logo-silhouette.svg';
import CautionIcon                          from '../../images/caution.svg';
import SmileyIcon                           from '../../images/editor/smiley.svg';
import ClockIcon                            from '../../images/editor/clock.svg';
import EyeIcon                              from '../../images/editor/eye.svg';
import HighlightIcon                        from '../../images/editor/highlight.svg';
import MagnifyingGlassIcon                  from '../../images/editor/magnifying-glass.svg';
import AddImageIcon                         from '../../images/editor/image-add.svg';
import GeekPortrait                         from '../../images/geek.svg';
import PhilosopherPortrait                  from '../../images/philosopher.svg';
import JournalistPortrait                   from '../../images/journalist.svg';
import InvestorPortrait                     from '../../images/investor.svg';
import MinorPortrait                        from '../../images/minor.svg';
import CartIcon                             from '../../images/cart.svg';
import LockIcon                             from '../../images/lock.svg';
import PencilIcon                           from '../../images/pencil.svg';
import ShelfIcon                            from '../../images/shelf.svg';
import LinkIcon                             from '../../images/link.svg';
import FileIcon                             from '../../images/file.svg';
import ChevronIcon                          from '../../images/editor/chevron.svg';
import GoogleIcon                           from '../../images/google.svg';
import GithubIcon                           from '../../images/github.svg';
import FacebookIcon                         from '../../images/facebook.svg';
import XTwitterIcon                         from '../../images/x-twitter.svg';
import EnvelopeIcon                         from '../../images/envelope.svg';
import UserIcon                             from '../../images/user.svg';
import BookmarkIcon                         from '../../images/bookmark.svg';
import SpinnerIcon                          from '../../images/editor/spinner.svg';
import PlayIcon                             from '../../images/editor/play-fill.svg';
import PauseIcon                            from '../../images/editor/pause-fill.svg';
import SkipBackwardIcon                     from '../../images/editor/skip-backward-15.svg';
import SkipForwardIcon                      from '../../images/editor/skip-forward-15.svg';

// ===== Styles =====

import {
    Container,
    EditorUpdateText,
    EditorStatusRowContainer,
    PostUpdateTextContainer,
    UploadProgressContainer,
    AnnotationContainer,
    PostInput,
    PostSubtitle,
    PostTitle,
    ModalSheetCloseButtonContainer,
    PostBanner,
    PostBannerLeftContainer,
    PostBannerRightContainer,
    PostThumbnail,
    PostContentContainer,
    PostContent,
    PostBannerCenterContainer,
    PostContentCenterContainer,
    PostMetadataContainer,
    PostContentLeftContainer,
    PostTitlesContainer,
    CollapsedPostTitle,
    PostAuthorContainer,
    PostAuthorName,
    PostMetadataContentContainer,
    PostDetailsContainer,
    PostDetail,
    PostDetailIcon,
    PostDetailText,
    ZoomLevelSelectorContainer,
    AnnotationVisibilityButtonContainer,
    InternalPostLinkFloatingStatusLabel,
    InternalPostLinkFloatingStatusPill,
    InternalPostLinkFloatingStatusSpinner,
    ResumeReaderPositionButtonContainer,
    FloatingAudioPlayerContainer,
    FloatingAudioPlayerTransportRow,
    FloatingAudioPlayerTime,
    CurrentZoomLevelText,
    CurrentZoomLevelIcon,
    AddPostThumbnailIcon,
    AddPostThumbnailContainer,
    AddPostThumbnailInput,
    PostThumbnailUploadProgressBarContainer,
    PostThumbnailUploadProgressBar,
    PostPlaceholderContainer,
    Handlebar,
    HandlebarContainer,
    PurchaseBookButtonContainer,
    PostBannerPurchaseActions,
    ModalContentsContainer,
    BookAboutDialogCloseButtonContainer,
    ModalHeader,
    ModalTitle,
    ModalSubtitle,
    ModalBody,
    BookAboutDialogBody,
    BookAboutDialogParagraph,
    AdventureCharacterContainer,
    AdventureCharacterAvatarContainer,
    AdventureCharacterAvatar,
    AdventureCharacterName,
    AdventureCharacterZoomLevel,
    CartAnimationItemsContainer,
    PaywallContainer,
    PaywallHeader,
    PaywallButtonContainer,
    VersionHistoryButtonContainer,
    PaywallSubheader,
    PaywallOverhang,
    LockedContainer,
    LockedIcon,
    LockedHeader,
    LockedSubheader,
    CollaborativePresenceContainer,
    CollaborativePresenceRow,
    CollaborativePresenceAvatarWrapper,
    CollaborativePresenceAvatarButton,
    CollaborativePresenceInitials,
    EditorStatusControlsRow,
    StickyPurchaseCtaContainer,
    StickyPurchaseChevronAnchor,
    StickyPurchaseHeader,
    StickyPurchaseHeaderButtonContainer,
    StickyPurchaseHeaderCopy,
    StickyPurchaseHeaderTitle,
    StickyPurchaseHeaderTitleEmphasis,
    StickyPurchaseHeaderSubtitle,
    StickyPurchaseBody,
    StickyPurchaseContentContainer,
    StickyPurchaseChoiceRow,
    StickyPurchaseChoiceCopy,
    StickyPurchaseRowContainer,
    InlineActionRow,
    StickyPurchaseMoreOptionsButtonContainer,
    StickyPurchaseMoreOptionsChevronContainer,
    StickyPurchaseMoreOptionsChevronIcon,
    AuthLockedContainer,
    AuthLockedActionsContainer,
    AuthLockedEmailRow,
    AuthLockedButtonContainer,
    HaggleButtonContainer,
    PaywallContent,
    ButtonContainer,
    DockedAnnotationEditorViewportRoot,
    MobileDetailSheetRoot,
    MobileDetailSheetBackdrop,
    MobileDetailSheetPanel,
    DetachedAnnotationShelfLayout,
    DetachedAnnotationsPanelContainer,
    DetachedAnnotationsPanelHeader,
    DetachedAnnotationsPanelTitle,
    DetachedAnnotationsPanelDescription,
    DetachedAnnotationsList,
}                                           from './styles';
import {
    PageLogo,
    DetailView,
    DropzoneInput,
    PlaceholderBox,
    AvatarsContainer,
    AvatarContainer,
    UserAvatar,
    Overlay,
}                                           from '../../styles';
import {
    resolvePostBannerRightContainerWidth,
    canRenderReaderPostEditor,
    hasRequiredReaderPostFetchContext,
    resolveAnnotationEditorPlacement,
    AnnotationEditorPlacement,
    resolveReaderViewportParagraphAnchor,
    type IReaderViewportParagraphAnchorCandidate,
    type ReaderProgressLocationSnapshot,
    resolveSelectionSpansMultipleLines,
    resolveSelectionIsBackward,
    resolveHighlightSelectionRect,
    resolveDockedAnnotationEditorRevealScrollTop,
    resolveReaderProgressHistoryIdentity,
    shouldDockAnnotationEditorToViewport,
    resolveReaderPostFileDragFeedback,
    resolveReaderPostFileDragFeedbackKey,
    resolveReaderPostFileDragMetadata,
    type ReaderPostFileDragMetadata,
}                                           from './helpers';
import { useDockedAnnotationViewportRoot }  from './useDockedAnnotationViewportRoot';
import { theme as themeObj }                from '../../themes/theme-context';

// ===== Initialize Firebase =====

const { auth } = fire();

// StrictMode remounts can replay mount effects during a single page load. Keep
// a module-scoped registry so route-level view recording only fires once per
// route/session/path combination even if ReaderView remounts during dev.
const recordedReadPageViewKeys = new Set<string>();

const BOOK_ABOUT_BLURB_PARAGRAPHS: IBookAboutBlurbParagraph[] = [
    {
        id: 'education-transfer',
        content: [
            'Most of us grew up with a pretty simple picture of education: knowledge is handed down from one ',
            'generation to the next, and learning\'s what takes place when that transfer happens. Teachers ',
            'lecture, students listen, memorize facts, learn formulas, and exams check whether the transfer ',
            'took place successfully. With enough checking, we then say a person has reached the end of their ',
            '"education." It\'s a quaint picture. For a while, ',
            <em key="it-even-worked">it even worked</em>,
            '. But it belonged to an older, more predictable world, one that\'s no longer with us.',
        ],
    },
    {
        id: 'complex-future',
        content: [
            'Today, we face problems no individual mind can fully grasp alone: accelerating technology, social',
            'fragmentation, ecological instability, institutional distrust, and the advent of artificial intelligence',
            'as a new kind of mind and collaborator. In this brave new world, more knowledge by itself won\'t save',
            'us. We need to become better at making sense, choosing wisely, acting effectively, and cooperating at',
            'scales our ancestors never would\'ve dreamed of.',
        ].join(' '),
    },
    {
        id: 'agency-argument',
        content: [
            'Against that backdrop, Learning the Game of Life (LTGOL) offers readers a wide-ranging argument ',
            'that draws on ideas from thermodynamics, cybernetics, mathematics, cognitive science, game design, ',
            'communication theory, and philosophy to explain why education should be rearchitected around a new ',
            'central aim: ',
            (
                <em key="agency-aim">
                    helping individuals cultivate the agency to act on their individual and shared interests
                </em>
            ),
            '.',
        ],
    },
    {
        id: 'video-games',
        content: [
            'That focus on agency leads the book to a surprising place',
            <em key="video-games">: video games</em>,
            '. Over the course of its discussions of math, games, AI, cooperation, and the future of learning, ',
            'LTGOL keeps coming back to the ideas that video games are more than entertainment. Properly ',
            'designed, they can become one of the strongest ways to learn, capable of turning abstract ',
            'systems, including mathematics, into worlds we can see, feel, explore, and gradually master.',
        ],
    },
    {
        id: 'less-helpless',
        content: [
            'However, more than anything, LTGOL is a map for readers who want to feel less helpless as they try to',
            'make sense of and navigate, the complex, uncertain future we\'re walking into.',
        ].join(' '),
    },
];
const READER_ANNOTATION_RUNTIME_LOG_KEY = '__VERASCOPE_READER_ANNOTATION_DEBUG__';

/**
 * Detached annotations keep their content in storage but no longer resolve to
 * live post text, so they should be hidden from normal reader rendering.
 */
function isDetachedAnnotation(annotation: IAnnotationItem): boolean {
    return (
        !isAnnotationComment(annotation)
        && annotation.detached?.detached === true
    );
}

/**
 * Comments attached to detached annotations are hidden alongside their parent
 * annotation so reader-facing counts and shelves stay consistent.
 */
function shouldHideAnnotationFromReader(
    annotation: IAnnotationItem,
    detachedAnnotationIds: Set<string>,
): boolean {
    if (isDetachedAnnotation(annotation)) {
        return true;
    }

    if (!isAnnotationComment(annotation)) {
        return false;
    }

    const parentAnnotationId = annotation.parentAnnotationId || annotation.comment?.parentId;
    if (!parentAnnotationId) {
        return false;
    }

    return detachedAnnotationIds.has(parentAnnotationId);
}

function buildAttachedAnnotationState(): IAnnotationItemDetached {
    return {
        detached: false,
        timestamp: null,
        detachedBy: null,
        reason: null,
    };
}

/**
 * Detached repair can move an annotation to a different reader scope, so the
 * persisted target metadata must follow the newly selected quote.
 */
function resolveAnnotationTargetScopeForReader(data: {
    postId: string,
    chapterId?: string,
    sectionId?: string,
}): {
    targetScope: ANNOTATION_TARGET_SCOPE,
    targetId: string,
} {
    const {
        postId,
        chapterId,
        sectionId,
    } = data;

    if (sectionId) {
        return {
            targetScope: ANNOTATION_TARGET_SCOPE.section,
            targetId: sectionId,
        };
    }

    if (chapterId) {
        return {
            targetScope: ANNOTATION_TARGET_SCOPE.chapter,
            targetId: chapterId,
        };
    }

    return {
        targetScope: ANNOTATION_TARGET_SCOPE.post,
        targetId: postId,
    };
}

/**
 * Keeps reader annotation debug payloads compact enough to scan in the console.
 */
function summarizeAnnotationForDebug(annotation: IAnnotationItem): Record<string, unknown> {
    const latestHistory = annotation.history?.[0];
    const latestQuoteHistory = annotation.quoteHistory?.[0];

    return {
        id: annotation.id,
        canonicalId: (annotation as IAnnotationItem & { canonicalId?: string }).canonicalId || null,
        comment: annotation.comment,
        userId: annotation.userId,
        chapterId: annotation.chapterId || null,
        sectionId: annotation.sectionId || null,
        textPreview: `${latestHistory?.text || ''}`.slice(0, 80),
        quoteCount: latestQuoteHistory?.texts?.length || 0,
        selectionCount: latestQuoteHistory?.selections?.length || 0,
    };
}

/**
 * Limits annotation debug output so older-reader investigations stay readable.
 */
function summarizeAnnotationsForDebug(
    annotations: IAnnotationItem[],
    limit = 5,
): Record<string, unknown>[] {
    if (annotations.length === 0) return [];

    return annotations
        .slice(0, limit)
        .map((annotation) => summarizeAnnotationForDebug(annotation));
}

/**
 * Reader-level audio shortcuts should yield to real text inputs and other
 * interactive controls so spacebar still behaves normally while annotating,
 * typing comments, or pressing focused buttons.
 */
function shouldIgnoreReadOnlyAudioSpacebarShortcut(
    target: EventTarget | null,
): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    if (target.isContentEditable) {
        return true;
    }

    return !!target.closest(
        'input, textarea, select, button, [role="button"], [contenteditable=""], [contenteditable="true"]',
    );
}

interface Props {
    /**
     * !!!IMPORTANT!!!
     * When you add a new prop variable, make sure to add it to the
     * dependencies of the pageLoadRendersCompleted side effect
    */
    write: boolean,
    customCursorActive: boolean,
    browserTabIsFocused: number | null,
    hasSound: boolean,
    user: IPartialUserItem | null,
    userEmailVerified: boolean,
    userEmail: string | null,
    userPostAccess: Map<string, IPostAccessItem> | undefined,
    userIsAdmin: boolean,
    fetchedUserAdminStatus: boolean,
    userCartItems: ICartItem[] | undefined,
    currentSessionId: string | null,
    viewportDimensions: IDimension,
    notifyUserToSignUpAfterAnnotationCreationAttempt: boolean,
    stickyPostBannerActive: boolean,
    signingInOut: boolean,
    queueRevealBuyButtons: boolean,
    setStickyPostBannerActive: React.Dispatch<React.SetStateAction<boolean>>,
    setCursorSigns: React.Dispatch<React.SetStateAction<string[]>>,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt: React.Dispatch<React.SetStateAction<boolean>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    setSigningInOut: React.Dispatch<React.SetStateAction<boolean>>,
    setRefreshNotifications: React.Dispatch<React.SetStateAction<boolean>>,
    setIsUserProfileDialogExpanded: (isExpanded: boolean) => void,
    setQueueRevealBuyButtons: React.Dispatch<React.SetStateAction<boolean>>,
    setUserCartItems: React.Dispatch<React.SetStateAction<ICartItem[] | undefined>>,
}
function ReaderView({
    write,
    customCursorActive,
    browserTabIsFocused,
    hasSound,
    user,
    userEmailVerified,
    userEmail,
    userPostAccess,
    userIsAdmin,
    fetchedUserAdminStatus,
    userCartItems,
    currentSessionId,
    viewportDimensions,
    notifyUserToSignUpAfterAnnotationCreationAttempt,
    stickyPostBannerActive,
    signingInOut,
    queueRevealBuyButtons,
    setStickyPostBannerActive,
    setCursorSigns,
    onCursorEnter,
    onCursorLeave,
    setInputFocused,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt,
    setSnackbarData,
    setSigningInOut,
    setRefreshNotifications,
    setIsUserProfileDialogExpanded,
    setQueueRevealBuyButtons,
    setUserCartItems,
}: Props): JSX.Element {
    // ===== General Constants =====
    const useStripeTestMode = isTestMode();
    const shouldShowPreviewDebugLogs = (
        useStripeTestMode
        || process.env.REACT_APP_ENABLE_PREVIEW_DEBUG_LOGS === 'true'
    );
    // Keep reader-resume console logging behind one shared toggle so it can be
    // enabled from query params, localStorage, or env without touching code.
    const shouldEmitReaderResumeConsoleLogs = useMemo(() => {
        // Production builds never emit reader-resume console logs, even if a
        // manual debug flag is present in the URL or localStorage.
        if (process.env.NODE_ENV === 'production') {
            return false;
        }

        if (typeof window === 'undefined') {
            return shouldEnableReaderResumeConsoleLogs({
                envValue: process.env.REACT_APP_ENABLE_READER_RESUME_LOGS,
            });
        }

        let storedValue: string | null = null;
        try {
            storedValue = window.localStorage.getItem(READER_RESUME_CONSOLE_LOG_STORAGE_KEY);
        } catch (_error) {
            storedValue = null;
        }

        return shouldEnableReaderResumeConsoleLogs({
            search: window.location.search,
            storedValue,
            envValue: process.env.REACT_APP_ENABLE_READER_RESUME_LOGS,
        });
    }, []);
    const shouldEmitReaderNudgeConsoleLogs = useMemo(() => {
        if (process.env.NODE_ENV === 'production') {
            return false;
        }

        if (typeof window === 'undefined') {
            return shouldEnableReaderNudgeConsoleLogs({
                envValue: process.env.REACT_APP_ENABLE_READER_NUDGE_LOGS,
            });
        }

        let storedValue: string | null = null;
        try {
            storedValue = window.localStorage.getItem(READER_NUDGE_CONSOLE_LOG_STORAGE_KEY);
        } catch (_error) {
            storedValue = null;
        }

        return shouldEnableReaderNudgeConsoleLogs({
            search: window.location.search,
            storedValue,
            envValue: process.env.REACT_APP_ENABLE_READER_NUDGE_LOGS,
        });
    }, []);
    const shouldEmitInternalPostLinkConsoleLogs = useMemo(() => {
        if (process.env.NODE_ENV === 'production') {
            return false;
        }

        if (typeof window === 'undefined') {
            return shouldEnableInternalPostLinkConsoleLogs({
                envValue: process.env.REACT_APP_ENABLE_INTERNAL_POST_LINK_LOGS,
            });
        }

        let storedValue: string | null = null;
        try {
            storedValue = window.localStorage.getItem(INTERNAL_POST_LINK_CONSOLE_LOG_STORAGE_KEY);
        } catch (_error) {
            storedValue = null;
        }

        return shouldEnableInternalPostLinkConsoleLogs({
            search: window.location.search,
            storedValue,
            envValue: process.env.REACT_APP_ENABLE_INTERNAL_POST_LINK_LOGS,
        });
    }, []);
    // Read-mode annotation tracing is env-only so production debugging can be
    // turned on for a deployment without broadening the shared debug surface.
    const shouldEmitReaderAnnotationConsoleLogs = (
        process.env.REACT_APP_ENABLE_READER_ANNOTATION_LOGS === 'true'
    );
    const useDocumentScroll = detectTouchDevice(document);

    const ANNOTATION_EDITOR_WIDTH = 15.9375 * BODY_FONT_SIZE;
    const DEFAULT_ANNOTATION_SECTION_WIDTH = 18.75 * BODY_FONT_SIZE;
    const MIN_ANNOTATION_SECTION_WIDTH = 10 * BODY_FONT_SIZE;
    const MIN_POST_SECTION_WIDTH = 40.625 * BODY_FONT_SIZE;
    const DETAIL_VIEW_BACKGROUND = 'inherit';
    const TITLE_INPUT_FONT_WEIGHT = 43.75 * BODY_FONT_SIZE;
    const SUBTITLE_INPUT_FONT_WEIGHT = 31.25 * BODY_FONT_SIZE;
    const TITLE_INPUT_MARGIN_TOP = 1.25 * BODY_FONT_SIZE;
    const SUBTITLE_INPUT_MARGIN_TOP = 1.25 * BODY_FONT_SIZE;
    const SNACKBAR_MESSAGE_FILE_ACCEPT = (multipleFiles: boolean): string => `Drop file${multipleFiles ? 's' : ''} in post.`;
    const SNACKBAR_MESSAGE_FILE_REJECT = (multipleFiles: boolean): string => `File type ${multipleFiles ? 's' : ''} not supported.`;
    const POST_TITLE_FONT_MULTIPLIER = (READER_VIEW_PERFECT_FOURTH_FONT_MULTIPLIER) ** 3 * READER_VIEW_PARAGRAPH_TEXT_FONT_MULTIPLIER;
    const POST_SUBTITLE_FONT_MULTIPLIER = 1.2;
    const POST_METADATA_FONT_MULTIPLIER = 0.8;
    const POST_TITLE_PLACEHOLDER_WIDTH_LARGE = 18.75 * BODY_FONT_SIZE;
    const POST_TITLE_PLACEHOLDER_WIDTH_MEDIUM = 15.625 * BODY_FONT_SIZE;
    const POST_TITLE_PLACEHOLDER_WIDTH_SMALL = 12.5 * BODY_FONT_SIZE;
    const POST_TITLE_PLACEHOLDER_WIDTH_EXTRA_SMALL = 6.25 * BODY_FONT_SIZE;
    // Max width of placehoder boxes for post text
    const POST_PLACEHOLDER_BOX_MAX_WIDTH = 35 * DEFAULT_FONT_SIZE;
    const POST_SUBTITLE_PLACEHOLDER_WIDTH_LARGE = POST_PLACEHOLDER_BOX_MAX_WIDTH;
    const POST_SUBTITLE_PLACEHOLDER_WIDTH_MEDIUM = POST_SUBTITLE_PLACEHOLDER_WIDTH_LARGE - 8.5 * BODY_FONT_SIZE;
    const POST_SUBTITLE_PLACEHOLDER_WIDTH_SMALL = POST_SUBTITLE_PLACEHOLDER_WIDTH_MEDIUM - 8.5 * BODY_FONT_SIZE;
    const POST_SUBTITLE_PLACEHOLDER_WIDTH_EXTRA_SMALL = POST_SUBTITLE_PLACEHOLDER_WIDTH_SMALL - 5.5 * BODY_FONT_SIZE;
    const POST_AUTHOR_NAME_PLACEHOLDER_WIDTH = 6.25 * BODY_FONT_SIZE;
    const POST_PUBLISHED_PLACEHOLDER_WIDTH = 3.125 * BODY_FONT_SIZE;
    const POST_VIEWS_PLACEHOLDER_WIDTH = 1.875 * BODY_FONT_SIZE;
    const POST_BANNER_MINIMUM_VIEWS_TO_DISPLAY = 1000;
    const STICKY_POST_BANNER_HEIGHT = 3.125 * BODY_FONT_SIZE;
    const DEFAULT_STICKY_BANNER_THRESHOLD = POST_BANNER_HEIGHT - STICKY_POST_BANNER_HEIGHT;
    const SNACKBAR_MESSAGE_FETCH_IMAGE_ERROR = 'There was a problem fetching post image.';
    const POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED = 0.625 * BODY_FONT_SIZE;
    const POST_BANNER_LEFT_CONTAINER_PADDING_CONTRACTED = 0.3125 * BODY_FONT_SIZE;
    const POST_THUMBNAIL_HEIGHT_EXPANDED = POST_BANNER_HEIGHT - 2 * POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED;
    const POST_THUMBNAIL_WIDTH_EXPANDED = 12.5 * BODY_FONT_SIZE;
    const POST_THUMBNAIL_HEIGHT_CONTRACTED = 2.5 * BODY_FONT_SIZE;
    const POST_THUMBNAIL_WIDTH_CONTRACTED = (POST_THUMBNAIL_HEIGHT_CONTRACTED / POST_THUMBNAIL_HEIGHT_EXPANDED) * POST_THUMBNAIL_WIDTH_EXPANDED;
    const POST_THUMBNAIL_BOTTOM_EXPANDED = 0;
    const POST_THUMBNAIL_BOTTOM_CONTRACTED = 0;
    const POST_THUMBNAIL_BORDER_RADIUS_EXPANDED = 0.3125 * BODY_FONT_SIZE;
    const POST_THUMBNAIL_BORDER_RADIUS_CONTRACTED = 0.15625 * BODY_FONT_SIZE;
    const POST_BANNER_LEFT_CONTAINER_HEIGHT_EXPANDED = POST_THUMBNAIL_HEIGHT_EXPANDED + 2 * POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED;
    const POST_BANNER_LEFT_CONTAINER_HEIGHT_CONTRACTED = POST_THUMBNAIL_HEIGHT_CONTRACTED + 2 * POST_BANNER_LEFT_CONTAINER_PADDING_CONTRACTED;
    const AVATAR_LENGTH = 1.75 * BODY_FONT_SIZE;
    const CONTENTS_TABLE_TOP_CONTRACTED = STICKY_POST_BANNER_HEIGHT;
    const LOCKED_CONTAINER_MARGIN = CONTENTS_TABLE_MARGIN;
    const ANNOTATION_VIEW_SMALL_VIEWPORT_WIDTH = 4.0625 * BODY_FONT_SIZE;
    const ZOOM_LEVEL_BUTTON_LENGTH = 4.375 * BODY_FONT_SIZE;
    const ZOOM_LEVEL_ITEM_WIDTH = 15.625 * BODY_FONT_SIZE;
    const ZOOM_LEVEL_ITEM_HEIGHT = 2.1875 * BODY_FONT_SIZE;
    const ANNOTATION_VISIBILITY_BUTTON_LENGTH = 2.2 * BODY_FONT_SIZE;
    const ANNOTATION_VISIBILITY_BUTTON_DEFAULT_BOTTOM_OFFSET = 0.5 * BODY_FONT_SIZE;
    const ANNOTATION_VISIBILITY_BUTTON_RIGHT_OFFSET = 0.5 * BODY_FONT_SIZE;
    const ANNOTATION_VISIBILITY_BUTTON_GAP = 0.625 * BODY_FONT_SIZE;
    const POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER = 1.5 * BODY_FONT_SIZE;
    const POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER_SMALL = 0.5 * BODY_FONT_SIZE;
    // Value of margin top for PostContent when composing a post
    const POST_CONTENT_MARGIN_TOP_WRITE = 3.125 * BODY_FONT_SIZE;
    // Snackbar message when user tries to upload a new post thumbnail while another upload is in progress
    const SNACKBAR_MESSAGE_EXISTING_AVATAR_UPLOAD = 'Unable to upload new image while uploading another.';
    const SNACKBAR_MESSAGE_HIDE_ANNOTATIONS_ENABLED = 'Annotations hidden.';
    const SNACKBAR_MESSAGE_HIDE_ANNOTATIONS_DISABLED = 'Annotations shown.';
    const SNACKBAR_MESSAGE_HIDE_ANNOTATIONS_SYNC_FAILURE = 'Could not update annotation visibility. Please try again.';
    const HIDE_ANNOTATIONS_SYNC_TIMEOUT = 5 * MILLISECONDS_IN_A_SECOND;
    const HIDE_ANNOTATIONS_FEEDBACK_DEBOUNCE_DURATION = 0.25 * MILLISECONDS_IN_A_SECOND;
    // Height of post bottom buffer
    const POST_EDITOR_BUFFER_HEIGHT = 18.75 * BODY_FONT_SIZE;
    // Total number of zoom levels
    const TOTAL_ZOOM_LEVELS = 5;
    // Width of Web Book Download Button within Post Metadata Container
    const WEB_DOWNLOAD_BUTTON_WIDTH_SMALL = 8 * BODY_FONT_SIZE;
    // Width of the longer "About the Book" line button within Post Metadata Container.
    const ABOUT_BOOK_BUTTON_WIDTH_SMALL = 9.5 * BODY_FONT_SIZE;
    // Height of Book Download Button within Post Metadata Container
    const DOWNLOAD_BUTTON_HEIGHT_SMALL = 1.875 * BODY_FONT_SIZE;
    // Width of Web Book Download Button at corner of Post Banner when Sticky Banner is Active
    const WEB_DOWNLOAD_BUTTON_WIDTH_REGULAR = 8.5 * BODY_FONT_SIZE;
    // Height of Book Download Button at corner of Post Banner when Sticky Banner is Active
    const DOWNLOAD_BUTTON_HEIGHT_REGULAR = 2.125 * BODY_FONT_SIZE;
    const POST_BANNER_PURCHASE_ACTION_VIEWPORT_PADDING = 2 * BODY_FONT_SIZE;
    const POST_BANNER_PURCHASE_ACTIONS_FIXED_WIDTH_SMALL = ABOUT_BOOK_BUTTON_WIDTH_SMALL
        + (2 * WEB_DOWNLOAD_BUTTON_WIDTH_SMALL)
        + (2 * POST_BANNER_PURCHASE_ACTION_GAP);
    const postBannerPurchaseActionsContentWidthThreshold = Math.max(
        POST_SUPER_SMALL_VIEWPORT_THRESHOLD,
        POST_BANNER_PURCHASE_ACTIONS_FIXED_WIDTH_SMALL + POST_BANNER_PURCHASE_ACTION_VIEWPORT_PADDING,
    );
    const postBannerPurchaseButtonsUseContentWidth = (
        viewportDimensions.width <= postBannerPurchaseActionsContentWidthThreshold
    );
    const postBannerAboutButtonWidthSmall = postBannerPurchaseButtonsUseContentWidth
        ? undefined
        : ABOUT_BOOK_BUTTON_WIDTH_SMALL;
    const postBannerPurchaseButtonWidthSmall = postBannerPurchaseButtonsUseContentWidth
        ? undefined
        : WEB_DOWNLOAD_BUTTON_WIDTH_SMALL;
    const postBannerPurchaseButtonWidthRegular = postBannerPurchaseButtonsUseContentWidth
        ? undefined
        : WEB_DOWNLOAD_BUTTON_WIDTH_REGULAR;
    const BOOK_ABOUT_DIALOG_WIDTH = 40 * BODY_FONT_SIZE;
    const BOOK_ABOUT_BUTTON_COLOR = themeObj.color.cream300;
    const STICKY_CTA_NUDGE_MESSAGE_SHARE_EMAIL_AUTH_LOCKED = 'Join our mailing list to read the chapter in full.';
    const STICKY_CTA_NUDGE_MESSAGE_BUY_BOOK = 'You\'re on a roll! Get the book to continue reading the full version.';
    // eslint-disable-next-line max-len
    const STICKY_CTA_NUDGE_MESSAGE_ANNOTATE = 'Enjoying the chapter? Share your thoughts with other readers by highlighting some text and leaving a short note.';
    // Snackbar message when attempt to add digital book to cart when physical book already in it
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_WITH_PHYSICAL_BOOK = 'Removed web book chapters and time passes from your cart. Physical preorder includes full web-book access.';
    // Snackbar message when attempt to add web book to cart when digital book already in it
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_WITH_DIGITAL_BOOK = 'Removed web book chapters and time passes from your cart. PDF preorder includes full web-book access.';
    // Snackbar message when attempt to add web book to cart when web book chapters and/or time passes already in it
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_CHAPTERS_WITH_WEB_BOOK = 'Removed web book chapters and time-based passes from cart because it is included with a purchase of the web book.';
    // Snackbar message when encounter cart item staging error
    const SNACKBAR_MESSAGE_ADD_CART_ITEM_TO_CART_ERROR = 'Encountered a problem adding item to cart. Please try again.';
    // Snackbar message when attempt to add web book chapter to cart when physical book already purchased
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PURCHASE_PHYSICAL_BOOK_ERROR = 'Already purchased a physical book which includes access to all the web book chapters.';
    // Snackbar message when attempt to add web book chapter to cart when digital book already purchased
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PURCHASE_DIGITAL_BOOK_ERROR = 'Already purchased a PDF book copy which includes access to all the web book chapters.';
    // Snackbar message when attempt to add web book chapter to cart when web book already purchased
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PURCHASE_WEB_BOOK_ERROR = 'Already purchased a web book copy which includes access to all the web book chapters.';
    // Snackbar message when attempt to add web book chapter to cart when already purchased it
    const SNACKBAR_MESSAGE_REPURCHASE_WEB_BOOK_CHAPTER_ERROR = 'Already purchased access to the web book chapter.';
    // Snackbar message when attempt to add web book chapter to cart when physical book already in cart
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PHYSICAL_BOOK_ALREADY_IN_CART_ERROR = 'Physical book already in cart. Item includes access to the web book chapter.';
    // Snackbar message when attempt to add web book chapter to cart when digital book already in cart
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_DIGITAL_BOOK_ALREADY_IN_CART_ERROR = 'PDF book already in cart. Item includes access to the web book chapter.';
    // Snackbar message when attempt to add web book chapter to cart when web book already in cart
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_WEB_BOOK_ALREADY_IN_CART_ERROR = 'Web book already in cart. Item includes access to the web book chapter.';
    // Snackbar message when attempt to add multiple copy of web book chapter
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_MULTIPLE_WEB_BOOK_CHAPTER_TO_CART_ERROR = 'Only one copy of the same digital item may be added to your cart.';
    // Snackbar message when attempt to add multiple copy of web book day pass
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_MULTIPLE_WEB_BOOK_DAY_PASS_TO_CART_ERROR = 'Only one copy of the same digital item may be added to your cart.';
    // Snackbar message when attempt to add multiple copy of web book week pass
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ADD_MULTIPLE_WEB_BOOK_WEEK_PASS_TO_CART_ERROR = 'Only one copy of the same digital item may be added to your cart.';
    // Snackbar message when attempt to add web book week pass to cart when web book day pass already in it
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_DAY_PASS_WITH_WEB_BOOK_WEEK_PASS = 'Removed web book day pass from cart because web book week pass is longer!';
    // Snackbar message when attempt to update a post but important information is missing
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_UPDATE_POST_ERROR = `Encountered a problem updating post. Missing information necessary to initiate update. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar message when attempt to fetch post but no data received
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_READ_POST_ERROR = `Encountered a problem fetching post. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar message when attempt to write post but not an admin
    const SNACKBAR_MESSAGE_WRITE_POST_ERROR = 'Encountered a problem fetching post to edit. Only admins have edit permissions.';
    // Snackbar message when attempt to create post
    const SNACKBAR_MESSAGE_CREATE_POST_ERROR = 'Encountered a problem creating post.';
    const SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_ERROR = 'Please enter a valid email address.';
    const SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_IN_USE_ERROR = (
        'An account already exists with this email. '
        + 'Please sign in with the method linked to this account or use another email address.'
    );
    const SNACKBAR_MESSAGE_AUTH_LOCKED_CONTAINER_SUCCESS = 'You\'re now signed in and can continue reading. Returning you to where you left off.';
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_CONVERSION_ERROR = `Unable to add you to the mailing list right now. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_PRECHECK_ERROR = `Unable to verify this email right now. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    const SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_USER_NOT_FOUND_ERROR = (
        'We could not find your guest reading session while creating your account. '
        + `Please contact support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_UNAUTHENTICATED_ERROR = (
        'Your session expired while creating your account. Please refresh and try again.'
    );
    const SNACKBAR_MESSAGE_INTERNAL_POST_LINK_PREVIEW_LIMIT = 'That part is outside the preview. Buy the book to jump there.';
    const SNACKBAR_MESSAGE_INTERNAL_POST_LINK_AUTH_LOCKED_LIMIT = 'You\'ll need to sign in or join our mailing list to jump to that part.';
    const AUTH_LOCK_SOCIAL_PROVIDER_TYPES = new Set<AUTHENTICATION_TYPE>([
        AUTHENTICATION_TYPE.google,
        AUTHENTICATION_TYPE.facebook,
        AUTHENTICATION_TYPE.github,
        AUTHENTICATION_TYPE.xTwitter,
    ]);
    const AUTH_LOCK_EXISTING_EMAIL_ERROR_CODES = new Set<string>([
        EMAIL_CONVERSION_CALLABLE_ERROR_CODE.alreadyExists,
        AUTHENTICATION_ERROR_CODE.emailAlreadyInUse,
        AUTHENTICATION_ERROR_CODE.emailAlreadyExists,
        AUTHENTICATION_ERROR_CODE.accountExistsWithDifferentCredential,
        AUTHENTICATION_ERROR_CODE.credentialAlreadyInUse,
    ]);
    // Snackbar message when annotation create error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ANNOTATION_CREATE_ERROR = `Encountered a problem creating annotation. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Snackbar message when annotation delete error occurs
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_ANNOTATION_DELETE_ERROR = `Encountered a problem delete annotation. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`;
    // Width of Choose Your Own Adventure Modal
    const CHARACTER_MODAL_WIDTH = 50 * BODY_FONT_SIZE;
    // Length of post placeholder box horizontal margin
    const POST_PLACEHOLDER_BOX_HORIZTONAL_MARGIN = 1.25 * BODY_FONT_SIZE;
    // Length of post placeholder box vertical margin
    const POST_PLACEHOLDER_BOX_VERTICAL_MARGIN = 0.3125 * BODY_FONT_SIZE;
    // Length of post placeholder block (five boxes) margin
    const POST_PLACEHOLDER_BLOCK_VERTICAL_MARGIN = 1.875 * BODY_FONT_SIZE;
    const POST_PLACEHOLDER_BOXES_PER_BLOCK = 5;
    const POST_PLACEHOLDER_MIN_BOX_COUNT = 15;
    // Height of paywall action button
    const PAYWALL_ACTION_BUTTON_HEIGHT = 2.5 * BODY_FONT_SIZE;
    // Width of paywall action
    const PAYWALL_ACTION_BUTTON_WIDTH = 10 * BODY_FONT_SIZE;
    const COMPACT_PURCHASE_CTA_HEIGHT = 2.25 * BODY_FONT_SIZE;
    const COMPACT_PURCHASE_CTA_WIDTH = 15.5 * BODY_FONT_SIZE;
    const stickyPurchaseIsNarrow = viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min;
    const compactPurchaseButtonWidth = stickyPurchaseIsNarrow
        ? Math.max(
            12.25 * BODY_FONT_SIZE,
            Math.min(
                15.5 * BODY_FONT_SIZE,
                viewportDimensions.width - (4.75 * BODY_FONT_SIZE),
            ),
        )
        : COMPACT_PURCHASE_CTA_WIDTH;
    const compactPurchaseCloseButtonWidth = (
        stickyPurchaseIsNarrow
            ? Math.max(
                5 * BODY_FONT_SIZE,
                compactPurchaseButtonWidth - (7 * BODY_FONT_SIZE),
            )
            : compactPurchaseButtonWidth
    );
    const compactAuthLockedCollapsedButtonWidth = 8.25 * BODY_FONT_SIZE;
    const compactInlineMoreOptionsButtonWidth = stickyPurchaseIsNarrow
        ? compactPurchaseButtonWidth
        : 8 * BODY_FONT_SIZE;
    const compactInlineHaggleButtonWidth = compactPurchaseButtonWidth;
    // Reader progress write/restore tuning values.
    const READER_PROGRESS_PERSIST_DEBOUNCE_DURATION = 2 * MILLISECONDS_IN_A_SECOND;
    const READER_PROGRESS_PERSIST_MIN_SCROLL_DELTA = 48;
    const READER_PROGRESS_RESUME_HIDE_SCROLL_THRESHOLD = 12;
    // Ignore near-top positions so resume CTA does not appear for "just opened" reads.
    const READER_PROGRESS_RESUME_MIN_SCROLL_TOP = 120;
    const READER_PROGRESS_ANCHOR_VIEWPORT_OFFSET_RATIO = 0.3;
    const READER_PROGRESS_LAYOUT_STABILITY_QUIET_DURATION = 0.35 * MILLISECONDS_IN_A_SECOND;
    const READER_PROGRESS_LAYOUT_STABILITY_SCROLL_MAX_DELTA = 2;
    const INTERNAL_POST_LINK_TARGET_MOUNT_WAIT_TIMEOUT = 1.5 * MILLISECONDS_IN_A_SECOND;
    const READER_PROGRESS_RESUME_CORRECTION_MIN_SCROLL_DELTA = 20;
    // Local storage fallback for resume progress (scoped to one effective user id).
    const LOCAL_READER_PROGRESS_STORAGE_KEY = 'verascope-reader-progress-v1';
    const LOCAL_READER_PROGRESS_MAX_ENTRIES = 60;
    // Session handoff key for auth-lock conversion so resume survives auth refresh cycles.
    const AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY = 'verascope-auth-lock-auto-resume-v1';
    const AUTH_LOCK_AUTO_RESUME_PENDING_MAX_AGE = 10 * MILLISECONDS_IN_A_MINUTE;
    // Sticky purchase CTA can occupy dynamic vertical space on mobile; keep resume button above it.
    const STICKY_PURCHASE_CTA_BOTTOM_OFFSET = 1 * BODY_FONT_SIZE;
    const RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET = 3.25 * BODY_FONT_SIZE;
    const RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET = 0.5 * BODY_FONT_SIZE;
    const RESUME_READER_POSITION_BUTTON_EXTRA_SMALL_GAP = 0.75 * BODY_FONT_SIZE;
    const FLOATING_AUDIO_PLAYER_GAP = 0.75 * BODY_FONT_SIZE;
    const READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_RATIO = 0.5;
    const READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_PIXELS = 2 * BODY_FONT_SIZE;
    const hasReaderPageParam = (nextSearchParams: URLSearchParams): boolean => (
        nextSearchParams.has(READER_PARAMS_TYPE.page)
    );
    const getReaderPageParam = (nextSearchParams: URLSearchParams): string | null => (
        nextSearchParams.get(READER_PARAMS_TYPE.page)
    );
    const hasReaderPathScopeSearchParams = (nextSearchParams: URLSearchParams): boolean => (
        hasReaderPageParam(nextSearchParams)
        || nextSearchParams.has(READER_PARAMS_TYPE.section)
        || nextSearchParams.has(READER_PARAMS_TYPE.chapterId)
        || nextSearchParams.has(READER_PARAMS_TYPE.sectionId)
    );
    const RESUME_READER_POSITION_BUTTON_HEIGHT = 2 * BODY_FONT_SIZE;
    const RESUME_READER_POSITION_BUTTON_WIDTH = Math.max(
        8.5 * BODY_FONT_SIZE,
        Math.min(9.5 * BODY_FONT_SIZE, viewportDimensions.width - (2 * BODY_FONT_SIZE)),
    );
    const RESUME_READING_DURATION = 0.2 * MILLISECONDS_IN_A_SECOND;
    const POST_METADATA_UPDATE_DEBOUNCE_DURATION = 500;

    // ===== Refs =====

    const containerRef = useRef<HTMLInputElement>(null);
    const annotationEditorRef = useRef<HTMLDivElement | null>(null);
    const postEditorContainerRef = useRef<HTMLDivElement | null>(null);
    const titleInputRef = useRef<HTMLInputElement | null>(null);
    const subtitleInputRef = useRef<HTMLInputElement | null>(null);
    const pendingPostMetadataUpdateTimeoutsRef = useRef<Record<string, number>>({});
    const pendingPostMetadataUpdateValuesRef = useRef<Record<string, string>>({});
    const authLockedEmailInputRef = useRef<HTMLInputElement | null>(null);
    const postBannerRef = useRef<HTMLDivElement | null>(null);
    const stickyPurchaseCtaRef = useRef<HTMLDivElement | null>(null);
    const stickyPurchaseHeaderRef = useRef<HTMLDivElement | null>(null);
    const stickyPurchaseContentRef = useRef<HTMLDivElement | null>(null);
    const authLockBannerRef = useRef<HTMLDivElement | null>(null);
    const paywallBannerRef = useRef<HTMLDivElement | null>(null);
    const buyBannerRef = useRef<HTMLDivElement | null>(null);
    const postContentContainerRef = useRef<HTMLDivElement | null>(null);
    const contentsTableRef = useRef<HTMLDivElement | null>(null);
    const contentsTableProgressBarRef = useRef<HTMLDivElement | null>(null);
    const contentsTableProgressBarContainerRef = useRef<HTMLDivElement | null>(null);
    const postBannerLeftContainerRef = useRef<HTMLDivElement | null>(null);
    const postThumbnailRef = useRef<HTMLDivElement | null>(null);
    const contentsTableToggleButtonContainerRef = useRef<HTMLDivElement | null>(null);
    const postScrollTop = useRef<number>(0);
    const pendingScrollTopRef = useRef<number | null>(null);
    const scrollRafRef = useRef<number | null>(null);
    const annotationEditorPositionRef = useRef<ICoord>({ x: 0, y: 0 });
    const annotationEditorScrollRafRef = useRef<number | null>(null);
    const annotationEditorDraftValueRef = useRef<Descendant[] | null>(null);
    const updateVisibleAnnotationEditorPositionRef = useRef<() => boolean>(() => false);
    const dockedAnnotationRevealRafRef = useRef<number | null>(null);
    const handledDockedAnnotationQuoteIdRef = useRef<string | null>(null);
    const pendingSecondaryScrollTopRef = useRef<number | null>(null);
    const secondaryScrollRafRef = useRef<number | null>(null);
    const lastSecondaryScrollUpdateRef = useRef<number>(0);
    const lastContentsProgressRef = useRef<string>('');
    const annotationHighlightRef = useRef<HTMLElement | null>(null);
    const annotationEditorPlacementRef = useRef<AnnotationEditorPlacement>('top');
    const annotationEditorSelectionSpansMultipleLinesRef = useRef<boolean>(false);
    const annotationEditorSelectionIsBackwardRef = useRef<boolean>(false);
    const stickyDeactivateTimeoutRef = useRef<number | null>(null);
    const stickyLockRecheckTimeoutRef = useRef<number | null>(null);
    const stickyPurchaseForceShowResetTimeoutRef = useRef<number | null>(null);
    const stickyPostBannerActiveRef = useRef<boolean>(stickyPostBannerActive);
    const stickyRevealHoldUntilRef = useRef<number>(0);
    const lastTrackedPostScrollTopRef = useRef<number>(0);
    const stickyLockUntilRef = useRef<number>(0);
    const activeReadOnlyAudioNoteVisibilityRafRef = useRef<number | null>(null);
    const scrollWillChangeTimeoutRef = useRef<number | null>(null);
    const scrollWillChangeActiveRef = useRef<boolean>(false);
    const currentPostParagraphCountRef = useRef<number>(0);
    const hideAnnotationsSyncTimeoutRef = useRef<number | null>(null);
    const hideAnnotationsFeedbackTimeoutRef = useRef<number | null>(null);
    const hideAnnotationsRequestIdRef = useRef<number>(0);
    const hideAnnotationsWriteInFlightRef = useRef<boolean>(false);
    const inFlightHideAnnotationsTargetRef = useRef<boolean | null>(null);
    const queuedHideAnnotationsRef = useRef<boolean | null>(null);
    const annotationsHiddenValueRef = useRef<boolean>(false);
    const zoomLevelButtonRef = useRef<HTMLElement | null>(null);
    const previousUserAuthenticationTypeRef = useRef<AUTHENTICATION_TYPE | null>(null);
    const previousSigningInOutRef = useRef<boolean>(signingInOut);
    // Keeps last-known chapter/section badge counts stable while local annotation state
    // is cleared/refetched during navigation.
    const cachedChapterAnnotationCountsRef = useRef<Map<string, number>>(new Map());
    const cachedSectionAnnotationCountsRef = useRef<Map<string, number>>(new Map());
    // Keeps post-banner annotation count stable per post id to avoid hide/show flicker
    // when critical payloads temporarily omit annotation arrays.
    const cachedPostBannerAnnotationCountRef = useRef<number>(0);
    const cachedPostBannerAnnotationPostIdRef = useRef<string | null>(null);
    // Debounce + dedupe helpers for reader position persistence.
    const readerProgressPersistTimeoutRef = useRef<number | null>(null);
    const readerProgressLastPersistedTopRef = useRef<Map<string, number>>(new Map());
    const readerProgressLastSubmittedRef = useRef<Map<string, IReaderChapterProgressItem>>(new Map());
    const readerProgressPersistenceBlockedRef = useRef<boolean>(false);
    // Keeps button callbacks reading latest resume target without stale closure issues.
    const resumeReaderProgressRef = useRef<{
        key: string,
        scrollTop: number,
        scrollProgress: number,
        anchorParagraphId?: string,
        anchorParagraphIndex?: number,
        anchorParagraphOffsetProgress?: number,
    } | null>(null);
    const resumeReaderProgressEligibilityWaitCancelRef = useRef<(() => void) | null>(null);
    const resumeReaderProgressActionWaitCancelRef = useRef<(() => void) | null>(null);
    const resumeReaderProgressCorrectionTimeoutRef = useRef<number | null>(null);
    const resumeReaderProgressRequestIdRef = useRef<number>(0);
    const resumeReaderProgressSourceRef = useRef<READER_RESUME_SOURCE>(READER_RESUME_SOURCE.unspecified);
    const resumeReaderProgressAwaitingStabilityRef = useRef<boolean>(false);
    const resumeReaderProgressAwaitingGeometryRef = useRef<boolean>(false);
    const authLockAutoResumeRetryTimeoutRef = useRef<number | null>(null);
    // Auth-lock auto-resume state machine:
    // pending -> auth complete -> optional post/path refresh -> resume execution.
    const authLockAutoResumePendingRef = useRef<boolean>(false);
    const authLockAutoResumeAwaitingPostRefreshRef = useRef<boolean>(false);
    const authLockAutoResumeSawPostRefreshRef = useRef<boolean>(false);
    const authLockAutoResumeRequestedPostRefreshRef = useRef<boolean>(false);
    const authLockAutoResumeAuthCompletedRef = useRef<boolean>(false);
    const authLockContentHydrationRefreshKeyRef = useRef<string | null>(null);
    const authLockContentHydrationRefreshRequestIdRef = useRef<number>(0);
    const authLockAutoResumeAwaitingPathRefreshRef = useRef<boolean>(false);
    const authLockAutoResumeSawPathRefreshRef = useRef<boolean>(false);
    const authLockAutoResumeTargetRef = useRef<{
        postId: string,
        path: number[],
        progress: IReaderChapterProgressItem,
        source: READER_PROGRESS_SOURCE,
    } | null>(null);
    // Tracks local-progress cache ownership to prevent leakage between accounts.
    const localReaderProgressOwnerUserIdRef = useRef<string | null>(null);
    // Temporarily disable anon fallback after sign-out until this anon session writes fresh progress.
    const suppressAnonymousLocalResumeFallbackRef = useRef<boolean>(false);
    const currentPostEngagementRefreshBaselineRef = useRef<{
        scopeKey: string | null,
        visibleAnnotationCount: number | null,
        deletedAnnotationCount: number | null,
    }>({
        scopeKey: null,
        visibleAnnotationCount: null,
        deletedAnnotationCount: null,
    });

    // ----- Sound Clips
    const handlebarGraspClip = useLazyAudioRef({
        src: ObjectGrasp,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const resizeConstraintThumpClip = useLazyAudioRef({
        src: ObjectThump,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const inputClickClip = useLazyAudioRef({
        src: InputClick,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const swooshInClip = useLazyAudioRef({
        src: SwooshIn,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const swooshOutClip = useLazyAudioRef({
        src: SwooshOut,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const tooltipEnterClip = useLazyAudioRef({
        src: TooltipEnter,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const buttonHoverClip = useLazyAudioRef({
        src: ButtonHover,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const successClip = useLazyAudioRef({
        src: Success,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const toggleSnapClip = useLazyAudioRef({
        src: ToggleSnap,
        volume: DEFAULT_AUDIO_VOLUME,
    });

    // ----- Views

    const detailViewRef = useRef<HTMLDivElement>(null);
    const detachedAnnotationsPanelRef = useRef<HTMLDivElement>(null);
    const detachedAnnotationsPanelPinAnimationFrameOneRef = useRef<number | null>(null);
    const detachedAnnotationsPanelPinAnimationFrameTwoRef = useRef<number | null>(null);

    // ===== React Router =====

    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const readPageAnnotationMatch = useMatch(`${PAGE_ROUTE.book}/:annotationId`);
    const annotationIsFocused = readPageAnnotationMatch;
    const [searchParams] = useSearchParams();
    const latestReaderPathnameRef = useRef<string>(location.pathname);
    const latestReaderSearchRef = useRef<string>(searchParams.toString());
    const latestReaderLocationStateRef = useRef<unknown>(location.state);
    latestReaderPathnameRef.current = location.pathname;
    latestReaderSearchRef.current = searchParams.toString();
    latestReaderLocationStateRef.current = location.state;
    const readerBasePath = `/${PAGE_ROUTE.book}`;
    // Annotation deep links normally omit page params, but explicit reader-scope
    // params in the URL should remain authoritative during fresh-load bootstrap.
    const readerUrlShouldIncludePathParams = !params.annotationId
        || hasReaderPathScopeSearchParams(searchParams);
    /**
     * Avoids same-destination navigations so reader bootstrap does not mint a
     * fresh location key and replay route-level view tracking.
     */
    const navigateReaderIfChanged = useCallback((
        nextPathname: string,
        nextSearchParams?: URLSearchParams,
    ): void => {
        const nextSearch = nextSearchParams?.toString() || '';
        if (
            latestReaderPathnameRef.current === nextPathname
            && latestReaderSearchRef.current === nextSearch
        ) {
            return;
        }
        navigate(
            `${nextPathname}${nextSearch.length > 0 ? `?${nextSearch}` : ''}`,
            {
                state: latestReaderLocationStateRef.current,
            },
        );
    }, [navigate]);
    /**
     * Builds the canonical reader query params for a selected path and zoom.
     * Keeping this centralized prevents bootstrap and zoom changes from drifting
     * out of sync when one arrives before the other.
     */
    const buildReaderScopeSearchParams = useCallback(({
        path,
        zoomLevel,
        includeRootZoom,
        includePathParams,
    }: {
        path: number[],
        zoomLevel: ZOOM_LEVEL,
        includeRootZoom: boolean,
        includePathParams: boolean,
    }): URLSearchParams | undefined => {
        if (path.length === 0) {
            if (!includeRootZoom) {
                return undefined;
            }
            return new URLSearchParams([
                [READER_PARAMS_TYPE.zoom.toString(), zoomLevel.toString()],
            ]);
        }

        if (!includePathParams) {
            return new URLSearchParams([
                [READER_PARAMS_TYPE.zoom.toString(), zoomLevel.toString()],
            ]);
        }

        if (path.length === 1) {
            const normalizedZoomLevel = zoomLevel < ZOOM_LEVEL.four
                ? ZOOM_LEVEL.four
                : zoomLevel;
            return new URLSearchParams([
                [READER_PARAMS_TYPE.page.toString(), (path[0] + 1).toString()],
                [READER_PARAMS_TYPE.zoom.toString(), normalizedZoomLevel.toString()],
            ]);
        }

        return new URLSearchParams([
            [READER_PARAMS_TYPE.page.toString(), (path[0] + 1).toString()],
            [READER_PARAMS_TYPE.section.toString(), (path[1] + 1).toString()],
            [READER_PARAMS_TYPE.zoom.toString(), ZOOM_LEVEL.five.toString()],
        ]);
    }, []);
    const shouldIncludeReaderPathParamsForPath = useCallback((path: number[]): boolean => (
        readerUrlShouldIncludePathParams
        || (
            !!params.annotationId
            && path.length > 0
        )
    ), [
        params.annotationId,
        readerUrlShouldIncludePathParams,
    ]);

    // ===== State =====
    /**
     * !!!IMPORTANT!!!
     * When you add a new state variable, make sure to add it to the
     * dependencies of the pageLoadRendersCompleted side effect
     */

    // Stores the current post id
    const [postId, setPostId] = useState<string | null>(BOOK_POST_ID);
    // Indicates whether we're updating post
    const [updatingPost, setUpdatingPost] = useState<boolean>(false);
    // Stores the current post title
    const [title, setTitle] = useState<string>('');
    // Stores the current post subtitle
    const [subtitle, setSubtitle] = useState<string>('');
    // Stores the current post data
    const [post, setPost] = useState<IReadPostItem | undefined>(undefined);
    const [readFetchedPostState, setReadFetchedPostState] = useState<{
        post?: IReadPostItem,
        chapter?: IReadPostChapterItem,
        chapterSection?: IReadPostSectionItem,
    }>({});
    // Stores the root engagement counters for the current post.
    const [rootPostEngagement, setRootPostEngagement] = useState<IPostEngagementItem | null>(null);
    // Stores engagement counters for the currently selected root/chapter/section scope.
    const [currentPostEngagement, setCurrentPostEngagement] = useState<IPostEngagementItem | null>(null);
    const [postEngagementItems, setPostEngagementItems] = useState<Map<string, IPostEngagementItem>>(new Map());
    const [postEngagementFallbackItems, setPostEngagementFallbackItems] = useState<Map<string, IPostEngagementItem>>(new Map());
    const [latestCurrentScopeUpdateEvent, setLatestCurrentScopeUpdateEvent] = useState<IPostUpdateEventItem | null>(null);
    const [currentWriteCollaborativeDoc, setCurrentWriteCollaborativeDoc] = useState<IPostCollabDocItem | null>(null);
    const [localScopeSaveTimestamps, setLocalScopeSaveTimestamps] = useState<Record<string, number>>({});
    const [draftStatusNow, setDraftStatusNow] = useState<number>(Date.now());
    // indicates whether we've fetched post
    const [fetchedPost, setFetchedPost] = useState<boolean>(false);
    // Enables a guarded metadata-only read if auth is ready but user hydration lags.
    const [readFetchAuthFallbackElapsed, setReadFetchAuthFallbackElapsed] = useState<boolean>(false);
    // Tracks whether the visible post shell still needs user-scoped content hydration.
    const [readMetadataOnlyFetchAwaitingUser, setReadMetadataOnlyFetchAwaitingUser] = useState<boolean>(false);
    // Seeds a temporary read zoom when auth is ready but user hydration lags.
    const [readZoomFallbackElapsed, setReadZoomFallbackElapsed] = useState<boolean>(false);
    // Indicates whether we should re-fetch post
    const [refreshPost, setRefreshPost] = useState<boolean>(false);
    // Indicates whether we've currently re-fetching post
    const [refreshingPost, setRefreshingPost] = useState<boolean>(false);
    // Keeps placeholder visible during auth/access upgrades until refreshed content settles.
    const [readAccessUpgradePending, setReadAccessUpgradePending] = useState<boolean>(false);
    // Keeps placeholder visible during sign-out downgrades until preview content returns.
    const [readAccessDowngradePending, setReadAccessDowngradePending] = useState<boolean>(false);
    // Distinguishes authenticated sign-out busy states from anonymous->auth upgrades.
    const [signingInOutStartedFromAuthenticated, setSigningInOutStartedFromAuthenticated] = useState<boolean>(false);
    // Bumps when an in-flight fetch finishes with a queued scope refresh still pending.
    const [queuedPostFetchVersion, setQueuedPostFetchVersion] = useState<number>(0);
    // Tracks which auth session produced the currently rendered read payload.
    const [readFetchedPostAuthSessionKey, setReadFetchedPostAuthSessionKey] = useState<string | null>(null);
    // Stores the user id of the user who last fetched the post
    const [userIdDuringPreviousFetchPost, setUserIdDuringPreviousFetchPost] = useState<string | null>(null);
    // Stores the current post annotation data
    const [annotations, setAnnotations] = useState<Map<string, IAnnotationItem>>(new Map());
    const [annotationEngagementItems, setAnnotationEngagementItems] = useState<Map<string, IAnnotationEngagementItem>>(new Map());
    // Author-only repair state for annotations whose quotes no longer map to current post text.
    const [detachedAnnotationReattachTargetId, setDetachedAnnotationReattachTargetId] = useState<string | null>(null);
    const [detachedAnnotationReattachInFlightId, setDetachedAnnotationReattachInFlightId] = useState<string | null>(null);
    const [detachedAnnotationPendingQuote, setDetachedAnnotationPendingQuote] = useState<IAnnotationQuote | null>(null);
    const [detachedAnnotationsPanelPinOffsetY, setDetachedAnnotationsPanelPinOffsetY] = useState<number>(0);
    const [detachedAnnotationsPanelAnimateToPinnedCenter, setDetachedAnnotationsPanelAnimateToPinnedCenter] = useState<boolean>(false);
    // Tracks optimistic annotations that have not been confirmed by the server yet
    const [pendingAnnotationCreationIds, setPendingAnnotationCreationIds] = useState<Set<string>>(new Set());
    // Tracks immediate fallback top positions for pending annotations while highlight lookup resolves.
    const [pendingAnnotationFallbackTops, setPendingAnnotationFallbackTops] = useState<Map<string, number>>(new Map());
    // Keeps optimistic annotations in the same paragraph bucket as settled annotations
    // before highlight lookup has resolved against the rendered editor DOM.
    const [pendingAnnotationProvisionalParagraphIds, setPendingAnnotationProvisionalParagraphIds] = useState<Map<string, string>>(new Map());
    // Failed optimistic creates that should retry silently in background.
    const [queuedAnnotationRetries, setQueuedAnnotationRetries] = useState<Map<string, {
        postId: string,
        value: string,
        text: string,
        quote: IAnnotationQuote,
        mediaIds: string[],
        zoomLevel: ZOOM_LEVEL,
        chapterId?: string,
        sectionId?: string,
        attempt: number,
        nextRetryAt: number,
        inFlight: boolean,
    }>>(new Map());
    // Indicates whether we've currently fetching annotations
    const [fetchingAnnotations, setFetchingAnnotations] = useState<boolean>(false);
    // Indicates whether we've fetched annotations
    const [fetchedAnnotations, setFetchedAnnotations] = useState<boolean>(false);
    // Tracks which reader scope the current annotation payload belongs to so
    // deep links can refetch after a prior root-scope annotation load.
    const [fetchedAnnotationScopeKey, setFetchedAnnotationScopeKey] = useState<string | null>(null);
    // Stores current selected annotation data
    const [annotationDetailData, setAnnotationDetailData] = useState<IAnnotationItem | null>(null);
    // Stores reference to editor component
    const [postEditor, setPostEditor] = useState<Editor | null>(null);
    // Indicates read-only post editor has initialized its value.
    const [postEditorInitialized, setPostEditorInitialized] = useState<boolean>(false);
    const [postPublishingVisible, setPostPublishingVisible] = useState<boolean>(false);
    const [postAccessVisible, setPostAccessVisible] = useState<boolean>(false);
    const [pendingPostVersionRestore, setPendingPostVersionRestore] = useState<{
        id: string,
        value: string,
    } | null>(null);
    const [postCollaborativeAuthToken, setPostCollaborativeAuthToken] = useState<string | undefined>(undefined);
    // Write mode gate: only reveal post body after contents table is visibly rendered.
    const [writeContentsTableRendered, setWriteContentsTableRendered] = useState<boolean>(!write);
    // Stores reference to annotation editor component
    const [annotationEditor, setAnnotationEditor] = useState<Editor | null>(null);
    // Stores the data of a post quote when text is highlighted by the user
    const [postQuote, setPostQuote] = useState<IAnnotationQuote | null>(null);
    // Indicates whether the post is being saved
    const [postValueIsSaving, setPostValueIsSaving] = useState<boolean>(false);
    // Indicates whether page must be rerendered due to initialization of root components
    const [initializationRerender, setInitializationRerender] = useState<boolean>(false);
    // Stores data of media being uploaded
    const [uploadingMedia, setUploadingMedia] = useState<Map<string, IMediaItem>>(new Map());
    // Indicates whether annotation editor is visible
    const [annotationEditorIsVisible, setAnnotationEditorIsVisible] = useState<boolean>(false);
    // Disables the default overshoot position tween while the reader is actively
    // scrolling so the annotation editor can use a tighter follow animation.
    const [annotationEditorPositionTransitionDisabled, setAnnotationEditorPositionTransitionDisabled] = useState<boolean>(false);
    // Stores the position of the block toolbar
    const [annotationEditorPosition, setAnnotationEditorPosition] = useState<ICoord>({
        y: 0,
        x: 0,
    });
    // the width of the screen (%) occupied by resizable annotation section
    const [annotationViewWidth, setAnnotationViewWidth] = useState<number>(DEFAULT_ANNOTATION_SECTION_WIDTH);
    // Width used for annotation bucket layout; held steady while steering so bucket reflow
    // does not compete with pointer/handle updates mid-drag.
    const [annotationBucketWidth, setAnnotationBucketWidth] = useState<number>(DEFAULT_ANNOTATION_SECTION_WIDTH);
    // Stores the annotation id of the annotation whose annotation bucket should
    // expand if its a stack
    const [expandAnnotationStackWithAnnotationId, setExpandAnnotationStackWithAnnotationId] = useState<string | null>(null);
    // indicates whether detail view is being resized
    const [isSteering, setIsSteering] = useState<boolean>(false);
    // indicates whether the cursor is over the split view handlebar
    const [handlebarHovered, setHandlebarHovered] = useState<boolean>(false);
    // stores the minimum width of the detail view permissible
    const [hitDetailMinimum, setHitDetailMinimum] = useState<boolean>(false);
    // stores the minimum width of the cartridge view permissible
    const [hitPostMinimum, setHitPostMinimum] = useState<boolean>(false);
    // Local optimistic override. `null` means "follow persisted user preference".
    const [annotationsHiddenOverride, setAnnotationsHiddenOverride] = useState<boolean | null>(null);
    // Tracks whether reader is waiting for persisted user preference to reflect latest toggle.
    const [annotationsHiddenPendingSync, setAnnotationsHiddenPendingSync] = useState<boolean>(false);
    const canAccessReaderAnnotations = !!(
        user
        && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
    );
    const remoteAnnotationsHidden = !!user?.hideAnnotations;
    const annotationsHidden = !canAccessReaderAnnotations
        || (annotationsHiddenOverride ?? remoteAnnotationsHidden);
    annotationsHiddenValueRef.current = annotationsHidden;
    // used to update annotation positioning
    const [annotationHighlightsRendered, setAnnotationHighlightsRendered] = useState<boolean>(false);
    const [postBodyPainted, setPostBodyPainted] = useState<boolean>(false);
    const [postBodyPaintVersion, setPostBodyPaintVersion] = useState<number>(0);
    const [pendingInternalPostLinkNavigation, setPendingInternalPostLinkNavigation] = useState<IInternalPostLinkTarget | null>(null);
    const [activeInternalPostLinkNavigationHref, setActiveInternalPostLinkNavigationHref] = useState<string | null>(null);
    const [pendingInternalPostLinkRevealTargetResolution, setPendingInternalPostLinkRevealTargetResolution] = useState<IReadOnlyRevealTargetResolution | null>(null);
    // Forces one extra DOM-backed annotation lookup after PostEditor paints a
    // newly acked canonical annotation. Without this, the canonical row can
    // exist in state before its highlight DOM exists, making it invisible until
    // some unrelated rerender happens.
    const [annotationDomRefreshVersion, setAnnotationDomRefreshVersion] = useState<number>(0);
    const [postEditorRenderedBlockCount, setPostEditorRenderedBlockCount] = useState<number>(0);
    const pendingInternalPostLinkScopeRefreshHrefRef = useRef<string | null>(null);
    const attemptedInternalPostLinkScopeRefreshHrefRef = useRef<string | null>(null);
    const pendingReaderUrlInternalPostLinkTargetRef = useRef<IInternalPostLinkTarget | null>(null);
    const handledReaderUrlInternalPostLinkHrefRef = useRef<string | null>(null);
    // Internal-link debugging needs current scope/value metadata inside
    // earlier callback sites without threading a wide dependency chain through
    // the whole reader.
    const internalPostLinkScopeDebugRef = useRef<{
        source: string | null,
        usesPreviewSource: boolean,
        serializedValue?: string,
        valueNodes?: Descendant[],
    }>({
        source: null,
        usesPreviewSource: false,
        serializedValue: undefined,
        valueNodes: undefined,
    });
    const internalPostLinkAccessStateRef = useRef<{
        currentContentAuthLocked: boolean,
        userIsSignedIn: boolean,
    }>({
        currentContentAuthLocked: false,
        userIsSignedIn: false,
    });
    // indicates which annotation is currently being hovered over
    const [targetAnnotationID, setTargetAnnotationID] = useState<string | null>(null);
    const routeAnnotationHighlightScrollIdRef = useRef<string | null>(null);
    // indicates whether we should remeasure post height
    const [remeasurePostHeight, setRemeasurePostHeight] = useState<boolean>(false);
    // stores url of post image
    const [postImageURL, setPostImageURL] = useState<string | undefined>(undefined);
    // stores storage path of post image
    const [postImagePath, setPostImagePath] = useState<string | undefined>(undefined);
    const handlePostBodyPainted = useCallback((): void => {
        setPostBodyPainted(true);
        setPostBodyPaintVersion((prev) => prev + 1);
    }, []);

    // stores value at which post banner becomes sticky
    const [stickyBannerScrollThreshold, setStickyBannerScrollThreshold] = useState<number>(DEFAULT_STICKY_BANNER_THRESHOLD);
    // Stores URLs of all post authors
    const [postUserAvatarURLs, setPostUserAvatarURLs] = useState<Map<string, string>>(new Map());
    // Stores the URL of post annotation dictation audio
    const [annotationAudioURLs, setAnnotationAudioURLs] = useState<Map<string, string>>(new Map());
    // Indicates whether we're updating annotationAudioURLs
    const [updatingAnnotationAudioURLs, setUpdatingAnnotationAudioURLs] = useState<boolean>(false);
    // Indicates whether we've currently fetching post authors
    const [fetchingPostAuthors, setFetchingPostAuthors] = useState<boolean>(false);
    // Stores a list of the post author ids
    const [postAuthorIds, setPostAuthorIds] = useState<string[]>([]);
    // Stores post user item data, incl. post, annotation, and comment authors
    const [postUserItems, setPostUserItems] = useState<Map<string, IPartialUserItem>>(new Map());
    // Indicates whether we're updating post user items
    const [updatingPostUserItems, setUpdatingPostUserItems] = useState<boolean>(false);
    // Indicates whether we're currently fetching contents table
    const [refreshingContentsTable, setRefreshingContentsTable] = useState<boolean>(false);
    // Stores contents table data of post when in reading mode
    const [contentsTable, setContentsTable] = useState<IContentsChapterItem[] | null>(null);
    // Indicates the path of the post value currently being edited or viewed
    const [selectedPostValuePath, setSelectedPostValuePath] = useState<number[]>([]);
    const [postEditorDeleteResetVersion, setPostEditorDeleteResetVersion] = useState<number>(0);
    const [postEditorDeleteTransitionInProgress, setPostEditorDeleteTransitionInProgress] = useState<boolean>(false);
    const [writeOutlineChapters, setWriteOutlineChapters] = useState<IReadPostChapterItem[] | null>(null);
    const [writeBannerPost, setWriteBannerPost] = useState<IReadPostItem | undefined>(undefined);
    const [collaborativeParticipants, setCollaborativeParticipants] = useState<ICollaborativeCursorData[]>([]);
    const getOutlineStructureKey = useCallback((chapters?: IReadPostChapterItem[] | null): string => (
        (chapters || []).map((chapter) => (
            `${chapter.id}:${(chapter.sections || []).map((section) => section.id).join(',')}`
        )).join('::')
    ), []);
    // Preserve optimistic structure in write mode while still sourcing saved
    // chapter and section values from the canonical post tree when possible.
    const mergeWriteOutlineChaptersIntoPost = useCallback((
        sourcePost: IReadPostItem,
        outlineChapters: IReadPostChapterItem[],
    ): IReadPostItem => {
        const canonicalChaptersById = new Map(
            (sourcePost.chapters || []).map((chapter) => [chapter.id, chapter]),
        );

        return {
            ...sourcePost,
            chapters: outlineChapters.map((outlineChapter) => {
                const canonicalChapter = canonicalChaptersById.get(outlineChapter.id);

                if (!canonicalChapter) {
                    return outlineChapter;
                }

                const canonicalSectionIds = (canonicalChapter.sections || []).map((section) => section.id);
                const outlineSectionIds = (outlineChapter.sections || []).map((section) => section.id);
                const sectionStructureChanged = (
                    canonicalSectionIds.length !== outlineSectionIds.length
                    || canonicalSectionIds.some((sectionId, index) => sectionId !== outlineSectionIds[index])
                );

                const canonicalSectionsById = new Map(
                    (canonicalChapter.sections || []).map((section) => [section.id, section]),
                );

                return {
                    ...canonicalChapter,
                    index: outlineChapter.index,
                    value: sectionStructureChanged ? outlineChapter.value : canonicalChapter.value,
                    previewValue: sectionStructureChanged
                        ? outlineChapter.previewValue
                        : canonicalChapter.previewValue,
                    sections: (outlineChapter.sections || []).map((outlineSection) => {
                        const canonicalSection = canonicalSectionsById.get(outlineSection.id);

                        if (!canonicalSection) {
                            return outlineSection;
                        }

                        return {
                            ...canonicalSection,
                            index: outlineSection.index,
                            value: sectionStructureChanged ? outlineSection.value : canonicalSection.value,
                            previewValue: sectionStructureChanged
                                ? outlineSection.previewValue
                                : canonicalSection.previewValue,
                        };
                    }),
                };
            }),
        };
    }, []);
    const writeContentOverridesRef = useRef<Map<string, {
        target: IPostContentTarget,
        value: string,
        version: number,
    }>>(new Map());
    const writeContentOverrideVersionRef = useRef<number>(0);
    const [userUndeletedAnnotationCount, setUserUndeletedAnnotationCount] = useState<number | null>(null);
    const writePostWithOutlinePreview = useMemo(() => {
        if (!write || !post) {
            return post;
        }

        if (writeOutlineChapters == null) {
            return post;
        }

        return mergeWriteOutlineChaptersIntoPost(post, writeOutlineChapters);
    }, [
        mergeWriteOutlineChaptersIntoPost,
        post,
        write,
        writeOutlineChapters,
    ]);
    const currentPostTargetSource = write ? writePostWithOutlinePreview : readFetchedPostState.post;
    const currentPostContentTarget = useMemo(() => resolvePostContentTarget({
        postId,
        post: currentPostTargetSource,
        path: selectedPostValuePath,
    }), [
        postId,
        currentPostTargetSource,
        selectedPostValuePath,
    ]);
    // On the shared dev route, signed-in users briefly have unresolved reader mode until
    // admin status finishes hydrating. During that window, suppress read bootstrap work so
    // we do not fetch the read doc and then immediately pivot into write mode.
    const readerModeResolutionPending = useMemo(() => (
        (process.env.NODE_ENV === 'development' || useStripeTestMode)
        && !write
        && !!auth.currentUser
        && !fetchedUserAdminStatus
    ), [
        useStripeTestMode,
        write,
        auth.currentUser,
        fetchedUserAdminStatus,
    ]);
    const hasPendingSelectedPathFromRouteParams = useMemo((): boolean => {
        if (write) return false;
        if (selectedPostValuePath.length > 0) return false;
        if (hasReaderPathScopeSearchParams(searchParams)) return true;
        if (!params.annotationId) return false;
        // Annotation deep links can carry chapter/section ids in route state, but resolving
        // those ids still requires the fetched post shape. Do not block the initial read fetch
        // on that state, or the reader deadlocks waiting for a path that depends on the fetch.
        return false;
    }, [
        params.annotationId,
        searchParams,
        selectedPostValuePath.length,
        write,
    ]);
    const currentReadPost = readFetchedPostState.post;
    const currentReadPostChapter = readFetchedPostState.chapter;
    const currentReadPostChapterSection = readFetchedPostState.chapterSection;
    const activeRootPost = write ? writePostWithOutlinePreview : currentReadPost;
    const postBannerRootPost = write
        ? (writePostWithOutlinePreview || writeBannerPost)
        : activeRootPost;
    const currentWritePostChapter = useMemo(() => {
        if (!write || !writePostWithOutlinePreview || selectedPostValuePath.length === 0) return undefined;
        return writePostWithOutlinePreview.chapters?.[selectedPostValuePath[0]];
    }, [
        write,
        writePostWithOutlinePreview,
        selectedPostValuePath,
    ]);
    const currentWritePostChapterSection = useMemo(() => {
        if (
            !write
            || !currentWritePostChapter
            || selectedPostValuePath.length < 2
        ) {
            return undefined;
        }
        return currentWritePostChapter.sections?.[selectedPostValuePath[1]];
    }, [
        write,
        currentWritePostChapter,
        selectedPostValuePath,
    ]);
    const activeScopedPostChapter = write ? currentWritePostChapter : currentReadPostChapter;
    const activeScopedPostChapterSection = write
        ? currentWritePostChapterSection
        : currentReadPostChapterSection;
    useEffect(() => {
        setPostBodyPainted(false);
        setPostBodyPaintVersion(0);
        setPostEditorReadOnlyChunkingIdle(false);
        setPostEditorReadOnlyFullRenderReady(false);
    }, [
        write,
        activeRootPost?.id,
        activeScopedPostChapter?.id,
        activeScopedPostChapterSection?.id,
        selectedPostValuePath.join('-'),
    ]);
    useEffect(() => {
        if (!write) {
            setWriteOutlineChapters(null);
        }
    }, [write, post?.id]);
    useEffect(() => {
        writeContentOverridesRef.current.clear();
        writeContentOverrideVersionRef.current = 0;
    }, [write, postId]);
    useEffect(() => {
        if (!write) {
            setWriteBannerPost(undefined);
            return;
        }
        setWriteBannerPost(undefined);
    }, [write, postId]);
    const currentPostEngagementScopeId = useMemo(() => {
        if (!postId || !currentPostContentTarget.isResolved) return null;
        if (currentPostContentTarget.scope === POST_CONTENT_SCOPE.section) {
            return currentPostContentTarget.sectionId || null;
        }
        if (currentPostContentTarget.scope === POST_CONTENT_SCOPE.chapter) {
            return currentPostContentTarget.chapterId || null;
        }
        return postId;
    }, [
        postId,
        currentPostContentTarget,
    ]);
    const currentScopeSaveStatusKey = useMemo(() => {
        if (!postId || !currentPostEngagementScopeId) return null;
        return buildPostScopeKey({
            postId,
            scope: currentPostContentTarget.scope,
            scopeId: currentPostEngagementScopeId,
        });
    }, [
        postId,
        currentPostContentTarget.scope,
        currentPostEngagementScopeId,
    ]);
    // Post engagement reads are backed by Firestore rules that require Firebase
    // auth, so cold loads must re-arm these listeners once auth hydration finishes.
    const readPostEngagementAuthUid = (
        write || signingInOut
    ) ? null : (auth.currentUser?.uid || null);
    useEffect(() => {
        let cancelled = false;

        if (
            !user
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
        ) {
            setUserUndeletedAnnotationCount(null);
            return undefined;
        }

        getUserUndeletedAnnotationCount({
            test: useStripeTestMode,
        }).then((count) => {
            if (!cancelled) setUserUndeletedAnnotationCount(count);
        }).catch(() => {
            if (!cancelled) setUserUndeletedAnnotationCount(null);
        });

        return () => {
            cancelled = true;
        };
    }, [
        user,
        useStripeTestMode,
    ]);
    useEffect(() => {
        if (!postId || (!write && !readPostEngagementAuthUid)) {
            setRootPostEngagement(null);
            return undefined;
        }

        return subscribeToPostEngagement({
            test: useStripeTestMode,
            postId,
            scope: POST_CONTENT_SCOPE.root,
            scopeId: postId,
            onNext: setRootPostEngagement,
        });
    }, [
        postId,
        useStripeTestMode,
        write,
        readPostEngagementAuthUid,
    ]);
    useEffect(() => {
        if (!postId || (!write && !readPostEngagementAuthUid)) {
            setPostEngagementItems(new Map());
            return undefined;
        }

        return subscribeToPostEngagementItems({
            test: useStripeTestMode,
            postId,
            onNext: setPostEngagementItems,
        });
    }, [
        postId,
        useStripeTestMode,
        write,
        readPostEngagementAuthUid,
    ]);
    useEffect(() => {
        let cancelled = false;

        if (
            write
            || !postId
            || !readPostEngagementAuthUid
        ) {
            setPostEngagementFallbackItems(new Map());
            return undefined;
        }

        getPostEngagementItems({
            test: useStripeTestMode,
            postId,
        }).then((engagementItems) => {
            if (!cancelled) {
                setPostEngagementFallbackItems(engagementItems);
            }
        }).catch(() => {
            if (!cancelled) {
                setPostEngagementFallbackItems(new Map());
            }
        });

        return () => {
            cancelled = true;
        };
    }, [
        postId,
        useStripeTestMode,
        write,
        readPostEngagementAuthUid,
    ]);
    useEffect(() => {
        if (
            !postId
            || !currentPostEngagementScopeId
            || (!write && !readPostEngagementAuthUid)
        ) {
            setCurrentPostEngagement(null);
            return undefined;
        }

        return subscribeToPostEngagement({
            test: useStripeTestMode,
            postId,
            scope: currentPostContentTarget.scope,
            scopeId: currentPostEngagementScopeId,
            onNext: setCurrentPostEngagement,
        });
    }, [
        postId,
        currentPostContentTarget.scope,
        currentPostEngagementScopeId,
        useStripeTestMode,
        write,
        readPostEngagementAuthUid,
    ]);
    const effectivePostEngagementItems = useMemo(() => {
        const nextItems = new Map(postEngagementFallbackItems);
        postEngagementItems.forEach((item, key) => {
            nextItems.set(key, item);
        });
        return nextItems;
    }, [
        postEngagementFallbackItems,
        postEngagementItems,
    ]);
    const effectiveCurrentPostEngagement = useMemo(() => {
        if (currentPostEngagement) return currentPostEngagement;
        if (!currentScopeSaveStatusKey) return null;
        return effectivePostEngagementItems.get(currentScopeSaveStatusKey) || null;
    }, [
        currentPostEngagement,
        currentScopeSaveStatusKey,
        effectivePostEngagementItems,
    ]);
    useEffect(() => {
        if (
            !write
            || !userIsAdmin
            || !postId
            || !currentPostEngagementScopeId
        ) {
            setLatestCurrentScopeUpdateEvent(null);
            return undefined;
        }

        return subscribeToLatestPostUpdateEvent({
            test: useStripeTestMode,
            postId,
            scope: currentPostContentTarget.scope,
            scopeId: currentPostEngagementScopeId,
            onNext: setLatestCurrentScopeUpdateEvent,
        });
    }, [
        write,
        userIsAdmin,
        postId,
        currentPostContentTarget.scope,
        currentPostEngagementScopeId,
        useStripeTestMode,
    ]);
    const currentScopeVisibleAnnotationCount = useMemo(() => {
        if (!effectiveCurrentPostEngagement) return null;
        return Math.max(
            effectiveCurrentPostEngagement.annotationCount
            - effectiveCurrentPostEngagement.commentCount
            - effectiveCurrentPostEngagement.deletedAnnotationCount,
            0,
        );
    }, [
        effectiveCurrentPostEngagement,
        effectiveCurrentPostEngagement?.annotationCount,
        effectiveCurrentPostEngagement?.commentCount,
        effectiveCurrentPostEngagement?.deletedAnnotationCount,
    ]);
    const shouldUseWriteModeCollabDoc = useCallback(({
        target,
        currentPost,
    }: {
        target: typeof currentPostContentTarget,
        currentPost?: IReadPostItem,
    }): boolean => (
        write
        && userIsAdmin
        && currentPost != null
        && target.isResolved
        && target.docId != null
    ), [
        write,
        userIsAdmin,
    ]);
    const applyContentValueToFetchedPost = useCallback(({
        postData,
        target,
        value,
    }: {
        postData: IGetPostResult,
        target: typeof currentPostContentTarget,
        value: string,
    }): IGetPostResult => {
        const updatedFetchedPost: IGetPostResult = {
            ...postData,
        };
        const chapterIndex = target.path.length > 0 ? target.path[0] : undefined;
        const sectionIndex = target.path.length > 1 ? target.path[1] : undefined;

        if (
            target.scope === POST_CONTENT_SCOPE.section
            && chapterIndex != null
            && sectionIndex != null
        ) {
            if (updatedFetchedPost.chapterSection) {
                updatedFetchedPost.chapterSection = {
                    ...updatedFetchedPost.chapterSection,
                    value,
                };
            }
            if (updatedFetchedPost.chapter?.sections) {
                const updatedSections = [...updatedFetchedPost.chapter.sections];
                if (updatedSections[sectionIndex]) {
                    updatedSections[sectionIndex] = {
                        ...updatedSections[sectionIndex],
                        value,
                    };
                    updatedFetchedPost.chapter = {
                        ...updatedFetchedPost.chapter,
                        sections: updatedSections,
                    };
                }
            }
            if (updatedFetchedPost.post?.chapters) {
                const updatedChapters = [...updatedFetchedPost.post.chapters];
                const chapter = updatedChapters[chapterIndex];
                if (chapter?.sections) {
                    const updatedSections = [...chapter.sections];
                    if (updatedSections[sectionIndex]) {
                        updatedSections[sectionIndex] = {
                            ...updatedSections[sectionIndex],
                            value,
                        };
                        updatedChapters[chapterIndex] = {
                            ...chapter,
                            sections: updatedSections,
                        };
                        updatedFetchedPost.post = {
                            ...updatedFetchedPost.post,
                            chapters: updatedChapters,
                        };
                    }
                }
            }
            return updatedFetchedPost;
        }

        if (
            target.scope === POST_CONTENT_SCOPE.chapter
            && chapterIndex != null
        ) {
            if (updatedFetchedPost.chapter) {
                updatedFetchedPost.chapter = {
                    ...updatedFetchedPost.chapter,
                    value,
                };
            }
            if (updatedFetchedPost.post?.chapters) {
                const updatedChapters = [...updatedFetchedPost.post.chapters];
                if (updatedChapters[chapterIndex]) {
                    updatedChapters[chapterIndex] = {
                        ...updatedChapters[chapterIndex],
                        value,
                    };
                    updatedFetchedPost.post = {
                        ...updatedFetchedPost.post,
                        chapters: updatedChapters,
                    };
                }
            }
            return updatedFetchedPost;
        }

        if (updatedFetchedPost.post) {
            updatedFetchedPost.post = {
                ...updatedFetchedPost.post,
                value,
            };
        }
        return updatedFetchedPost;
    }, []);
    const syncLocalContentValue = useCallback(({
        target,
        value,
    }: {
        target: typeof currentPostContentTarget,
        value: string,
    }): void => {
        setReadFetchedPostState((prev) => {
            const updatedFetchedPost = applyContentValueToFetchedPost({
                postData: {
                    post: prev.post || null,
                    chapter: prev.chapter || undefined,
                    chapterSection: prev.chapterSection || undefined,
                },
                target,
                value,
            });
            return {
                post: updatedFetchedPost.post || undefined,
                chapter: updatedFetchedPost.chapter || undefined,
                chapterSection: updatedFetchedPost.chapterSection || undefined,
            };
        });

        setPost((prev) => {
            if (!prev) return prev;
            const updatedFetchedPost = applyContentValueToFetchedPost({
                postData: {
                    post: prev,
                    chapter: undefined,
                    chapterSection: undefined,
                },
                target,
                value,
            });
            return updatedFetchedPost.post || prev;
        });
    }, [
        applyContentValueToFetchedPost,
    ]);
    /**
     * Keeps write-mode scope access state fresh locally after staging saves so
     * returning to a chapter does not rehydrate the old access metadata.
     */
    const syncLocalScopeAccessMetadata = useCallback((
        docId: string,
        serializedAccessMetadata: ISerializedScopeAccessMetadata,
    ): void => {
        setPost((prev) => (
            applySerializedScopeAccessMetadataToPost(
                prev ?? null,
                docId,
                serializedAccessMetadata,
            ) || prev
        ));
    }, []);
    /**
     * Strips full-value read payloads during authenticated sign-out so stale
     * in-memory post data cannot flash back into view before anon refresh.
     * Preview values are cleared here too so the reader stays on the placeholder
     * until the fresh anonymous payload arrives for the new auth session.
     */
    const downgradeFetchedReadPostForSignOut = useCallback((postData: {
        post?: IReadPostItem | null,
        chapter?: IReadPostChapterItem,
        chapterSection?: IReadPostSectionItem,
    }): {
        post?: IReadPostItem,
        chapter?: IReadPostChapterItem,
        chapterSection?: IReadPostSectionItem,
    } => ({
        post: postData.post
            ? {
                ...postData.post,
                value: undefined,
                previewValue: undefined,
                chapters: postData.post.chapters?.map((chapter) => ({
                    ...chapter,
                    value: undefined,
                    previewValue: undefined,
                    sections: chapter.sections?.map((section) => ({
                        ...section,
                        value: '',
                        previewValue: undefined,
                    })),
                })),
            }
            : undefined,
        chapter: postData.chapter
            ? {
                ...postData.chapter,
                value: undefined,
                previewValue: undefined,
                sections: postData.chapter.sections?.map((section) => ({
                    ...section,
                    value: '',
                    previewValue: undefined,
                })),
            }
            : undefined,
        chapterSection: postData.chapterSection
            ? {
                ...postData.chapterSection,
                value: '',
                previewValue: undefined,
            }
            : undefined,
    }), []);
    const applyWriteContentOverridesToFetchedPost = useCallback(({
        postData,
        fetchStartedAtVersion,
    }: {
        postData: {
            post: IReadPostItem | null,
            chapter?: IReadPostChapterItem,
            chapterSection?: IReadPostSectionItem,
        },
        fetchStartedAtVersion: number,
    }): {
        post: IReadPostItem | null,
        chapter?: IReadPostChapterItem,
        chapterSection?: IReadPostSectionItem,
    } => {
        let updatedPostData = postData;

        writeContentOverridesRef.current.forEach((override) => {
            if (override.version <= fetchStartedAtVersion) {
                return;
            }

            updatedPostData = applyContentValueToFetchedPost({
                postData: updatedPostData,
                target: override.target,
                value: override.value,
            });
        });

        return updatedPostData;
    }, [
        applyContentValueToFetchedPost,
    ]);
    // CTA state to let users jump back to their previously persisted chapter location.
    const [resumeReaderProgress, setResumeReaderProgress] = useState<{
        key: string,
        scrollTop: number,
        scrollProgress: number,
        anchorParagraphId?: string,
        anchorParagraphIndex?: number,
        anchorParagraphOffsetProgress?: number,
    } | null>(null);
    resumeReaderProgressRef.current = resumeReaderProgress;
    // Keeps the explicit "Resume reading" CTA mounted as a spinner while we
    // force full read-only render and wait for layout to settle before scroll.
    const [resumeReaderProgressButtonLoading, setResumeReaderProgressButtonLoading] = useState<boolean>(false);
    const [readOnlyAudioPlaybackState, setReadOnlyAudioPlaybackState] = useState<IReadOnlyAudioNotePlaybackState | null>(null);
    const [activeReadOnlyAudioNoteVisibleInViewport, setActiveReadOnlyAudioNoteVisibleInViewport] = useState<boolean>(true);
    // Keeps the sticky "Read Free" CTA mounted as a spinner while we force
    // full read-only render so the auth-lock email capture UI actually exists.
    const [authLockedStickyCtaButtonLoading, setAuthLockedStickyCtaButtonLoading] = useState<boolean>(false);
    const [forceFullReadOnlyRenderForResume, setForceFullReadOnlyRenderForResume] = useState<boolean>(false);
    const pendingResumeAfterFullRenderSourceRef = useRef<READER_RESUME_SOURCE | null>(null);
    const pendingResumeAfterFullRenderPaintVersionRef = useRef<number | null>(null);
    const pendingAuthLockedStickyCtaAfterFullRenderRef = useRef<boolean>(false);
    const pendingAuthLockedStickyCtaPaintVersionRef = useRef<number | null>(null);
    const authLockedStickyCtaLayoutWaitCancelRef = useRef<(() => void) | null>(null);
    const authLockedStickyCtaWaitFrameRef = useRef<number | null>(null);
    const authLockAutoResumeAppliedScrollTopRef = useRef<number | null>(null);
    const authLockAutoResumeAppliedTargetRef = useRef<{
        targetScrollMax: number,
        targetScrollTop: number,
    } | null>(null);
    // Forces auth-lock auto-resume effect re-evaluation when internal refs advance
    // outside React state transitions.
    const [authLockAutoResumeTick, setAuthLockAutoResumeTick] = useState<number>(0);
    // Prevents placeholder/body flicker while read mode state is still settling.
    const [showPostBodyContentSettled, setShowPostBodyContentSettled] = useState<boolean>(false);
    const [postEditorReadOnlyChunkingIdle, setPostEditorReadOnlyChunkingIdle] = useState<boolean>(false);
    const [postEditorReadOnlyFullRenderReady, setPostEditorReadOnlyFullRenderReady] = useState<boolean>(false);
    // Stores current zoom level
    const [currentZoomLevel, setCurrentZoomLevel] = useState<IZoomLevelItem | null>(null);
    const currentAnnotationFetchTarget = useMemo(() => {
        const annotationTargetPost = write
            ? writePostWithOutlinePreview
            : currentReadPost;
        const selectedChapter = selectedPostValuePath.length > 0
            ? annotationTargetPost?.chapters?.[selectedPostValuePath[0]]
            : undefined;
        const selectedSection = selectedPostValuePath.length > 1
            ? selectedChapter?.sections?.[selectedPostValuePath[1]]
            : undefined;

        if (selectedSection) {
            return {
                chapterId: selectedChapter?.id || currentPostContentTarget.chapterId,
                sectionId: selectedSection.id,
            };
        }

        if (selectedChapter) {
            return {
                chapterId: selectedChapter.id,
                sectionId: undefined,
            };
        }

        return {
            chapterId: currentPostContentTarget.chapterId,
            sectionId: currentPostContentTarget.sectionId,
        };
    }, [
        write,
        currentReadPost,
        writePostWithOutlinePreview,
        selectedPostValuePath,
        currentPostContentTarget.chapterId,
        currentPostContentTarget.sectionId,
    ]);
    const currentAnnotationFetchScopeKey = useMemo(() => {
        if (
            !postId
            || !currentZoomLevel
            || !currentPostContentTarget.isResolved
        ) {
            return null;
        }

        return JSON.stringify({
            postId,
            zoomLevel: currentZoomLevel.level,
            chapterId: currentAnnotationFetchTarget.chapterId || null,
            sectionId: currentAnnotationFetchTarget.sectionId || null,
        });
    }, [
        postId,
        currentZoomLevel,
        currentZoomLevel?.level,
        currentPostContentTarget.isResolved,
        currentAnnotationFetchTarget.chapterId,
        currentAnnotationFetchTarget.sectionId,
    ]);
    // Stores data of post thumbnail being uploaded
    const [uploadingPostThumbnail, setUploadingPostThumbnail] = useState<IMediaItem | null>(null);
    // We track the progress of post thumbnail upload here
    const [uploadingThumbnailStateChangeEvent, setUploadingThumbnailStateChangeEvent] = useState<{
        mediaItem: IMediaItem,
        snapshot: UploadTaskSnapshot,
        progress: number,
    } | null>(null);
    // We track the completion of post thumbnail here
    const [uploadingThumbnailCompleteEvent, setUploadingThumbnailCompleteEvent] = useState<{
        mediaItem: IMediaItem,
        storageRef: StorageReference,
    } | null>(null);
    // Stores the number of number levels applied to the post
    const [appliedZoomLevelCount, setAppliedZoomLevelCount] = useState<number | null>(null);
    // Indicates whether we're currently fetching post zoom level count
    const [determiningPostZoomLevelCount, setDeterminingPostZoomLevelCount] = useState<boolean>(false);
    // One active collapsed-CTA nudge tooltip at a time (target + copy).
    const [stickyCtaNudge, setStickyCtaNudge] = useState<{
        visible: boolean,
        target: STICKY_CTA_NUDGE_TARGET | null,
        text: string,
    }>({
        visible: false,
        target: null,
        text: '',
    });
    // Track which scenarios already nudged in this page session.
    const [completedStickyCtaNudgeScenarios, setCompletedStickyCtaNudgeScenarios] = useState<Record<STICKY_CTA_NUDGE_SCENARIO, boolean>>({
        [STICKY_CTA_NUDGE_SCENARIO.authLockedAnonymous]: false,
        [STICKY_CTA_NUDGE_SCENARIO.signedInNoWebAccess]: false,
        [STICKY_CTA_NUDGE_SCENARIO.signedInHasWebAccess]: false,
    });
    // Prevent duplicate timer starts while a scenario is already scheduled.
    const [scheduledStickyCtaNudgeScenario, setScheduledStickyCtaNudgeScenario] = useState<STICKY_CTA_NUDGE_SCENARIO | null>(null);
    // Indicates whether compact sticky purchase panel is expanded.
    const [purchasePanelExpanded, setPurchasePanelExpanded] = useState<boolean>(false);
    // Indicates whether secondary purchase options are visible within compact purchase panel.
    const [purchaseMoreOptionsExpanded, setPurchaseMoreOptionsExpanded] = useState<boolean>(false);
    const [stickyPurchaseMeasuredHeights, setStickyPurchaseMeasuredHeights] = useState<{
        collapsed: number,
        expanded: number,
    }>({
        collapsed: 1,
        expanded: 1,
    });
    const [forceShowStickyPurchaseCta, setForceShowStickyPurchaseCta] = useState<boolean>(false);
    const hasAutoOpenedPurchasePanelRef = useRef<boolean>(false);
    // Stores email input for auth-lock capture card.
    const [authLockedEmail, setAuthLockedEmail] = useState<string>('');
    const [authLockedEmailSubmitting, setAuthLockedEmailSubmitting] = useState<boolean>(false);
    const [authLockedSignInMethod, setAuthLockedSignInMethod] = useState<AUTHENTICATION_TYPE | null>(null);
    // Indicates whether contents table is visible
    const [contentsTableVisible, setContentsTableVisible] = useState<boolean>(
        viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min,
    );
    // Indicates whether character modal is visible
    // Visible when anonymous user navigates to ReaderView
    const [characterModalIsVisible, setCharacterModalIsVisible] = useState<boolean>(false);
    const [bookAboutDialogIsVisible, setBookAboutDialogIsVisible] = useState<boolean>(false);
    // Indicates whether we've shown character selection modal to new users already
    const [checkedForCharacterModal, setCheckedForCharacterModal] = useState<boolean>(false);
    // Indicates whether we should show zoom tooltip to new users
    const [showZoomTooltip, setShowZoomTooltip] = useState<boolean>(false);
    // Indicates whether we've shown zoom tooltip to new users already
    const [presentedZoomTooltip, setPresentedZoomTooltip] = useState<boolean>(false);
    // Stores timestamp of when user entered page
    const [startOrResumeBrowsingTimestamp, setStartOrResumeBrowsingTimestamp] = useState<number>(Date.now());
    // Stores duration reader has been reading post before tab is unfocused
    const [cachedPostFocusedDuration, setCachedPostFocusedDuration] = useState<number>(0);
    // Indicates whether we've begun reading timers
    const [initiatedReadingTimers, setInitiatedReadingTimers] = useState<boolean>(false);
    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);
    // Use a stable route-level key for page-view dedupe so reload bootstrap and
    // StrictMode remounts do not replay the same chapter/section view.
    const currentReadPageViewRecordKey = useMemo(() => {
        if (!currentSessionId) return null;
        // Track the logical reader destination instead of location.key so
        // reload-time URL normalization and StrictMode remounts cannot record
        // duplicate views for the same page/section.
        const logicalPage = searchParams.get(READER_PARAMS_TYPE.page) || 'root';
        const logicalSection = searchParams.get(READER_PARAMS_TYPE.section) || 'root';
        const logicalChapterId = searchParams.get(READER_PARAMS_TYPE.chapterId) || 'none';
        const logicalSectionId = searchParams.get(READER_PARAMS_TYPE.sectionId) || 'none';
        return [
            currentSessionId,
            location.pathname,
            postId || 'none',
            logicalPage,
            logicalSection,
            logicalChapterId,
            logicalSectionId,
        ].join('|');
    }, [
        currentSessionId,
        location.pathname,
        postId,
        searchParams,
    ]);
    // Indicates that rerenders upon page load have completed
    const [pageLoadRendersCompleted, setPageLoadRendersCompleted] = useState<boolean>(false);
    // One-way gate for read-mode initial render after high-churn fetches settle.
    const [initialReadLayoutCommitted, setInitialReadLayoutCommitted] = useState<boolean>(write);
    // Stores a list of web book items to animate to cart button in navigation bar
    const [animateWebBookToCartButtonItems, setAnimateWebBookToCartButtonItems] = useState<Map<string, IMediaItem>>(new Map());
    // Stores a list of web book items to animate to cart button in navigation bar
    const [animateDigitalBookToCartButtonItems, setAnimateDigitalBookToCartButtonItems] = useState<Map<string, IMediaItem>>(new Map());
    // Stores a list of web book items to animate to cart button in navigation bar
    const [animatePhysicalBookToCartButtonItems, setAnimatePhysicalBookToCartButtonItems] = useState<Map<string, IMediaItem>>(new Map());
    // Stores a list of cart items staged to be added to cart following animation completion
    const [stagedCartItems, setStagedCartItems] = useState<Map<string, IStagedCartItem>>(new Map());
    // Indicates whether web book cart item is currently being animated
    const [animatingWebBookCartItem, setAnimatingWebBookCartItem] = useState<boolean>(false);
    // Indicates whether digital book cart item is currently being animated
    const [animatingDigitalBookCartItem, setAnimatingDigitalBookCartItem] = useState<boolean>(false);
    // Indicates whether physical book cart item is currently being animated
    const [animatingPhysicalBookCartItem, setAnimatingPhysicalBookCartItem] = useState<boolean>(false);
    // Tracks which sticky CTA button initiated the current cart animation cycle.
    const [activeCartAnimationSourceId, setActiveCartAnimationSourceId] = useState<string | null>(null);
    // Captures clicked source button center at interaction time for stable cart-fly origin.
    const [activeCartAnimationSourceCenter, setActiveCartAnimationSourceCenter] = useState<ICoord | null>(null);
    // Stores a map of store items by product
    const [storeItemsByProduct, setStoreItemByProduct] = useState<Map<string, IStoreItem>>(new Map());
    // Indicates whether we'r currently fecthing store items
    const [fetchingStoreItems, setFetchingStoreItems] = useState<boolean>(false);
    // Indicates whether store items have been fetched
    const [fetchedStoreItems, setFetchedStoreItems] = useState<boolean>(false);
    // Guards freshly-acked annotations from snapshot prune races while fan-out catches up.
    const recentlyAckedAnnotationIdsRef = useRef<Map<string, number>>(new Map());
    // Synchronous protection set for optimistic/just-acked ids to avoid render-gap prune races.
    const protectedAnnotationIdsRef = useRef<Set<string>>(new Set());
    // Prevent overlapping post fetches; queue one trailing fetch when contention happens.
    const postFetchInFlightRef = useRef<boolean>(false);
    const postFetchQueuedRef = useRef<boolean>(false);
    const queuedCartAddRafRef = useRef<number | null>(null);
    const viewedPaywallBannerRef = useRef<Set<string>>(new Set());
    const viewedBuyBannerRef = useRef<Set<string>>(new Set());
    const viewedAuthLockBannerRef = useRef<Set<string>>(new Set());
    const readAccessUpgradeSawRefreshRef = useRef<boolean>(false);
    const readAccessUpgradeRequestedRefreshRef = useRef<boolean>(false);
    // Lets auth-lock upgrade refresh requests survive ordinary rerenders while
    // still invalidating stale token refresh completions after later auth flips.
    const readAccessUpgradeRefreshRequestIdRef = useRef<number>(0);
    const readerViewUnmountedRef = useRef<boolean>(false);
    // Preserves cart -> reader CTA expansion through the first path sync update.
    const skipPurchasePanelResetOnPathChangeRef = useRef<boolean>(false);
    const readPostFetchCycleRef = useRef<number>(0);
    const writePostFetchCycleRef = useRef<number>(0);
    const previousReaderAccessRefreshKeyRef = useRef<string | null>(null);
    const readZoomInitializedFromAuthFallbackRef = useRef<boolean>(false);
    // Double-rAF post-paint queue for annotation refreshes.
    const annotationRefreshRafOneRef = useRef<number | null>(null);
    const annotationRefreshRafTwoRef = useRef<number | null>(null);
    const annotationRefreshQueuedRef = useRef<boolean>(false);
    const [currentPostParagraphCount, setCurrentPostParagraphCount] = useState<number>(0);

    const READ_ACCESS_UPGRADE_TOKEN_REFRESH_FALLBACK_MS = 1500;
    const ANNOTATION_RETRY_BASE_DELAY_MS = 2000;
    const ANNOTATION_RETRY_MAX_DELAY_MS = 30000;
    const ANNOTATION_ACK_PROTECTION_DURATION_MS = 15000;
    // Shared online guard for initial submit + background retry loops.
    const clientIsOnline = (): boolean => (
        typeof navigator === 'undefined' || navigator.onLine
    );
    useEffect(() => () => {
        readerViewUnmountedRef.current = true;
    }, []);
    /**
     * Requests the signed-in reader refresh exactly once per auth upgrade handoff.
     * The request can outlive ordinary rerenders, but stale completions are ignored
     * once a later auth transition bumps the request id.
     */
    const scheduleReadAccessUpgradeRefresh = useCallback((currentAuthUser: typeof auth.currentUser): void => {
        if (
            !currentAuthUser
            || currentAuthUser.isAnonymous
            || readAccessUpgradeRequestedRefreshRef.current
        ) {
            return;
        }

        readAccessUpgradeRequestedRefreshRef.current = true;
        const refreshRequestId = readAccessUpgradeRefreshRequestIdRef.current + 1;
        readAccessUpgradeRefreshRequestIdRef.current = refreshRequestId;

        let refreshStarted = false;
        const startRefresh = (): void => {
            if (readerViewUnmountedRef.current) return;
            if (readAccessUpgradeRefreshRequestIdRef.current !== refreshRequestId) return;
            if (refreshStarted) return;

            refreshStarted = true;
            setRefreshPost(true);
        };
        const tokenRefreshFallbackTimeout = window.setTimeout(
            startRefresh,
            READ_ACCESS_UPGRADE_TOKEN_REFRESH_FALLBACK_MS,
        );

        currentAuthUser.getIdToken(true)
            .catch(() => undefined)
            .then(() => {
                window.clearTimeout(tokenRefreshFallbackTimeout);
                startRefresh();
            });
    }, [
        auth,
    ]);
    // Indicates whether user has bought a web book current chapter
    const [purchasedWebBookCurrentChapter, setPurchasedWebBookCurrentChapter] = useState<boolean>(false);
    // Indicates whether user has bought a web book
    const [purchasedWebBook, setPurchasedWebBook] = useState<boolean>(false);
    // Indicates whether user has bought a digital book
    const [purchasedDigitalBook, setPurchasedDigitalBook] = useState<boolean>(false);
    // Indicates whether user has bought a audio book
    // Indicates whether user has bought a physical book
    const [purchasedPhysicalBook, setPurchasedPhysicalBook] = useState<boolean>(false);
    // Preserve the last purchase payload locally so chapter switches can reuse it
    // instead of re-fetching purchases on every scope change.
    const [bookPurchases, setBookPurchases] = useState<IUserBookPurchaseItem | null>(null);
    // Indicats whether we're currently checking for book purchases
    const [checkingBookPurchases, setCheckingBookPurchases] = useState<boolean>(false);
    // Indicates whether checked for book purchases
    const [checkedBookPurchases, setCheckedBookPurchases] = useState<boolean>(false);
    // Allows access-based CTA/nudge fallback when purchase lookup fails.
    const [bookPurchasesLookupFailed, setBookPurchasesLookupFailed] = useState<boolean>(false);
    // Indicates whether we must refresh book purchases
    const [refreshBookPurchases, setRefreshBookPurchases] = useState<boolean>(false);
    // Indicates whether PostContentContainer is scrolling
    const [postContentContainerIsScrolling, setPostContentContainerIsScrolling] = useState<boolean>(false);
    // Indicates whether client is currently running a queued post update
    const [runningPostUpdateQueueTask, setRunningPostUpdateQueueTask] = useState<boolean>(false);
    // Indicates whether client is currently running a queued post user item update
    const [runningPostUserItemUpdateQueueTask, setRunningPostUserItemUpdateQueueTask] = useState<boolean>(false);
    // Indicates whether client is currently clearing post user item update queue
    const [clearingPostUserItemUpdateQueue, setClearingPostUserItemUpdateQueue] = useState<boolean>(false);
    // Indicates whether client is currently running a queued annotation audio url update
    const [runningAnnotationAudioURLUpdateQueueTask, setRunningAnnotationAudioURLUpdateQueueTask] = useState<boolean>(false);
    // Indicates whether client is currently clearing annotation audio url update queue
    const [clearingAnnotationAudioURLUpdateQueue, setClearingAnnotationAudioURLUpdateQueue] = useState<boolean>(false);
    const [postFileDragMetadata, setPostFileDragMetadata] = useState<ReaderPostFileDragMetadata | null>(null);
    const [postFileDragFeedbackSuppressed, setPostFileDragFeedbackSuppressed] = useState<boolean>(false);
    const activePostFileDragFeedbackKeyRef = useRef<string | null>(null);

    /**
     * Captures browser drag metadata before react-dropzone converts it into
     * hover feedback, because some browsers hide MIME types until drop.
     */
    const updatePostFileDragMetadata = useCallback((event: React.DragEvent<HTMLElement>): void => {
        const nextMetadata = resolveReaderPostFileDragMetadata(event.dataTransfer);
        setPostFileDragFeedbackSuppressed(false);
        setPostFileDragMetadata((currentMetadata) => {
            if (
                currentMetadata?.fileCount === nextMetadata?.fileCount
                && currentMetadata?.hasSupportedType === nextMetadata?.hasSupportedType
            ) {
                return currentMetadata;
            }

            return nextMetadata;
        });
    }, []);

    const clearPostFileDragMetadata = useCallback((): void => {
        setPostFileDragFeedbackSuppressed(true);
        setPostFileDragMetadata(null);
    }, []);

    // Set up dropzone for images and audio
    const {
        getRootProps,
        getInputProps,
        rootRef,
        acceptedFiles,
        isDragActive,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        multiple: true,
        noClick: true,
        noKeyboard: true,
        accept: ['image/*', 'audio/*'],
        onDragEnter: updatePostFileDragMetadata,
        onDrop: clearPostFileDragMetadata,
    });

    const postFileDragFeedback = useMemo(() => resolveReaderPostFileDragFeedback({
        write,
        isDragActive,
        isDragAccept,
        isDragReject,
        feedbackSuppressed: postFileDragFeedbackSuppressed,
        metadata: postFileDragMetadata,
    }), [
        write,
        isDragActive,
        isDragAccept,
        isDragReject,
        postFileDragFeedbackSuppressed,
        postFileDragMetadata,
    ]);

    /**
     * Handles valid image for thumbnail
     */
    const onDropAccepted = useCallback((acceptedThumbnailFiles: File[]) => {
        if (write && acceptedThumbnailFiles.length > 0 && postId) {
            handlePostThumbnailReplace(acceptedThumbnailFiles[0]);
        }
    }, [
        write,
        user,
    ]);
    // Set up dropzone for thumbnail image
    const {
        getRootProps: getPostThumbnailRootProps,
        getInputProps: getPostThumbnailInputProps,
        isDragAccept: isPostThumbnailDragAccept,
        isDragReject: isPostThumbnailDragReject,
        open: openPostThumbnailDropzone,
    } = useDropzone({
        multiple: false,
        maxFiles: 1,
        noClick: true,
        noDrag: true,
        noKeyboard: true,
        noDragEventsBubbling: true, // Because a parent element has a drag event listener
        accept: ['image/*'],
        onDropAccepted,
    });

    const {
        enqueue: enqueuePostUpdate,
        dequeue: dequeuePostUpdate,
        clear: clearPostUpdateQueue,
        runNextWithoutDequeue: runNextPostUpdateWithoutDequeue,
        size: postUpdateQueueSize,
    } = useTaskQueue();

    const {
        enqueue: enqueuePostUserItemUpdate,
        dequeue: dequeuePostUserItemUpdate,
        runNextWithoutDequeue: runNextPostUserItemUpdateWithoutDequeue,
        size: postUserItemUpdateQueueSize,
    } = useTaskQueue();

    const {
        enqueue: enqueueAnnotationAudioURLUpdate,
        dequeue: dequeueAnnotationAudioURLUpdate,
        runNextWithoutDequeue: runNextAnnotationAudioURLUpdateWithoutDequeue,
        size: annotationAudioURLUpdateQueueSize,
    } = useTaskQueue();

    // Choose Your Own Adventure Characters
    const adventureCharacters = new Map([
        [ZOOM_LEVEL.five, {
            id: ADVENTURE_CHARACTER.meticulousGeek,
            level: ZOOM_LEVEL.five,
            name: 'Meticulous Geek',
            icon: GeekPortrait,
            duration: 10 * MILLISECONDS_IN_AN_HOUR,
            background: themeObj.verascopeColor.blue200,
            nameColor: themeObj.verascopeColor.blue100,
            zoomColor: themeObj.verascopeColor.blue300,
            zoomHoverColor: themeObj.verascopeColor.blue300,
        }],
        [ZOOM_LEVEL.four, {
            id: ADVENTURE_CHARACTER.analyticalPhilosopher,
            level: ZOOM_LEVEL.four,
            name: 'Analytical Philosopher',
            icon: PhilosopherPortrait,
            duration: 2 * MILLISECONDS_IN_AN_HOUR,
            background: themeObj.verascopeColor.purple400,
            nameColor: themeObj.verascopeColor.purple300,
            zoomColor: themeObj.verascopeColor.purple500,
            zoomHoverColor: themeObj.verascopeColor.purple500,
        }],
        [ZOOM_LEVEL.three, {
            id: ADVENTURE_CHARACTER.committedJournalist,
            level: ZOOM_LEVEL.three,
            name: 'Committed Journalist',
            icon: JournalistPortrait,
            duration: 30 * MILLISECONDS_IN_A_MINUTE,
            background: themeObj.verascopeColor.red200,
            nameColor: themeObj.verascopeColor.red100,
            zoomColor: themeObj.verascopeColor.red300,
            zoomHoverColor: themeObj.verascopeColor.red300,
        }],
        [ZOOM_LEVEL.two, {
            id: ADVENTURE_CHARACTER.busyInvestor,
            level: ZOOM_LEVEL.two,
            name: 'Busy Investor',
            icon: InvestorPortrait,
            duration: 5 * MILLISECONDS_IN_A_MINUTE,
            background: themeObj.verascopeColor.green200,
            nameColor: themeObj.verascopeColor.green100,
            zoomColor: themeObj.verascopeColor.green300,
            zoomHoverColor: themeObj.verascopeColor.green300,
        }],
        [ZOOM_LEVEL.one, {
            id: ADVENTURE_CHARACTER.restlessMinor,
            level: ZOOM_LEVEL.one,
            name: 'Restless Minor',
            icon: MinorPortrait,
            duration: 30 * MILLISECONDS_IN_A_SECOND,
            background: themeObj.verascopeColor.yellow200,
            nameColor: themeObj.verascopeColor.yellow100,
            zoomColor: themeObj.verascopeColor.yellow100,
            zoomHoverColor: themeObj.verascopeColor.yellow100,
        }],
    ]);

    // ===== Animation Constants =====

    const UPLOAD_TRANSITION_DELAY = 200;
    // Swap this single value to change fly-to-cart easing:
    // themeObj.motion.eagerEasing | delayEasing | standardEasing | bounceEasing | overshoot | cartDropEasing
    const CART_FLY_TO_BUTTON_EASING = themeObj.motion.cartDropEasing;
    const POST_BANNER_TRANSITION_DURATION = 300;
    const COLLAPSED_POST_TITLE_TRANSITION_DURATION = 200;
    const COLLAPSED_PURCHASE_BUTTONS_TRANSITION_DURATION = COLLAPSED_POST_TITLE_TRANSITION_DURATION;
    const POST_METADATA_TRANSITION_DURATION = 200;
    const POST_SUBTITLE_TRANSITION_DURATION = 200;
    const POST_THUMBNAIL_TRANSITION_DURATION = 200;
    // Wait until user has oriented themselves with the UI
    const SHOW_ZOOM_TOOLTIP_DURATION = 15 * MILLISECONDS_IN_A_SECOND;
    const HIDE_ZOOM_TOOLTIP_DURATION = 8000;
    const OVERLAY_TRANSITION_DURATION = 200;
    // Durations after which nudges should be presented
    const STICKY_CTA_NUDGE_AUTH_LOCKED_ANONYMOUS_THRESHOLD_DURATION = 5 * MILLISECONDS_IN_A_MINUTE;
    const STICKY_CTA_NUDGE_SIGNED_IN_NO_WEB_ACCESS_THRESHOLD_DURATION = 10 * MILLISECONDS_IN_A_MINUTE;
    const STICKY_CTA_NUDGE_SIGNED_IN_WEB_ACCESS_THRESHOLD_DURATION = 20 * MILLISECONDS_IN_A_MINUTE;
    // Duration after which nudges should be hidden
    const HIDE_STICKY_CTA_NUDGE_TIMEOUT_DURATION = EXTENDED_SNACKBAR_VISIBLE_DURATION;
    // Duration of transition of Choose Your Own Adventure characters
    const ADVENTURE_CHARACTER_TRANSITION_DURATION = 200;
    // Duration representing ten minutes
    const TEN_MINUTES_DURATION = 10 * MILLISECONDS_IN_A_MINUTE;
    // Duration representing thirty minutes
    const THIRTY_MINUTES_DURATION = 30 * MILLISECONDS_IN_A_MINUTE;
    // Duration representing an hour
    const HOUR_DURATION = MILLISECONDS_IN_AN_HOUR;
    // Fallback duration to unblock initial render if readiness signals stall.
    const PAGE_LOAD__RENDERS_COMPLETED_TIMEOUT_DURATION = 4 * MILLISECONDS_IN_A_SECOND;
    // Duration to wait for post user item updates to transpire
    const UPDATING_POST_USER_ITEMS_TIMEOUT_DURATION = 0.5 * MILLISECONDS_IN_A_SECOND;
    // Duration to wait for annotation audio url updates to transpire
    const UPDATING_ANNOTATION_AUDIO_URLS_TIMEOUT_DURATION = 0.5 * MILLISECONDS_IN_A_SECOND;
    // Duration to scroll to email auth input when user presses share email button
    const AUTH_LOCKED_EMAIL_DURATION = 0.5 * MILLISECONDS_IN_A_SECOND;

    // ===== Methods =====

    /**
     * Uses failure styling for cart/business-rule guidance without counting the
     * message as an application crash in telemetry digests.
     */
    const showUserCorrectableFailureSnackbar = ({
        text,
        duration = EXTENDED_SNACKBAR_VISIBLE_DURATION,
        icon = CartIcon,
    }: {
        text: string,
        duration?: number,
        icon?: string,
    }): void => {
        setSnackbarData({
            visible: true,
            duration,
            text,
            icon,
            hasFailure: true,
            failureTelemetryType: FAILURE_TELEMETRY_TYPE.userCorrectable,
        });
    };

    /**
     * Manages response to page logo hovering over
     * @param e mouse or touch event
     */
    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to page logo no longer hovering over
     * @param e mouse or touch event
     */
    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Manages response to page logo selection
     * @param e mouse or touch event
     */
    const onLogoClick = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.clickHomeButton,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.book,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Fetch stripe product prices
     */
    const fetchStoreItemsLatest = useLatest({
        fetchedStoreItems,
        fetchingStoreItems,
    });
    const fetchStoreItems = (): void => {
        if (!fetchingStoreItems) setFetchingStoreItems(true);
        const negotiationRequestPriceIds: string[] = userCartItems
            ? userCartItems.filter((item: ICartItem) => item.saleNegotiationPriceId != null)
                .map((item: ICartItem) => item.saleNegotiationPriceId!)
            : [];
        stripeGetStoreItems({
            negotiationRequestPriceIds,
            test: useStripeTestMode,
        }).then((itemsByPrice) => {
            const itemsByProduct = buildStoreItemsByProduct({
                itemsByPrice,
                negotiationRequestPriceIds,
            });
            setStoreItemByProduct(itemsByProduct);
            const cur = fetchStoreItemsLatest.current;
            if (!cur.fetchedStoreItems) setFetchedStoreItems(true);
            if (cur.fetchingStoreItems) setFetchingStoreItems(false);
        }).catch((error) => {
            const cur = fetchStoreItemsLatest.current;
            if (cur.fetchingStoreItems) setFetchingStoreItems(false);
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: `Encountered a problem while fetching store items in ReaderView: ${(error as Error)?.message || String(error)}.`,
                        source: 'reader-store-items-fetch',
                    },
                });
            }
        });
    };

    const determineBookPurchasesLatest = useLatest({
        checkedBookPurchases,
        checkingBookPurchases,
        bookPurchasesLookupFailed,
        refreshBookPurchases,
    });
    function getPurchasedCurrentChapterFromPurchases(purchases: IUserBookPurchaseItem): boolean {
        if (currentPageChapterBookType === BOOK_TYPE.webChapterOne) return purchases.webChapterOne.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterTwo) return purchases.webChapterTwo.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterThree) return purchases.webChapterThree.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterFour) return purchases.webChapterFour.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterFive) return purchases.webChapterFive.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterSix) return purchases.webChapterSix.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterSeven) return purchases.webChapterSeven.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterEight) return purchases.webChapterEight.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterNine) return purchases.webChapterNine.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterTen) return purchases.webChapterTen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterEleven) return purchases.webChapterEleven.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterTwelve) return purchases.webChapterTwelve.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterThirteen) return purchases.webChapterThirteen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterFourteen) return purchases.webChapterFourteen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterFifteen) return purchases.webChapterFifteen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterSixteen) return purchases.webChapterSixteen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterSeventeen) return purchases.webChapterSeventeen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterEighteen) return purchases.webChapterEighteen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webChapterNineteen) return purchases.webChapterNineteen.length > 0;
        if (currentPageChapterBookType === BOOK_TYPE.webAppendixA) return purchases.webAppendixA.length > 0;
        return false;
    }
    const determineBookPurchases = (): void => {
        if (!checkingBookPurchases) setCheckingBookPurchases(true);
        if (bookPurchasesLookupFailed) setBookPurchasesLookupFailed(false);
        getUserBookPurchases({
            test: useStripeTestMode,
        }).then((purchases) => {
            setBookPurchases(purchases);
            setPurchasedWebBook(purchases.web.length > 0);
            setPurchasedWebBookCurrentChapter(getPurchasedCurrentChapterFromPurchases(purchases));
            setPurchasedDigitalBook((purchases.digitalPreorder.length + purchases.digital.length) > 0);
            setPurchasedPhysicalBook((purchases.physicalPreorder.length + purchases.physical.length) > 0);
            const cur = determineBookPurchasesLatest.current;
            if (!cur.checkedBookPurchases) setCheckedBookPurchases(true);
            if (cur.checkingBookPurchases) setCheckingBookPurchases(false);
            if (cur.bookPurchasesLookupFailed) setBookPurchasesLookupFailed(false);
            if (cur.refreshBookPurchases) setRefreshBookPurchases(false);
        }).catch(() => {
            const cur = determineBookPurchasesLatest.current;
            if (!cur.bookPurchasesLookupFailed) setBookPurchasesLookupFailed(true);
            if (cur.checkingBookPurchases) setCheckingBookPurchases(false);
            if (cur.refreshBookPurchases) setRefreshBookPurchases(false);
        });
    };

    /**
     * Retrieves post data from Firestore if it exists, otherwise creates a new post
     */
    const handleFetchPostLatest = useLatest({
        post,
        readFetchedPostState,
        refreshPost,
        refreshingPost,
        updatingPost,
        postUpdateQueueSize,
        postId,
        selectedPostValuePath,
        currentPostContentTarget,
    });
    const doesRequestedPathMatchLatest = ({
        requestedPostId,
        requestedPath,
    }: {
        requestedPostId: string,
        requestedPath?: number[],
    }): boolean => {
        const latest = handleFetchPostLatest.current;
        if (latest.postId !== requestedPostId) return false;
        // Path equality is the stable signal during scope switches. Chapter/section
        // ids can be temporarily unresolved while the next scope is hydrating, so
        // comparing them here can incorrectly drop the valid response and deadlock
        // the reader in a loading state.
        if (
            requestedPath
            && !deepEqual(latest.selectedPostValuePath, requestedPath)
        ) {
            return false;
        }
        return true;
    };
    const handleFetchPost = (): void => {
        let id = postId;
        if (!id) {
            id = uuidv4();
            setPostId(id);
        }
        if (currentZoomLevel == null) {
            // eslint-disable-next-line max-len
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered an error fetching post: missing zoom level. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Encountered an error fetching post: missing critical data. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!write && id) {
            if (hasPendingSelectedPathFromRouteParams) return;
            const readPostId = id;
            if (postFetchInFlightRef.current) {
                postFetchQueuedRef.current = true;
                if (!refreshPost) setRefreshPost(true);
                return;
            }
            postFetchInFlightRef.current = true;
            if (!refreshingPost) setRefreshingPost(true);
            const zoomLevel = currentZoomLevel?.level;
            // Monotonic token to ignore stale secondary hydration responses.
            readPostFetchCycleRef.current += 1;
            const fetchCycle = readPostFetchCycleRef.current;
            const applyFetchedPost = ({
                postResult,
                showErrorToastForMissingPost,
            }: {
                postResult: IGetPostResult,
                showErrorToastForMissingPost: boolean,
            }): {
                initiateAnnotationRefresh: boolean,
                resolvedReadScope?: {
                    chapterId?: string,
                    sectionId?: string,
                },
            } => {
                const cur = handleFetchPostLatest.current;
                let initiateAnnotationRefresh = false;
                let resolvedReadScope: {
                    chapterId?: string,
                    sectionId?: string,
                } | undefined;
                setReadFetchedPostState((prev) => {
                    const shouldPreserveScopedSnapshot = write;
                    let nextChapter = postResult.chapter;
                    if (nextChapter === undefined && shouldPreserveScopedSnapshot) {
                        nextChapter = prev.chapter;
                    }
                    let nextChapterSection = postResult.chapterSection;
                    if (
                        nextChapterSection === undefined
                        && shouldPreserveScopedSnapshot
                    ) {
                        nextChapterSection = prev.chapterSection;
                    }

                    return {
                        post: postResult.post ?? prev.post,
                        chapter: (
                            selectedPostValuePath.length === 0
                                ? undefined
                                : nextChapter
                        ),
                        chapterSection: (
                            selectedPostValuePath.length < 2
                                ? undefined
                                : nextChapterSection
                        ),
                    };
                });
                // We set this last because certain logic may trigger based on its presence
                // i.e., waiting for the post to arrive. We don't want these to trigger when
                // they expect chapters and sections to be there but arent in state yet.
                const currentRootPost = write
                    ? cur.post
                    : cur.readFetchedPostState.post;
                if (
                    postResult.post
                    && (
                        currentRootPost == null
                        || currentRootPost.title !== postResult.post.title
                        || currentRootPost.subtitle !== postResult.post.subtitle
                        || !deepEqual(currentRootPost.authors, postResult.post.authors)
                        || currentRootPost.imagePath !== postResult.post.imagePath
                        || currentRootPost.value !== postResult.post.value
                        || currentRootPost.previewValue !== postResult.post.previewValue
                    )
                ) {
                    if (!write) {
                        const shouldRequestResolvedReadScope = (
                            (
                                selectedPostValuePath.length === 1
                                && postResult.chapter == null
                            )
                            || (
                                selectedPostValuePath.length === 2
                                && postResult.chapterSection == null
                            )
                        );
                        if (shouldRequestResolvedReadScope) {
                            const resolvedChapterIdFromPost = (
                                postResult.post.chapters
                                && selectedPostValuePath.length > 0
                                && selectedPostValuePath[0] != null
                                && selectedPostValuePath[0] >= 0
                                && selectedPostValuePath[0] < postResult.post.chapters.length
                                && postResult.post.chapters[selectedPostValuePath[0]]
                            )
                                ? postResult.post.chapters[selectedPostValuePath[0]].id
                                : undefined;
                            const postChapterOrder = (
                                postResult.post as IReadPostItem & {
                                    chapterOrder?: string[],
                                }
                            ).chapterOrder || [];
                            const resolvedChapterIdFromOrder = (
                                selectedPostValuePath.length > 0
                                && selectedPostValuePath[0] != null
                                && selectedPostValuePath[0] >= 0
                                && selectedPostValuePath[0] < postChapterOrder.length
                            )
                                ? postChapterOrder[selectedPostValuePath[0]]
                                : undefined;
                            const resolvedChapterId = resolvedChapterIdFromPost != null
                                ? resolvedChapterIdFromPost
                                : resolvedChapterIdFromOrder;
                            resolvedReadScope = {
                                chapterId: resolvedChapterId,
                                sectionId: undefined,
                            };
                        }
                    }
                    if (write) {
                        // Keep local write state aligned with any optimistic outline edits
                        // so stale fetches do not reintroduce deleted scopes.
                        const nextPost = (
                            writeOutlineChapters != null
                            && getOutlineStructureKey(postResult.post?.chapters) !== getOutlineStructureKey(writeOutlineChapters)
                        )
                            ? mergeWriteOutlineChaptersIntoPost(postResult.post, writeOutlineChapters)
                            : postResult.post;
                        setPost(nextPost);
                    }
                    initiateAnnotationRefresh = true;
                } else if (
                    showErrorToastForMissingPost
                    && !postResult.post
                ) {
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_READ_POST_ERROR,
                        icon: ShelfIcon,
                        hasFailure: true,
                    });
                }
                if (
                    !write
                    && (
                        (
                            selectedPostValuePath.length === 1
                            && !!postResult.chapter
                        )
                        || (
                            selectedPostValuePath.length === 2
                            && !!postResult.chapterSection
                        )
                    )
                ) {
                    initiateAnnotationRefresh = true;
                }
                return {
                    initiateAnnotationRefresh,
                    resolvedReadScope,
                };
            };
            const shouldWaitForUserBeforeContent = !!(
                !write
                && user == null
            );
            const shouldDoMetadataOnlyRead = !!(
                !write
                && (
                    !fetchedPost
                    || shouldWaitForUserBeforeContent
                )
            );
            if (shouldDoMetadataOnlyRead && shouldWaitForUserBeforeContent) {
                setReadMetadataOnlyFetchAwaitingUser(true);
            }
            const requestedReadPath = shouldDoMetadataOnlyRead
                ? undefined
                : [...selectedPostValuePath];
            const requestedReadAuthSessionKey = getCurrentReadAuthSessionKey();
            if (shouldDoMetadataOnlyRead) {
                setReadFetchedPostState({
                    post: undefined,
                    chapter: undefined,
                    chapterSection: undefined,
                });
            }
            getPost({
                test: useStripeTestMode,
                postId: readPostId,
                fullPost: false,
                chapterId: shouldDoMetadataOnlyRead
                    ? undefined
                    : currentPostContentTarget.chapterId,
                sectionId: shouldDoMetadataOnlyRead
                    ? undefined
                    : currentPostContentTarget.sectionId,
                requestedPath: shouldDoMetadataOnlyRead
                    ? undefined
                    : selectedPostValuePath,
                zoomLevel,
                metadataOnly: shouldDoMetadataOnlyRead,
                includeReadOnlyValueNodes: !shouldDoMetadataOnlyRead,
                // Critical payload minimizes metadata for faster first render.
                responseTier: POST_RESPONSE_TIER.critical,
            }).then(async (getPostResult) => {
                if (
                    // Sign-out can bump the read fetch cycle after this request
                    // has already started. Drop that stale response before it
                    // can reapply signed-in content for a frame.
                    readPostFetchCycleRef.current !== fetchCycle
                    || (
                        !shouldDoMetadataOnlyRead
                        && !doesRequestedPathMatchLatest({
                            requestedPostId: readPostId,
                            requestedPath: requestedReadPath,
                        })
                    )
                ) {
                    return;
                }
                const cur = handleFetchPostLatest.current;
                const {
                    initiateAnnotationRefresh,
                    resolvedReadScope,
                } = applyFetchedPost({
                    postResult: getPostResult,
                    showErrorToastForMissingPost: true,
                });
                let shouldRefreshAnnotations = initiateAnnotationRefresh;
                if (
                    resolvedReadScope
                    && readPostFetchCycleRef.current === fetchCycle
                ) {
                    if (
                        !doesRequestedPathMatchLatest({
                            requestedPostId: readPostId,
                            requestedPath: requestedReadPath,
                        })
                    ) {
                        return;
                    }
                    const scopedPostResult = await getPost({
                        test: useStripeTestMode,
                        postId: readPostId,
                        fullPost: false,
                        chapterId: resolvedReadScope.chapterId,
                        sectionId: resolvedReadScope.sectionId,
                        requestedPath: selectedPostValuePath,
                        zoomLevel,
                        includeReadOnlyValueNodes: true,
                        responseTier: POST_RESPONSE_TIER.critical,
                    });
                    if (
                        readPostFetchCycleRef.current !== fetchCycle
                        || !doesRequestedPathMatchLatest({
                            requestedPostId: readPostId,
                            requestedPath: requestedReadPath,
                        })
                    ) {
                        return;
                    }
                    const scopedApplyResult = applyFetchedPost({
                        postResult: scopedPostResult,
                        showErrorToastForMissingPost: false,
                    });
                    shouldRefreshAnnotations = (
                        shouldRefreshAnnotations
                        || scopedApplyResult.initiateAnnotationRefresh
                    );
                }
                setReadFetchedPostAuthSessionKey(requestedReadAuthSessionKey);
                if (!shouldDoMetadataOnlyRead) {
                    setReadMetadataOnlyFetchAwaitingUser(false);
                }
                if (cur.refreshPost) setRefreshPost(false);
                if (cur.refreshingPost) setRefreshingPost(false);
                if (cur.updatingPost) setUpdatingPost(false);
                if (shouldRefreshAnnotations) {
                    // Defer annotation fetch/render until the post frame has painted.
                    queueFetchAnnotationsPostPaint({
                        resetHighlights: true,
                    });
                }
            }).catch(() => {
                const cur = handleFetchPostLatest.current;
                if (cur.refreshingPost) setRefreshingPost(false);
                if (cur.updatingPost) setUpdatingPost(false);
            }).finally(() => {
                const cur = handleFetchPostLatest.current;
                // Scope switches can invalidate this response before the happy-path
                // state cleanup runs. Always release the refreshing flag here so
                // queued follow-up fetches can actually start instead of leaving
                // the reader parked on the placeholder forever.
                if (cur.refreshingPost) setRefreshingPost(false);
                if (cur.updatingPost) setUpdatingPost(false);
                postFetchInFlightRef.current = false;
                if (postFetchQueuedRef.current) {
                    postFetchQueuedRef.current = false;
                    setQueuedPostFetchVersion((prev) => prev + 1);
                    setRefreshPost(true);
                }
            });
        } else if (
            write
            && userIsAdmin
            && id
        ) {
            if (postFetchInFlightRef.current) {
                postFetchQueuedRef.current = true;
                if (!refreshPost) setRefreshPost(true);
                return;
            }
            writePostFetchCycleRef.current += 1;
            const fetchCycle = writePostFetchCycleRef.current;
            const fetchStartedAtVersion = writeContentOverrideVersionRef.current;
            const requestedWritePath = [...selectedPostValuePath];
            postFetchInFlightRef.current = true;
            if (!refreshingPost) setRefreshingPost(true);
            if (user && user.id !== userIdDuringPreviousFetchPost) {
                setUserIdDuringPreviousFetchPost(user.id);
            }
            getPost({
                test: useStripeTestMode,
                postId: id,
                fullPost: true,
                metadataOnly: true,
                responseTier: POST_RESPONSE_TIER.critical,
            }).then((metadataOnlyPost) => {
                if (
                    writePostFetchCycleRef.current === fetchCycle
                    && doesRequestedPathMatchLatest({
                        requestedPostId: id!,
                        requestedPath: requestedWritePath,
                    })
                    && metadataOnlyPost.post
                ) {
                    setWriteBannerPost(metadataOnlyPost.post);
                }
            }).catch(() => undefined);
            getPost({
                test: useStripeTestMode,
                postId: id,
                fullPost: true,
                chapterId: currentPostContentTarget.chapterId,
                sectionId: currentPostContentTarget.sectionId,
            }).then(async (fullPost) => {
                if (
                    !doesRequestedPathMatchLatest({
                        requestedPostId: id!,
                        requestedPath: requestedWritePath,
                    })
                ) {
                    return;
                }
                if (
                    fullPost.post
                    && writePostFetchCycleRef.current === fetchCycle
                ) {
                    setWriteBannerPost(fullPost.post);
                }
                let hydratedFullPost = fullPost;
                if (fullPost.post) {
                    const fetchedPostContentTarget = resolvePostContentTarget({
                        postId: id,
                        post: fullPost.post,
                        path: selectedPostValuePath,
                    });
                    if (shouldUseWriteModeCollabDoc({
                        target: fetchedPostContentTarget,
                        currentPost: fullPost.post,
                    })) {
                        try {
                            const exportResult = await exportPostCollaborativeDoc({
                                test: useStripeTestMode,
                                docId: fetchedPostContentTarget.docId!,
                            });
                            hydratedFullPost = applyContentValueToFetchedPost({
                                postData: fullPost,
                                target: fetchedPostContentTarget,
                                value: exportResult.value,
                            });
                        } catch (_error) {
                            // Keep the fetched post shape as a fallback so write
                            // mode does not stall if draft export temporarily fails.
                        }
                    }
                }
                if (
                    writePostFetchCycleRef.current !== fetchCycle
                    || !doesRequestedPathMatchLatest({
                        requestedPostId: id!,
                        requestedPath: requestedWritePath,
                    })
                ) {
                    return;
                }
                hydratedFullPost = applyWriteContentOverridesToFetchedPost({
                    postData: hydratedFullPost,
                    fetchStartedAtVersion,
                });
                const cur = handleFetchPostLatest.current;
                setReadFetchedPostState({
                    post: hydratedFullPost.post || undefined,
                    chapter: hydratedFullPost.chapter || undefined,
                    chapterSection: hydratedFullPost.chapterSection || undefined,
                });
                if (writePostFetchCycleRef.current === fetchCycle) {
                    setWriteBannerPost(hydratedFullPost.post || undefined);
                }
                if (hydratedFullPost.post) {
                    // Reapply optimistic outline structure onto hydrated write data
                    // before it replaces local post state.
                    const nextPost = (
                        write
                        && writeOutlineChapters != null
                        && getOutlineStructureKey(hydratedFullPost.post.chapters) !== getOutlineStructureKey(writeOutlineChapters)
                    )
                        ? mergeWriteOutlineChaptersIntoPost(hydratedFullPost.post, writeOutlineChapters)
                        : hydratedFullPost.post;
                    setPost(nextPost);
                } else if (hydratedFullPost.post == null && id) {
                    // create new post
                    createPost({
                        test: useStripeTestMode,
                        postId: id,
                        title,
                        subtitle,
                    }).then((created) => {
                        if (!created) {
                            setSnackbarData({
                                visible: true,
                                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                                text: SNACKBAR_MESSAGE_CREATE_POST_ERROR,
                                icon: ShelfIcon,
                                hasFailure: true,
                            });
                        }
                    });
                }
                if (cur.refreshPost) setRefreshPost(false);
                if (cur.refreshingPost) setRefreshingPost(false);
                if (cur.updatingPost) setUpdatingPost(false);
            }).catch(() => {
                const cur = handleFetchPostLatest.current;
                if (cur.refreshingPost) setRefreshingPost(false);
                if (cur.updatingPost) setUpdatingPost(false);
            }).finally(() => {
                postFetchInFlightRef.current = false;
                if (postFetchQueuedRef.current) {
                    postFetchQueuedRef.current = false;
                    setQueuedPostFetchVersion((prev) => prev + 1);
                    setRefreshPost(true);
                }
            });
        } else if (
            write
            && !userIsAdmin
        ) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_WRITE_POST_ERROR,
                icon: ShelfIcon,
                hasFailure: true,
            });
        }
    };

    /**
     * Retrieves user items of all the current posts authors
     */
    const handleFetchPostAuthorsLatest = useLatest({
        postUserItems,
        fetchingPostAuthors,
        signingInOut,
    });
    const handleFetchPostAuthors = (): void => {
        if (!postId) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered an error fetching post authors: post id hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Encountered an error fetching post authors: post id hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (signingInOut || !auth.currentUser) {
            return;
        }
        if (!fetchingPostAuthors) setFetchingPostAuthors(true);
        getPostAuthorItems({
            test: useStripeTestMode,
            postId,
        }).then((authorItems) => {
            const cur = handleFetchPostAuthorsLatest.current;
            const authorIds = authorItems.map((item) => item.id);
            setPostAuthorIds(authorIds);
            addToPostUserItems({ userItems: authorItems }).then(() => {
                if (cur.fetchingPostAuthors) setFetchingPostAuthors(false);
            });
        }).catch((error: any) => {
            const cur = handleFetchPostAuthorsLatest.current;
            if (cur.fetchingPostAuthors) setFetchingPostAuthors(false);
            const errorCode = `${error?.code || ''}`.toLowerCase();
            const errorMessage = `${error?.message || ''}`.toLowerCase();
            const authTransitionFailure = (
                cur.signingInOut
                || !auth.currentUser
                || errorCode.includes('unauth')
                || errorMessage.includes('sign-in required')
                || errorMessage.includes('unauthorized')
            );
            if (authTransitionFailure) {
                return;
            }
            throw error;
        });
    };

    /**
     * Retrieves annotation data from Firestore if it exists, otherwise creates a new post
     * NOTE: No annotations published during calls to this method will be presented.
     * This decision is chosen to avoid constant reloads that would take place due to other
     * readers making new annotations, which would degrade the reading experience.
     */
    const fetchAnnotationsLatest = useLatest({
        annotations,
        annotationEngagementItems,
        pendingAnnotationCreationIds,
        pendingAnnotationFallbackTops,
        queuedAnnotationRetries,
        fetchedAnnotations,
        fetchedAnnotationScopeKey,
        fetchingAnnotations,
        write,
        user,
        refreshingContentsTable,
        currentAnnotationFetchScopeKey,
        postId,
        currentZoomLevel,
        annotationFetchChapterId: currentAnnotationFetchTarget.chapterId,
        annotationFetchSectionId: currentAnnotationFetchTarget.sectionId,
    });
    const queuedAnnotationRetriesLatest = useLatest({
        queuedAnnotationRetries,
    });
    const readModeAuthGuardLatest = useLatest({
        write,
        signingInOut,
        userId: user?.id || null,
        authUserId: auth.currentUser?.uid || null,
    });
    const shouldSkipReadModeAuthCalls = (): boolean => {
        const currentReadModeAuthGuard = readModeAuthGuardLatest.current;
        return !!(
            !currentReadModeAuthGuard.write
            && (
                currentReadModeAuthGuard.signingInOut
                || !currentReadModeAuthGuard.authUserId
                || !currentReadModeAuthGuard.userId
                || currentReadModeAuthGuard.userId !== currentReadModeAuthGuard.authUserId
            )
        );
    };
    const isTransientMissingUserForAnnotationFetch = (error: unknown): boolean => {
        const code = (error as { code?: string } | undefined)?.code;
        const message = (error as { message?: string } | undefined)?.message || '';
        return (
            code === 'functions/not-found'
            || code === 'not-found'
            || code === 'functions/unauthenticated'
            || code === 'unauthenticated'
            || message.includes('Sign-in required.')
            || message.includes('User doesn\'t exist: Abort fetching annotations.')
            || message.includes('User doesn’t exist: Abort fetching annotations.')
            || message.includes('User doesn\'t exist: Abort updating post.')
            || message.includes('User doesn’t exist: Abort updating post.')
        );
    };
    const fetchAnnotations = (): void => {
        if (shouldSkipReadModeAuthCalls()) return;
        const fetchCur = fetchAnnotationsLatest.current;
        if (!fetchCur.write && !fetchCur.user) return;
        if (!fetchCur.write && !canAccessReaderAnnotations) return;
        if (!fetchCur.postId) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered an error fetching annotations: post id hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Encountered an error fetching annotations: post id hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!fetchCur.currentZoomLevel) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered an error fetching annotations: current zoom level hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Encountered an error fetching annotations: current zoom level hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        const requestScopeKey = JSON.stringify({
            postId: fetchCur.postId,
            zoomLevel: fetchCur.currentZoomLevel.level,
            chapterId: fetchCur.annotationFetchChapterId || null,
            sectionId: fetchCur.annotationFetchSectionId || null,
        });
        if (!fetchCur.fetchingAnnotations) setFetchingAnnotations(true);
        logReaderAnnotation('annotation-fetch-start', {
            write: fetchCur.write,
            requestScopeKey,
            postId: fetchCur.postId,
            zoomLevel: fetchCur.currentZoomLevel.level,
            chapterId: fetchCur.annotationFetchChapterId || null,
            sectionId: fetchCur.annotationFetchSectionId || null,
            canAccessReaderAnnotations,
            currentAnnotationFetchScopeKey: fetchCur.currentAnnotationFetchScopeKey,
        });
        getAnnotations({
            test: useStripeTestMode,
            postId: fetchCur.postId,
            zoomLevel: fetchCur.currentZoomLevel.level,
            chapterId: fetchCur.annotationFetchChapterId,
            sectionId: fetchCur.annotationFetchSectionId,
        }).then((mostRecentAnnotations) => {
            const cur = fetchAnnotationsLatest.current;
            if (requestScopeKey !== cur.currentAnnotationFetchScopeKey) {
                return;
            }
            const latestAnnotations = cur.annotations;
            const now = Date.now();
            recentlyAckedAnnotationIdsRef.current.forEach((expiresAt, annotationId) => {
                if (expiresAt <= now) recentlyAckedAnnotationIdsRef.current.delete(annotationId);
            });
            const keyDiff = mapKeyDiff(mostRecentAnnotations, latestAnnotations);
            const updatedAnnotations = new Map([
                ...Array.from(latestAnnotations),
            ]);
            const reconciledPendingAnnotationIds = new Set<string>();
            // Tracks which optimistic annotation id should hand off its
            // placement metadata to which canonical server id during ack.
            const reconciledPendingToCanonicalIds = new Map<string, string>();
            const persistedPendingAnnotationIds = new Set<string>();
            let changed = false;

            if (keyDiff.missingFromB.length > 0) {
                for (let i = 0; i < keyDiff.missingFromB.length; i += 1) {
                    const annotationId = keyDiff.missingFromB[i];
                    if (mostRecentAnnotations.get(annotationId)) {
                        updatedAnnotations.set(annotationId, mostRecentAnnotations.get(annotationId)!);
                        changed = true;
                    }
                }
            }

            // Resolve concurrent updates on existing ids by preferring the fresher payload.
            mostRecentAnnotations.forEach((serverAnnotation, annotationId) => {
                const localAnnotation = latestAnnotations.get(annotationId);
                if (!localAnnotation) return;
                const annotationUpdatedAt = cur.annotationEngagementItems.get(annotationId)?.updatedAt;

                const serverFreshness = getAnnotationFreshnessTimestamp(
                    serverAnnotation,
                    annotationUpdatedAt,
                );
                const localFreshness = getAnnotationFreshnessTimestamp(
                    localAnnotation,
                    annotationUpdatedAt,
                );

                if (serverFreshness > localFreshness) {
                    updatedAnnotations.set(annotationId, serverAnnotation);
                    changed = true;
                }
            });

            if (keyDiff.missingFromA.length > 0) {
                for (let i = 0; i < keyDiff.missingFromA.length; i += 1) {
                    const annotationId = keyDiff.missingFromA[i];
                    const annotationIsOptimistic = cur.pendingAnnotationCreationIds.has(annotationId);
                    const annotationHasFallbackTop = cur.pendingAnnotationFallbackTops.has(annotationId);
                    const annotationHasQueuedRetry = cur.queuedAnnotationRetries.has(annotationId);
                    const annotationIdLooksOptimistic = annotationId.startsWith('pending-annotation-');
                    const annotationIsProtected = protectedAnnotationIdsRef.current.has(annotationId);
                    const annotationIsRecentlyAcked = (
                        recentlyAckedAnnotationIdsRef.current.get(annotationId) || 0
                    ) > now;
                    if (
                        !annotationIsOptimistic
                        && !annotationHasFallbackTop
                        && !annotationHasQueuedRetry
                        && !annotationIdLooksOptimistic
                        && !annotationIsProtected
                        && !annotationIsRecentlyAcked
                        && latestAnnotations.get(annotationId)
                    ) {
                        updatedAnnotations.delete(annotationId);
                        changed = true;
                    }
                }
            }

            // Reconcile optimistic top-level annotations with server-acknowledged
            // annotations so they don't briefly render as duplicates.
            if (cur.pendingAnnotationCreationIds.size > 0) {
                cur.pendingAnnotationCreationIds.forEach((pendingAnnotationId) => {
                    if (mostRecentAnnotations.has(pendingAnnotationId)) {
                        recentlyAckedAnnotationIdsRef.current.delete(pendingAnnotationId);
                        protectedAnnotationIdsRef.current.delete(pendingAnnotationId);
                        persistedPendingAnnotationIds.add(pendingAnnotationId);
                        return;
                    }
                    const pendingAnnotation = updatedAnnotations.get(pendingAnnotationId);
                    if (!pendingAnnotation || pendingAnnotation.comment) return;

                    const serverAnnotationMatch = Array.from(mostRecentAnnotations.values()).find((annotation) => (
                        !annotation.comment
                        && annotation.id !== pendingAnnotation.id
                        && annotation.userId === pendingAnnotation.userId
                        && annotation.chapterId === pendingAnnotation.chapterId
                        && annotation.sectionId === pendingAnnotation.sectionId
                        && annotation.history[0]?.text === pendingAnnotation.history[0]?.text
                        && deepEqual(
                            annotation.quoteHistory[0]?.texts || [],
                            pendingAnnotation.quoteHistory[0]?.texts || [],
                        )
                        && deepEqual(
                            annotation.quoteHistory[0]?.selections || [],
                            pendingAnnotation.quoteHistory[0]?.selections || [],
                        )
                    ));

                    if (serverAnnotationMatch) {
                        protectedAnnotationIdsRef.current.delete(pendingAnnotationId);
                        updatedAnnotations.delete(pendingAnnotationId);
                        reconciledPendingAnnotationIds.add(pendingAnnotationId);
                        reconciledPendingToCanonicalIds.set(
                            pendingAnnotationId,
                            serverAnnotationMatch.id,
                        );
                        changed = true;
                    }
                });
            }

            mostRecentAnnotations.forEach((_, annotationId) => {
                protectedAnnotationIdsRef.current.delete(annotationId);
            });

            if (changed) {
                setAnnotations((prev) => {
                    const merged = new Map([
                        ...Array.from(updatedAnnotations),
                    ]);
                    prev.forEach((annotation, annotationId) => {
                        const annotationIsProtected = protectedAnnotationIdsRef.current.has(annotationId);
                        const annotationIsRecentlyAcked = (
                            recentlyAckedAnnotationIdsRef.current.get(annotationId) || 0
                        ) > Date.now();
                        const annotationLooksOptimistic = annotationId.startsWith('pending-annotation-');
                        if (
                            reconciledPendingAnnotationIds.has(annotationId)
                        ) {
                            return;
                        }
                        if (
                            annotationIsProtected
                            || annotationIsRecentlyAcked
                            || annotationLooksOptimistic
                        ) {
                            const annotationCanonicalId = (
                                annotation as IAnnotationItem & { canonicalId?: string }
                            ).canonicalId;
                            if (
                                annotationCanonicalId
                                && merged.has(annotationCanonicalId)
                            ) {
                                return;
                            }
                            merged.set(annotationId, annotation);
                        }
                    });
                    return merged;
                });
            }
            logReaderAnnotation('annotation-fetch-success', {
                requestScopeKey,
                fetchedCount: mostRecentAnnotations.size,
                localCountBeforeMerge: latestAnnotations.size,
                missingFromLocalCount: keyDiff.missingFromB.length,
                missingFromServerCount: keyDiff.missingFromA.length,
                reconciledPendingCount: reconciledPendingAnnotationIds.size,
                persistedPendingCount: persistedPendingAnnotationIds.size,
                changed,
                sample: summarizeAnnotationsForDebug(
                    Array.from(mostRecentAnnotations.values()),
                    6,
                ),
            });
            if (reconciledPendingAnnotationIds.size > 0) {
                setPendingAnnotationCreationIds((prev) => {
                    const updated = new Set(prev);
                    reconciledPendingAnnotationIds.forEach((annotationId) => updated.delete(annotationId));
                    return updated;
                });
                setPendingAnnotationFallbackTops((prev) => {
                    const updated = new Map(prev);
                    reconciledPendingAnnotationIds.forEach((annotationId) => {
                        const canonicalAnnotationId = reconciledPendingToCanonicalIds.get(annotationId);
                        const fallbackTop = updated.get(annotationId);
                        updated.delete(annotationId);
                        if (
                            canonicalAnnotationId
                            && fallbackTop != null
                            && !updated.has(canonicalAnnotationId)
                        ) {
                            updated.set(canonicalAnnotationId, fallbackTop);
                        }
                    });
                    return updated;
                });
                setPendingAnnotationProvisionalParagraphIds((prev) => {
                    const updated = new Map(prev);
                    reconciledPendingAnnotationIds.forEach((annotationId) => {
                        const canonicalAnnotationId = reconciledPendingToCanonicalIds.get(annotationId);
                        const provisionalParagraphId = updated.get(annotationId);
                        updated.delete(annotationId);
                        if (
                            canonicalAnnotationId
                            && provisionalParagraphId
                            && !updated.has(canonicalAnnotationId)
                        ) {
                            updated.set(canonicalAnnotationId, provisionalParagraphId);
                        }
                    });
                    return updated;
                });
            }
            if (persistedPendingAnnotationIds.size > 0) {
                setPendingAnnotationCreationIds((prev) => {
                    const updated = new Set(prev);
                    persistedPendingAnnotationIds.forEach((annotationId) => updated.delete(annotationId));
                    return updated;
                });
            }
            if (
                !cur.fetchedAnnotations
                || cur.fetchedAnnotationScopeKey !== requestScopeKey
            ) {
                setFetchedAnnotations(true);
                setFetchedAnnotationScopeKey(requestScopeKey);
            }
            // Refresh contents table
            if (!cur.write && cur.user && !cur.refreshingContentsTable) {
                refreshContentsTable();
            }
        }).catch((error) => {
            const cur = fetchAnnotationsLatest.current;
            logReaderAnnotation('annotation-fetch-error', {
                requestScopeKey,
                message: (error as Error)?.message || String(error),
            });
            if (!cur.write) {
                return;
            }
            if (!isTransientMissingUserForAnnotationFetch(error)) {
                throw error;
            }
        }).finally(() => {
            const cur = fetchAnnotationsLatest.current;
            if (cur.fetchingAnnotations) setFetchingAnnotations(false);
            if (annotationRefreshQueuedRef.current) {
                annotationRefreshQueuedRef.current = false;
                queueFetchAnnotationsPostPaint({
                    resetHighlights: false,
                });
            }
        });
    };
    const queueFetchAnnotationsPostPaint = ({
        resetHighlights,
    }: {
        resetHighlights: boolean,
    }): void => {
        if (shouldSkipReadModeAuthCalls()) return;
        if (!write && !canAccessReaderAnnotations) return;
        // Coalesce repeated triggers and defer network work until after paint.
        if (
            fetchingAnnotations
            || annotationRefreshRafOneRef.current != null
            || annotationRefreshRafTwoRef.current != null
        ) {
            annotationRefreshQueuedRef.current = true;
        }
        if (
            annotationRefreshRafOneRef.current != null
            || annotationRefreshRafTwoRef.current != null
            || fetchingAnnotations
        ) {
            return;
        }
        if (
            resetHighlights
            && annotationHighlightsRendered
        ) {
            setAnnotationHighlightsRendered(false);
        }
        annotationRefreshRafOneRef.current = window.requestAnimationFrame(() => {
            annotationRefreshRafOneRef.current = null;
            annotationRefreshRafTwoRef.current = window.requestAnimationFrame(() => {
                annotationRefreshRafTwoRef.current = null;
                fetchAnnotations();
            });
        });
    };
    useEffect(() => {
        if (annotations.size === 0) {
            setAnnotationEngagementItems(new Map());
            return undefined;
        }

        return subscribeToAnnotationEngagementItems({
            test: useStripeTestMode,
            // Engagement docs are keyed by the canonical server id, so use that
            // whenever an optimistic row has already been acked.
            annotationIds: Array.from(annotations.values()).map((annotation) => (
                (annotation as IAnnotationItem & { canonicalId?: string }).canonicalId || annotation.id
            )),
            onNext: setAnnotationEngagementItems,
        });
    }, [
        useStripeTestMode,
        annotations,
    ]);
    useEffect(() => {
        const scopeKey = currentPostContentTarget.docId || null;
        const deletedAnnotationCount = effectiveCurrentPostEngagement?.deletedAnnotationCount ?? null;
        const previousBaseline = currentPostEngagementRefreshBaselineRef.current;

        currentPostEngagementRefreshBaselineRef.current = {
            scopeKey,
            visibleAnnotationCount: currentScopeVisibleAnnotationCount,
            deletedAnnotationCount,
        };

        if (
            scopeKey == null
            || currentScopeVisibleAnnotationCount == null
            || deletedAnnotationCount == null
            || previousBaseline.scopeKey !== scopeKey
            || previousBaseline.visibleAnnotationCount == null
            || previousBaseline.deletedAnnotationCount == null
        ) {
            return;
        }

        if (
            previousBaseline.visibleAnnotationCount !== currentScopeVisibleAnnotationCount
            || previousBaseline.deletedAnnotationCount !== deletedAnnotationCount
        ) {
            if (
                fetchedAnnotations
                && !fetchingAnnotations
            ) {
                queueFetchAnnotationsPostPaint({
                    resetHighlights: false,
                });
            }
        }
    }, [
        currentPostContentTarget.docId,
        currentScopeVisibleAnnotationCount,
        effectiveCurrentPostEngagement?.deletedAnnotationCount,
        fetchedAnnotations,
        fetchingAnnotations,
    ]);
    useEffect(() => {
        if (
            write
            || !postId
            || !currentZoomLevel
            || !canAccessReaderAnnotations
            || (
                fetchedAnnotations
                && fetchedAnnotationScopeKey === currentAnnotationFetchScopeKey
            )
            || fetchingAnnotations
            || !currentPostContentTarget.isResolved
        ) {
            return;
        }
        queueFetchAnnotationsPostPaint({
            resetHighlights: true,
        });
    }, [
        write,
        postId,
        currentZoomLevel,
        canAccessReaderAnnotations,
        fetchedAnnotations,
        fetchedAnnotationScopeKey,
        fetchingAnnotations,
        currentPostContentTarget.isResolved,
        currentAnnotationFetchScopeKey,
        currentAnnotationFetchTarget.chapterId,
        currentAnnotationFetchTarget.sectionId,
        queueFetchAnnotationsPostPaint,
    ]);
    useEffect(() => () => {
        annotationRefreshQueuedRef.current = false;
        if (annotationRefreshRafOneRef.current != null) {
            window.cancelAnimationFrame(annotationRefreshRafOneRef.current);
            annotationRefreshRafOneRef.current = null;
        }
        if (annotationRefreshRafTwoRef.current != null) {
            window.cancelAnimationFrame(annotationRefreshRafTwoRef.current);
            annotationRefreshRafTwoRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (queuedAnnotationRetries.size === 0) return;
        const interval = setInterval(() => {
            const cur = queuedAnnotationRetriesLatest.current;
            const now = Date.now();
            if (!clientIsOnline()) return;
            cur.queuedAnnotationRetries.forEach((retryItem, optimisticAnnotationId) => {
                if (retryItem.inFlight || retryItem.nextRetryAt > now) return;

                setQueuedAnnotationRetries((prev) => {
                    const updated = new Map(prev);
                    const target = updated.get(optimisticAnnotationId);
                    if (target) {
                        updated.set(optimisticAnnotationId, {
                            ...target,
                            inFlight: true,
                        });
                    }
                    return updated;
                });

                createAnnotation({
                    test: useStripeTestMode,
                    postId: retryItem.postId,
                    isComment: false,
                    value: retryItem.value,
                    text: retryItem.text,
                    quote: retryItem.quote,
                    mediaIds: retryItem.mediaIds,
                    zoomLevel: retryItem.zoomLevel,
                    chapterId: retryItem.chapterId,
                    sectionId: retryItem.sectionId,
                    requestId: optimisticAnnotationId,
                }).then((annotation) => {
                    protectedAnnotationIdsRef.current.add(optimisticAnnotationId);
                    recentlyAckedAnnotationIdsRef.current.set(
                        annotation.id,
                        Date.now() + ANNOTATION_ACK_PROTECTION_DURATION_MS,
                    );
                    recentlyAckedAnnotationIdsRef.current.delete(optimisticAnnotationId);
                    if (shouldShowPreviewDebugLogs) {
                        // eslint-disable-next-line no-console
                        console.log('[optimistic-annotation]', 'acked', {
                            optimisticAnnotationId,
                            annotationId: annotation.id,
                            postId: retryItem.postId,
                            recoveredFromRetry: true,
                        });
                    }
                    setAnnotations((prev) => {
                        const updated = new Map([
                            ...Array.from(prev),
                        ]);
                        const optimisticAnnotation = updated.get(optimisticAnnotationId);
                        if (optimisticAnnotation) {
                            // Keep optimistic id until server snapshot contains canonical row.
                            // Prevents ack-time disappear/reappear when fan-out is delayed.
                            const ackedOptimisticAnnotation: IAnnotationItem & { canonicalId?: string } = {
                                ...annotation,
                                id: optimisticAnnotationId,
                                canonicalId: annotation.id,
                            };
                            updated.set(optimisticAnnotationId, ackedOptimisticAnnotation);
                        }
                        return updated;
                    });
                    setPendingAnnotationCreationIds((prev) => {
                        const updated = new Set(prev);
                        updated.add(optimisticAnnotationId);
                        return updated;
                    });
                    setPendingAnnotationFallbackTops((prev) => prev);
                    setQueuedAnnotationRetries((prev) => {
                        const updated = new Map(prev);
                        updated.delete(optimisticAnnotationId);
                        return updated;
                    });
                    clearTimeoutAnnotationNudge();
                    setExpandAnnotationStackWithAnnotationId(optimisticAnnotationId);
                }).catch(() => {
                    setQueuedAnnotationRetries((prev) => {
                        const updated = new Map(prev);
                        const target = updated.get(optimisticAnnotationId);
                        if (!target) return updated;
                        const nextAttempt = target.attempt + 1;
                        const nextDelayMs = Math.min(
                            ANNOTATION_RETRY_BASE_DELAY_MS * (2 ** nextAttempt),
                            ANNOTATION_RETRY_MAX_DELAY_MS,
                        );
                        updated.set(optimisticAnnotationId, {
                            ...target,
                            attempt: nextAttempt,
                            nextRetryAt: Date.now() + nextDelayMs,
                            inFlight: false,
                        });
                        return updated;
                    });
                });
            });
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [
        queuedAnnotationRetries.size,
    ]);

    /**
     * Locally updates a single annotation
     */
    const refreshAnnotationLatest = useLatest({
        annotations,
    });
    const refreshAnnotation = ({ annotationId }: { annotationId: string }): void => {
        if (!postId) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered an error updating annotation: post id hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Encountered an error updating annotation: post id hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!currentZoomLevel) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `Encountered an error updating annotation: current zoom level hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`Encountered an error updating annotation: current zoom level hasn't been set. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        const currentAnnotation = annotations.get(annotationId);
        // While we keep optimistic id in UI, server pushes land on canonical id.
        const sourceAnnotationId = (
            currentAnnotation as (IAnnotationItem & { canonicalId?: string }) | undefined
        )?.canonicalId || annotationId;
        getAnnotationById({
            test: useStripeTestMode,
            annotationId: sourceAnnotationId,
        }).then((serverAnnotation) => {
            const cur = refreshAnnotationLatest.current;
            const updatedAnnotations = new Map([
                ...Array.from(cur.annotations),
            ]);
            if (serverAnnotation) {
                const updatedAnnotation = sourceAnnotationId === annotationId
                    ? serverAnnotation
                    : ({
                        ...serverAnnotation,
                        id: annotationId,
                        canonicalId: sourceAnnotationId,
                    } as IAnnotationItem & { canonicalId?: string });
                const existingAnnotation = cur.annotations.get(annotationId);
                if (
                    existingAnnotation
                    && existingAnnotation.history.length === updatedAnnotation.history.length
                    && areAnnotationDictationAssetsEqual({
                        currentDictationFilePaths: existingAnnotation.dictationFilePaths,
                        nextDictationFilePaths: updatedAnnotation.dictationFilePaths,
                        currentTranscripts: existingAnnotation.transcripts,
                        nextTranscripts: updatedAnnotation.transcripts,
                    })
                    && existingAnnotation.media.length === updatedAnnotation.media.length
                    && existingAnnotation.quoteHistory.length === updatedAnnotation.quoteHistory.length
                    && existingAnnotation.detached?.detached === updatedAnnotation.detached?.detached
                ) {
                    return;
                }
                updatedAnnotations.set(annotationId, updatedAnnotation);
            }
            setAnnotations(updatedAnnotations);
        }).catch((error) => {
            if (!isTransientMissingUserForAnnotationFetch(error)) {
                throw error;
            }
        });
    };

    /**
     * Determines whethr post update can be executed immediately
     * or whether it must be queued because someone else is currently
     * updating the post
     */
    const handleUpdatePost = async (updates: {
        test: boolean,
        postId: string,
        title?: string,
        subtitle?: string,
        description?: string,
        imagePath?: string,
    }): Promise<boolean | void> => {
        const executeWriterPostUpdate = async (): Promise<boolean> => {
            setUpdatingPost(true);
            try {
                const result = await updatePost(updates);
                return result.success;
            } finally {
                setUpdatingPost(false);
            }
        };

        if (user && post) {
            if (updatingPost) {
                enqueuePostUpdate(executeWriterPostUpdate);
                return false;
            }
            return executeWriterPostUpdate();
        }
        setSnackbarData({
            visible: true,
            duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
            text: SNACKBAR_MESSAGE_UPDATE_POST_ERROR,
            icon: PencilIcon,
            hasFailure: true,
        });
    };

    const handleUpdatePostOutline = async (updates: {
        test: boolean,
        postId: string,
        action: POST_OUTLINE_MUTATION_TYPE,
        chapterId?: string,
        sectionId?: string,
        title?: string,
        description?: string,
        targetIndex?: number,
        newChapterId?: string,
        newSectionId?: string,
    }): Promise<boolean | void> => {
        const executeWriterPostOutlineUpdate = async (): Promise<boolean> => {
            setUpdatingPost(true);
            try {
                const result = await updatePostOutline(updates);
                return result.success;
            } finally {
                setUpdatingPost(false);
            }
        };

        if (user && post) {
            if (updatingPost) {
                enqueuePostUpdate(executeWriterPostOutlineUpdate);
                return false;
            }
            return executeWriterPostOutlineUpdate();
        }
        setSnackbarData({
            visible: true,
            duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
            text: SNACKBAR_MESSAGE_UPDATE_POST_ERROR,
            icon: PencilIcon,
            hasFailure: true,
        });
    };

    /**
     * Modifies the title of the post in Firestore
     * @param newTitle updated title
     */
    const handleTitleChange = (newTitle: string): void => {
        if (post) {
            handleUpdatePost({
                test: useStripeTestMode,
                postId: post.id,
                title: newTitle,
            });
        }
    };

    /**
     * Modifies the subtitle of the post in Firestore
     * @param newSubtitle updated subtitle
     */
    const handleSubtitleChange = (newSubtitle: string): void => {
        if (post) {
            handleUpdatePost({
                test: useStripeTestMode,
                postId: post.id,
                subtitle: newSubtitle,
            });
        }
    };

    const queuePostMetadataUpdate = useCallback((
        field: 'title' | 'subtitle',
        value: string,
    ): void => {
        pendingPostMetadataUpdateValuesRef.current[field] = value;

        if (pendingPostMetadataUpdateTimeoutsRef.current[field] != null) {
            window.clearTimeout(pendingPostMetadataUpdateTimeoutsRef.current[field]);
        }

        pendingPostMetadataUpdateTimeoutsRef.current[field] = window.setTimeout(() => {
            const nextValue = pendingPostMetadataUpdateValuesRef.current[field];
            if (field === 'title') {
                handleTitleChange(nextValue);
            } else {
                handleSubtitleChange(nextValue);
            }
            delete pendingPostMetadataUpdateTimeoutsRef.current[field];
        }, POST_METADATA_UPDATE_DEBOUNCE_DURATION);
    }, [handleSubtitleChange, handleTitleChange]);

    const flushPostMetadataUpdate = useCallback((
        field: 'title' | 'subtitle',
        value: string,
    ): void => {
        pendingPostMetadataUpdateValuesRef.current[field] = value;

        if (pendingPostMetadataUpdateTimeoutsRef.current[field] != null) {
            window.clearTimeout(pendingPostMetadataUpdateTimeoutsRef.current[field]);
            delete pendingPostMetadataUpdateTimeoutsRef.current[field];
        }

        if (field === 'title') {
            handleTitleChange(value);
        } else {
            handleSubtitleChange(value);
        }
    }, [handleSubtitleChange, handleTitleChange]);

    /**
     * Modifies the post content in Firestore
     * @param value updated post content
     */
    const handlePostChange = (
        value: string,
        path?: number[],
        checkpointReason?: POST_COLLAB_DOC_CHECKPOINT_REASON,
    ): void => {
        if (!!post && user && write) {
            const writePostTargetSource = writePostWithOutlinePreview || post;
            const target = resolvePostContentTarget({
                postId: post.id,
                post: writePostTargetSource,
                path: path || [],
            });
            setPostValueIsSaving(true);
            if (shouldUseWriteModeCollabDoc({
                target,
                currentPost: writePostTargetSource,
            })) {
                replacePostCollaborativeDoc({
                    test: useStripeTestMode,
                    docId: target.docId!,
                    value,
                    checkpointReason,
                }).then(() => {
                    const updatedTimestamp = Date.now();
                    if (target.docId) {
                        writeContentOverrideVersionRef.current += 1;
                        writeContentOverridesRef.current.set(target.docId, {
                            target,
                            value,
                            version: writeContentOverrideVersionRef.current,
                        });
                    }
                    setLocalScopeSaveTimestamps((prev) => {
                        let scopeId: string | undefined;
                        if (target.scope === POST_CONTENT_SCOPE.section) {
                            scopeId = target.sectionId;
                        } else if (target.scope === POST_CONTENT_SCOPE.chapter) {
                            scopeId = target.chapterId;
                        } else {
                            scopeId = post.id;
                        }
                        if (!scopeId) return prev;
                        const scopeSaveKey = buildPostScopeKey({
                            postId: post.id,
                            scope: target.scope,
                            scopeId,
                        });
                        return {
                            ...prev,
                            [scopeSaveKey]: updatedTimestamp,
                        };
                    });
                    setDraftStatusNow(updatedTimestamp);
                    syncLocalContentValue({
                        target,
                        value,
                    });
                    if (user.id && !post.authors.includes(user.id)) {
                        return addPostAuthor({
                            test: useStripeTestMode,
                            postId: post.id,
                            authorId: user.id,
                        }).then(() => {
                            setReadFetchedPostState((prev) => {
                                if (!prev.post || prev.post.authors.includes(user.id)) {
                                    return prev;
                                }
                                return {
                                    ...prev,
                                    post: {
                                        ...prev.post,
                                        authors: [
                                            ...prev.post.authors,
                                            user.id,
                                        ],
                                    },
                                };
                            });
                            setPost((prev) => {
                                if (!prev || prev.authors.includes(user.id)) {
                                    return prev;
                                }
                                return {
                                    ...prev,
                                    authors: [
                                        ...prev.authors,
                                        user.id,
                                    ],
                                };
                            });
                        });
                    }
                    return undefined;
                }).catch(() => {
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_UPDATE_POST_ERROR,
                        icon: PencilIcon,
                        hasFailure: true,
                    });
                }).finally(() => {
                    setPostValueIsSaving(false);
                });
            } else {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_UPDATE_POST_ERROR,
                    icon: PencilIcon,
                    hasFailure: true,
                });
                setPostValueIsSaving(false);
            }
        }
    };

    const handleRestorePostVersion = (value: string): void => {
        setPendingPostVersionRestore({
            id: uuidv4(),
            value,
        });
        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: 'Version restored!',
            icon: PencilIcon,
            hasSuccess: true,
        });
    };

    const handleRestorePostValueApplied = (requestId: string): void => {
        setPendingPostVersionRestore((currentPendingRestore) => (
            currentPendingRestore?.id === requestId
                ? null
                : currentPendingRestore
        ));
    };

    /**
     * Adds a new media item to the uploading queue
     * @param mediaItem media item to upload
     */
    const addUploadingMedia = (mediaItem: IMediaItem): void => {
        setUploadingMedia(new Map([
            ...Array.from(uploadingMedia.entries()),
            [mediaItem.id, mediaItem],
        ]));
    };

    /**
     * Updates an existing media item in the uploading queue
     * @param mediaItem media item to update
     */
    const updateUploadingMedia = (mediaItem: IMediaItem): void => {
        setUploadingMedia(new Map([
            ...Array.from(uploadingMedia.entries()),
            [mediaItem.id, mediaItem],
        ]));
    };

    /**
     * Removes a media item from the uploading queue
     * @param mediaId media item to remove from the uploading queue
     */
    const removeUploadingMedia = (mediaId: string): void => {
        if (uploadingMedia.has(mediaId)) {
            uploadingMedia.delete(mediaId);
            setUploadingMedia(new Map([
                ...Array.from(uploadingMedia.entries()),
            ]));
        }
    };

    /**
     * Queues a cart item to animate from a particular book purchase button
     * @param item details of the item being animated
     * @param type type of book being animated which maps to one of three book purchase buttons
     */
    const addAnimatingCartItem = (
        item: IMediaItem,
        type: BOOK_TYPE,
    ): void => {
        if (item.description) {
            setActiveCartAnimationSourceId(item.description);
        }
        switch (type) {
        case BOOK_TYPE.physical:
            setAnimatePhysicalBookToCartButtonItems(new Map([
                ...Array.from(animatePhysicalBookToCartButtonItems.entries()),
                [item.id, item],
            ]));
            break;
        case BOOK_TYPE.audio:
        case BOOK_TYPE.digital:
            setAnimateDigitalBookToCartButtonItems(new Map([
                ...Array.from(animateDigitalBookToCartButtonItems.entries()),
                [item.id, item],
            ]));
            break;
        default:
            // BOOK_TYPE.web
            setAnimateWebBookToCartButtonItems(new Map([
                ...Array.from(animateWebBookToCartButtonItems.entries()),
                [item.id, item],
            ]));
        }
    };

    const onCartItemAnimationComplete = (id: string): void => {
        const updatedWebItems = new Map(animateWebBookToCartButtonItems);
        const updatedDigitalItems = new Map(animateDigitalBookToCartButtonItems);
        const updatedPhysicalItems = new Map(animatePhysicalBookToCartButtonItems);

        if (updatedWebItems.has(id)) {
            updatedWebItems.delete(id);
            setAnimateWebBookToCartButtonItems(updatedWebItems);
        } else if (updatedDigitalItems.has(id)) {
            updatedDigitalItems.delete(id);
            setAnimateDigitalBookToCartButtonItems(updatedDigitalItems);
        } else if (updatedPhysicalItems.has(id)) {
            updatedPhysicalItems.delete(id);
            setAnimatePhysicalBookToCartButtonItems(updatedPhysicalItems);
        }

        if (animatingWebBookCartItem && updatedWebItems.size === 0) setAnimatingWebBookCartItem(false);
        if (animatingDigitalBookCartItem && updatedDigitalItems.size === 0) setAnimatingDigitalBookCartItem(false);
        if (animatingPhysicalBookCartItem && updatedPhysicalItems.size === 0) setAnimatingPhysicalBookCartItem(false);

        if (
            updatedWebItems.size === 0
            && updatedDigitalItems.size === 0
            && updatedPhysicalItems.size === 0
            && activeCartAnimationSourceId != null
        ) {
            setActiveCartAnimationSourceId(null);
            setActiveCartAnimationSourceCenter(null);
        }

        const stagedCartItem = stagedCartItems.get(id);
        let updatedCart;
        if (
            !!stagedCartItem
            && userCartItems
        ) {
            switch (stagedCartItem.cartOperation) {
            case STAGED_CART_ITEM_CART_OPERATION.filterOut:
                updatedCart = [
                    ...(userCartItems.filter((item: ICartItem) => !stagedCartItem.operationArgument!.includes(item.productId))),
                    stagedCartItem.cartItem,
                ];
                break;
            default:
                // STAGED_CART_ITEM_CART_OPERATION.none
                updatedCart = [
                    ...userCartItems,
                    stagedCartItem.cartItem,
                ];
            }
            // Update locally
            setUserCartItems(updatedCart);
            // Update in server
            updateUser({
                test: useStripeTestMode,
                cart: updatedCart,
            });

            // Play Sound
            if (hasSound && successClip.current) {
                successClip.current.pause();
                successClip.current.currentTime = 0;
                playAudio(successClip.current);
            }
        } else if (
            !!stagedCartItem
            && !userCartItems
        ) {
            // Update locally
            setUserCartItems([stagedCartItem.cartItem]);
            // Update in server
            updateUser({
                test: useStripeTestMode,
                cart: [stagedCartItem.cartItem],
            });

            // Play Sound
            if (hasSound && successClip.current) {
                successClip.current.pause();
                successClip.current.currentTime = 0;
                playAudio(successClip.current);
            }
        } else {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_ADD_CART_ITEM_TO_CART_ERROR,
                icon: CartIcon,
                hasFailure: true,
            });
        }
    };

    /**
     * Submits an annotation to Firestore
     * @param value serialized annotation text
     * @param text annotation text
     * @param quote highlighed quote associated with annotation
     * @param media optional media associated with annotation
     */
    const handleSubmitAnnotation = (
        value: string,
        text: string,
        quote: IAnnotationQuote,
        mediaIds: string[],
    ): void => {
        const logOptimisticAnnotationLifecycle = (
            event: 'created' | 'acked' | 'failed',
            data: Record<string, unknown>,
        ): void => {
            if (!shouldShowPreviewDebugLogs || event !== 'acked') return;
            // eslint-disable-next-line no-console
            console.log('[optimistic-annotation]', event, data);
        };

        if (!annotationEditor) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem creating annotation. Editor reference not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem creating annotation. Editor reference not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!user) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem creating annotation. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem creating annotation. User data not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!activeRootPost) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem creating annotation. Post ID not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem creating annotation. Post ID not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }
        if (!currentZoomLevel) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: `There was a problem creating annotation. Zoom level not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    },
                });
            }
            // eslint-disable-next-line max-len
            throw Error(`There was a problem creating annotation. Zoom level not found. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
        }

        // Shouldn't ever get here
        // In the event it does prevent anonymous user from creating an annotation
        if (
            auth.currentUser
            && auth.currentUser.isAnonymous
        ) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SIGN_IN_ANNOTATION_CREATE_MESSAGE,
                icon: HighlightIcon,
                hasFailure: true,
            });
            return;
        }

        const annotationDraftStorageKey = `reader-annotation-draft:${activeRootPost.id}:${
            selectedPostValuePath.length > 0
                ? selectedPostValuePath.join('-')
                : 'root'
        }:${currentZoomLevel.level}`;
        const annotationReopenStorageKey = `reader-annotation-reopen:${activeRootPost.id}`;
        const resolvePendingAnnotationPlacement = (): {
            top: number,
            paragraphId?: string,
        } => {
            if (postEditor) {
                const targetSelection = quote.selections[0];
                if (targetSelection) {
                    try {
                        const domRange = ReactEditor.toDOMRange(
                            postEditor as ReactEditor,
                            targetSelection as Range,
                        );
                        const { startContainer } = domRange;
                        const startElement = startContainer.nodeType === Node.ELEMENT_NODE
                            ? startContainer as HTMLElement
                            : startContainer.parentElement;
                        if (startElement) {
                            const paragraph = findParentNodeWithClass(
                                SLATE_PARAGRAPH_CLASSNAME,
                                startElement,
                                10,
                            );
                            if (paragraph) {
                                const paragraphId = `${paragraph.dataset.paragraphId || ''}`.trim() || undefined;
                                return {
                                    // Keep optimistic annotation placement in the post
                                    // editor's own coordinate space so later chunk appends
                                    // do not drift existing buckets.
                                    top: getAnnotationParagraphTopWithinEditor(paragraph),
                                    paragraphId,
                                };
                            }
                        }
                    } catch (error) {
                        // Selection path may become stale while UI is reconciling.
                    }
                }
            }

            // Fallback if quote highlight cannot be resolved.
            return {
                top: Math.max(annotationEditorPositionRef.current.y + postScrollTop.current, 0),
            };
        };
        const restoreAnnotationDraft = (): void => {
            if (!annotationEditor) return;
            try {
                const draft = sessionStorage.getItem(annotationDraftStorageKey);
                if (!draft) return;
                const parsedDraft = JSON.parse(draft) as { value?: string };
                if (!parsedDraft.value) return;
                const slateValue = JSON.parse(parsedDraft.value) as Descendant[];
                if (!Array.isArray(slateValue) || slateValue.length === 0) return;
                annotationEditor.children = slateValue;
                annotationEditor.onChange();
                const endPoint = Editor.end(annotationEditor, []);
                Transforms.select(annotationEditor, endPoint);
            } catch (error) {
                // Best-effort local recovery only
            }
        };

        try {
            try {
                sessionStorage.removeItem(annotationReopenStorageKey);
                sessionStorage.setItem(
                    annotationDraftStorageKey,
                    JSON.stringify({
                        value,
                        text,
                        savedAt: Date.now(),
                    }),
                );
            } catch (error) {
                // Best-effort local recovery only
            }
            const optimisticAnnotationId = `pending-annotation-${uuidv4()}`;
            protectedAnnotationIdsRef.current.add(optimisticAnnotationId);
            const createdTimestamp = Date.now();
            const optimisticChapter = selectedPostValuePath.length > 0
                ? activeRootPost.chapters?.[selectedPostValuePath[0]]
                : undefined;
            const optimisticSection = selectedPostValuePath.length === 2
                ? optimisticChapter?.sections?.[selectedPostValuePath[1]]
                : undefined;
            let optimisticTargetScope = ANNOTATION_TARGET_SCOPE.post;
            let optimisticTargetId = activeRootPost.id;
            if (optimisticChapter) {
                optimisticTargetScope = ANNOTATION_TARGET_SCOPE.chapter;
                optimisticTargetId = optimisticChapter.id;
            }
            if (optimisticSection) {
                optimisticTargetScope = ANNOTATION_TARGET_SCOPE.section;
                optimisticTargetId = optimisticSection.id;
            }
            const optimisticAnnotation: IAnnotationItem = {
                id: optimisticAnnotationId,
                postId: activeRootPost.id,
                targetScope: optimisticTargetScope,
                targetId: optimisticTargetId,
                chapterId: optimisticChapter?.id,
                sectionId: optimisticSection?.id,
                isComment: false,
                history: [{
                    id: uuidv4(),
                    timestamp: createdTimestamp,
                    value,
                    text,
                }],
                userId: user.id,
                media: [...mediaIds],
                quoteHistory: [{
                    ...quote,
                    selections: [...quote.selections],
                    texts: [...quote.texts],
                    textAnchors: quote.textAnchors?.map((textAnchor) => ({
                        ...textAnchor,
                    })),
                }],
                published: createdTimestamp,
                detached: {
                    detached: false,
                    timestamp: null,
                    detachedBy: null,
                    reason: null,
                },
                deleted: {
                    deleted: false,
                    timestamp: null,
                    deletedBy: null,
                },
            };
            setAnnotations((prev) => {
                const updated = new Map([
                    ...Array.from(prev),
                ]);
                updated.set(optimisticAnnotationId, optimisticAnnotation);
                return updated;
            });
            setPendingAnnotationCreationIds((prev) => {
                const updated = new Set(prev);
                updated.add(optimisticAnnotationId);
                return updated;
            });
            const pendingAnnotationPlacement = resolvePendingAnnotationPlacement();
            setPendingAnnotationFallbackTops((prev) => {
                const updated = new Map(prev);
                updated.set(
                    optimisticAnnotationId,
                    pendingAnnotationPlacement.top,
                );
                return updated;
            });
            setPendingAnnotationProvisionalParagraphIds((prev) => {
                if (!pendingAnnotationPlacement.paragraphId) return prev;
                const updated = new Map(prev);
                updated.set(optimisticAnnotationId, pendingAnnotationPlacement.paragraphId);
                return updated;
            });
            incrementContentsTableAnnotationCount({
                chapterId: currentPostContentTarget.chapterId,
                sectionId: currentPostContentTarget.sectionId,
            });
            logOptimisticAnnotationLifecycle('created', {
                optimisticAnnotationId,
                postId: activeRootPost.id,
                path: selectedPostValuePath,
                zoomLevel: currentZoomLevel.level,
            });
            if (!clientIsOnline()) {
                // Silent offline path: keep optimistic row and let queued retry handle delivery.
                setQueuedAnnotationRetries((prev) => {
                    const updated = new Map(prev);
                    updated.set(optimisticAnnotationId, {
                        postId: activeRootPost.id,
                        value,
                        text,
                        quote,
                        mediaIds,
                        zoomLevel: currentZoomLevel.level,
                        chapterId: currentPostContentTarget.chapterId,
                        sectionId: currentPostContentTarget.sectionId,
                        attempt: 0,
                        nextRetryAt: Date.now() + ANNOTATION_RETRY_BASE_DELAY_MS,
                        inFlight: false,
                    });
                    return updated;
                });
                logOptimisticAnnotationLifecycle('failed', {
                    optimisticAnnotationId,
                    postId: activeRootPost.id,
                    reason: 'offline',
                });
                return;
            }
            createAnnotation({
                test: useStripeTestMode,
                postId: activeRootPost.id,
                isComment: false,
                value,
                text,
                quote,
                mediaIds,
                zoomLevel: currentZoomLevel.level,
                chapterId: currentPostContentTarget.chapterId,
                sectionId: currentPostContentTarget.sectionId,
                // Reuse optimistic id as idempotency key for backend create retries.
                requestId: optimisticAnnotationId,
            }).then((annotation) => {
                protectedAnnotationIdsRef.current.add(optimisticAnnotationId);
                recentlyAckedAnnotationIdsRef.current.set(
                    annotation.id,
                    Date.now() + ANNOTATION_ACK_PROTECTION_DURATION_MS,
                );
                recentlyAckedAnnotationIdsRef.current.delete(optimisticAnnotationId);
                try {
                    sessionStorage.removeItem(annotationReopenStorageKey);
                    sessionStorage.removeItem(annotationDraftStorageKey);
                } catch (error) {
                    // Best-effort local recovery only
                }
                // Swap to the server id immediately so downstream live updates
                // (e.g. dictation readiness) bind to the canonical annotation id.
                logOptimisticAnnotationLifecycle('acked', {
                    optimisticAnnotationId,
                    annotationId: annotation.id,
                    postId: activeRootPost.id,
                });
                clearTimeoutAnnotationNudge();
                setAnnotations((prev) => {
                    const updated = new Map([
                        ...Array.from(prev),
                    ]);
                    const existingOptimisticAnnotation = updated.get(optimisticAnnotationId);
                    if (existingOptimisticAnnotation) {
                        // Keep optimistic id until server snapshot contains canonical row.
                        // Prevents ack-time disappear/reappear when fan-out is delayed.
                        const ackedOptimisticAnnotation: IAnnotationItem & { canonicalId?: string } = {
                            ...annotation,
                            id: optimisticAnnotationId,
                            canonicalId: annotation.id,
                        };
                        updated.set(optimisticAnnotationId, ackedOptimisticAnnotation);
                    }
                    return updated;
                });
                setPendingAnnotationCreationIds((prev) => {
                    const updated = new Set(prev);
                    updated.add(optimisticAnnotationId);
                    return updated;
                });
                setPendingAnnotationFallbackTops((prev) => prev);
                setAnnotationEditorIsVisible(false);
                // If annotation is part of an annotation stack, we want to expand it so the user
                // sees that their annotation was created. The problem is that we can't determine if it's
                // part of a stack before the fact, so we set a flag with the new comment's id that'll notify
                // the relevant annotation backet to expand if it is a stack
                setExpandAnnotationStackWithAnnotationId(optimisticAnnotationId);

                if (currentSessionId) {
                    // Record user action
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.createAnnotation,
                        sessionId: currentSessionId,
                        payload: {
                            annotationId: annotation.id,
                        },
                    });
                }
            }).catch(() => {
                setQueuedAnnotationRetries((prev) => {
                    const updated = new Map(prev);
                    updated.set(optimisticAnnotationId, {
                        postId: activeRootPost.id,
                        value,
                        text,
                        quote,
                        mediaIds,
                        zoomLevel: currentZoomLevel.level,
                        chapterId: currentPostContentTarget.chapterId,
                        sectionId: currentPostContentTarget.sectionId,
                        attempt: 0,
                        nextRetryAt: Date.now() + ANNOTATION_RETRY_BASE_DELAY_MS,
                        inFlight: false,
                    });
                    return updated;
                });
                logOptimisticAnnotationLifecycle('failed', {
                    optimisticAnnotationId,
                    postId: activeRootPost.id,
                });
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_ANNOTATION_CREATE_ERROR,
                    icon: HighlightIcon,
                    hasFailure: true,
                });
            });
        } catch (e) {
            try {
                sessionStorage.setItem(annotationReopenStorageKey, '1');
            } catch (error) {
                // Best-effort local recovery only
            }
            setPostQuote({
                ...quote,
                selections: [...quote.selections],
                texts: [...quote.texts],
            });
            setAnnotationEditorIsVisible(true);
            restoreAnnotationDraft();
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_ANNOTATION_CREATE_ERROR,
                icon: HighlightIcon,
                hasFailure: true,
            });
        }
    };

    /**
     * Detached annotations keep their original id and content. Reattachment
     * only needs a new anchored quote plus a cleared detached state.
     */
    const resolveAnnotationById = useCallback((annotationId: string): IAnnotationItem | null => {
        const directAnnotation = annotations.get(annotationId);
        if (directAnnotation) {
            return directAnnotation;
        }

        return Array.from(annotations.values()).find((annotation) => (
            (annotation as IAnnotationItem & { canonicalId?: string }).canonicalId === annotationId
            || annotation.id === annotationId
        )) || null;
    }, [annotations]);

    const clearDetachedAnnotationReattachState = useCallback(({
        clearQuote = true,
    }: {
        clearQuote?: boolean,
    } = {}): void => {
        if (detachedAnnotationsPanelPinAnimationFrameOneRef.current != null) {
            window.cancelAnimationFrame(detachedAnnotationsPanelPinAnimationFrameOneRef.current);
            detachedAnnotationsPanelPinAnimationFrameOneRef.current = null;
        }
        if (detachedAnnotationsPanelPinAnimationFrameTwoRef.current != null) {
            window.cancelAnimationFrame(detachedAnnotationsPanelPinAnimationFrameTwoRef.current);
            detachedAnnotationsPanelPinAnimationFrameTwoRef.current = null;
        }
        setDetachedAnnotationReattachTargetId(null);
        setDetachedAnnotationReattachInFlightId(null);
        setDetachedAnnotationPendingQuote(null);
        setDetachedAnnotationsPanelPinOffsetY(0);
        setDetachedAnnotationsPanelAnimateToPinnedCenter(false);
        setAnnotationEditorIsVisible(false);
        annotationEditorDraftValueRef.current = null;

        if (clearQuote) {
            setPostQuote(null);
        }
    }, []);
    /**
     * Measures the detached panel before it pins so the desktop repair lane can
     * animate from its in-flow position into the centered fixed state.
     */
    const prepareDetachedAnnotationsPanelPinAnimation = useCallback((): void => {
        if (detachedAnnotationsPanelPinAnimationFrameOneRef.current != null) {
            window.cancelAnimationFrame(detachedAnnotationsPanelPinAnimationFrameOneRef.current);
            detachedAnnotationsPanelPinAnimationFrameOneRef.current = null;
        }
        if (detachedAnnotationsPanelPinAnimationFrameTwoRef.current != null) {
            window.cancelAnimationFrame(detachedAnnotationsPanelPinAnimationFrameTwoRef.current);
            detachedAnnotationsPanelPinAnimationFrameTwoRef.current = null;
        }

        const panelElement = detachedAnnotationsPanelRef.current;
        if (
            viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min
            || !panelElement
        ) {
            setDetachedAnnotationsPanelPinOffsetY(0);
            setDetachedAnnotationsPanelAnimateToPinnedCenter(false);
            return;
        }

        const panelRect = panelElement.getBoundingClientRect();
        const centeredTop = (window.innerHeight - panelRect.height) / 2;
        const nextPinOffsetY = panelRect.top - centeredTop;

        if (Math.abs(nextPinOffsetY) < 1) {
            setDetachedAnnotationsPanelPinOffsetY(0);
            setDetachedAnnotationsPanelAnimateToPinnedCenter(false);
            return;
        }

        setDetachedAnnotationsPanelPinOffsetY(nextPinOffsetY);
        setDetachedAnnotationsPanelAnimateToPinnedCenter(false);
        detachedAnnotationsPanelPinAnimationFrameOneRef.current = window.requestAnimationFrame(() => {
            detachedAnnotationsPanelPinAnimationFrameOneRef.current = null;
            detachedAnnotationsPanelPinAnimationFrameTwoRef.current = window.requestAnimationFrame(() => {
                detachedAnnotationsPanelPinAnimationFrameTwoRef.current = null;
                setDetachedAnnotationsPanelAnimateToPinnedCenter(true);
                setDetachedAnnotationsPanelPinOffsetY(0);
            });
        });
    }, [
        viewportDimensions.width,
    ]);
    useEffect(() => () => {
        if (detachedAnnotationsPanelPinAnimationFrameOneRef.current != null) {
            window.cancelAnimationFrame(detachedAnnotationsPanelPinAnimationFrameOneRef.current);
            detachedAnnotationsPanelPinAnimationFrameOneRef.current = null;
        }
        if (detachedAnnotationsPanelPinAnimationFrameTwoRef.current != null) {
            window.cancelAnimationFrame(detachedAnnotationsPanelPinAnimationFrameTwoRef.current);
            detachedAnnotationsPanelPinAnimationFrameTwoRef.current = null;
        }
    }, []);

    /**
     * Detached repair guidance needs to remain visible while the author works
     * in the post body, so keep these snackbars open until the next repair
     * action replaces them or the author dismisses them.
     */
    const showDetachedAnnotationRepairSnackbar = useCallback((text: string): void => {
        setSnackbarData({
            visible: true,
            text,
            icon: HighlightIcon,
            dismissable: true,
        });
    }, [
        setSnackbarData,
    ]);

    const startDetachedAnnotationReattach = useCallback((annotation: IAnnotationItem): void => {
        prepareDetachedAnnotationsPanelPinAnimation();
        setDetachedAnnotationReattachTargetId(annotation.id);
        setDetachedAnnotationReattachInFlightId(null);
        setDetachedAnnotationPendingQuote(null);
        setAnnotationEditorIsVisible(false);
        annotationEditorDraftValueRef.current = null;
        setPostQuote(null);
        showDetachedAnnotationRepairSnackbar(
            'Select replacement text in the post to reattach this annotation.',
        );
    }, [
        prepareDetachedAnnotationsPanelPinAnimation,
        showDetachedAnnotationRepairSnackbar,
    ]);

    const cancelDetachedAnnotationReattach = useCallback((): void => {
        clearDetachedAnnotationReattachState();
        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: 'Stopped reattaching annotation.',
            icon: HighlightIcon,
        });
    }, [
        clearDetachedAnnotationReattachState,
        setSnackbarData,
    ]);

    /**
     * Stages a replacement quote so the author can confirm the repair before
     * we write the updated anchor to Firestore.
     */
    const stageDetachedAnnotationReattach = useCallback((quote: IAnnotationQuote): void => {
        setDetachedAnnotationPendingQuote(quote);
        setPostQuote(quote);
        showDetachedAnnotationRepairSnackbar(
            'Replacement text selected. Confirm reattach in the detached annotation card, or select different text.',
        );
    }, [
        showDetachedAnnotationRepairSnackbar,
    ]);

    /**
     * Clears a staged replacement while keeping the detached annotation in
     * repair mode so the author can choose another passage.
     */
    const clearDetachedAnnotationPendingQuoteState = useCallback((): void => {
        setDetachedAnnotationPendingQuote(null);
        setPostQuote(null);
        showDetachedAnnotationRepairSnackbar(
            'Select replacement text in the post to reattach this annotation.',
        );
    }, [
        showDetachedAnnotationRepairSnackbar,
    ]);

    const reattachDetachedAnnotation = useCallback(async (quote: IAnnotationQuote): Promise<void> => {
        if (!detachedAnnotationReattachTargetId) {
            setPostQuote(quote);
            return;
        }

        const targetAnnotation = resolveAnnotationById(detachedAnnotationReattachTargetId);
        if (!targetAnnotation) {
            clearDetachedAnnotationReattachState({ clearQuote: false });
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: 'Could not find the detached annotation to reattach.',
                icon: HighlightIcon,
                hasFailure: true,
            });
            return;
        }

        const canonicalAnnotationId = (
            targetAnnotation as IAnnotationItem & { canonicalId?: string }
        ).canonicalId || targetAnnotation.id;
        const attachedState = buildAttachedAnnotationState();
        const repairedChapterId = currentPostContentTarget.chapterId || undefined;
        const repairedSectionId = currentPostContentTarget.sectionId || undefined;
        const repairedTarget = resolveAnnotationTargetScopeForReader({
            postId: targetAnnotation.postId,
            chapterId: repairedChapterId,
            sectionId: repairedSectionId,
        });

        setDetachedAnnotationReattachInFlightId(canonicalAnnotationId);

        try {
            await reattachDetachedAnnotationService({
                test: useStripeTestMode,
                annotationId: canonicalAnnotationId,
                quote,
                chapterId: currentPostContentTarget.chapterId || null,
                sectionId: currentPostContentTarget.sectionId || null,
            });

            const updatedAnnotation: IAnnotationItem = {
                ...targetAnnotation,
                ...repairedTarget,
                chapterId: repairedChapterId,
                sectionId: repairedSectionId,
                detached: attachedState,
                quoteHistory: [
                    quote,
                    ...targetAnnotation.quoteHistory,
                ],
            };
            setAnnotations((prev) => {
                const updated = new Map(prev);
                updated.set(targetAnnotation.id, updatedAnnotation);
                return updated;
            });
            clearDetachedAnnotationReattachState();
            refreshAnnotation({ annotationId: targetAnnotation.id });
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: 'Annotation reattached.',
                icon: HighlightIcon,
            });
        } catch (_error) {
            setDetachedAnnotationReattachInFlightId(null);
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: 'Could not reattach annotation. Please try again.',
                icon: HighlightIcon,
                hasFailure: true,
            });
        }
    }, [
        detachedAnnotationReattachTargetId,
        currentPostContentTarget.chapterId,
        currentPostContentTarget.sectionId,
        resolveAnnotationById,
        clearDetachedAnnotationReattachState,
        refreshAnnotation,
        reattachDetachedAnnotationService,
        setSnackbarData,
        useStripeTestMode,
    ]);

    /**
     * Detached annotation repair is explicit: selection capture stages the
     * quote, and a second confirmation commits it.
     */
    const confirmDetachedAnnotationReattach = useCallback(async (): Promise<void> => {
        if (!detachedAnnotationPendingQuote) {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: 'Select replacement text before confirming reattach.',
                icon: HighlightIcon,
                hasFailure: true,
            });
            return;
        }

        await reattachDetachedAnnotation(detachedAnnotationPendingQuote);
    }, [
        detachedAnnotationPendingQuote,
        reattachDetachedAnnotation,
        setSnackbarData,
    ]);

    const handlePostQuoteChange = useCallback((
        nextQuote: React.SetStateAction<IAnnotationQuote | null>,
    ): void => {
        if (typeof nextQuote === 'function') {
            setPostQuote(nextQuote);
            return;
        }

        if (!nextQuote) {
            setPostQuote(null);
            return;
        }

        if (!detachedAnnotationReattachTargetId) {
            setPostQuote(nextQuote);
            return;
        }

        stageDetachedAnnotationReattach(nextQuote);
    }, [
        detachedAnnotationReattachTargetId,
        stageDetachedAnnotationReattach,
    ]);

    /**
     * Write mode usually does not mount the floating annotation composer. A
     * detached annotation repair still needs that quote-capture layer, so we
     * mount it only while a reattach target is active.
     */
    const shouldRenderDetachedAnnotationQuoteCaptureEditor = useMemo(() => (
        write
        && !!detachedAnnotationReattachTargetId
    ), [
        write,
        detachedAnnotationReattachTargetId,
    ]);

    const selectionSpansMultipleLines = (selection: Selection): boolean => (
        selection.rangeCount > 0
            ? resolveSelectionSpansMultipleLines(Array.from(selection.getRangeAt(0).getClientRects()))
            : false
    );
    /**
     * Keep the live draft outside AnnotationEditor so a breakpoint-driven
     * remount can reopen the same unsaved content in the docked or floating
     * layout without forcing ReaderView to rerender on every Slate change.
     */
    const persistAnnotationEditorDraftValue = useCallback((draftValue: Descendant[] | null): void => {
        annotationEditorDraftValueRef.current = draftValue;
    }, []);

    const handleAnnotationEditorVisibilityChange = useCallback((show: boolean): void => {
        setAnnotationEditorIsVisible(show);
    }, []);

    const updateAnnotationEditorPosition = (
        recomputePlacement?: boolean,
        selectionPlacementHint?: {
            selectionIsBackward: boolean,
            selectionIsMultiLine: boolean,
        },
    ): void => {
        const nativeSelection = window.getSelection();
        const shouldRecomputePlacement = recomputePlacement ?? false;
        if (shouldRecomputePlacement) {
            /**
             * A fresh text selection should use the normal floating-editor
             * transition even if the reader just scrolled. Otherwise deep-post
             * highlights can appear to snap because the temporary scroll-follow
             * transition suppression is still active.
             */
            if (scrollWillChangeTimeoutRef.current != null) {
                window.clearTimeout(scrollWillChangeTimeoutRef.current);
                scrollWillChangeTimeoutRef.current = null;
            }
            setScrollWillChange(false);
        }
        let y = 0;
        let x = 0;
        let placement = annotationEditorPlacementRef.current;
        if (shouldRecomputePlacement || !postQuote) {
            const selectionRange = nativeSelection && nativeSelection.rangeCount > 0
                ? nativeSelection.getRangeAt(0)
                : null;
            let selectionIsMultiLine = false;
            if (typeof selectionPlacementHint?.selectionIsMultiLine === 'boolean') {
                selectionIsMultiLine = selectionPlacementHint.selectionIsMultiLine;
            } else if (nativeSelection) {
                selectionIsMultiLine = selectionSpansMultipleLines(nativeSelection);
            }

            let selectionIsBackward = false;
            if (typeof selectionPlacementHint?.selectionIsBackward === 'boolean') {
                selectionIsBackward = selectionPlacementHint.selectionIsBackward;
            } else if (nativeSelection) {
                selectionIsBackward = resolveSelectionIsBackward({
                    anchorNode: nativeSelection.anchorNode,
                    focusNode: nativeSelection.focusNode,
                    anchorOffset: nativeSelection.anchorOffset,
                    focusOffset: nativeSelection.focusOffset,
                    rangeStartNode: selectionRange?.startContainer || null,
                    rangeStartOffset: selectionRange?.startOffset,
                    rangeEndNode: selectionRange?.endContainer || null,
                    rangeEndOffset: selectionRange?.endOffset,
                    documentRef: document,
                });
            }
            annotationEditorSelectionSpansMultipleLinesRef.current = selectionIsMultiLine;
            annotationEditorSelectionIsBackwardRef.current = selectionIsBackward;
            placement = nativeSelection
                ? resolveAnnotationEditorPlacement({
                    isMultiLineSelection: selectionIsMultiLine,
                    selectionIsBackward,
                })
                : 'top';
        }
        if (placement !== annotationEditorPlacementRef.current) {
            annotationEditorPlacementRef.current = placement;
        }
        if (
            nativeSelection
            && nativeSelection.rangeCount > 0
            && containerRef.current
            && annotationEditorRef.current
        ) {
            // For read
            // We don't use Dropzone Ref
            const range = nativeSelection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const toolbarWidth = annotationEditorRef.current.children[0]
                ? annotationEditorRef.current.children[0].clientWidth
                : 0;
            y = placement === 'bottom'
                ? Math.max(rect.bottom - containerRect.top + 7, 0)
                : Math.max(
                    rect.top - containerRect.top - (DEFAULT_ANNOTATION_EDITOR_HEIGHT + 7),
                    0,
                );
            x = Math.max(
                ((rect.left - containerRect.left + rect.width / 2)
                    - (toolbarWidth / 2)
                ),
                0,
            );
        } else if (
            nativeSelection
            && nativeSelection.rangeCount > 0
            && rootRef.current
            && annotationEditorRef.current
        ) {
            // For write
            // We use Dropzone Ref
            const range = nativeSelection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const rootRect = rootRef.current.getBoundingClientRect();
            const toolbarWidth = annotationEditorRef.current.children[0]
                ? annotationEditorRef.current.children[0].clientWidth
                : 0;
            y = placement === 'bottom'
                ? rect.bottom - rootRect.top + 7
                : rect.top - rootRect.top - (DEFAULT_ANNOTATION_EDITOR_HEIGHT + 7);
            x = Math.max(
                ((rect.left - rootRect.left + rect.width / 2)
                    - (toolbarWidth / 2)
                ),
                0,
            );
        }
        commitAnnotationEditorPosition({
            x,
            y,
        });
    };

    useEffect(() => {
        annotationEditorPositionRef.current = annotationEditorPosition;
        if (shouldDockAnnotationEditorToViewport(viewportDimensions.width)) {
            resetAnnotationEditorDomPosition();
            return;
        }

        if (!scrollWillChangeActiveRef.current) {
            syncAnnotationEditorDomPosition(annotationEditorPosition);
        }
    }, [
        annotationEditorPosition,
        viewportDimensions.width,
    ]);

    useEffect(() => {
        if (annotationEditorIsVisible || postQuote) {
            return;
        }

        annotationEditorDraftValueRef.current = null;
    }, [
        annotationEditorIsVisible,
        postQuote,
    ]);

    /**
     * Resizes the relative viewport partitioning between the annotation view and the post view\
     * Also enforces minimum and maximum constraints for the annotation view
     * @param e resize event
     * @param data resize data
     */
    const onResize = (
        e: React.SyntheticEvent<Element, Event>,
        data: ResizeCallbackData,
    ): void => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) return;

        setAnnotationViewWidth(data.size.width);
        if (data.size.width <= MIN_ANNOTATION_SECTION_WIDTH && !hitDetailMinimum) {
            setHitDetailMinimum(true);

            // Play Sound
            if (hasSound && resizeConstraintThumpClip.current) {
                resizeConstraintThumpClip.current.pause();
                resizeConstraintThumpClip.current.currentTime = 0;
                playAudio(resizeConstraintThumpClip.current);
            }

            // Change Cursor
            setCursorSigns([CURSOR_SIGN.caution]);
        } else if (hitDetailMinimum && data.size.width > MIN_ANNOTATION_SECTION_WIDTH) {
            setHitDetailMinimum(false);

            // Change Cursor
            if (isSteering) {
                setCursorSigns([CURSOR_SIGN.horizontalSteer]);
            } else if (handlebarHovered) {
                setCursorSigns([CURSOR_SIGN.grab]);
            }
        } else if ((viewportDimensions.width - data.size.width) <= MIN_POST_SECTION_WIDTH && !hitPostMinimum) {
            setHitPostMinimum(true);

            // Play Sound
            if (hasSound && resizeConstraintThumpClip.current) {
                resizeConstraintThumpClip.current.pause();
                resizeConstraintThumpClip.current.currentTime = 0;
                playAudio(resizeConstraintThumpClip.current);
            }

            // Change Cursor
            setCursorSigns([CURSOR_SIGN.caution]);
        } else if (hitPostMinimum && (viewportDimensions.width - data.size.width) > MIN_POST_SECTION_WIDTH) {
            setHitPostMinimum(false);

            // Change Cursor
            if (isSteering) {
                setCursorSigns([CURSOR_SIGN.horizontalSteer]);
            } else if (handlebarHovered) {
                setCursorSigns([CURSOR_SIGN.grab]);
            }
        }
    };

    /**
     * Manages response to annotation view resize handlebar hovering over
     * @param e mouse or touch event
     */
    const onHandlebarMouseEnter = (e: React.MouseEvent): void => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) return;

        setHandlebarHovered(true);
        if (!isSteering) {
            onCursorEnter(
                CURSOR_TARGET.handlebar,
                [CURSOR_SIGN.grab],
                e.currentTarget as HTMLElement,
            );
        }
    };

    /**
     * Manages response to annotation view resize handlebar no longer hovering over
     * @param e mouse or touch event
     */
    const onHandlebarMouseLeave = (e: React.MouseEvent): void => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) return;

        setHandlebarHovered(false);
        if (!isSteering) {
            onCursorLeave(e);
        }
    };

    /**
     * Manages response to starting resize annotation view
     * @param e resize event
     */
    const onResizeStart = (e: React.SyntheticEvent): void => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) return;

        // Freeze bucket width for steering duration to avoid expensive layout churn while dragging.
        setAnnotationBucketWidth(annotationViewWidth);
        setIsSteering(true);
        onCursorEnter(
            CURSOR_TARGET.handlebar,
            [CURSOR_SIGN.horizontalSteer],
            e.currentTarget as HTMLElement,
        );

        // Play Sound
        if (hasSound && handlebarGraspClip.current) {
            handlebarGraspClip.current.pause();
            handlebarGraspClip.current.currentTime = 0;
            playAudio(handlebarGraspClip.current);
        }
    };

    /**
     * Manages response to ending resize annotation view
     * @param e resize event
     */
    const onResizeStop = (
        e: React.SyntheticEvent,
        data: ResizeCallbackData,
    ): void => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) return;

        // Re-sync bucket width to final split width once steering finishes.
        setAnnotationBucketWidth(data.size.width);
        setIsSteering(false);
        if (hitDetailMinimum) {
            setHitDetailMinimum(false);
        }
        if (hitPostMinimum) {
            setHitPostMinimum(false);
        }
        if (!handlebarHovered) {
            onCursorLeave(e);
        } else {
            // Change Cursor
            setCursorSigns([CURSOR_SIGN.grab]);
        }

        updateAnnotationEditorPosition();

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.adjustPostSplitView,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Manages presenting an annotation to user
     * @param annotation annotation to present
     */
    const onShowAnnotationDetail = (annotation: IAnnotationItem): void => {
        setAnnotationDetailData(annotation);
    };

    /**
     * Manages hiding an annotation from user
     */
    const onHideAnnotationDetail = (onlyClearAnnotationDetailData?: boolean): void => {
        setAnnotationDetailData(null);
        setTargetAnnotationID(null);

        if (
            !onlyClearAnnotationDetailData
            && annotationIsFocused?.params.annotationId
        ) {
            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.closeAnnotation,
                    sessionId: currentSessionId,
                    payload: {
                        annotationId: annotationIsFocused?.params.annotationId,
                    },
                });
            }

            // Close button automatically disappears before unlocking cursor
            onCursorLeave();

            navigate(
                `${readerBasePath}?${searchParams.toString()}`,
                {
                    state: location.state,
                },
            );
        }
    };

    /**
     * Manages determining whether keystroke is enter or escape
     * If enter: moves focus to subtitle input
     * If escape: blurs focused input
     * @param e keyboard event
     */
    const checkForEnter = (e: React.KeyboardEvent): void => {
        if (e.key === KEYCODE.enter) {
            // Move from title field to subtitle field
            if (
                titleInputRef.current
                && titleInputRef.current === document.activeElement
            ) {
                subtitleInputRef.current?.focus();
            }
        } else if (e.key === KEYCODE.escape) {
            // Blur Focused Input
            (document.activeElement as HTMLInputElement).blur();
        }
    };

    /**
     * Manages response to post title or subtitle input hovering over
     * @param e mouse event
     */
    const onInputMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to post title or subtitle input no longer hovering over
     * @param e mouse event
     */
    const onInputMouseLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Manages response to auth-locked email input hovering over
     * @param e mouse event
     */
    const onAuthLockedEmailInputMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.type],
            e.currentTarget as HTMLElement,
        );
    };

    const onAuthLockedEmailInputFocus = (e: React.FocusEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.input,
            [CURSOR_SIGN.type],
            e.currentTarget as HTMLElement,
        );
    };
    const onAuthLockedEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === KEYCODE.enter) {
            e.preventDefault();
            if (
                !authLockedEmailValid
                || signingInOut
                || authLockedEmailSubmitting
                || authLockedSignInMethod != null
            ) {
                return;
            }
            handleAuthLockedEmailCapture();
        } else if (e.key === KEYCODE.escape) {
            (document.activeElement as HTMLInputElement)?.blur();
        }
    };

    /**
     * Manages response to button hovering over
     * @param e mouse event
     */
    const onButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        const target = e.currentTarget as HTMLElement;
        if (!target?.isConnected) return;
        const rect = target.getBoundingClientRect();
        if (
            rect.width <= 0
            || rect.height <= 0
        ) {
            return;
        }
        onCursorEnter(
            CURSOR_TARGET.editorButton,
            [CURSOR_SIGN.click],
            target,
        );
    };

    /**
     * Manages response to button no longer hovering over
     * @param e mouse event
     */
    const onButtonMouseLeave = (e?: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    /**
     * Uses the modal close cursor treatment for dialog exit affordances.
     */
    const onModalCloseButtonMouseEnter = useCallback((e: React.MouseEvent | React.TouchEvent): void => {
        const target = e.currentTarget as HTMLElement;
        if (!target?.isConnected) return;

        onCursorEnter(
            CURSOR_TARGET.modalCloseButton,
            [CURSOR_SIGN.click],
            target,
        );
    }, [onCursorEnter]);

    /**
     * Forces click cursor sign for the annotation visibility toggle hover.
     */
    const onAnnotationVisibilityButtonMouseEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.annotationToggleButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to title or subtitle input focusing
     */
    const onInputFocus = (): void => {
        // Play Sound
        if (hasSound && inputClickClip.current) {
            inputClickClip.current.pause();
            inputClickClip.current.currentTime = 0;
            playAudio(inputClickClip.current);
        }
    };

    /**
     * Manages response to title input changing
     */
    const onTitleInputChange = (): void => {
        if (titleInputRef.current) {
            const newTitle = titleInputRef.current.value;
            if (newTitle !== title) queuePostMetadataUpdate('title', newTitle);

            updatePageTitle(
                write ? 'Write' : 'Read',
                newTitle.length > 0 ? newTitle : DEFAULT_POST_TITLE,
            );
        }
    };

    /**
     * Manages response to subtitle input changing
     */
    const onSubtitleInputChange = (): void => {
        if (subtitleInputRef.current) {
            const newSubtitle = subtitleInputRef.current.value;
            if (newSubtitle !== subtitle) queuePostMetadataUpdate('subtitle', newSubtitle);
        }
    };

    const onTitleInputBlur = (): void => {
        if (titleInputRef.current) {
            flushPostMetadataUpdate('title', titleInputRef.current.value);
        }
    };

    const onSubtitleInputBlur = (): void => {
        if (subtitleInputRef.current) {
            flushPostMetadataUpdate('subtitle', subtitleInputRef.current.value);
        }
    };

    // Stable key per post/chapter(/section) so we can persist independent positions.
    const buildReaderProgressKey = useCallback(({
        resolvedPostId,
        path,
    }: {
        resolvedPostId: string | null | undefined,
        path: number[],
    }): string | null => {
        if (!resolvedPostId) {
            return null;
        }
        if (path.length === 0) {
            return `${resolvedPostId}::root`;
        }
        if (path[0] == null) {
            return null;
        }
        return path.length > 1 && path[1] != null
            ? `${resolvedPostId}:${path[0]}:${path[1]}`
            : `${resolvedPostId}:${path[0]}`;
    }, []);

    // Clears pending debounce writes whenever we force flush or unmount.
    const clearReaderProgressPersistTimeout = useCallback((): void => {
        if (readerProgressPersistTimeoutRef.current != null) {
            window.clearTimeout(readerProgressPersistTimeoutRef.current);
            readerProgressPersistTimeoutRef.current = null;
        }
    }, []);
    const clearResumeReaderProgressEligibilityWait = useCallback((): void => {
        if (resumeReaderProgressEligibilityWaitCancelRef.current) {
            resumeReaderProgressEligibilityWaitCancelRef.current();
            resumeReaderProgressEligibilityWaitCancelRef.current = null;
        }
    }, []);
    const clearResumeReaderProgressActionWait = useCallback((): void => {
        if (resumeReaderProgressActionWaitCancelRef.current) {
            resumeReaderProgressActionWaitCancelRef.current();
            resumeReaderProgressActionWaitCancelRef.current = null;
        }
        resumeReaderProgressAwaitingStabilityRef.current = false;
    }, []);
    const clearResumeReaderProgressCorrectionTimeout = useCallback((): void => {
        if (resumeReaderProgressCorrectionTimeoutRef.current != null) {
            window.cancelAnimationFrame(resumeReaderProgressCorrectionTimeoutRef.current);
            resumeReaderProgressCorrectionTimeoutRef.current = null;
        }
        resumeReaderProgressAwaitingGeometryRef.current = false;
        authLockAutoResumeAppliedTargetRef.current = null;
    }, []);
    const clearAuthLockAutoResumeRetryTimeout = useCallback((): void => {
        if (authLockAutoResumeRetryTimeoutRef.current != null) {
            window.cancelAnimationFrame(authLockAutoResumeRetryTimeoutRef.current);
            authLockAutoResumeRetryTimeoutRef.current = null;
        }
    }, []);
    const notifyAuthLockAutoResumeTick = useCallback((): void => {
        setAuthLockAutoResumeTick((currentTick) => currentTick + 1);
    }, []);
    // Keeps existing memo instrumentation call sites intact without emitting timing noise.
    const logReaderMemoTiming = useCallback((
        _label: string,
        _startedAt: number,
        _payload?: Record<string, unknown>,
    ): void => undefined, []);
    // Reader-resume tracing now has one path: optional console logs for live
    // debugging plus the silent runtime hook that keeps tests deterministic.
    const logResumeFlow = useCallback((
        event: READER_RESUME_LOG_EVENT | 'authLockAutoPostRefreshRequested' | 'authLockAutoPostRefreshReady' | 'authLockAutoWaitingForResumePrereqs',
        payload?: Record<string, unknown>,
    ): void => {
        const nextPayload = payload || {};

        // Console logging stays opt-in so resume tracing is available when we
        // need it without reintroducing the default browser-console noise.
        if (shouldEmitReaderResumeConsoleLogs) {
            // eslint-disable-next-line no-console
            console.info('[reader-resume]', event, nextPayload);
        }

        if (useStripeTestMode) {
            const runtimeLogger = (
                globalThis as typeof globalThis & {
                    [key: string]: ((entry: {
                        event: string,
                        payload: Record<string, unknown>,
                    }) => void) | undefined,
                }
            )[READER_RESUME_RUNTIME_LOG_KEY];
            runtimeLogger?.({
                event,
                payload: nextPayload,
            });
        }
    }, [shouldEmitReaderResumeConsoleLogs, useStripeTestMode]);
    const logReaderNudge = useCallback((
        event: string,
        payload?: Record<string, unknown>,
    ): void => {
        if (!shouldEmitReaderNudgeConsoleLogs) return;

        const nextPayload = payload || {};
        // eslint-disable-next-line no-console
        console.info('[reader-nudge]', event, nextPayload);
    }, [shouldEmitReaderNudgeConsoleLogs]);
    const logInternalPostLink = useCallback((
        event: string,
        payload?: Record<string, unknown>,
    ): void => {
        if (!shouldEmitInternalPostLinkConsoleLogs) return;

        const nextPayload = payload || {};
        // eslint-disable-next-line no-console
        console.info('[internal-post-link]', event, nextPayload);
    }, [shouldEmitInternalPostLinkConsoleLogs]);
    const logReaderAnnotation = useCallback((
        event: string,
        payload?: Record<string, unknown>,
    ): void => {
        const nextPayload = payload || {};

        if (
            shouldEmitReaderAnnotationConsoleLogs
            || useStripeTestMode
        ) {
            const runtimeEntries = (
                globalThis as typeof globalThis & {
                    [READER_ANNOTATION_RUNTIME_LOG_KEY]?: Array<{
                        event: string,
                        payload: Record<string, unknown>,
                        timestamp: string,
                    }>,
                }
            )[READER_ANNOTATION_RUNTIME_LOG_KEY];
            const nextEntry = {
                event,
                payload: nextPayload,
                timestamp: new Date().toISOString(),
            };

            if (Array.isArray(runtimeEntries)) {
                runtimeEntries.push(nextEntry);
                if (runtimeEntries.length > 200) {
                    runtimeEntries.splice(0, runtimeEntries.length - 200);
                }
            } else {
                (
                    globalThis as typeof globalThis & {
                        [READER_ANNOTATION_RUNTIME_LOG_KEY]?: Array<{
                            event: string,
                            payload: Record<string, unknown>,
                            timestamp: string,
                        }>,
                    }
                )[READER_ANNOTATION_RUNTIME_LOG_KEY] = [nextEntry];
            }
        }

        if (!shouldEmitReaderAnnotationConsoleLogs) return;

        // eslint-disable-next-line no-console
        console.info('[reader-annotation]', event, nextPayload);
    }, [shouldEmitReaderAnnotationConsoleLogs, useStripeTestMode]);
    // Reads local resume cache for the current effective user only.
    const readLocalReaderProgressCache = useCallback((): Record<string, IReaderChapterProgressItem> => {
        if (typeof window === 'undefined') return {};
        const ownerUserId = user?.id || auth.currentUser?.uid || null;
        if (!ownerUserId) return {};
        try {
            const serializedReaderProgress = window.localStorage.getItem(LOCAL_READER_PROGRESS_STORAGE_KEY);
            if (!serializedReaderProgress) return {};
            const parsedReaderProgress = JSON.parse(serializedReaderProgress) as unknown;
            if (!parsedReaderProgress || typeof parsedReaderProgress !== 'object') return {};
            const parsedReaderProgressRecord = parsedReaderProgress as Record<string, unknown>;
            const parsedOwnerUserId = typeof parsedReaderProgressRecord.ownerUserId === 'string'
                ? parsedReaderProgressRecord.ownerUserId.trim()
                : '';
            if (!parsedOwnerUserId) {
                // Legacy unscoped cache shape is ignored to avoid cross-user noise.
                window.localStorage.removeItem(LOCAL_READER_PROGRESS_STORAGE_KEY);
                return {};
            }
            if (parsedOwnerUserId !== ownerUserId) {
                // Prevent fallback leakage across account transitions.
                window.localStorage.removeItem(LOCAL_READER_PROGRESS_STORAGE_KEY);
                return {};
            }
            const parsedProgressByKey = parsedReaderProgressRecord.progressByKey;
            return parsedProgressByKey && typeof parsedProgressByKey === 'object'
                ? parsedProgressByKey as Record<string, IReaderChapterProgressItem>
                : {};
        } catch (error) {
            return {};
        }
    }, [
        user?.id,
        auth.currentUser?.uid,
        LOCAL_READER_PROGRESS_STORAGE_KEY,
    ]);
    /**
     * Guards every persisted reader-progress access so older cached/server entries that
     * do not match the required saved shape are ignored instead of partially reused.
     */
    const isPersistedReaderProgressItem = useCallback((
        progress: Partial<IReaderChapterProgressItem> | null | undefined,
    ): progress is IReaderChapterProgressItem => {
        if (!progress) return false;
        const { timestamp } = progress;
        if (typeof progress.id !== 'string' || progress.id.trim().length === 0) return false;
        if (typeof progress.userId !== 'string' || progress.userId.trim().length === 0) return false;
        if (
            typeof progress.readerChapterProgressKey !== 'string'
            || progress.readerChapterProgressKey.trim().length === 0
        ) {
            return false;
        }
        if (typeof progress.postId !== 'string' || progress.postId.trim().length === 0) return false;
        if (!Number.isFinite(progress.scrollTop)) return false;
        if (!Number.isFinite(progress.scrollProgress)) return false;
        if (timestamp == null || !Number.isFinite(timestamp) || timestamp <= 0) return false;
        if (
            progress.anchorParagraphIndex != null
            && !Number.isFinite(progress.anchorParagraphIndex)
        ) {
            return false;
        }
        if (
            progress.anchorParagraphOffsetProgress != null
            && !Number.isFinite(progress.anchorParagraphOffsetProgress)
        ) {
            return false;
        }
        return true;
    }, []);
    // Returns one validated local progress item by key; malformed entries are ignored.
    const getLocalReaderProgressByKey = useCallback((readerProgressKey: string): IReaderChapterProgressItem | null => {
        const localReaderProgressCache = readLocalReaderProgressCache();
        const localReaderProgress = localReaderProgressCache[readerProgressKey];
        if (
            !isPersistedReaderProgressItem(localReaderProgress)
        ) {
            return null;
        }
        return localReaderProgress;
    }, [
        isPersistedReaderProgressItem,
        readLocalReaderProgressCache,
    ]);
    // Returns all validated local progress entries for one post id.
    const getLocalReaderProgressForPost = useCallback((targetPostId: string): IReaderChapterProgressItem[] => (
        Object.values(readLocalReaderProgressCache()).filter((localReaderProgress) => (
            isPersistedReaderProgressItem(localReaderProgress)
            && localReaderProgress.postId === targetPostId
        ))
    ), [
        isPersistedReaderProgressItem,
        readLocalReaderProgressCache,
    ]);
    const saveLocalReaderProgress = useCallback((
        readerProgressKey: string,
        readerProgressToSet: IReaderChapterProgressItem,
    ): void => {
        if (typeof window === 'undefined') return;
        const ownerUserId = user?.id || auth.currentUser?.uid || null;
        if (!ownerUserId) return;
        try {
            const localReaderProgressCache = readLocalReaderProgressCache();
            const nextLocalReaderProgressCache: Record<string, IReaderChapterProgressItem> = {
                ...localReaderProgressCache,
                [readerProgressKey]: readerProgressToSet,
            };
            const localReaderProgressEntries = Object.entries(nextLocalReaderProgressCache).filter(([, progress]) => (
                isPersistedReaderProgressItem(progress)
            ));
            // Keep cache bounded and prefer newest progress when pruning.
            if (localReaderProgressEntries.length > LOCAL_READER_PROGRESS_MAX_ENTRIES) {
                localReaderProgressEntries.sort(([, firstProgress], [, secondProgress]) => (
                    secondProgress.timestamp - firstProgress.timestamp
                ));
            }
            const boundedLocalReaderProgressCache = Object.fromEntries(
                localReaderProgressEntries.slice(0, LOCAL_READER_PROGRESS_MAX_ENTRIES),
            ) as Record<string, IReaderChapterProgressItem>;
            window.localStorage.setItem(
                LOCAL_READER_PROGRESS_STORAGE_KEY,
                JSON.stringify({
                    ownerUserId,
                    progressByKey: boundedLocalReaderProgressCache,
                }),
            );
        } catch (error) {
            // best effort local fallback cache only
        }
    }, [
        user?.id,
        auth.currentUser?.uid,
        isPersistedReaderProgressItem,
        readLocalReaderProgressCache,
        LOCAL_READER_PROGRESS_STORAGE_KEY,
        LOCAL_READER_PROGRESS_MAX_ENTRIES,
    ]);
    const clearLocalReaderProgressCache = useCallback((): void => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.removeItem(LOCAL_READER_PROGRESS_STORAGE_KEY);
        } catch (error) {
            // best effort cleanup only
        }
    }, [
        LOCAL_READER_PROGRESS_STORAGE_KEY,
    ]);
    // Clears pending auth-lock resume target after completion/failure to prevent stale resumes.
    const clearAuthLockAutoResumeStoredTarget = useCallback((): void => {
        if (typeof window === 'undefined') return;
        try {
            window.sessionStorage.removeItem(AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY);
        } catch (error) {
            // best effort cleanup only
        }
    }, [
        AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY,
    ]);
    const persistAuthLockAutoResumeTarget = useCallback((target: {
        postId: string,
        path: number[],
        progress: IReaderChapterProgressItem,
        source: READER_PROGRESS_SOURCE,
    } | null): void => {
        // Persists one pending auth-lock resume target in session storage so redirect/reload
        // during auth does not lose where to resume.
        if (!target) {
            clearAuthLockAutoResumeStoredTarget();
            return;
        }
        if (typeof window === 'undefined') return;
        try {
            window.sessionStorage.setItem(
                AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY,
                JSON.stringify({
                    createdAt: Date.now(),
                    target,
                }),
            );
        } catch (error) {
            // best effort persistence only
        }
    }, [
        clearAuthLockAutoResumeStoredTarget,
        AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY,
    ]);
    const readAuthLockAutoResumeStoredTarget = useCallback((): {
        postId: string,
        path: number[],
        progress: IReaderChapterProgressItem,
        source: READER_PROGRESS_SOURCE,
    } | null => {
        // Restores and validates pending auth-lock resume target, clearing invalid/expired data.
        if (typeof window === 'undefined') return null;
        try {
            const serializedTarget = window.sessionStorage.getItem(AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY);
            if (!serializedTarget) return null;
            const parsedTarget = JSON.parse(serializedTarget) as {
                createdAt?: number,
                target?: {
                    postId?: string,
                    path?: number[],
                    progress?: IReaderChapterProgressItem,
                    source?: string,
                },
            };
            if (
                !parsedTarget
                || parsedTarget.createdAt == null
                || !Number.isFinite(parsedTarget.createdAt)
                || Date.now() - parsedTarget.createdAt > AUTH_LOCK_AUTO_RESUME_PENDING_MAX_AGE
            ) {
                clearAuthLockAutoResumeStoredTarget();
                return null;
            }
            const parsedPath = parsedTarget.target?.path;
            const parsedProgress = parsedTarget.target?.progress;
            if (
                !parsedTarget.target?.postId
                || !Array.isArray(parsedPath)
                || parsedPath.length === 0
                || !parsedPath.every((pathPart) => Number.isFinite(pathPart))
                || !isPersistedReaderProgressItem(parsedProgress)
            ) {
                clearAuthLockAutoResumeStoredTarget();
                return null;
            }
            return {
                postId: parsedTarget.target.postId,
                path: parsedPath.map((pathPart) => Math.max(0, Math.round(pathPart))),
                progress: parsedProgress,
                source: parsedTarget.target.source === READER_PROGRESS_SOURCE.localReaderProgress
                    ? READER_PROGRESS_SOURCE.localReaderProgress
                    : READER_PROGRESS_SOURCE.userReaderProgress,
            };
        } catch (error) {
            clearAuthLockAutoResumeStoredTarget();
            return null;
        }
    }, [
        AUTH_LOCK_AUTO_RESUME_PENDING_STORAGE_KEY,
        AUTH_LOCK_AUTO_RESUME_PENDING_MAX_AGE,
        clearAuthLockAutoResumeStoredTarget,
    ]);
    useEffect(() => {
        // Clear fallback whenever effective account changes.
        // This prevents old-account cached progress from driving resume CTA state.
        const currentUserId = user?.id || auth.currentUser?.uid || null;
        const previousUserId = localReaderProgressOwnerUserIdRef.current;
        if (previousUserId != null && previousUserId !== currentUserId) {
            clearLocalReaderProgressCache();
        }
        if (previousUserId !== currentUserId) {
            readerProgressLastSubmittedRef.current.clear();
        }
        localReaderProgressOwnerUserIdRef.current = currentUserId;
    }, [
        user?.id,
        auth.currentUser?.uid,
        clearLocalReaderProgressCache,
    ]);

    const readerProgressLatest = useLatest({
        write,
        signingInOut,
        user,
        postId: post?.id || postId,
        post,
        currentReadPost,
        selectedPostValuePath,
    });
    const getReaderProgressScrollMax = useCallback((): number => (
        useDocumentScroll
            ? Math.max(document.documentElement.scrollHeight - window.innerHeight, 0)
            : Math.max(
                (postContentContainerRef.current?.scrollHeight || 0) - (postContentContainerRef.current?.clientHeight || 0),
                0,
            )
    ), [useDocumentScroll]);
    const getReaderProgressViewportHeight = useCallback((): number => (
        useDocumentScroll
            ? window.innerHeight
            : postContentContainerRef.current?.clientHeight || window.innerHeight
    ), [useDocumentScroll]);
    const getReaderProgressCurrentScrollTop = useCallback((): number => (
        useDocumentScroll
            ? Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0)
            : postContentContainerRef.current?.scrollTop || 0
    ), [useDocumentScroll]);
    const resolveReaderProgressScrollBehavior = useCallback((
        targetTop: number,
        currentTop: number,
    ): ScrollBehavior => {
        // Auth-lock auto-resume is a correction step, so it should snap into
        // place instead of animating the reader away from their anchor first.
        if (resumeReaderProgressSourceRef.current === READER_RESUME_SOURCE.authLockAuto) {
            return 'auto';
        }

        return targetTop > currentTop ? 'smooth' : 'auto';
    }, []);
    const scrollReaderProgressTo = useCallback((top: number, behavior: ScrollBehavior): void => {
        if (useDocumentScroll) {
            window.scrollTo({
                top,
                behavior,
            });
            return;
        }

        postContentContainerRef.current?.scrollTo({
            top,
            behavior,
        });
    }, [useDocumentScroll]);
    /**
     * Finds the currently rendered DOM node for an in-post link target without
     * depending on CSS escaping or assuming the target is already in view.
     */
    const getInternalPostLinkTargetElement = useCallback((paragraphId: string): HTMLElement | null => {
        const normalizedParagraphId = `${paragraphId || ''}`.trim();
        if (!normalizedParagraphId) {
            return null;
        }

        const targetElements = Array.from(
            (postEditorContainerRef.current || document).querySelectorAll('[data-post-link-target-id]'),
        ) as HTMLElement[];

        return targetElements.find((targetElement) => (
            `${targetElement.dataset.postLinkTargetId || ''}`.trim() === normalizedParagraphId
        )) || null;
    }, []);
    /**
     * Scrolls the active reader/editor surface to a resolved in-post anchor.
     * The targets themselves own the top offset through scroll-margin styling.
     */
    const scrollToInternalPostLinkTarget = useCallback((targetElement: HTMLElement): void => {
        targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    }, []);
    const getReaderProgressParagraphElements = useCallback((): HTMLElement[] => (
        Array.from(
            (postEditorContainerRef.current || document).querySelectorAll(`.${SLATE_PARAGRAPH_CLASSNAME}`),
        ) as HTMLElement[]
    ), []);
    const getReaderProgressParagraphTop = useCallback((paragraph: HTMLElement): number => {
        if (useDocumentScroll) {
            return Math.max(0, window.scrollY + paragraph.getBoundingClientRect().top);
        }

        const scrollContainer = postContentContainerRef.current;
        if (!scrollContainer) return 0;

        return Math.max(
            0,
            scrollContainer.scrollTop
                + (paragraph.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top),
        );
    }, [useDocumentScroll]);
    const getReaderProgressParagraphAnchorCandidates = useCallback((): IReaderViewportParagraphAnchorCandidate[] => (
        getReaderProgressParagraphElements().map((paragraph) => ({
            paragraphId: `${paragraph.dataset.paragraphId || ''}`.trim() || undefined,
            paragraphTop: getReaderProgressParagraphTop(paragraph),
            paragraphHeight: Math.max(paragraph.getBoundingClientRect().height, 1),
        }))
    ), [
        getReaderProgressParagraphElements,
        getReaderProgressParagraphTop,
    ]);
    // While progressive read-only chunking is still appending, temporarily
    // disable smooth scrolling and native scroll anchoring on whichever scroll
    // surface is active so the browser does not fight PostEditor's scroll lock.
    useLayoutEffect(() => {
        if (write || postEditorReadOnlyChunkingIdle) {
            return undefined;
        }

        if (useDocumentScroll) {
            // On touch/document-scroll layouts, the document element owns the
            // active scroll position, so the temporary overrides must live there.
            const { documentElement, body } = document;
            const { style: documentElementStyle } = documentElement;
            const { style: bodyStyle } = body;
            const previousDocumentScrollBehavior = documentElementStyle.scrollBehavior;
            const previousDocumentOverflowAnchor = documentElementStyle.overflowAnchor;
            const previousBodyOverflowAnchor = bodyStyle.overflowAnchor;

            documentElementStyle.scrollBehavior = 'auto';
            documentElementStyle.overflowAnchor = 'none';
            bodyStyle.overflowAnchor = 'none';

            return () => {
                documentElementStyle.scrollBehavior = previousDocumentScrollBehavior;
                documentElementStyle.overflowAnchor = previousDocumentOverflowAnchor;
                bodyStyle.overflowAnchor = previousBodyOverflowAnchor;
            };
        }

        const scrollContainer = postContentContainerRef.current;
        if (!scrollContainer) {
            return undefined;
        }

        // On desktop/container-scroll layouts, apply the same overrides to the
        // reader pane itself instead of the global document.
        const previousScrollBehavior = scrollContainer.style.scrollBehavior;
        const previousOverflowAnchor = scrollContainer.style.overflowAnchor;
        scrollContainer.style.scrollBehavior = 'auto';
        scrollContainer.style.overflowAnchor = 'none';

        return () => {
            scrollContainer.style.scrollBehavior = previousScrollBehavior;
            scrollContainer.style.overflowAnchor = previousOverflowAnchor;
        };
    }, [
        write,
        useDocumentScroll,
        postEditorReadOnlyChunkingIdle,
        postContentContainerRef.current,
    ]);
    const waitForReaderProgressLayoutStability = useCallback((onStable: () => void): (() => void) => {
        let cancelled = false;
        let frameId: number | null = null;
        let stableSince: number | null = null;
        let previousMetrics: { scrollMax: number, paragraphCount: number } | null = null;
        const clearPending = (): void => {
            if (frameId != null) {
                window.cancelAnimationFrame(frameId);
                frameId = null;
            }
        };
        const tick = (): void => {
            if (cancelled) return;
            const now = window.performance.now();
            const metrics = {
                scrollMax: Math.round(getReaderProgressScrollMax()),
                paragraphCount: getReaderProgressParagraphElements().length,
            };

            // Keep polling until both scroll extent and paragraph count stop changing.
            if (!previousMetrics) {
                previousMetrics = metrics;
                stableSince = now;
            } else if (
                Math.abs(metrics.scrollMax - previousMetrics.scrollMax) > READER_PROGRESS_LAYOUT_STABILITY_SCROLL_MAX_DELTA
                || metrics.paragraphCount !== previousMetrics.paragraphCount
            ) {
                previousMetrics = metrics;
                stableSince = null;
            } else if (stableSince == null) {
                stableSince = now;
            }

            const stableDuration = stableSince == null ? 0 : now - stableSince;
            if (stableDuration >= READER_PROGRESS_LAYOUT_STABILITY_QUIET_DURATION) {
                onStable();
                return;
            }

            frameId = window.requestAnimationFrame(tick);
        };

        frameId = window.requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            clearPending();
        };
    }, [
        getReaderProgressScrollMax,
        getReaderProgressParagraphElements,
        READER_PROGRESS_LAYOUT_STABILITY_SCROLL_MAX_DELTA,
        READER_PROGRESS_LAYOUT_STABILITY_QUIET_DURATION,
    ]);
    /**
     * Progressive block release can make the reader look "done" slightly
     * before the exact internal-link anchor has mounted. Wait for that target
     * id to appear first, then reuse the existing layout-stability guard
     * before performing the final smooth scroll.
     */
    const waitForInternalPostLinkTargetMount = useCallback(
        (
            paragraphId: string,
            onTargetReady: (targetElement: HTMLElement) => void,
            onTargetMissing: () => void,
        ) => {
            let cancelled = false;
            let frameId: number | null = null;
            let cancelLayoutWait: (() => void) | null = null;
            const startedAt = window.performance.now();
            logInternalPostLink('targetMountWaitStarted', {
                paragraphId,
            });

            const clearPending = (): void => {
                if (frameId != null) {
                    window.cancelAnimationFrame(frameId);
                    frameId = null;
                }
                cancelLayoutWait?.();
                cancelLayoutWait = null;
            };

            const tick = (): void => {
                if (cancelled) {
                    return;
                }

                const targetElement = getInternalPostLinkTargetElement(paragraphId);
                if (targetElement) {
                    logInternalPostLink('targetMountFound', {
                        paragraphId,
                        elapsedMs: Math.round(window.performance.now() - startedAt),
                    });
                    cancelLayoutWait = waitForReaderProgressLayoutStability(() => {
                        if (cancelled) {
                            return;
                        }

                        const stableTargetElement = getInternalPostLinkTargetElement(paragraphId);
                        if (!stableTargetElement) {
                            logInternalPostLink('targetMountLostBeforeStableScroll', {
                                paragraphId,
                            });
                            onTargetMissing();
                            return;
                        }

                        logInternalPostLink('targetMountStableReady', {
                            paragraphId,
                        });
                        onTargetReady(stableTargetElement);
                    });
                    return;
                }

                if (window.performance.now() - startedAt >= INTERNAL_POST_LINK_TARGET_MOUNT_WAIT_TIMEOUT) {
                    logInternalPostLink('targetMountTimedOut', {
                        paragraphId,
                        timeoutMs: INTERNAL_POST_LINK_TARGET_MOUNT_WAIT_TIMEOUT,
                    });
                    onTargetMissing();
                    return;
                }

                frameId = window.requestAnimationFrame(tick);
            };

            frameId = window.requestAnimationFrame(tick);

            return (): void => {
                cancelled = true;
                clearPending();
            };
        },
        [
            getInternalPostLinkTargetElement,
            logInternalPostLink,
            waitForReaderProgressLayoutStability,
            INTERNAL_POST_LINK_TARGET_MOUNT_WAIT_TIMEOUT,
        ],
    );
    const resolveSavedReaderProgressScrollTop = useCallback((savedProgress: {
        scrollTop: number,
        scrollProgress: number,
        anchorParagraphId?: string,
        anchorParagraphIndex?: number,
        anchorParagraphOffsetProgress?: number,
    }): number => {
        const targetScrollMax = getReaderProgressScrollMax();
        const targetViewportHeight = getReaderProgressViewportHeight();
        const anchorViewportOffset = targetViewportHeight * READER_PROGRESS_ANCHOR_VIEWPORT_OFFSET_RATIO;
        const paragraphs = getReaderProgressParagraphElements();
        let anchoredParagraph: HTMLElement | null = null;

        // Preferred anchor: persisted paragraph id survives viewport changes and paragraph insertions.
        if (savedProgress.anchorParagraphId) {
            anchoredParagraph = paragraphs.find(
                (paragraph) => paragraph.dataset.paragraphId === savedProgress.anchorParagraphId,
            ) || null;
        }

        // Backward-compatible fallback for older progress payloads that only persisted index.
        if (
            !anchoredParagraph
            && savedProgress.anchorParagraphIndex != null
            && Number.isFinite(savedProgress.anchorParagraphIndex)
        ) {
            const resolvedIndex = Math.max(
                0,
                Math.min(
                    paragraphs.length - 1,
                    Math.round(savedProgress.anchorParagraphIndex),
                ),
            );
            anchoredParagraph = paragraphs.length > 0
                ? paragraphs[resolvedIndex]
                : null;
        }

        if (
            anchoredParagraph
            && savedProgress.anchorParagraphOffsetProgress != null
            && Number.isFinite(savedProgress.anchorParagraphOffsetProgress)
        ) {
            const paragraphTop = getReaderProgressParagraphTop(anchoredParagraph);
            const paragraphHeight = Math.max(anchoredParagraph.getBoundingClientRect().height, 1);
            const paragraphOffsetProgress = Math.max(
                0,
                Math.min(1, savedProgress.anchorParagraphOffsetProgress),
            );
            const anchorTop = paragraphTop + (paragraphHeight * paragraphOffsetProgress);
            return Math.max(
                0,
                Math.min(anchorTop - anchorViewportOffset, targetScrollMax),
            );
        }

        return Math.max(
            0,
            Math.min(savedProgress.scrollProgress * targetScrollMax, targetScrollMax),
        );
    }, [
        getReaderProgressScrollMax,
        getReaderProgressViewportHeight,
        getReaderProgressParagraphElements,
        getReaderProgressParagraphTop,
        READER_PROGRESS_ANCHOR_VIEWPORT_OFFSET_RATIO,
    ]);
    // Persists scrollTop to user profile (debounced during scrolling, forced on key lifecycle events).
    const persistReaderProgress = useCallback((force = false): void => {
        const cur = readerProgressLatest.current;
        if (
            cur.write
            || cur.signingInOut
            || !cur.user
            || !cur.postId
            || cur.user.saveReaderProgress === false
            || (!force && readerProgressPersistenceBlockedRef.current)
        ) {
            return;
        }
        if (
            cur.user.authenticationType === AUTHENTICATION_TYPE.anonymous
            && suppressAnonymousLocalResumeFallbackRef.current
        ) {
            return;
        }

        const key = buildReaderProgressKey({
            resolvedPostId: cur.postId,
            path: cur.selectedPostValuePath,
        });
        if (!key) return;
        const progressTarget = resolvePostContentTarget({
            postId: cur.postId,
            post: cur.currentReadPost || cur.post || undefined,
            path: cur.selectedPostValuePath,
        });
        const supportsRootReaderProgress = progressTarget.scope === POST_CONTENT_SCOPE.root
            && ((cur.currentReadPost?.chapters?.length || cur.post?.chapters?.length || 0) === 0);
        if (
            progressTarget.scope === POST_CONTENT_SCOPE.root
            && !supportsRootReaderProgress
        ) {
            return;
        }
        if (
            progressTarget.scope !== POST_CONTENT_SCOPE.root
            && !progressTarget.chapterId
        ) {
            return;
        }
        const scrollTop = Math.max(0, Math.round(postScrollTop.current));
        const viewportHeight = getReaderProgressViewportHeight();
        // Ignore near-top positions to avoid noisy progress writes for brief/initial reads.
        if (scrollTop < viewportHeight) return;
        const scrollMax = getReaderProgressScrollMax();
        // Normalized progress improves resume accuracy when viewport width changes.
        const scrollProgress = scrollMax > 0
            ? Math.min(1, Math.max(0, roundToNDecimals(scrollTop / scrollMax, 6)))
            : 0;
        const anchorViewportOffset = viewportHeight * READER_PROGRESS_ANCHOR_VIEWPORT_OFFSET_RATIO;
        const anchorScrollTop = scrollTop + anchorViewportOffset;
        const paragraphAnchor = resolveReaderViewportParagraphAnchor({
            anchorScrollTop,
            paragraphs: getReaderProgressParagraphAnchorCandidates(),
        });
        const anchorParagraphId = paragraphAnchor?.anchorParagraphId;
        const anchorParagraphIndex = paragraphAnchor?.anchorParagraphIndex;
        const anchorParagraphOffsetProgress = paragraphAnchor != null
            ? roundToNDecimals(paragraphAnchor.anchorParagraphOffsetProgress, 6)
            : undefined;
        const existingReaderProgress = cur.user.readerChapterProgress?.[key];
        const lastPersistedScrollTop = readerProgressLastPersistedTopRef.current.get(key);
        const shouldSkipWrite = !force && (
            (lastPersistedScrollTop != null
                && Math.abs(lastPersistedScrollTop - scrollTop) < READER_PROGRESS_PERSIST_MIN_SCROLL_DELTA)
            || (
                existingReaderProgress != null
                && Math.abs(existingReaderProgress.scrollTop - scrollTop) < READER_PROGRESS_PERSIST_MIN_SCROLL_DELTA
            )
        );
        if (shouldSkipWrite) {
            return;
        }

        const progressTimestamp = Date.now();
        // Omit undefined optional fields so the saved payload can be persisted
        // through Firestore without relying on ignoreUndefinedProperties.
        const readerProgressDraft: ReaderProgressLocationSnapshot = {
            userId: cur.user.id,
            readerChapterProgressKey: key,
            postId: cur.postId,
            scrollTop,
            scrollProgress,
            ...(progressTarget.chapterId ? { chapterId: progressTarget.chapterId } : {}),
            ...(progressTarget.sectionId ? { sectionId: progressTarget.sectionId } : {}),
            ...(anchorParagraphId ? { anchorParagraphId } : {}),
            ...(anchorParagraphIndex != null ? { anchorParagraphIndex } : {}),
            ...(anchorParagraphOffsetProgress != null ? { anchorParagraphOffsetProgress } : {}),
        };
        const previousSubmittedReaderProgress = readerProgressLastSubmittedRef.current.get(key);
        const readerProgressHistoryIdentity = resolveReaderProgressHistoryIdentity({
            previousProgress: previousSubmittedReaderProgress,
            nextProgress: readerProgressDraft,
            nextTimestamp: progressTimestamp,
            createId: uuidv4,
        });
        const readerProgressToSet: IReaderChapterProgressItem = {
            ...readerProgressDraft,
            ...readerProgressHistoryIdentity,
        };
        readerProgressLastPersistedTopRef.current.set(key, scrollTop);
        readerProgressLastSubmittedRef.current.set(key, readerProgressToSet);
        saveLocalReaderProgress(key, readerProgressToSet);
        const currentAuthUserId = auth.currentUser?.uid || null;
        if (currentAuthUserId !== cur.user.id) {
            return;
        }
        // Lifecycle flushes are best-effort because sign-out can invalidate the
        // callable auth token after the local progress fallback has already saved.
        updateUser({
            test: useStripeTestMode,
            readerChapterProgressToSet: readerProgressToSet,
        }).catch(() => null);
    }, [
        readerProgressLatest,
        buildReaderProgressKey,
        getReaderProgressScrollMax,
        getReaderProgressViewportHeight,
        getReaderProgressParagraphAnchorCandidates,
        READER_PROGRESS_ANCHOR_VIEWPORT_OFFSET_RATIO,
        saveLocalReaderProgress,
    ]);

    // Schedules one write after scrolling settles.
    const timeoutPersistReaderProgress = useCallback((): void => {
        clearReaderProgressPersistTimeout();
        readerProgressPersistTimeoutRef.current = window.setTimeout(() => {
            readerProgressPersistTimeoutRef.current = null;
            persistReaderProgress();
        }, READER_PROGRESS_PERSIST_DEBOUNCE_DURATION);
    }, [
        clearReaderProgressPersistTimeout,
        persistReaderProgress,
    ]);

    // Smooth-scrolls the active chapter container/document back to persisted location.
    const onResumeReaderProgress = useCallback((): void => {
        if (!resumeReaderProgressRef.current) {
            setResumeReaderProgressButtonLoading(false);
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeSkippedMissingProgress, {
                source: resumeReaderProgressSourceRef.current,
                path: selectedPostValuePath,
            });
            return;
        }
        const savedProgress = resumeReaderProgressRef.current;
        logResumeFlow(READER_RESUME_LOG_EVENT.resumeStarted, {
            source: resumeReaderProgressSourceRef.current,
            key: savedProgress.key,
            path: selectedPostValuePath,
        });
        const requestId = resumeReaderProgressRequestIdRef.current + 1;
        resumeReaderProgressRequestIdRef.current = requestId;

        clearResumeReaderProgressActionWait();
        clearResumeReaderProgressCorrectionTimeout();
        setResumeReaderProgress(null);

        // Wait for chapter geometry to stop shifting before applying restore.
        resumeReaderProgressAwaitingStabilityRef.current = true;
        resumeReaderProgressActionWaitCancelRef.current = waitForReaderProgressLayoutStability(() => {
            resumeReaderProgressActionWaitCancelRef.current = null;
            resumeReaderProgressAwaitingStabilityRef.current = false;
            if (resumeReaderProgressRequestIdRef.current !== requestId) return;

            const savedProgressSuggestsDeepRestore = (
                savedProgress.scrollTop >= READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                || savedProgress.scrollProgress > 0
                || savedProgress.anchorParagraphId != null
                || savedProgress.anchorParagraphIndex != null
            );
            const scheduleResumeCorrection = (attempt = 0): void => {
                resumeReaderProgressCorrectionTimeoutRef.current = window.requestAnimationFrame(() => {
                    resumeReaderProgressCorrectionTimeoutRef.current = null;
                    if (resumeReaderProgressRequestIdRef.current !== requestId) return;

                    const currentScrollTop = getReaderProgressCurrentScrollTop();
                    const currentScrollMax = getReaderProgressScrollMax();
                    const correctionTargetScrollTop = Math.max(
                        0,
                        Math.min(
                            Math.round(resolveSavedReaderProgressScrollTop(savedProgress)),
                            currentScrollMax,
                        ),
                    );
                    const geometryUnavailable = (
                        savedProgressSuggestsDeepRestore
                        && currentScrollMax < READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                        && correctionTargetScrollTop < READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                    );
                    if (geometryUnavailable && attempt < 24) {
                        resumeReaderProgressAwaitingGeometryRef.current = true;
                        scheduleResumeCorrection(attempt + 1);
                        return;
                    }
                    resumeReaderProgressAwaitingGeometryRef.current = false;
                    if (
                        Math.abs(correctionTargetScrollTop - currentScrollTop)
                        < READER_PROGRESS_RESUME_CORRECTION_MIN_SCROLL_DELTA
                    ) {
                        return;
                    }
                    setResumeReaderProgressButtonLoading(false);
                    scrollReaderProgressTo(
                        correctionTargetScrollTop,
                        resolveReaderProgressScrollBehavior(
                            correctionTargetScrollTop,
                            currentScrollTop,
                        ),
                    );
                });
            };
            const currentScrollTopBeforeApply = getReaderProgressCurrentScrollTop();
            const targetScrollMax = getReaderProgressScrollMax();
            const resolvedTargetScrollTop = resolveSavedReaderProgressScrollTop(savedProgress);
            const targetScrollTop = Math.max(
                0,
                Math.min(
                    Math.round(resolvedTargetScrollTop),
                    targetScrollMax,
                ),
            );
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeApplied, {
                source: resumeReaderProgressSourceRef.current,
                path: selectedPostValuePath,
                currentScrollTop: currentScrollTopBeforeApply,
                targetScrollMax,
                targetScrollTop,
            });
            const geometryUnavailable = (
                savedProgressSuggestsDeepRestore
                && targetScrollMax < READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                && targetScrollTop < READER_PROGRESS_RESUME_MIN_SCROLL_TOP
            );
            if (geometryUnavailable) {
                authLockAutoResumeAppliedScrollTopRef.current = null;
                authLockAutoResumeAppliedTargetRef.current = null;
                resumeReaderProgressAwaitingGeometryRef.current = true;
                scheduleResumeCorrection();
                return;
            }
            resumeReaderProgressAwaitingGeometryRef.current = false;
            if (resumeReaderProgressSourceRef.current === READER_RESUME_SOURCE.authLockAuto) {
                authLockAutoResumeAppliedScrollTopRef.current = currentScrollTopBeforeApply;
                authLockAutoResumeAppliedTargetRef.current = {
                    targetScrollMax,
                    targetScrollTop,
                };
            } else {
                authLockAutoResumeAppliedScrollTopRef.current = null;
                authLockAutoResumeAppliedTargetRef.current = null;
            }
            // Once the scroll actually starts, the reader has visible feedback
            // and we can replace the temporary CTA spinner with the motion.
            setResumeReaderProgressButtonLoading(false);
            scrollReaderProgressTo(
                targetScrollTop,
                resolveReaderProgressScrollBehavior(
                    targetScrollTop,
                    currentScrollTopBeforeApply,
                ),
            );

            // One correction pass catches late reflow after the smooth scroll starts.
            scheduleResumeCorrection();
        });
    }, [
        getReaderProgressScrollMax,
        getReaderProgressCurrentScrollTop,
        resolveSavedReaderProgressScrollTop,
        readerProgressLatest,
        selectedPostValuePath,
        clearResumeReaderProgressActionWait,
        clearResumeReaderProgressCorrectionTimeout,
        waitForReaderProgressLayoutStability,
        scrollReaderProgressTo,
        resolveReaderProgressScrollBehavior,
        READER_PROGRESS_RESUME_MIN_SCROLL_TOP,
        READER_PROGRESS_RESUME_CORRECTION_MIN_SCROLL_DELTA,
        logResumeFlow,
    ]);
    const triggerResumeReaderProgress = useCallback((source: READER_RESUME_SOURCE): void => {
        resumeReaderProgressSourceRef.current = source;
        if (source !== READER_RESUME_SOURCE.authLockAuto) {
            authLockAutoResumeAppliedScrollTopRef.current = null;
            authLockAutoResumeAppliedTargetRef.current = null;
        }
        setResumeReaderProgressButtonLoading(
            source === READER_RESUME_SOURCE.resumeButton
            || source === READER_RESUME_SOURCE.authLockAuto,
        );
        logResumeFlow(READER_RESUME_LOG_EVENT.resumeTriggered, {
            source,
            path: selectedPostValuePath,
        });
        if (!write && !forceFullReadOnlyRenderForResume && resumeReaderProgressRef.current) {
            pendingResumeAfterFullRenderSourceRef.current = source;
            pendingResumeAfterFullRenderPaintVersionRef.current = postBodyPaintVersion;
            setForceFullReadOnlyRenderForResume(true);
            return;
        }
        onResumeReaderProgress();
    }, [
        selectedPostValuePath,
        write,
        forceFullReadOnlyRenderForResume,
        postBodyPaintVersion,
        onResumeReaderProgress,
        logResumeFlow,
    ]);
    useEffect(() => {
        if (
            !forceFullReadOnlyRenderForResume
            || pendingResumeAfterFullRenderSourceRef.current == null
            || pendingResumeAfterFullRenderPaintVersionRef.current == null
            || !resumeReaderProgressRef.current
            || !postEditorReadOnlyFullRenderReady
            || !postBodyPainted
            || postBodyPaintVersion <= pendingResumeAfterFullRenderPaintVersionRef.current
        ) {
            return undefined;
        }

        let cancelled = false;
        // Progressive read mode can report a fresh paint before the forced
        // full-rendered chapter geometry has actually settled. Wait for the
        // same layout-stability signal used by the eventual resume scroll so
        // auth-lock auto-resume does not jump against a truncated document.
        const cancelWait = waitForReaderProgressLayoutStability(() => {
            if (cancelled) return;
            pendingResumeAfterFullRenderSourceRef.current = null;
            pendingResumeAfterFullRenderPaintVersionRef.current = null;
            onResumeReaderProgress();
        });

        return () => {
            cancelled = true;
            cancelWait();
        };
    }, [
        forceFullReadOnlyRenderForResume,
        postEditorReadOnlyFullRenderReady,
        postBodyPainted,
        postBodyPaintVersion,
        onResumeReaderProgress,
        waitForReaderProgressLayoutStability,
    ]);
    useEffect(() => {
        if (
            !forceFullReadOnlyRenderForResume
            || !pendingAuthLockedStickyCtaAfterFullRenderRef.current
            || pendingAuthLockedStickyCtaPaintVersionRef.current == null
            || !postEditorReadOnlyFullRenderReady
            || !postBodyPainted
            || postBodyPaintVersion <= pendingAuthLockedStickyCtaPaintVersionRef.current
            || refreshingPost
        ) {
            return undefined;
        }

        let cancelled = false;
        // Match the resume-reading path: once full render is requested, wait for
        // chapter geometry to stop shifting before trying to target the auth
        // lock banner at the real end of the post.
        authLockedStickyCtaLayoutWaitCancelRef.current = waitForReaderProgressLayoutStability(() => {
            authLockedStickyCtaLayoutWaitCancelRef.current = null;
            if (cancelled) return;

            const waitForAuthLockedEmailInput = (): void => {
                if (cancelled) return;

                // Wait for the real auth-lock target after the full render has
                // settled; otherwise the sticky CTA can scroll toward a moving
                // bottom edge while progressive content is still expanding.
                if (scrollToAuthLockedEmailInput()) {
                    pendingAuthLockedStickyCtaAfterFullRenderRef.current = false;
                    pendingAuthLockedStickyCtaPaintVersionRef.current = null;
                    authLockedStickyCtaWaitFrameRef.current = null;
                    return;
                }

                authLockedStickyCtaWaitFrameRef.current = window.requestAnimationFrame(waitForAuthLockedEmailInput);
            };

            authLockedStickyCtaWaitFrameRef.current = window.requestAnimationFrame(waitForAuthLockedEmailInput);
        });
        return () => {
            cancelled = true;
            if (authLockedStickyCtaLayoutWaitCancelRef.current) {
                authLockedStickyCtaLayoutWaitCancelRef.current();
                authLockedStickyCtaLayoutWaitCancelRef.current = null;
            }
            if (authLockedStickyCtaWaitFrameRef.current != null) {
                window.cancelAnimationFrame(authLockedStickyCtaWaitFrameRef.current);
                authLockedStickyCtaWaitFrameRef.current = null;
            }
        };
    }, [
        forceFullReadOnlyRenderForResume,
        postEditorReadOnlyFullRenderReady,
        postBodyPainted,
        postBodyPaintVersion,
        refreshingPost,
        waitForReaderProgressLayoutStability,
    ]);

    /**
     * Manages response to changing active contents table item
     */
    const onChangeSelectedPostValuePath = ({
        path,
        e,
        expandItems,
        clearEditorSelection = false,
    }: IPostPathEvent): void => {
        if (!postId) return;
        if (
            !clearEditorSelection
            && deepEqual(selectedPostValuePath, path)
        ) {
            // Re-clicking the active outline item should not invalidate state or
            // refetch the same scope. We still honor expand requests so the table
            // of contents can open the chapter rows without a redundant fetch.
            if (
                !!contentsTable
                && expandItems
            ) {
                onSetExpandContentsTableChapter(expandItems);
            }
            return;
        }
        // Flush current chapter progress before changing path so we don't lose the prior location.
        clearReaderProgressPersistTimeout();
        clearResumeReaderProgressEligibilityWait();
        clearResumeReaderProgressActionWait();
        clearResumeReaderProgressCorrectionTimeout();
        pendingResumeAfterFullRenderSourceRef.current = null;
        pendingResumeAfterFullRenderPaintVersionRef.current = null;
        authLockAutoResumeAppliedScrollTopRef.current = null;
        authLockAutoResumeAppliedTargetRef.current = null;
        pendingAuthLockedStickyCtaAfterFullRenderRef.current = false;
        pendingAuthLockedStickyCtaPaintVersionRef.current = null;
        if (authLockedStickyCtaLayoutWaitCancelRef.current) {
            authLockedStickyCtaLayoutWaitCancelRef.current();
            authLockedStickyCtaLayoutWaitCancelRef.current = null;
        }
        if (authLockedStickyCtaWaitFrameRef.current != null) {
            window.cancelAnimationFrame(authLockedStickyCtaWaitFrameRef.current);
            authLockedStickyCtaWaitFrameRef.current = null;
        }
        setResumeReaderProgressButtonLoading(false);
        setAuthLockedStickyCtaButtonLoading(false);
        setForceFullReadOnlyRenderForResume(false);
        persistReaderProgress(true);
        if (clearEditorSelection) {
            // Delete-driven scope changes need to clear Slate selection and pause
            // the editor for one frame to avoid DOM point resolution crashes.
            const nativeSelection = window.getSelection();
            if (nativeSelection && nativeSelection.rangeCount > 0) {
                nativeSelection.removeAllRanges();
            }
            if (postEditor) {
                try {
                    Transforms.deselect(postEditor);
                } catch (error) {
                    // Ignore stale editor teardown races during scope switches.
                }
                postEditor.selection = null;
            }
            if (annotationEditor) {
                try {
                    Transforms.deselect(annotationEditor);
                } catch (error) {
                    // Ignore stale editor teardown races during scope switches.
                }
                annotationEditor.selection = null;
            }
            setPostEditorDeleteTransitionInProgress(true);
            setPostEditorDeleteResetVersion((prev) => prev + 1);
        }
        setResumeReaderProgress(null);
        logReaderAnnotation('annotation-state-cleared', {
            reason: clearEditorSelection ? 'post-scope-delete-transition' : 'post-scope-change',
            selectedPostValuePath,
            currentAnnotationFetchScopeKey,
            currentPostValueSource,
        });
        // Clear annotations
        // This is done to prevent PostEditor from deleting annotations
        // because value changes before new annotations are loaded
        setAnnotations(new Map());
        setPendingAnnotationCreationIds(new Set());
        setPendingAnnotationFallbackTops(new Map());
        setPendingAnnotationProvisionalParagraphIds(new Map());
        setQueuedAnnotationRetries(new Map());
        setFetchedAnnotations(false);
        setFetchedAnnotationScopeKey(null);
        protectedAnnotationIdsRef.current.clear();
        recentlyAckedAnnotationIdsRef.current.clear();
        // Invalidate any in-flight post fetch immediately so an older scope
        // response cannot apply after the user has already navigated away.
        if (write) {
            writePostFetchCycleRef.current += 1;
        } else {
            readPostFetchCycleRef.current += 1;
            setPostEditorInitialized(false);
            setPostEditorReadOnlyChunkingIdle(false);
            setPostEditorReadOnlyFullRenderReady(false);
            setShowPostBodyContentSettled(false);
        }

        setReadFetchedPostState((prev) => ({
            ...prev,
            chapter: undefined,
            chapterSection: undefined,
        }));

        // Update path
        setSelectedPostValuePath([...path]);
        if (
            !write
            && canAccessReaderAnnotations
        ) {
            window.requestAnimationFrame(() => {
                queueFetchAnnotationsPostPaint({
                    resetHighlights: true,
                });
            });
        }

        if (clearEditorSelection) {
            window.requestAnimationFrame(() => {
                setPostEditorDeleteTransitionInProgress(false);
            });
        }

        // Update post
        // Route-driven read-mode scope changes can happen during the initial metadata pass
        // before page-load rendering has fully completed. Once we've fetched any read payload,
        // allow the scope change to trigger its own follow-up fetch immediately. If a fetch is
        // already in flight, queue the follow-up so the stale scope cannot win the race.
        if (postFetchInFlightRef.current) {
            postFetchQueuedRef.current = true;
            setRefreshPost(true);
        } else if (pageLoadRendersCompleted || write || (!write && fetchedPost)) {
            setRefreshPost(true);
        }

        // Update Scroll Position
        if (useDocumentScroll || postContentContainerRef.current) {
            if (useDocumentScroll) {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            } else {
                postContentContainerRef.current!.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            }
            setPostContentContainerIsScrolling(true);
            const checkScrolling = (): void => {
                const container = postContentContainerRef.current;
                // The container can disappear during scope teardown, especially
                // on delete-driven transitions, so stop polling in that case.
                if (!useDocumentScroll && !container) {
                    setPostContentContainerIsScrolling(false);
                    return;
                }
                const scrollTop = useDocumentScroll
                    ? Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0)
                    : container!.scrollTop;
                if (
                    postContentContainerIsScrolling
                    && scrollTop === 0
                ) {
                    setPostContentContainerIsScrolling(false);
                    // Wait for scroll settle paint before running annotation fetch work.
                    queueFetchAnnotationsPostPaint({
                        resetHighlights: false,
                    });
                } else if (postContentContainerIsScrolling) {
                    requestAnimationFrame(checkScrolling);
                }
            };
            requestAnimationFrame(checkScrolling);
        }

        // Reveal sections if chapter has any
        if (
            !!contentsTable
            && expandItems
        ) {
            onSetExpandContentsTableChapter(expandItems);
        }

        // Set URL Params for chapter and section
        if (path.length === 0 && currentZoomLevel) {
            const nextPathname = `${readerBasePath}${params.annotationId ? `/${params.annotationId}` : ''}`;
            const newSearchParams = buildReaderScopeSearchParams({
                path,
                zoomLevel: currentZoomLevel.level,
                includeRootZoom: searchParams.has(READER_PARAMS_TYPE.zoom.toString()),
                includePathParams: shouldIncludeReaderPathParamsForPath(path),
            });
            navigateReaderIfChanged(nextPathname, newSearchParams);
        } else if (path.length === 1 && currentZoomLevel) {
            const nextPathname = `${readerBasePath}${params.annotationId ? `/${params.annotationId}` : ''}`;
            const newSearchParams = buildReaderScopeSearchParams({
                path,
                zoomLevel: currentZoomLevel.level,
                includeRootZoom: true,
                includePathParams: shouldIncludeReaderPathParamsForPath(path),
            });

            // Reload and URL-bootstrap flows can already be on the exact target
            // route. Avoid issuing a same-URL navigate here because it creates a
            // fresh location.key and double-records a chapter view.
            navigateReaderIfChanged(nextPathname, newSearchParams);
        } else if (path.length === 2 && currentZoomLevel) {
            const nextPathname = `${readerBasePath}${params.annotationId ? `/${params.annotationId}` : ''}`;
            const newSearchParams = buildReaderScopeSearchParams({
                path,
                zoomLevel: currentZoomLevel.level,
                includeRootZoom: true,
                includePathParams: shouldIncludeReaderPathParamsForPath(path),
            });

            navigateReaderIfChanged(nextPathname, newSearchParams);
        }
    };
    /**
     * Converts a stored internal-link scope into the concrete reader path that
     * ReaderView can render before we attempt any smooth scrolling.
     */
    const resolveInternalPostLinkPath = useCallback((target: IInternalPostLinkTarget): number[] | null => {
        if (!activeRootPost) {
            return null;
        }

        if (!target.chapterId) {
            return [];
        }

        const resolvedPath = resolvePostPathFromScopeIds({
            post: activeRootPost,
            chapterId: target.chapterId,
            sectionId: target.sectionId,
        });
        if (resolvedPath.length === 0) {
            return null;
        }

        return resolvedPath;
    }, [activeRootPost]);
    const handleReadOnlyRevealTargetResolutionChange = useCallback((
        resolution: IReadOnlyRevealTargetResolution | null,
    ): void => {
        const paragraphId = resolution?.paragraphId || null;
        const currentScopeDebug = internalPostLinkScopeDebugRef.current;
        const serializedValueContainsParagraphId = (
            shouldEmitInternalPostLinkConsoleLogs
            && paragraphId
        )
            ? !!currentScopeDebug.serializedValue?.includes(paragraphId)
            : null;
        const valueNodesContainParagraphId = (
            shouldEmitInternalPostLinkConsoleLogs
            && paragraphId
        )
            ? JSON.stringify(currentScopeDebug.valueNodes || []).includes(paragraphId)
            : null;
        logInternalPostLink('readerRevealResolutionChanged', {
            paragraphId,
            found: resolution?.found ?? null,
            requiredVisibleBlockCount: resolution?.requiredVisibleBlockCount ?? null,
            renderedBlockCount: postEditorRenderedBlockCount,
            currentScopeValueSource: currentScopeDebug.source,
            currentScopeUsesPreviewSource: currentScopeDebug.usesPreviewSource,
            serializedValueContainsParagraphId,
            valueNodesContainParagraphId,
        });
        setPendingInternalPostLinkRevealTargetResolution(resolution);
    }, [
        logInternalPostLink,
        postEditorRenderedBlockCount,
        shouldEmitInternalPostLinkConsoleLogs,
    ]);
    /**
     * Centralizes cleanup for in-post navigation state so both success and
     * failure paths leave the reader in a consistent idle state.
     */
    const clearPendingInternalPostLinkNavigation = useCallback((): void => {
        pendingInternalPostLinkScopeRefreshHrefRef.current = null;
        attemptedInternalPostLinkScopeRefreshHrefRef.current = null;
        setPendingInternalPostLinkNavigation(null);
        setPendingInternalPostLinkRevealTargetResolution(null);
        setActiveInternalPostLinkNavigationHref(null);
    }, []);
    /**
     * Preview readers should get an explicit access hint when an internal link
     * points beyond the visible preview cutoff instead of watching the loading
     * pill disappear with no explanation.
     */
    const showInternalPostLinkPreviewLimitSnackbar = useCallback((target: IInternalPostLinkTarget): void => {
        const isAuthLockedPreviewLimit = internalPostLinkAccessStateRef.current.currentContentAuthLocked
            && !internalPostLinkAccessStateRef.current.userIsSignedIn;
        logInternalPostLink('readNavigationPreviewLimitSnackbarShown', {
            href: target.href,
            paragraphId: target.paragraphId,
            currentScopeValueSource: internalPostLinkScopeDebugRef.current.source,
            isAuthLockedPreviewLimit,
        });
        setSnackbarData({
            visible: true,
            duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
            text: isAuthLockedPreviewLimit
                ? SNACKBAR_MESSAGE_INTERNAL_POST_LINK_AUTH_LOCKED_LIMIT
                : SNACKBAR_MESSAGE_INTERNAL_POST_LINK_PREVIEW_LIMIT,
            icon: LinkIcon,
            dismissable: true,
            hasFailure: true,
        });
        if (isAuthLockedPreviewLimit) {
            revealAuthLockedContainerFromInternalPostLink();
        }
    }, [
        logInternalPostLink,
        setSnackbarData,
    ]);
    /**
     * Same-scope internal links can briefly point beyond the scoped payload the
     * reader currently has in memory. Refresh that exact scope once before we
     * conclude the paragraph id is genuinely missing.
     */
    const refreshReadScopeForInternalPostLink = useCallback((target: IInternalPostLinkTarget): void => {
        if (
            !postId
            || currentZoomLevel == null
        ) {
            logInternalPostLink('readNavigationScopeHydrationSkipped', {
                href: target.href,
                paragraphId: target.paragraphId,
                reason: 'missing-fetch-prerequisites',
                hasPostId: !!postId,
                hasZoomLevel: currentZoomLevel != null,
            });
            clearPendingInternalPostLinkNavigation();
            return;
        }

        const requestedPath = [...selectedPostValuePath];
        const refreshHref = target.href;
        pendingInternalPostLinkScopeRefreshHrefRef.current = refreshHref;
        attemptedInternalPostLinkScopeRefreshHrefRef.current = refreshHref;
        logInternalPostLink('readNavigationScopeHydrationRequested', {
            href: target.href,
            paragraphId: target.paragraphId,
            chapterId: currentPostContentTarget.chapterId || target.chapterId || null,
            sectionId: currentPostContentTarget.sectionId || target.sectionId || null,
            requestedPath,
            zoomLevel: currentZoomLevel.level,
        });
        getPost({
            test: useStripeTestMode,
            postId,
            fullPost: false,
            chapterId: currentPostContentTarget.chapterId || target.chapterId,
            sectionId: currentPostContentTarget.sectionId || target.sectionId,
            requestedPath,
            zoomLevel: currentZoomLevel.level,
            includeReadOnlyValueNodes: true,
            responseTier: POST_RESPONSE_TIER.critical,
        }).then((postResult) => {
            if (
                pendingInternalPostLinkScopeRefreshHrefRef.current !== refreshHref
                || !doesRequestedPathMatchLatest({
                    requestedPostId: postId,
                    requestedPath,
                })
            ) {
                return;
            }

            logInternalPostLink('readNavigationScopeHydrationResolved', {
                href: target.href,
                paragraphId: target.paragraphId,
                hasChapter: !!postResult.chapter,
                hasSection: !!postResult.chapterSection,
            });
            setReadFetchedPostState((prev) => ({
                post: postResult.post ?? prev.post,
                chapter: requestedPath.length === 0
                    ? undefined
                    : (postResult.chapter ?? prev.chapter),
                chapterSection: requestedPath.length < 2
                    ? undefined
                    : (postResult.chapterSection ?? prev.chapterSection),
            }));
        }).catch((error) => {
            if (pendingInternalPostLinkScopeRefreshHrefRef.current !== refreshHref) {
                return;
            }

            logInternalPostLink('readNavigationScopeHydrationFailed', {
                href: target.href,
                paragraphId: target.paragraphId,
                error: error instanceof Error ? error.message : 'unknown',
            });
            clearPendingInternalPostLinkNavigation();
        }).finally(() => {
            if (pendingInternalPostLinkScopeRefreshHrefRef.current === refreshHref) {
                pendingInternalPostLinkScopeRefreshHrefRef.current = null;
            }
        });
    }, [
        clearPendingInternalPostLinkNavigation,
        currentPostContentTarget.chapterId,
        currentPostContentTarget.sectionId,
        currentZoomLevel,
        doesRequestedPathMatchLatest,
        logInternalPostLink,
        postId,
        selectedPostValuePath,
        useStripeTestMode,
    ]);
    /**
     * Starts internal post navigation by switching scopes first when needed,
     * then letting a paint/layout-aware effect perform the final scroll once
     * the target paragraph or heading actually exists in the DOM.
     */
    const handleNavigateInternalPostLink = useCallback((target: IInternalPostLinkTarget): void => {
        pendingInternalPostLinkScopeRefreshHrefRef.current = null;
        attemptedInternalPostLinkScopeRefreshHrefRef.current = null;
        const resolvedPath = resolveInternalPostLinkPath(target);
        logInternalPostLink('navigateRequested', {
            href: target.href,
            paragraphId: target.paragraphId,
            chapterId: target.chapterId || null,
            sectionId: target.sectionId || null,
            selectedPostValuePath,
            resolvedPath,
        });
        if (resolvedPath == null) {
            logInternalPostLink('navigateMissingResolvedPath', {
                href: target.href,
                paragraphId: target.paragraphId,
                chapterId: target.chapterId || null,
                sectionId: target.sectionId || null,
                selectedPostValuePath,
            });
            setActiveInternalPostLinkNavigationHref(null);
            setPendingInternalPostLinkRevealTargetResolution(null);
            return;
        }

        setPendingInternalPostLinkNavigation(target);
        setPendingInternalPostLinkRevealTargetResolution(null);

        if (!deepEqual(selectedPostValuePath, resolvedPath)) {
            logInternalPostLink('navigateSwitchingScope', {
                href: target.href,
                paragraphId: target.paragraphId,
                fromPath: selectedPostValuePath,
                toPath: resolvedPath,
            });
            setActiveInternalPostLinkNavigationHref(null);
            onChangeSelectedPostValuePath({
                path: resolvedPath,
                ...(resolvedPath.length === 2
                    ? {
                        expandItems: [{
                            chapterIndex: resolvedPath[0],
                            expand: true,
                        }],
                    }
                    : {}
                ),
            });
            return;
        }
        logInternalPostLink('navigateSameScopeReady', {
            href: target.href,
            paragraphId: target.paragraphId,
            path: resolvedPath,
        });
        setActiveInternalPostLinkNavigationHref(target.href);
    }, [
        logInternalPostLink,
        resolveInternalPostLinkPath,
        selectedPostValuePath,
        onChangeSelectedPostValuePath,
    ]);
    /**
     * Pasted/shared internal-link URLs carry `paragraphId` in the query string,
     * but reader URL normalization can replace scope params after bootstrap.
     * Capture the route target before that happens and replay it through the
     * same paint-aware navigation pipeline used by clicked internal links.
     */
    useEffect(() => {
        const target = resolveInternalPostLinkTarget(`${readerBasePath}${location.search}`);
        if (target) {
            pendingReaderUrlInternalPostLinkTargetRef.current = target;
        }

        const pendingReaderUrlInternalPostLinkTarget = pendingReaderUrlInternalPostLinkTargetRef.current;
        if (
            write
            || !pendingReaderUrlInternalPostLinkTarget
            || handledReaderUrlInternalPostLinkHrefRef.current === pendingReaderUrlInternalPostLinkTarget.href
            || !activeRootPost
            || !currentZoomLevel
            || resolveInternalPostLinkPath(pendingReaderUrlInternalPostLinkTarget) == null
        ) {
            return;
        }

        let requiredReaderUrlInternalPostLinkZoomLevel: ZOOM_LEVEL | null = null;
        if (pendingReaderUrlInternalPostLinkTarget.sectionId) {
            requiredReaderUrlInternalPostLinkZoomLevel = ZOOM_LEVEL.five;
        } else if (pendingReaderUrlInternalPostLinkTarget.chapterId) {
            requiredReaderUrlInternalPostLinkZoomLevel = ZOOM_LEVEL.four;
        }
        if (
            requiredReaderUrlInternalPostLinkZoomLevel
            && currentZoomLevel.level < requiredReaderUrlInternalPostLinkZoomLevel
        ) {
            setCurrentZoomLevel({
                level: requiredReaderUrlInternalPostLinkZoomLevel,
                icon: adventureCharacters.get(requiredReaderUrlInternalPostLinkZoomLevel)!.icon,
            });
            return;
        }

        handledReaderUrlInternalPostLinkHrefRef.current = pendingReaderUrlInternalPostLinkTarget.href;
        pendingReaderUrlInternalPostLinkTargetRef.current = null;
        handleNavigateInternalPostLink(pendingReaderUrlInternalPostLinkTarget);
    }, [
        activeRootPost,
        currentZoomLevel,
        handleNavigateInternalPostLink,
        location.search,
        readerBasePath,
        resolveInternalPostLinkPath,
        write,
    ]);
    useEffect(() => {
        if (!pendingInternalPostLinkNavigation) {
            return undefined;
        }

        const resolvedPath = resolveInternalPostLinkPath(pendingInternalPostLinkNavigation);
        if (resolvedPath == null) {
            clearPendingInternalPostLinkNavigation();
            return undefined;
        }

        if (!deepEqual(selectedPostValuePath, resolvedPath) || !postBodyPainted) {
            return undefined;
        }

        const targetElement = getInternalPostLinkTargetElement(pendingInternalPostLinkNavigation.paragraphId);
        if (!targetElement) {
            if (write) {
                clearPendingInternalPostLinkNavigation();
                return undefined;
            }

            if (
                !pendingInternalPostLinkRevealTargetResolution
                || pendingInternalPostLinkRevealTargetResolution.paragraphId !== pendingInternalPostLinkNavigation.paragraphId
            ) {
                return undefined;
            }

            if (!pendingInternalPostLinkRevealTargetResolution.found) {
                const scopeRefreshHref = pendingInternalPostLinkNavigation.href;
                if (pendingInternalPostLinkScopeRefreshHrefRef.current === scopeRefreshHref) {
                    logInternalPostLink('readNavigationWaitingForScopeHydration', {
                        paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                        selectedPostValuePath,
                    });
                    return undefined;
                }

                if (attemptedInternalPostLinkScopeRefreshHrefRef.current !== scopeRefreshHref) {
                    refreshReadScopeForInternalPostLink(pendingInternalPostLinkNavigation);
                    return undefined;
                }

                if (internalPostLinkScopeDebugRef.current.usesPreviewSource) {
                    showInternalPostLinkPreviewLimitSnackbar(pendingInternalPostLinkNavigation);
                    clearPendingInternalPostLinkNavigation();
                    return undefined;
                }

                logInternalPostLink('readNavigationMissingTargetAfterScopeHydration', {
                    paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                    selectedPostValuePath,
                    currentScopeValueSource: internalPostLinkScopeDebugRef.current.source,
                    currentScopeUsesPreviewSource: internalPostLinkScopeDebugRef.current.usesPreviewSource,
                });
                clearPendingInternalPostLinkNavigation();
                return undefined;
            }

            if (
                pendingInternalPostLinkRevealTargetResolution.requiredVisibleBlockCount != null
                && postEditorRenderedBlockCount < pendingInternalPostLinkRevealTargetResolution.requiredVisibleBlockCount
            ) {
                return undefined;
            }
        }

        let cancelled = false;
        if (write) {
            if (!targetElement) {
                logInternalPostLink('writeNavigationMissingTargetElement', {
                    paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                    selectedPostValuePath,
                });
                clearPendingInternalPostLinkNavigation();
                return undefined;
            }

            const scrollFrame = window.requestAnimationFrame(() => {
                if (cancelled) {
                    return;
                }
                logInternalPostLink('writeNavigationScrollingToTarget', {
                    paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                    selectedPostValuePath,
                });
                scrollToInternalPostLinkTarget(targetElement);
                clearPendingInternalPostLinkNavigation();
            });

            return () => {
                cancelled = true;
                window.cancelAnimationFrame(scrollFrame);
            };
        }

        const cancelWait = waitForInternalPostLinkTargetMount(
            pendingInternalPostLinkNavigation.paragraphId,
            (stableTargetElement) => {
                if (cancelled) {
                    return;
                }

                logInternalPostLink('readNavigationScrollingToTarget', {
                    paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                    selectedPostValuePath,
                    renderedBlockCount: postEditorRenderedBlockCount,
                    forceFullReadOnlyRenderForResume,
                });
                scrollToInternalPostLinkTarget(stableTargetElement);
                clearPendingInternalPostLinkNavigation();
            },
            () => {
                if (cancelled) {
                    return;
                }

                if (!forceFullReadOnlyRenderForResume) {
                    /**
                     * Partial reveal is the preferred path, but if the anchor
                     * still never mounts after that reveal finishes, promote
                     * just this scope to a full read-only render instead of
                     * dropping the navigation on the floor.
                     */
                    logInternalPostLink('readNavigationEscalatingToFullRender', {
                        paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                        selectedPostValuePath,
                        renderedBlockCount: postEditorRenderedBlockCount,
                        requiredVisibleBlockCount: pendingInternalPostLinkRevealTargetResolution?.requiredVisibleBlockCount ?? null,
                    });
                    setForceFullReadOnlyRenderForResume(true);
                    return;
                }

                if (!postEditorReadOnlyFullRenderReady) {
                    logInternalPostLink('readNavigationWaitingForFullRenderReady', {
                        paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                    });
                    return;
                }

                logInternalPostLink('readNavigationFailedAfterFullRender', {
                    paragraphId: pendingInternalPostLinkNavigation.paragraphId,
                    selectedPostValuePath,
                });
                clearPendingInternalPostLinkNavigation();
            },
        );

        return () => {
            cancelled = true;
            cancelWait();
        };
    }, [
        pendingInternalPostLinkNavigation,
        resolveInternalPostLinkPath,
        selectedPostValuePath,
        postBodyPainted,
        write,
        forceFullReadOnlyRenderForResume,
        pendingInternalPostLinkRevealTargetResolution,
        postEditorRenderedBlockCount,
        postEditorReadOnlyFullRenderReady,
        clearPendingInternalPostLinkNavigation,
        getInternalPostLinkTargetElement,
        logInternalPostLink,
        refreshReadScopeForInternalPostLink,
        scrollToInternalPostLinkTarget,
        showInternalPostLinkPreviewLimitSnackbar,
        waitForInternalPostLinkTargetMount,
        waitForReaderProgressLayoutStability,
    ]);

    /**
     * Expands or collapses chapter body in contents table
     * @param index toggled chapter index
     * @param e mouse or touch event
     */
    const onSetExpandContentsTableChapter = (
        expandItems: IPostPathExpandChapterItem[],
        e?: React.MouseEvent | React.TouchEvent,
    ): void => {
        e?.stopPropagation();
        if (!contentsTable) return;

        const updatedContentsTable = [...contentsTable];
        expandItems.forEach(({ chapterIndex, expand }) => {
            const updatedChapter = {
                ...contentsTable[chapterIndex],
                expanded: expand,
            };
            updatedContentsTable[chapterIndex] = updatedChapter;

            if (
                currentSessionId
                && e // Only record user action if event is triggered by user
            ) {
                // Record User Action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.toggleCollapsedPostChapter,
                    sessionId: currentSessionId,
                    payload: {
                        chapterId: contentsTable[chapterIndex].id,
                        expanded: expand,
                    },
                });
            }
        });
        setContentsTable(updatedContentsTable);
    };

    /**
     * Adjusts Post Banner presentation based on scroll position of post
     * @param e scroll event
     */
    const trackPostScrollTopLatest = useLatest({
        stickyBannerScrollThreshold,
        viewportDimensions,
        annotationEditorIsVisible,
        postEditorReadOnlyChunkingIdle,
        write,
    });
    /**
     * Marks scroll-bound UI as actively scrolling so sticky chrome can pre-optimize
     * styles and the annotation editor can switch between scroll-follow motion and
     * its normal overshoot transition.
     */
    const setScrollWillChange = (active: boolean): void => {
        if (scrollWillChangeActiveRef.current === active) {
            return;
        }

        scrollWillChangeActiveRef.current = active;
        const dockAnnotationEditorToViewport = shouldDockAnnotationEditorToViewport(viewportDimensions.width);

        /**
         * The docked mobile editor already snaps to its viewport box, so
         * toggling the desktop scroll-follow transition state at scroll start
         * only forces an avoidable rerender/layer churn right when the gesture
         * begins.
         */
        if (dockAnnotationEditorToViewport) {
            setAnnotationEditorPositionTransitionDisabled(false);
        } else {
            setAnnotationEditorPositionTransitionDisabled(active);
        }

        const value = active ? 'transform, top, height' : 'auto';

        if (postBannerRef.current) postBannerRef.current.style.willChange = value;
        if (postBannerLeftContainerRef.current) postBannerLeftContainerRef.current.style.willChange = value;
        if (postThumbnailRef.current) postThumbnailRef.current.style.willChange = value;
        if (contentsTableRef.current) contentsTableRef.current.style.willChange = value;
        if (annotationEditorRef.current) {
            if (!active) {
                annotationEditorRef.current.style.willChange = 'auto';
            } else if (dockAnnotationEditorToViewport) {
                annotationEditorRef.current.style.willChange = 'auto';
            } else {
                annotationEditorRef.current.style.willChange = 'bottom, left';
            }
        }
        if (contentsTableToggleButtonContainerRef.current) {
            contentsTableToggleButtonContainerRef.current.style.willChange = value;
        }
        if (!active) {
            const { x, y } = annotationEditorPositionRef.current;
            setAnnotationEditorPosition((prev) => (
                prev.x === x && prev.y === y
                    ? prev
                    : { x, y }
            ));
        }
    };
    const SCROLL_STYLE_QUANTIZATION_STEP = 0.5;
    const SCROLL_PROGRESS_QUANTIZATION_STEP = 0.25;
    const SECONDARY_SCROLL_UPDATE_MIN_INTERVAL_MS = 33;
    const STICKY_ENTRY_LOCK_MS = 120;
    const STICKY_EXIT_HYSTERESIS_PX = 4;
    const quantizeValue = (value: number, step: number): number => (
        Math.round(value / step) * step
    );
    const quantizePx = (value: number): number => quantizeValue(value, SCROLL_STYLE_QUANTIZATION_STEP);
    const toQuantizedPx = (value: number): string => `${quantizePx(value)}px`;
    const quantizePercent = (value: number): number => quantizeValue(value, SCROLL_PROGRESS_QUANTIZATION_STEP);
    /**
     * Removes inline floating coordinates so the small-viewport docked editor can
     * fall back to its stylesheet-driven fixed positioning.
     */
    const resetAnnotationEditorDomPosition = (): void => {
        if (!annotationEditorRef.current) {
            return;
        }

        annotationEditorRef.current.style.left = '';
        annotationEditorRef.current.style.bottom = '';
    };
    /**
     * Writes the floating annotation editor position straight to the DOM node so
     * scroll-follow updates do not wait on a full ReaderView rerender.
     */
    const syncAnnotationEditorDomPosition = (nextPosition: ICoord): void => {
        if (!annotationEditorRef.current) {
            return;
        }

        if (shouldDockAnnotationEditorToViewport(viewportDimensions.width)) {
            resetAnnotationEditorDomPosition();
            return;
        }

        annotationEditorRef.current.style.left = `${nextPosition.x}px`;
        annotationEditorRef.current.style.bottom = `${viewportDimensions.height - nextPosition.y - DEFAULT_ANNOTATION_EDITOR_HEIGHT}px`;
    };
    /**
     * Stores the latest editor position and optionally mirrors it into React state
     * for non-scroll flows that still rely on declarative placement.
     */
    const commitAnnotationEditorPosition = (
        nextPosition: ICoord,
        syncState = true,
    ): void => {
        annotationEditorPositionRef.current = nextPosition;
        syncAnnotationEditorDomPosition(nextPosition);

        if (!syncState) {
            return;
        }

        setAnnotationEditorPosition((prev) => (
            prev.x === nextPosition.x && prev.y === nextPosition.y
                ? prev
                : nextPosition
        ));
    };

    /**
     * Repositions the visible annotation editor from rendered highlight geometry so
     * large post bodies cannot delay highlight-follow behavior behind progress work.
     */
    const updateVisibleAnnotationEditorPosition = (): boolean => {
        if (!trackPostScrollTopLatest.current.annotationEditorIsVisible) {
            annotationHighlightRef.current = null;
            return false;
        }

        if (shouldDockAnnotationEditorToViewport(viewportDimensions.width)) {
            resetAnnotationEditorDomPosition();
            return false;
        }

        const highlightQueryRoot = postContentContainerRef.current || rootRef.current || document;
        const highlights = Array.from(highlightQueryRoot.querySelectorAll<HTMLElement>('[data-quote=\'post-quote\']'))
            .filter((highlight) => highlight.isConnected);
        annotationHighlightRef.current = highlights[0] || null;
        const highlightRects = highlights.map((highlight) => highlight.getBoundingClientRect());
        const rect = resolveHighlightSelectionRect(
            highlightRects,
        );

        if (!rect) {
            annotationHighlightRef.current = null;
            return false;
        }

        /**
         * Rendered highlight spans can collapse a wrapped multi-line quote into a
         * single DOM box, so the remeasure pass must preserve the original
         * selection classification and only refresh the actual geometry.
         */
        const renderedPlacement = resolveAnnotationEditorPlacement({
            isMultiLineSelection: annotationEditorSelectionSpansMultipleLinesRef.current,
            selectionIsBackward: annotationEditorSelectionIsBackwardRef.current,
        });
        if (renderedPlacement !== annotationEditorPlacementRef.current) {
            annotationEditorPlacementRef.current = renderedPlacement;
        }

        let y = 0;
        let x = 0;
        if (
            containerRef.current
            && annotationEditorRef.current
        ) {
            // For read
            // We don't use Dropzone Ref
            const containerRect = containerRef.current.getBoundingClientRect();
            const toolbarWidth = annotationEditorRef.current.children[0]
                ? annotationEditorRef.current.children[0].clientWidth
                : 0;
            y = renderedPlacement === 'bottom'
                ? Math.max(rect.bottom - containerRect.top + 7, 0)
                : Math.max(
                    rect.top - containerRect.top - (DEFAULT_ANNOTATION_EDITOR_HEIGHT + 7),
                    0,
                );
            x = Math.max(
                rect.left - containerRect.left + rect.width / 2 - toolbarWidth / 2,
                0,
            );
        } else if (
            rootRef.current
            && annotationEditorRef.current
        ) {
            // For write
            // We use Dropzone Ref
            const rootRect = rootRef.current.getBoundingClientRect();
            const toolbarWidth = annotationEditorRef.current.children[0]
                ? annotationEditorRef.current.children[0].clientWidth
                : 0;
            y = renderedPlacement === 'bottom'
                ? rect.bottom - rootRect.top + 7
                : rect.top - rootRect.top - (DEFAULT_ANNOTATION_EDITOR_HEIGHT + 7);
            x = Math.max(
                rect.left - rootRect.left + rect.width / 2 - toolbarWidth / 2,
                0,
            );
        }

        // Keep desktop scroll-follow in React state as well so wide layouts do not
        // fall back to a stale absolute position after intermediary rerenders.
        commitAnnotationEditorPosition({ x, y });
        return true;
    };
    updateVisibleAnnotationEditorPositionRef.current = updateVisibleAnnotationEditorPosition;

    /**
     * Medium-and-up editors still float relative to the rendered highlight. The
     * first selection-time measurement can run before the quote decorators land
     * in the DOM, which leaves the editor parked at the fallback origin until a
     * later scroll forces a remeasure. Re-check the rendered highlight right
     * after open so desktop placement settles against real highlight geometry.
     */
    useLayoutEffect(() => {
        if (
            !annotationEditorIsVisible
            || !postQuote
            || shouldDockAnnotationEditorToViewport(viewportDimensions.width)
        ) {
            return undefined;
        }

        let rafId: number | null = null;
        let remainingAttempts = 4;

        const syncFromRenderedHighlight = (): void => {
            rafId = null;
            const positioned = updateVisibleAnnotationEditorPositionRef.current();
            if (positioned || remainingAttempts <= 0) {
                return;
            }

            remainingAttempts -= 1;
            rafId = window.requestAnimationFrame(syncFromRenderedHighlight);
        };

        rafId = window.requestAnimationFrame(syncFromRenderedHighlight);

        return () => {
            if (rafId != null) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [
        annotationEditorIsVisible,
        postQuote?.id,
        viewportDimensions.width,
        annotationDomRefreshVersion,
        postBodyPaintVersion,
    ]);

    const flushAnnotationEditorScrollPosition = (): void => {
        annotationEditorScrollRafRef.current = null;
        updateVisibleAnnotationEditorPosition();
    };
    const applySecondaryTrackPostScrollTop = (scrollTop: number): void => {
        const cur = trackPostScrollTopLatest.current;
        const shouldUpdateProgress = !!contentsTableProgressBarRef.current;
        let nextProgressHeight: string | null = null;

        if (!shouldUpdateProgress) {
            return;
        }

        // Contents Table Progress Bar
        if (shouldUpdateProgress) {
            let progress: number | null = null;
            const progressContainer = postContentContainerRef.current;
            // Limit progress tracking to the main post editor so annotation editors or
            // other Slate surfaces inside the same container do not distort the result.
            const renderedParagraphs = progressContainer
                ? Array.from(progressContainer.querySelectorAll<HTMLElement>(
                    `.${SLATE_EDITOR_POST_EDITOR_EDITABLE_CLASSNAME} [data-paragraph-id]`,
                ))
                : [];

            if (
                currentPostParagraphCountRef.current > 0
                && renderedParagraphs.length > 0
            ) {
                const progressContainerRect = progressContainer?.getBoundingClientRect();
                const viewportTop = useDocumentScroll
                    ? 0
                    : progressContainerRect?.top || 0;
                const viewportBottom = useDocumentScroll
                    ? window.innerHeight
                    : progressContainerRect?.bottom || window.innerHeight;
                // Clamp completion once the active scroll surface reaches the end of
                // the post so short final viewports do not leave the bar just shy of full.
                const hasReachedPostEnd = useDocumentScroll
                    ? !!progressContainerRect && progressContainerRect.bottom <= viewportBottom + 1
                    : !!progressContainer
                        && scrollTop + progressContainer.clientHeight >= progressContainer.scrollHeight - 1;

                progress = resolveReaderContentsProgress({
                    currentPostParagraphCount: currentPostParagraphCountRef.current,
                    renderedParagraphMetrics: renderedParagraphs.map((paragraph) => {
                        const { top, bottom, height } = paragraph.getBoundingClientRect();

                        return { top, bottom, height };
                    }),
                    viewportTop,
                    viewportBottom,
                    hasReachedPostEnd,
                    hasRenderedWholePost: cur.write || cur.postEditorReadOnlyChunkingIdle,
                });
            }

            if (progress == null && currentPostParagraphCountRef.current === 0) {
                // Hold the progress bar at the start until we know the full paragraph
                // count. Falling back to scroll height here causes the bar to jump
                // backwards once the real total length becomes available.
                progress = 0;
            }

            if (progress != null) {
                const quantizedProgress = quantizePercent(progress);
                nextProgressHeight = `${quantizedProgress}%`;
            }
        }

        // Write phase
        if (
            nextProgressHeight
            && nextProgressHeight !== lastContentsProgressRef.current
        ) {
            contentsTableProgressBarRef.current?.style.setProperty('--reader-contents-progress-height', nextProgressHeight);
            lastContentsProgressRef.current = nextProgressHeight;
        }
    };
    const flushSecondaryTrackPostScrollTop = (): void => {
        secondaryScrollRafRef.current = null;

        const now = window.performance.now();
        if (now - lastSecondaryScrollUpdateRef.current < SECONDARY_SCROLL_UPDATE_MIN_INTERVAL_MS) {
            secondaryScrollRafRef.current = window.requestAnimationFrame(flushSecondaryTrackPostScrollTop);
            return;
        }
        lastSecondaryScrollUpdateRef.current = now;

        if (pendingSecondaryScrollTopRef.current == null) {
            return;
        }

        applySecondaryTrackPostScrollTop(pendingSecondaryScrollTopRef.current);
        pendingSecondaryScrollTopRef.current = null;
    };
    const applyTrackPostScrollTop = (scrollTopOverride?: number): void => {
        const cur = trackPostScrollTopLatest.current;
        const liveViewportHeight = useDocumentScroll
            ? Math.max(
                window.visualViewport?.height || 0,
                window.innerHeight || 0,
                document.documentElement.clientHeight || 0,
            )
            : cur.viewportDimensions.height;

        let scrollTop = scrollTopOverride;
        if (scrollTop == null) {
            scrollTop = useDocumentScroll
                ? Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0)
                : postContentContainerRef.current?.scrollTop || 0;
        }

        // Necessary for AnnotationBucket
        postScrollTop.current = scrollTop;

        const now = window.performance.now();
        let withinStickyLock = now < stickyLockUntilRef.current;
        const withinStickyRevealHold = now < stickyRevealHoldUntilRef.current;
        const stickyExitThreshold = Math.max(
            cur.stickyBannerScrollThreshold - STICKY_EXIT_HYSTERESIS_PX,
            0,
        );
        const isScrollingUp = scrollTop < lastTrackedPostScrollTopRef.current;
        lastTrackedPostScrollTopRef.current = scrollTop;
        if (scrollTop >= cur.stickyBannerScrollThreshold) {
            stickyLockUntilRef.current = now + STICKY_ENTRY_LOCK_MS;
            if (stickyLockRecheckTimeoutRef.current != null) {
                window.clearTimeout(stickyLockRecheckTimeoutRef.current);
                stickyLockRecheckTimeoutRef.current = null;
            }
            if (stickyDeactivateTimeoutRef.current != null) {
                window.clearTimeout(stickyDeactivateTimeoutRef.current);
                stickyDeactivateTimeoutRef.current = null;
            }
            if (!stickyPostBannerActiveRef.current) {
                stickyPostBannerActiveRef.current = true;
                setStickyPostBannerActive(true);
            }
        } else if (stickyPostBannerActiveRef.current && (withinStickyLock || withinStickyRevealHold)) {
            if (!withinStickyRevealHold && isScrollingUp && scrollTop < stickyExitThreshold) {
                if (stickyLockRecheckTimeoutRef.current != null) {
                    window.clearTimeout(stickyLockRecheckTimeoutRef.current);
                    stickyLockRecheckTimeoutRef.current = null;
                }
                // Exit lock immediately so fixed banner/content offsets don't diverge for one frame.
                stickyLockUntilRef.current = now;
                withinStickyLock = false;
                stickyPostBannerActiveRef.current = false;
                setStickyPostBannerActive(false);
            } else if (stickyLockRecheckTimeoutRef.current == null) {
                const delay = Math.max(
                    Math.max(stickyLockUntilRef.current, stickyRevealHoldUntilRef.current) - now,
                    0,
                ) + 1;
                stickyLockRecheckTimeoutRef.current = window.setTimeout(() => {
                    stickyLockRecheckTimeoutRef.current = null;
                    // Lock expiry may happen after scrolling stops; force a re-evaluation pass.
                    applyTrackPostScrollTop(postScrollTop.current);
                }, delay);
            }
        } else if (stickyPostBannerActiveRef.current && !withinStickyLock) {
            if (!useDocumentScroll) {
                if (stickyDeactivateTimeoutRef.current != null) {
                    window.clearTimeout(stickyDeactivateTimeoutRef.current);
                    stickyDeactivateTimeoutRef.current = null;
                }
                if (scrollTop < stickyExitThreshold) {
                    stickyPostBannerActiveRef.current = false;
                    setStickyPostBannerActive(false);
                }
            } else {
                // On iOS/Safari, visual viewport events can briefly perturb scroll metrics
                // while still scrolling down. Only exit sticky when user is actively scrolling up.
                if (stickyDeactivateTimeoutRef.current != null) {
                    window.clearTimeout(stickyDeactivateTimeoutRef.current);
                    stickyDeactivateTimeoutRef.current = null;
                }
                if (isScrollingUp && scrollTop < stickyExitThreshold) {
                    stickyPostBannerActiveRef.current = false;
                    setStickyPostBannerActive(false);
                }
            }
        }

        const fixedToViewport = stickyPostBannerActiveRef.current
            || scrollTop >= cur.stickyBannerScrollThreshold
            || withinStickyLock
            || withinStickyRevealHold;
        const stickyThreshold = Math.max(cur.stickyBannerScrollThreshold, 1);
        const currentPostBannerHeight = quantizePx(fixedToViewport
            ? POST_BANNER_LEFT_CONTAINER_HEIGHT_CONTRACTED
            : ((POST_BANNER_LEFT_CONTAINER_HEIGHT_CONTRACTED - POST_BANNER_LEFT_CONTAINER_HEIGHT_EXPANDED) / stickyThreshold) * scrollTop
                + POST_BANNER_LEFT_CONTAINER_HEIGHT_EXPANDED);

        // Keep focused annotation bucket height aligned with banner collapse.
        document.documentElement.style.setProperty(
            '--reader-post-banner-height',
            toQuantizedPx(currentPostBannerHeight),
        );

        // Post Banner Left Container
        // The large layout keeps the cover aligned with the visible banner.
        // Smaller layouts retain their existing expanded/sticky sizing.
        let coverCollapseProgress = fixedToViewport ? 1 : 0;
        if (!fixedToViewport && cur.viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min) {
            coverCollapseProgress = Math.min(Math.max(scrollTop / stickyThreshold, 0), 1);
        }
        /** Keeps cover dimensions and spacing on the same collapse progress. */
        const scaleCoverDimension = (expanded: number, contracted: number): string => (
            toQuantizedPx(expanded + (contracted - expanded) * coverCollapseProgress)
        );

        // Preserve the responsive layout and sticky-lock rules; only restore
        // continuous sizing between the expanded and contracted dimensions.
        if (postBannerLeftContainerRef.current) {
            postBannerLeftContainerRef.current.style.setProperty(
                '--reader-post-banner-left-collapse-progress',
                `${coverCollapseProgress}`,
            );
            postBannerLeftContainerRef.current.style.setProperty(
                '--reader-post-banner-left-height',
                scaleCoverDimension(POST_BANNER_LEFT_CONTAINER_HEIGHT_EXPANDED, POST_BANNER_LEFT_CONTAINER_HEIGHT_CONTRACTED),
            );
            postBannerLeftContainerRef.current.style.setProperty(
                '--reader-post-banner-left-padding',
                scaleCoverDimension(POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED, POST_BANNER_LEFT_CONTAINER_PADDING_CONTRACTED),
            );
        }
        // Post Thumbnail
        if (postThumbnailRef.current) {
            let expandedThumbnailHeight = POST_THUMBNAIL_MAX_WIDTH_SMALL;
            if (cur.viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min) {
                expandedThumbnailHeight = POST_THUMBNAIL_HEIGHT_EXPANDED;
            } else if (cur.viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min) {
                expandedThumbnailHeight = POST_THUMBNAIL_MAX_WIDTH_MEDIUM;
            }
            postThumbnailRef.current.style.setProperty(
                '--reader-post-thumbnail-bottom',
                scaleCoverDimension(POST_THUMBNAIL_BOTTOM_EXPANDED, POST_THUMBNAIL_BOTTOM_CONTRACTED),
            );
            postThumbnailRef.current.style.setProperty(
                '--reader-post-thumbnail-width',
                scaleCoverDimension(POST_THUMBNAIL_WIDTH_EXPANDED, POST_THUMBNAIL_WIDTH_CONTRACTED),
            );
            postThumbnailRef.current.style.setProperty(
                '--reader-post-thumbnail-border-radius',
                scaleCoverDimension(POST_THUMBNAIL_BORDER_RADIUS_EXPANDED, POST_THUMBNAIL_BORDER_RADIUS_CONTRACTED),
            );
            postThumbnailRef.current.style.setProperty(
                '--reader-post-thumbnail-height',
                scaleCoverDimension(expandedThumbnailHeight, POST_THUMBNAIL_HEIGHT_CONTRACTED),
            );
        }

        // Contents Table
        if (contentsTableRef.current) {
            const contentsTableHeightExpanded = liveViewportHeight - POST_BANNER_HEIGHT;
            const contentsTableHeightContracted = liveViewportHeight - STICKY_POST_BANNER_HEIGHT;
            contentsTableRef.current.style.setProperty(
                '--reader-contents-table-top',
                fixedToViewport
                    ? toQuantizedPx(CONTENTS_TABLE_TOP_CONTRACTED)
                    : toQuantizedPx(
                        ((CONTENTS_TABLE_TOP_CONTRACTED - CONTENTS_TABLE_TOP_EXPANDED) / cur.stickyBannerScrollThreshold) * scrollTop
                        + CONTENTS_TABLE_TOP_EXPANDED,
                    ),
            );
            contentsTableRef.current.style.setProperty(
                '--reader-contents-table-height',
                fixedToViewport
                    ? toQuantizedPx(contentsTableHeightContracted - 2 * CONTENTS_TABLE_MARGIN)
                    : toQuantizedPx(
                        ((contentsTableHeightContracted - contentsTableHeightExpanded) / cur.stickyBannerScrollThreshold) * scrollTop
                        + (contentsTableHeightExpanded - 2 * CONTENTS_TABLE_MARGIN),
                    ),
            );
        }

        // Contents Table Toggle Button Container
        if (contentsTableToggleButtonContainerRef.current) {
            const contentsTableToggleButtonContainerTopExpanded = POST_BANNER_HEIGHT + CONTENTS_TABLE_MARGIN;
            const contentsTableToggleButtonContainerTopContracted = STICKY_POST_BANNER_HEIGHT + CONTENTS_TABLE_MARGIN;
            if (cur.viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min) {
                contentsTableToggleButtonContainerRef.current.style.setProperty(
                    '--reader-contents-toggle-top',
                    fixedToViewport
                        ? toQuantizedPx(contentsTableToggleButtonContainerTopContracted)
                        : toQuantizedPx(
                            ((contentsTableToggleButtonContainerTopContracted - contentsTableToggleButtonContainerTopExpanded) / cur.stickyBannerScrollThreshold) * scrollTop
                            + contentsTableToggleButtonContainerTopExpanded,
                        ),
                );
            } else {
                contentsTableToggleButtonContainerRef.current.style.setProperty(
                    '--reader-contents-toggle-top',
                    fixedToViewport
                        ? toQuantizedPx(
                            contentsTableToggleButtonContainerTopContracted + CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_Y_OFFSET_SMALL_VIEWPORT,
                        )
                        : toQuantizedPx(
                            ((contentsTableToggleButtonContainerTopContracted - contentsTableToggleButtonContainerTopExpanded) / cur.stickyBannerScrollThreshold) * scrollTop
                            + contentsTableToggleButtonContainerTopExpanded
                            + CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_Y_OFFSET_SMALL_VIEWPORT,
                        ),
                );
            }
        }

        // Post Banner
        // Must be placed after stickyPostBannerActive changes
        // If it is before, it's changes will cause scrollHeight to change
        // and the scroll event will be triggered again, reverting sticky banner
        if (postBannerRef.current) {
            postBannerRef.current.style.position = fixedToViewport
                ? 'fixed'
                : 'relative';
            postBannerRef.current.style.top = fixedToViewport
                ? toQuantizedPx(0)
                : 'auto';
            postBannerRef.current.style.transform = fixedToViewport
                ? `translateY(calc(-100% + ${STICKY_POST_BANNER_HEIGHT}px))`
                : 'none';
        }

        if (cur.annotationEditorIsVisible) {
            if (annotationEditorScrollRafRef.current == null) {
                annotationEditorScrollRafRef.current = window.requestAnimationFrame(flushAnnotationEditorScrollPosition);
            }
        } else {
            annotationHighlightRef.current = null;
        }

        if (contentsTableProgressBarRef.current) {
            pendingSecondaryScrollTopRef.current = scrollTop;
            if (secondaryScrollRafRef.current == null) {
                secondaryScrollRafRef.current = window.requestAnimationFrame(flushSecondaryTrackPostScrollTop);
            }
        }
    };
    const trackPostScrollTop = (e?: Event): void => {
        const target: HTMLElement | null = e?.target as HTMLElement;
        // Re-enable anonymous local fallback once the current anon session records intentional scrolling.
        if (suppressAnonymousLocalResumeFallbackRef.current) {
            suppressAnonymousLocalResumeFallbackRef.current = false;
        }
        setScrollWillChange(true);
        if (scrollWillChangeTimeoutRef.current != null) {
            window.clearTimeout(scrollWillChangeTimeoutRef.current);
        }
        scrollWillChangeTimeoutRef.current = window.setTimeout(() => {
            setScrollWillChange(false);
            scrollWillChangeTimeoutRef.current = null;
        }, 120);

        pendingScrollTopRef.current = useDocumentScroll
            ? Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0)
            : target?.scrollTop || 0;
        // Once user scrolls manually, hide the resume CTA and debounce-save new position.
        if (
            resumeReaderProgressRef.current
            && pendingScrollTopRef.current > READER_PROGRESS_RESUME_HIDE_SCROLL_THRESHOLD
        ) {
            setResumeReaderProgress(null);
        }
        if (
            !readerProgressLatest.current.write
            && readerProgressLatest.current.user?.saveReaderProgress !== false
        ) {
            timeoutPersistReaderProgress();
        }

        if (scrollRafRef.current != null) {
            return;
        }

        scrollRafRef.current = window.requestAnimationFrame(() => {
            scrollRafRef.current = null;
            const nextScrollTop = pendingScrollTopRef.current || 0;
            // Applying sticky state can render and queue another frame. Consume
            // this frame's position first so that new position is not cleared.
            pendingScrollTopRef.current = null;
            applyTrackPostScrollTop(nextScrollTop);
        });
    };

    /**
     * Adjusts the zoom level of the post
     * @param level desired zoom level
     */
    const handleZoomLevel = (level: ZOOM_LEVEL): void => {
        // A direct zoom change supersedes any temporary auth-gap fallback zoom.
        readZoomInitializedFromAuthFallbackRef.current = false;
        setCurrentZoomLevel({
            level,
            icon: adventureCharacters.get(level)!.icon,
        });

        // Set URL Params for zoom level
        let zoomLevel: number;
        switch (level) {
        case ZOOM_LEVEL.one:
            zoomLevel = 1;
            break;
        case ZOOM_LEVEL.two:
            zoomLevel = 2;
            break;
        case ZOOM_LEVEL.three:
            zoomLevel = 3;
            break;
        case ZOOM_LEVEL.four:
            zoomLevel = 4;
            break;
        case ZOOM_LEVEL.five:
        default:
            zoomLevel = 5;
            break;
        }

        if (
            activeRootPost
            && currentSessionId
        ) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.adjustPostZoom,
                sessionId: currentSessionId,
                payload: {
                    currentZoom: zoomLevel,
                    postId: activeRootPost.id,
                },
            });

            // Update user zoom level preference
            if (
                user
                && user.zoom !== level
            ) {
                updateUser({
                    test: useStripeTestMode,
                    zoom: level,
                });
            }
        }
    };

    const clearHideAnnotationsSyncTimeout = useCallback((): void => {
        if (hideAnnotationsSyncTimeoutRef.current != null) {
            window.clearTimeout(hideAnnotationsSyncTimeoutRef.current);
            hideAnnotationsSyncTimeoutRef.current = null;
        }
    }, []);

    const clearHideAnnotationsFeedbackTimeout = useCallback((): void => {
        if (hideAnnotationsFeedbackTimeoutRef.current != null) {
            window.clearTimeout(hideAnnotationsFeedbackTimeoutRef.current);
            hideAnnotationsFeedbackTimeoutRef.current = null;
        }
    }, []);

    const queueHideAnnotationsFeedback = useCallback((hide: boolean): void => {
        clearHideAnnotationsFeedbackTimeout();
        hideAnnotationsFeedbackTimeoutRef.current = window.setTimeout(() => {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: hide ? SNACKBAR_MESSAGE_HIDE_ANNOTATIONS_ENABLED : SNACKBAR_MESSAGE_HIDE_ANNOTATIONS_DISABLED,
                icon: HighlightIcon,
                hasSuccess: true,
            });
        }, HIDE_ANNOTATIONS_FEEDBACK_DEBOUNCE_DURATION);
    }, [
        hasSound,
        clearHideAnnotationsFeedbackTimeout,
        setSnackbarData,
    ]);

    const persistHideAnnotationsPreference = useCallback((hide: boolean): void => {
        if (!user) return;

        hideAnnotationsWriteInFlightRef.current = true;
        inFlightHideAnnotationsTargetRef.current = hide;
        hideAnnotationsRequestIdRef.current += 1;
        const requestId = hideAnnotationsRequestIdRef.current;
        clearHideAnnotationsSyncTimeout();

        updateUser({
            test: useStripeTestMode,
            hideAnnotations: hide,
        });

        hideAnnotationsSyncTimeoutRef.current = window.setTimeout(() => {
            if (hideAnnotationsRequestIdRef.current !== requestId) return;

            hideAnnotationsWriteInFlightRef.current = false;
            inFlightHideAnnotationsTargetRef.current = null;
            queuedHideAnnotationsRef.current = null;
            clearHideAnnotationsFeedbackTimeout();
            setAnnotationsHiddenPendingSync(false);
            setAnnotationsHiddenOverride(null);
            annotationsHiddenValueRef.current = remoteAnnotationsHidden;
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_HIDE_ANNOTATIONS_SYNC_FAILURE,
                icon: HighlightIcon,
                hasFailure: true,
            });
        }, HIDE_ANNOTATIONS_SYNC_TIMEOUT);
    }, [
        user,
        remoteAnnotationsHidden,
        setSnackbarData,
        clearHideAnnotationsSyncTimeout,
        clearHideAnnotationsFeedbackTimeout,
    ]);

    /**
     * Toggles annotation visibility in read mode and persists preference for signed-in users.
     * Uses optimistic UI and a queued write so users can keep toggling without local/remote drift.
     */
    const handleToggleAnnotationVisibility = useCallback((): void => {
        if (!user) return;

        const hide = !annotationsHiddenValueRef.current;
        annotationsHiddenValueRef.current = hide;
        setAnnotationsHiddenOverride(hide);
        setAnnotationsHiddenPendingSync(true);
        // Delay feedback until user pauses toggling; do not block on remote preference write latency.
        queueHideAnnotationsFeedback(hide);

        if (hideAnnotationsWriteInFlightRef.current) {
            queuedHideAnnotationsRef.current = hide;
        } else {
            queuedHideAnnotationsRef.current = null;
            persistHideAnnotationsPreference(hide);
        }

        if (hasSound && toggleSnapClip.current) {
            toggleSnapClip.current.pause();
            toggleSnapClip.current.currentTime = 0;
            playAudio(toggleSnapClip.current);
        }

        if (currentSessionId) {
            recordUserAction({
                test: useStripeTestMode,
                type: hide ? USER_ACTION_TYPE.hideAnnotations : USER_ACTION_TYPE.unhideAnnotations,
                sessionId: currentSessionId,
            });
        }
    }, [
        user,
        currentSessionId,
        queueHideAnnotationsFeedback,
        persistHideAnnotationsPreference,
    ]);

    /**
     * Manages response to toggling the zoom level button
     * @param visible reveal or hide the button
     */
    const handleToggleZoomLevelButton = (visible: boolean): void => {
        // Play Sound
        if (
            visible
            && hasSound
            && swooshInClip.current
        ) {
            swooshInClip.current.pause();
            swooshInClip.current.currentTime = 0;
            playAudio(swooshInClip.current);
        } else if (
            !visible
            && hasSound
            && swooshOutClip.current
        ) {
            swooshOutClip.current.pause();
            swooshOutClip.current.currentTime = 0;
            playAudio(swooshOutClip.current);
        }
    };

    /**
     * Generates the contents table for the post based on the post's chapters and sections
     */
    const refreshContentsTableLatest = useLatest({
        refreshingContentsTable,
        contentsTable,
        selectedPostValuePath,
    });
    const refreshContentsTable = async (): Promise<void> => {
        const cur = refreshContentsTableLatest.current;
        if (!postId) return;
        if (!refreshingContentsTable) setRefreshingContentsTable(true);
        try {
            const updatedContentsTable = await getPostContentsTable({
                test: useStripeTestMode,
                postId,
                prevContentsTable: cur.contentsTable || undefined,
                expandedChapterIndex: cur.selectedPostValuePath.length === 2
                    ? cur.selectedPostValuePath[0]
                    : undefined,
            });
            setContentsTable(updatedContentsTable);
        } catch (error) {
            // Keep previous table on fetch failures; avoid leaving spinner state stuck.
        } finally {
            setRefreshingContentsTable(false);
        }
    };

    /**
     * Applies an immediate optimistic bump to contents-table annotation counts
     * so chapter/section badges stay responsive while backend fan-out finishes.
     */
    const incrementContentsTableAnnotationCount = ({
        chapterId,
        sectionId,
    }: {
        chapterId?: string,
        sectionId?: string,
    }): void => {
        setContentsTable((prev) => {
            if (!prev || chapterId == null) return prev;

            const updated = [...prev];
            const chapterIndex = updated.findIndex((chapter) => chapter.id === chapterId);
            if (chapterIndex === -1) return prev;
            const targetChapter = updated[chapterIndex];
            let changed = false;

            if (sectionId != null && targetChapter.sections) {
                const sectionIndex = targetChapter.sections.findIndex((section) => section.id === sectionId);
                if (sectionIndex !== -1 && targetChapter.sections[sectionIndex]) {
                    const updatedSections = [...targetChapter.sections];
                    updatedSections[sectionIndex] = {
                        ...updatedSections[sectionIndex],
                        annotationCount: updatedSections[sectionIndex].annotationCount + 1,
                    };
                    updated[chapterIndex] = {
                        ...targetChapter,
                        sections: updatedSections,
                        annotationCount: targetChapter.annotationCount + 1,
                    };
                    changed = true;
                }
            } else {
                updated[chapterIndex] = {
                    ...targetChapter,
                    annotationCount: targetChapter.annotationCount + 1,
                };
                changed = true;
            }

            return changed ? updated : prev;
        });
    };

    /**
     * Applies an immediate optimistic decrement to contents-table annotation counts
     * when a top-level annotation is removed locally.
     */
    const decrementContentsTableAnnotationCount = ({
        chapterId,
        sectionId,
    }: {
        chapterId?: string,
        sectionId?: string,
    }): void => {
        setContentsTable((prev) => {
            if (!prev || chapterId == null) return prev;

            const updated = [...prev];
            const chapterIndex = updated.findIndex((chapter) => chapter.id === chapterId);
            if (chapterIndex === -1) return prev;
            const targetChapter = updated[chapterIndex];
            let changed = false;

            if (sectionId != null && targetChapter.sections) {
                const sectionIndex = targetChapter.sections.findIndex((section) => section.id === sectionId);
                if (sectionIndex !== -1 && targetChapter.sections[sectionIndex]) {
                    const updatedSections = [...targetChapter.sections];
                    updatedSections[sectionIndex] = {
                        ...updatedSections[sectionIndex],
                        annotationCount: Math.max(updatedSections[sectionIndex].annotationCount - 1, 0),
                    };
                    updated[chapterIndex] = {
                        ...targetChapter,
                        sections: updatedSections,
                        annotationCount: Math.max(targetChapter.annotationCount - 1, 0),
                    };
                    changed = true;
                }
            } else {
                updated[chapterIndex] = {
                    ...targetChapter,
                    annotationCount: Math.max(targetChapter.annotationCount - 1, 0),
                };
                changed = true;
            }

            return changed ? updated : prev;
        });
    };

    const selectPostThumbnail = (e: React.MouseEvent): void => {
        if (!uploadingPostThumbnail) {
            e.stopPropagation();
            openPostThumbnailDropzone();
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_EXISTING_AVATAR_UPLOAD,
                icon: CautionIcon,
                hasFailure: true,
            });
        }
    };

    const handlePostThumbnailReplace = (file: File): void => {
        if (!postId) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: 'Aborted replacing post thumbnail because post id could not be sourced.',
                    },
                });
            }
            throw Error('Aborted replacing post thumbnail because post id could not be sourced.');
        }
        const mediaId = uuidv4();
        const uniqueId = new ShortUniqueId({ length: 6 })(); // avoid file name collisions
        const mediaBucket = getMediaStorageBucket(MEDIA_TYPE.image);
        const fileName = file.name.toLowerCase().split('.')[0].replace(' ', '_');
        const fileExtension = mime.getExtension(file.type);
        if (fileExtension == null) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: `Unable to determine file extension. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                icon: FileIcon,
                hasFailure: true,
            });
            return;
        }
        const storageEntity = useStripeTestMode
            ? STORAGE_ENTITY.stagingPosts
            : STORAGE_ENTITY.posts;
        const filePath = `${storageEntity}/${mediaBucket}/${mediaId}/${fileName}-${uniqueId}.${fileExtension}`;
        const mediaItem: IMediaItem = {
            id: mediaId,
            type: MEDIA_TYPE.image,
            userId: user!.id,
            file,
            filePath,
            uploadProgress: 0,
        };
        setUploadingPostThumbnail(mediaItem);

        // make db entry of media
        // execute before file upload so upload cloud function has a place to write to
        createMedia({
            test: useStripeTestMode,
            postId,
            mediaId,
            type: MEDIA_TYPE.image,
            storageEntity,
            fileName: `${fileName}-${uniqueId}`,
            fileExtension: fileExtension.toString(),
        });

        // upload file to cloud storage
        // url will be set by cloud function
        const uploadTask = uploadToCloudStorage(
            file,
            filePath,
            user!.id,
        );

        uploadTask.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
                // Observe state change events such as progress, pause, and resume
                // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadingThumbnailStateChangeEvent({
                    mediaItem,
                    snapshot,
                    progress,
                });
            },
            (error: StorageError) => {
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: getStorageErrorMessage(error.code as STORAGE_ERROR_CODE),
                        },
                    });
                }
                throw Error(getStorageErrorMessage(error.code as STORAGE_ERROR_CODE));
            },
            () => {
                setUploadingThumbnailCompleteEvent({
                    mediaItem,
                    storageRef: uploadTask.snapshot.ref,
                });
            },
        );
    };

    /**
     * Manages progress update of an uploading post thumbnail
     * @param mediaItem thumbnail being uploaded
     * @param _snapshot upload task snapshot
     * @param progress perentage of upload completed
     */
    const handleUploadingPostThumbnailStateChange = (
        mediaItem: IMediaItem,
        _snapshot: UploadTaskSnapshot,
        progress: number,
    ): void => {
        if (uploadingPostThumbnail) {
            setUploadingPostThumbnail({
                ...uploadingPostThumbnail,
                uploadProgress: progress,
            });
        } else {
            setUploadingPostThumbnail(mediaItem);
        }
    };

    /**
     * Manages completion update of an uploading post thumbnail
     */
    const handleUploadingPostThumbnailComplete = (): void => {
        // Add post thumbnail file path to user item
        if (post && uploadingPostThumbnail) {
            handleUpdatePost({
                test: useStripeTestMode,
                postId: post.id,
                imagePath: uploadingPostThumbnail.filePath,
            });
        }
        // Remove from uploading queue
        setUploadingPostThumbnail(null);
    };

    /**
     * Manages determining the number of zoom levels applied to the post
     */
    const determineNumberAppliedZoomLevelsLatest = useLatest({
        determiningPostZoomLevelCount,
    });
    const determineNumberAppliedZoomLevels = (): void => {
        if (
            !write
            && postId
            && !appliedZoomLevelCount
            && !determiningPostZoomLevelCount
        ) {
            if (!determiningPostZoomLevelCount) setDeterminingPostZoomLevelCount(true);
            getPostZoomLevelCount({
                test: useStripeTestMode,
                postId,
            }).then((numAppliedZoomLevels) => {
                const cur = determineNumberAppliedZoomLevelsLatest.current;
                setAppliedZoomLevelCount(numAppliedZoomLevels);
                if (cur.determiningPostZoomLevelCount) setDeterminingPostZoomLevelCount(false);
            });
        }
    };

    /**
     * Manages adding book to cart
     */
    const addBookToCart = ({ type, sourceId }: { type?: BOOK_TYPE, sourceId?: string }): void => {
        if (!type) {
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error: 'There was an error adding item to cart. Unable to retrieve item code',
                    },
                });
            }
            throw Error('There was an error adding item to cart. Unable to retrieve item code');
        }
        if (
            !fetchedStoreItems
            || storeItemsByProduct.size === 0
        ) {
            if (!fetchingStoreItems) {
                fetchStoreItems();
            }
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: 'Preparing purchase options. Please try again in a moment.',
                icon: CartIcon,
            });
            return;
        }
        const addingCurrentChapterItem = (
            currentPageChapterBookType != null
            && type === currentPageChapterBookType
        );
        const currentChapterLockedUntil = currentReadPostChapter?.lockedUntil;
        const currentChapterLockedIndefinitely = isLockedUntilIndefinite(currentChapterLockedUntil);
        const currentChapterReleaseLabel = (
            currentChapterLockedUntil != null
            && !currentChapterLockedIndefinitely
        )
            ? formatReleaseTimestamp(currentChapterLockedUntil)
            : null;
        let currentChapterLockedReleaseText = 'Unable to add item to cart. Item is not yet available for purchase.';
        if (currentChapterReleaseLabel?.isExactDate) {
            currentChapterLockedReleaseText = `Unable to add item to cart. Item will be available for purchase ${currentChapterReleaseLabel.text}`;
        } else if (currentChapterReleaseLabel) {
            currentChapterLockedReleaseText = `Unable to add item to cart. Item will be available for purchase in ${currentChapterReleaseLabel.text}`;
        }
        if (
            addingCurrentChapterItem
            && currentChapterLockedUntil != null
            && currentChapterLockedUntil > Date.now()
            && !(!!user && userIsAdmin)
        ) {
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                // Only gate adding the currently loaded chapter when that chapter is unreleased.
                text: currentChapterLockedReleaseText,
                icon: CartIcon,
                hasFailure: true,
            });
            return;
        }
        // Add book to cart
        if (user) {
            const stagedItemId = uuidv4();
            const cartMediaItem: IMediaItem = {
                id: stagedItemId,
                userId: user.id,
                file: new File([], type),
                // We set this to the ID of the cart navigation button
                // This is how it is located
                filePath: NAVIGATION_CART_BUTTON_ID,
                type: MEDIA_TYPE.image,
                uploadProgress: 100,
                description: sourceId,
            };
            const webBookProductIdMap: Map<string, boolean> = new Map([
                [WEB_BOOK_PRODUCT_ID, true],
                [WEB_BOOK_DAY_PASS_PRODUCT_ID, true],
                [WEB_BOOK_WEEK_PASS_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_ONE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_TWO_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_THREE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SIX_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_NINE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_TEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID, true],
                [WEB_BOOK_APPENDIX_A_PRODUCT_ID, true],
            ]);
            const webBookChapterAndTimePassProductIdMap: Map<string, boolean> = new Map([
                [WEB_BOOK_DAY_PASS_PRODUCT_ID, true],
                [WEB_BOOK_WEEK_PASS_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_ONE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_TWO_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_THREE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SIX_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_NINE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_TEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID, true],
                [WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID, true],
                [WEB_BOOK_APPENDIX_A_PRODUCT_ID, true],
            ]);
            const resolveStoreItemForCart = (productId?: string): IStoreItem | null => {
                if (!productId) {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: 'Unable to add this item to cart right now. Please try again in a moment.',
                        icon: CartIcon,
                        hasFailure: true,
                    });
                    return null;
                }

                const resolvedStoreItem = storeItemsByProduct.get(productId);
                if (resolvedStoreItem && resolvedStoreItem.priceId) {
                    return resolvedStoreItem;
                }

                if (!fetchingStoreItems) {
                    fetchStoreItems();
                }
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: 'This item is currently unavailable for purchase. Please try again in a moment.',
                    icon: CartIcon,
                    hasFailure: true,
                });
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: (
                                `Missing or invalid store item for cart add. productId=${productId}, `
                                + `type=${type}, sourceId=${sourceId || 'unknown'}, `
                                + `hasStoreItem=${!!resolvedStoreItem}, hasPriceId=${!!resolvedStoreItem?.priceId}, `
                                + `availableProductIds=${Array.from(storeItemsByProduct.keys()).join(',')}`
                            ),
                            source: 'reader-add-to-cart-missing-store-item',
                        },
                    });
                }
                return null;
            };
            switch (type) {
            case BOOK_TYPE.physical:
                if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes physical book
                    // Increment quantity and remove existing cart item
                    let cartItem = userCartItems.find((item: ICartItem) => item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID)!;
                    cartItem = {
                        ...cartItem,
                        quantity: cartItem.quantity + 1,
                        updated: Date.now(),
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: [PHYSICAL_BOOK_PREORDER_PRODUCT_ID],
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingPhysicalBookCartItem(true);
                    const associatedStoreItem = storeItemsByProduct.get(PHYSICAL_BOOK_PREORDER_PRODUCT_ID);
                    if (associatedStoreItem) {
                        [cartMediaItem.url] = associatedStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => webBookChapterAndTimePassProductIdMap.get(item.productId))
                    && !userCartItems.find((item: ICartItem) => item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes chapter/time access items
                    // Remove and replace with physical book
                    const webBookProductIds: string[] = [];
                    userCartItems.forEach((item: ICartItem) => {
                        const webBookProductId = webBookChapterAndTimePassProductIdMap.get(item.productId);
                        if (webBookProductId) webBookProductIds.push(item.productId);
                    });
                    const physicalBookStoreItem = resolveStoreItemForCart(PHYSICAL_BOOK_PREORDER_PRODUCT_ID);
                    if (!physicalBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: physicalBookStoreItem.priceId,
                        productId: PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: webBookProductIds,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingPhysicalBookCartItem(true);
                    if (physicalBookStoreItem) {
                        [cartMediaItem.url] = physicalBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_WITH_PHYSICAL_BOOK,
                        icon: CartIcon,
                    });
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !userCartItems.find((item: ICartItem) => item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include physical book
                    const physicalBookStoreItem = resolveStoreItemForCart(PHYSICAL_BOOK_PREORDER_PRODUCT_ID);
                    if (!physicalBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: physicalBookStoreItem.priceId,
                        productId: PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingPhysicalBookCartItem(true);
                    if (physicalBookStoreItem) {
                        [cartMediaItem.url] = physicalBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (fetchedStoreItems) {
                    // Cart is empty
                    const physicalBookStoreItem = resolveStoreItemForCart(PHYSICAL_BOOK_PREORDER_PRODUCT_ID);
                    if (!physicalBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: physicalBookStoreItem.priceId,
                        productId: PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingPhysicalBookCartItem(true);
                    if (physicalBookStoreItem) {
                        [cartMediaItem.url] = physicalBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                }
                break;
            case BOOK_TYPE.audio:
                if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === AUDIO_BOOK_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes audio book
                    // Increment quantity and replace existing audio-book cart item
                    let cartItem = userCartItems.find((item: ICartItem) => item.productId === AUDIO_BOOK_PRODUCT_ID)!;
                    cartItem = {
                        ...cartItem,
                        quantity: cartItem.quantity + 1,
                        updated: Date.now(),
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: [AUDIO_BOOK_PRODUCT_ID],
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    const associatedStoreItem = storeItemsByProduct.get(AUDIO_BOOK_PRODUCT_ID);
                    if (associatedStoreItem) {
                        [cartMediaItem.url] = associatedStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !userCartItems.find((item: ICartItem) => item.productId === AUDIO_BOOK_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include audio book
                    const audioBookStoreItem = resolveStoreItemForCart(AUDIO_BOOK_PRODUCT_ID);
                    if (!audioBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: audioBookStoreItem.priceId,
                        productId: AUDIO_BOOK_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    if (audioBookStoreItem) {
                        [cartMediaItem.url] = audioBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (fetchedStoreItems) {
                    // Cart is empty
                    const audioBookStoreItem = resolveStoreItemForCart(AUDIO_BOOK_PRODUCT_ID);
                    if (!audioBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: audioBookStoreItem.priceId,
                        productId: AUDIO_BOOK_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    if (audioBookStoreItem) {
                        [cartMediaItem.url] = audioBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                }
                break;
            case BOOK_TYPE.digital:
                if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes digital book
                    // Increment quantity and replace existing digital cart item
                    let cartItem = userCartItems.find((item: ICartItem) => item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID)!;
                    cartItem = {
                        ...cartItem,
                        quantity: cartItem.quantity + 1,
                        updated: Date.now(),
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: [DIGITAL_BOOK_PREORDER_PRODUCT_ID],
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    const associatedStoreItem = storeItemsByProduct.get(DIGITAL_BOOK_PREORDER_PRODUCT_ID);
                    if (associatedStoreItem) {
                        [cartMediaItem.url] = associatedStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => webBookChapterAndTimePassProductIdMap.get(item.productId))
                    && !userCartItems.find((item: ICartItem) => item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes chapter/time access items
                    // Remove and replace with digital book
                    const webBookProductIds: string[] = [];
                    userCartItems.forEach((item: ICartItem) => {
                        const webBookProductId = webBookChapterAndTimePassProductIdMap.get(item.productId);
                        if (webBookProductId) webBookProductIds.push(item.productId);
                    });
                    const digitalBookStoreItem = resolveStoreItemForCart(DIGITAL_BOOK_PREORDER_PRODUCT_ID);
                    if (!digitalBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: digitalBookStoreItem.priceId,
                        productId: DIGITAL_BOOK_PREORDER_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: webBookProductIds,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    if (digitalBookStoreItem) {
                        [cartMediaItem.url] = digitalBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_WITH_DIGITAL_BOOK,
                        icon: CartIcon,
                    });
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !userCartItems.find((item: ICartItem) => item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include digital book
                    const digitalBookStoreItem = resolveStoreItemForCart(DIGITAL_BOOK_PREORDER_PRODUCT_ID);
                    if (!digitalBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: digitalBookStoreItem.priceId,
                        productId: DIGITAL_BOOK_PREORDER_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    if (digitalBookStoreItem) {
                        [cartMediaItem.url] = digitalBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (fetchedStoreItems) {
                    // Cart is empty
                    const digitalBookStoreItem = resolveStoreItemForCart(DIGITAL_BOOK_PREORDER_PRODUCT_ID);
                    if (!digitalBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: digitalBookStoreItem.priceId,
                        productId: DIGITAL_BOOK_PREORDER_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingDigitalBookCartItem(true);
                    if (digitalBookStoreItem) {
                        [cartMediaItem.url] = digitalBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                }
                break;
            case BOOK_TYPE.web:
                if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes web book
                    // Increment quantity and replace existing web-book cart item
                    let cartItem = userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_PRODUCT_ID)!;
                    cartItem = {
                        ...cartItem,
                        quantity: cartItem.quantity + 1,
                        updated: Date.now(),
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: [WEB_BOOK_PRODUCT_ID],
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    const associatedStoreItem = storeItemsByProduct.get(WEB_BOOK_PRODUCT_ID);
                    if (associatedStoreItem) {
                        [cartMediaItem.url] = associatedStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => webBookChapterAndTimePassProductIdMap.get(item.productId))
                    && !userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes web book chapters or time passes
                    // Remove and replace with web book
                    const webBookChapterAndTimePassProductIds: string[] = [];
                    userCartItems.forEach((item: ICartItem) => {
                        const webBookProductId = webBookChapterAndTimePassProductIdMap.get(item.productId);
                        if (webBookProductId) webBookChapterAndTimePassProductIds.push(item.productId);
                    });
                    const webBookStoreItem = resolveStoreItemForCart(WEB_BOOK_PRODUCT_ID);
                    if (!webBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookStoreItem.priceId,
                        productId: WEB_BOOK_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: webBookChapterAndTimePassProductIds,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookStoreItem) {
                        [cartMediaItem.url] = webBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_CHAPTERS_WITH_WEB_BOOK,
                        icon: CartIcon,
                    });
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_PRODUCT_ID)
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include web book
                    const webBookStoreItem = resolveStoreItemForCart(WEB_BOOK_PRODUCT_ID);
                    if (!webBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookStoreItem.priceId,
                        productId: WEB_BOOK_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookStoreItem) {
                        [cartMediaItem.url] = webBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (fetchedStoreItems) {
                    // Cart is empty
                    const webBookStoreItem = resolveStoreItemForCart(WEB_BOOK_PRODUCT_ID);
                    if (!webBookStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookStoreItem.priceId,
                        productId: WEB_BOOK_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookStoreItem) {
                        [cartMediaItem.url] = webBookStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                }
                break;
            default:
                // Web Book Chapters and Time Passes
                if (purchasedPhysicalBook) {
                    // Purchased physical book which includes a complimentary web book.
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PURCHASE_PHYSICAL_BOOK_ERROR,
                    });
                    break;
                } else if (purchasedDigitalBook) {
                    // Purchased digital book which includes a complimentary web book.
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PURCHASE_DIGITAL_BOOK_ERROR,
                    });
                    break;
                } else if (purchasedWebBook) {
                    // Already purchased web book
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PURCHASE_WEB_BOOK_ERROR,
                    });
                    break;
                } else if (
                    purchasedWebBookCurrentChapter
                    && (
                        type === BOOK_TYPE.webChapterOne
                        || type === BOOK_TYPE.webChapterTwo
                        || type === BOOK_TYPE.webChapterThree
                        || type === BOOK_TYPE.webChapterFour
                        || type === BOOK_TYPE.webChapterFive
                        || type === BOOK_TYPE.webChapterSix
                        || type === BOOK_TYPE.webChapterSeven
                        || type === BOOK_TYPE.webChapterEight
                        || type === BOOK_TYPE.webChapterNine
                        || type === BOOK_TYPE.webChapterTen
                        || type === BOOK_TYPE.webChapterEleven
                        || type === BOOK_TYPE.webChapterTwelve
                        || type === BOOK_TYPE.webChapterThirteen
                        || type === BOOK_TYPE.webChapterFourteen
                        || type === BOOK_TYPE.webChapterFifteen
                        || type === BOOK_TYPE.webChapterSixteen
                        || type === BOOK_TYPE.webChapterSeventeen
                        || type === BOOK_TYPE.webChapterEighteen
                        || type === BOOK_TYPE.webChapterNineteen
                        || type === BOOK_TYPE.webAppendixA
                    )
                    && currentPageChapterProductId
                    && (
                        currentPageChapterProductId === WEB_BOOK_CHAPTER_ONE_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_TWO_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_THREE_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_SIX_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_NINE_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_TEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID
                        || currentPageChapterProductId === WEB_BOOK_APPENDIX_A_PRODUCT_ID
                    )
                ) {
                    // Already purchased web book chapter
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_REPURCHASE_WEB_BOOK_CHAPTER_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === PHYSICAL_BOOK_PREORDER_PRODUCT_ID)
                ) {
                    // Physical book purchase includes a complimentary web book.
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_PHYSICAL_BOOK_ALREADY_IN_CART_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === DIGITAL_BOOK_PREORDER_PRODUCT_ID)
                ) {
                    // Digital book purchase includes a complimentary web book.
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_DIGITAL_BOOK_ALREADY_IN_CART_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_PRODUCT_ID)
                ) {
                    // Cart isn't empty
                    // Includes web book
                    // No need to include chapters or time passes as well
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_WEB_BOOK_CHAPTER_TO_CART_WHEN_WEB_BOOK_ALREADY_IN_CART_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && currentPageChapterProductId
                    && !!userCartItems.find((item: ICartItem) => item.productId === currentPageChapterProductId)
                    && (
                        type === BOOK_TYPE.webChapterOne
                        || type === BOOK_TYPE.webChapterTwo
                        || type === BOOK_TYPE.webChapterThree
                        || type === BOOK_TYPE.webChapterFour
                        || type === BOOK_TYPE.webChapterFive
                        || type === BOOK_TYPE.webChapterSix
                        || type === BOOK_TYPE.webChapterSeven
                        || type === BOOK_TYPE.webChapterEight
                        || type === BOOK_TYPE.webChapterNine
                        || type === BOOK_TYPE.webChapterTen
                        || type === BOOK_TYPE.webChapterEleven
                        || type === BOOK_TYPE.webChapterTwelve
                        || type === BOOK_TYPE.webChapterThirteen
                        || type === BOOK_TYPE.webChapterFourteen
                        || type === BOOK_TYPE.webChapterFifteen
                        || type === BOOK_TYPE.webChapterSixteen
                        || type === BOOK_TYPE.webChapterSeventeen
                        || type === BOOK_TYPE.webChapterEighteen
                        || type === BOOK_TYPE.webChapterNineteen
                        || type === BOOK_TYPE.webAppendixA
                    )
                ) {
                    // Cart isn't empty
                    // Includes web book
                    // Digital item can only be purchased once
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_MULTIPLE_WEB_BOOK_CHAPTER_TO_CART_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_DAY_PASS_PRODUCT_ID)
                    && type === BOOK_TYPE.webDayPass
                ) {
                    // Cart isn't empty
                    // Includes web book day pass
                    // Digital item can only be purchased once
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_MULTIPLE_WEB_BOOK_DAY_PASS_TO_CART_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_WEEK_PASS_PRODUCT_ID)
                    && type === BOOK_TYPE.webWeekPass
                ) {
                    // Cart isn't empty
                    // Includes web book week pass
                    // Digital item can only be purchased once
                    showUserCorrectableFailureSnackbar({
                        text: SNACKBAR_MESSAGE_ADD_MULTIPLE_WEB_BOOK_WEEK_PASS_TO_CART_ERROR,
                    });
                    break;
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !!userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_DAY_PASS_PRODUCT_ID)
                    && type === BOOK_TYPE.webWeekPass
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Includes web book day pass
                    // Remove and replace with web book week pass
                    const webBookWeekPassStoreItem = resolveStoreItemForCart(WEB_BOOK_WEEK_PASS_PRODUCT_ID);
                    if (!webBookWeekPassStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookWeekPassStoreItem.priceId,
                        productId: WEB_BOOK_WEEK_PASS_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.filterOut,
                        operationArgument: [WEB_BOOK_DAY_PASS_PRODUCT_ID],
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookWeekPassStoreItem) {
                        [cartMediaItem.url] = webBookWeekPassStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                    setSnackbarData({
                        visible: true,
                        duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_REPLACE_WEB_BOOK_DAY_PASS_WITH_WEB_BOOK_WEEK_PASS,
                        icon: CartIcon,
                    });
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_DAY_PASS_PRODUCT_ID)
                    && type === BOOK_TYPE.webDayPass
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include web book day pass
                    const webBookDayPassStoreItem = resolveStoreItemForCart(WEB_BOOK_DAY_PASS_PRODUCT_ID);
                    if (!webBookDayPassStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookDayPassStoreItem.priceId,
                        productId: WEB_BOOK_DAY_PASS_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookDayPassStoreItem) {
                        [cartMediaItem.url] = webBookDayPassStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && !userCartItems.find((item: ICartItem) => item.productId === WEB_BOOK_WEEK_PASS_PRODUCT_ID)
                    && type === BOOK_TYPE.webWeekPass
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include web book week pass
                    const webBookWeekPassStoreItem = resolveStoreItemForCart(WEB_BOOK_WEEK_PASS_PRODUCT_ID);
                    if (!webBookWeekPassStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookWeekPassStoreItem.priceId,
                        productId: WEB_BOOK_WEEK_PASS_PRODUCT_ID,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookWeekPassStoreItem) {
                        [cartMediaItem.url] = webBookWeekPassStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (
                    userCartItems
                    && userCartItems.length > 0
                    && currentPageChapterProductId
                    && !userCartItems.find((item: ICartItem) => item.productId === currentPageChapterProductId)
                    && (
                        type === BOOK_TYPE.webChapterOne
                        || type === BOOK_TYPE.webChapterTwo
                        || type === BOOK_TYPE.webChapterThree
                        || type === BOOK_TYPE.webChapterFour
                        || type === BOOK_TYPE.webChapterFive
                        || type === BOOK_TYPE.webChapterSix
                        || type === BOOK_TYPE.webChapterSeven
                        || type === BOOK_TYPE.webChapterEight
                        || type === BOOK_TYPE.webChapterNine
                        || type === BOOK_TYPE.webChapterTen
                        || type === BOOK_TYPE.webChapterEleven
                        || type === BOOK_TYPE.webChapterTwelve
                        || type === BOOK_TYPE.webChapterThirteen
                        || type === BOOK_TYPE.webChapterFourteen
                        || type === BOOK_TYPE.webChapterFifteen
                        || type === BOOK_TYPE.webChapterSixteen
                        || type === BOOK_TYPE.webChapterSeventeen
                        || type === BOOK_TYPE.webChapterEighteen
                        || type === BOOK_TYPE.webChapterNineteen
                        || type === BOOK_TYPE.webAppendixA
                    )
                    && fetchedStoreItems
                ) {
                    // Cart isn't empty
                    // Doesn't include web book current chapter
                    const webBookCurrentPageChapterStoreItem = resolveStoreItemForCart(currentPageChapterProductId);
                    if (!webBookCurrentPageChapterStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookCurrentPageChapterStoreItem.priceId,
                        productId: currentPageChapterProductId,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookCurrentPageChapterStoreItem) {
                        [cartMediaItem.url] = webBookCurrentPageChapterStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                } else if (fetchedStoreItems) {
                    // Cart is empty
                    let itemProductId: string | undefined;
                    switch (type) {
                    case BOOK_TYPE.webDayPass:
                        itemProductId = WEB_BOOK_DAY_PASS_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webWeekPass:
                        itemProductId = WEB_BOOK_WEEK_PASS_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterOne:
                        itemProductId = WEB_BOOK_CHAPTER_ONE_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterTwo:
                        itemProductId = WEB_BOOK_CHAPTER_TWO_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterThree:
                        itemProductId = WEB_BOOK_CHAPTER_THREE_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterFour:
                        itemProductId = WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterFive:
                        itemProductId = WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterSix:
                        itemProductId = WEB_BOOK_CHAPTER_SIX_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterSeven:
                        itemProductId = WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterEight:
                        itemProductId = WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterNine:
                        itemProductId = WEB_BOOK_CHAPTER_NINE_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterTen:
                        itemProductId = WEB_BOOK_CHAPTER_TEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterEleven:
                        itemProductId = WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterTwelve:
                        itemProductId = WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterThirteen:
                        itemProductId = WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterFourteen:
                        itemProductId = WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterFifteen:
                        itemProductId = WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterSixteen:
                        itemProductId = WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterSeventeen:
                        itemProductId = WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterEighteen:
                        itemProductId = WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webChapterNineteen:
                        itemProductId = WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID;
                        break;
                    case BOOK_TYPE.webAppendixA:
                        itemProductId = WEB_BOOK_APPENDIX_A_PRODUCT_ID;
                        break;
                    default:
                        // Should never hit here
                        if (currentSessionId) {
                            recordUserAction({
                                test: useStripeTestMode,
                                type: USER_ACTION_TYPE.error,
                                sessionId: currentSessionId,
                                payload: {
                                    // eslint-disable-next-line max-len
                                    error: `Unable to identify which chapter to add to cart. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                                },
                            });
                        }
                        // eslint-disable-next-line max-len
                        throw Error(`Unable to identify which chapter to add to cart. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
                    }
                    const webBookChapterOrTimePassStoreItem = resolveStoreItemForCart(itemProductId);
                    if (!webBookChapterOrTimePassStoreItem) return;
                    const cartItem: ICartItem = {
                        priceId: webBookChapterOrTimePassStoreItem.priceId,
                        productId: itemProductId,
                        timestamp: Date.now(),
                        updated: Date.now(),
                        quantity: 1,
                    };
                    const stagedCartItem = {
                        id: stagedItemId,
                        cartItem,
                        cartOperation: STAGED_CART_ITEM_CART_OPERATION.none,
                    };
                    setStagedCartItems(new Map([
                        ...Array.from(stagedCartItems.entries()),
                        [stagedCartItem.id, stagedCartItem],
                    ]));
                    setAnimatingWebBookCartItem(true);
                    if (webBookChapterOrTimePassStoreItem) {
                        [cartMediaItem.url] = webBookChapterOrTimePassStoreItem.imageURLs;
                    }
                    addAnimatingCartItem(cartMediaItem, type);
                }
            }
        }

        if (
            activeRootPost
            && currentSessionId
        ) {
            const addToCartParagraphAnchor = resolveReaderViewportParagraphAnchor({
                anchorScrollTop: Math.max(0, Math.round(postScrollTop.current))
                    + (getReaderProgressViewportHeight() * READER_PROGRESS_ANCHOR_VIEWPORT_OFFSET_RATIO),
                paragraphs: getReaderProgressParagraphAnchorCandidates(),
            });
            const addToCartPayloadDetails = {
                chapterId: currentPostContentTarget.chapterId,
                sectionId: currentPostContentTarget.sectionId,
                anchorParagraphId: addToCartParagraphAnchor?.anchorParagraphId,
                anchorParagraphIndex: addToCartParagraphAnchor?.anchorParagraphIndex,
            };
            const buildAddToCartPayload = (
                storeItemType: STORE_ITEM_TYPE,
            ): Record<string, string | number> => ({
                postId: activeRootPost.id,
                type: storeItemType,
                ...(addToCartPayloadDetails.chapterId
                    ? { chapterId: addToCartPayloadDetails.chapterId }
                    : {}),
                ...(addToCartPayloadDetails.sectionId
                    ? { sectionId: addToCartPayloadDetails.sectionId }
                    : {}),
                ...(addToCartPayloadDetails.anchorParagraphId
                    ? { anchorParagraphId: addToCartPayloadDetails.anchorParagraphId }
                    : {}),
                ...(addToCartPayloadDetails.anchorParagraphIndex != null
                    ? { anchorParagraphIndex: addToCartPayloadDetails.anchorParagraphIndex }
                    : {}),
            });
            // Record user action
            switch (type) {
            case BOOK_TYPE.physical:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.physicalBookPreorder),
                });
                break;
            case BOOK_TYPE.audio:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.audioBook),
                });
                break;
            case BOOK_TYPE.digital:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.digitalBookPreorder),
                });
                break;
            case BOOK_TYPE.web:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBook),
                });
                break;
            case BOOK_TYPE.webDayPass:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookDayPass),
                });
                break;
            case BOOK_TYPE.webWeekPass:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookWeekPass),
                });
                break;
            case BOOK_TYPE.webChapterOne:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterOne),
                });
                break;
            case BOOK_TYPE.webChapterTwo:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterTwo),
                });
                break;
            case BOOK_TYPE.webChapterThree:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterThree),
                });
                break;
            case BOOK_TYPE.webChapterFour:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterFour),
                });
                break;
            case BOOK_TYPE.webChapterFive:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterFive),
                });
                break;
            case BOOK_TYPE.webChapterSix:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterSix),
                });
                break;
            case BOOK_TYPE.webChapterSeven:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterSeven),
                });
                break;
            case BOOK_TYPE.webChapterEight:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterEight),
                });
                break;
            case BOOK_TYPE.webChapterNine:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterNine),
                });
                break;
            case BOOK_TYPE.webChapterTen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterTen),
                });
                break;
            case BOOK_TYPE.webChapterEleven:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterEleven),
                });
                break;
            case BOOK_TYPE.webChapterTwelve:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterTwelve),
                });
                break;
            case BOOK_TYPE.webChapterThirteen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterThirteen),
                });
                break;
            case BOOK_TYPE.webChapterFourteen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterFourteen),
                });
                break;
            case BOOK_TYPE.webChapterFifteen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterFifteen),
                });
                break;
            case BOOK_TYPE.webChapterSixteen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterSixteen),
                });
                break;
            case BOOK_TYPE.webChapterSeventeen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterSeventeen),
                });
                break;
            case BOOK_TYPE.webChapterEighteen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterEighteen),
                });
                break;
            case BOOK_TYPE.webChapterNineteen:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookChapterNineteen),
                });
                break;
            case BOOK_TYPE.webAppendixA:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.addItemToCart,
                    sessionId: currentSessionId,
                    payload: buildAddToCartPayload(STORE_ITEM_TYPE.webBookAppendixA),
                });
                break;
            default:
                // Should never hit here
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: `Unable to finalize adding item to cart. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        },
                    });
                }
                throw Error(`Unable to finalize adding item to cart. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`);
            }
        }
    };

    /**
     * Manages presenting sticky CTA nudge tooltip
     */
    const performStickyCtaNudge = (scenario: STICKY_CTA_NUDGE_SCENARIO): void => {
        logReaderNudge('perform', {
            scenario,
            purchasePanelExpanded,
            stickyCtaVisible: showStickyPurchaseCta,
            browserFocused: browserTabIsFocused != null,
            cachedPostFocusedDuration,
        });
        if (scenario === STICKY_CTA_NUDGE_SCENARIO.signedInHasWebAccess) {
            // This scenario uses snackbar copy instead of a collapsed-CTA tooltip.
            setStickyCtaNudge({
                visible: false,
                target: null,
                text: '',
            });
            setSnackbarData({
                visible: true,
                duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                text: STICKY_CTA_NUDGE_MESSAGE_ANNOTATE,
                icon: HighlightIcon,
                dismissable: true,
            });

            if (
                stickyCtaNudgePost
                && currentSessionId
            ) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.presentedMakeAnnotationNudgeMessage,
                    sessionId: currentSessionId,
                    payload: {
                        postId: stickyCtaNudgePost.id,
                    },
                });
            }
            setCompletedStickyCtaNudgeScenarios((prev) => ({
                ...prev,
                [scenario]: true,
            }));
            setScheduledStickyCtaNudgeScenario(null);
            return;
        }

        // Scenario determines which collapsed CTA button gets highlighted.
        let target: STICKY_CTA_NUDGE_TARGET = STICKY_CTA_NUDGE_TARGET.buy;
        let text = STICKY_CTA_NUDGE_MESSAGE_BUY_BOOK;

        switch (scenario) {
        case STICKY_CTA_NUDGE_SCENARIO.authLockedAnonymous:
            target = STICKY_CTA_NUDGE_TARGET.shareEmail;
            text = STICKY_CTA_NUDGE_MESSAGE_SHARE_EMAIL_AUTH_LOCKED;
            break;
        case STICKY_CTA_NUDGE_SCENARIO.signedInNoWebAccess:
        default:
            target = STICKY_CTA_NUDGE_TARGET.buy;
            text = STICKY_CTA_NUDGE_MESSAGE_BUY_BOOK;
            break;
        }

        setStickyCtaNudge({
            visible: true,
            target,
            text,
        });
        setCompletedStickyCtaNudgeScenarios((prev) => ({
            ...prev,
            [scenario]: true,
        }));
        setScheduledStickyCtaNudgeScenario(null);
        clearTimeoutHideStickyCtaNudge();
        timeoutHideStickyCtaNudge();

        if (tooltipEnterClip.current) {
            tooltipEnterClip.current.pause();
            tooltipEnterClip.current.currentTime = 0;
            playAudio(tooltipEnterClip.current);
        }

        if (
            stickyCtaNudgePost
            && currentSessionId
        ) {
            // Record user action
            switch (scenario) {
            case STICKY_CTA_NUDGE_SCENARIO.authLockedAnonymous:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.presentedShareEmailNudgeMessage,
                    sessionId: currentSessionId,
                    payload: {
                        postId: stickyCtaNudgePost.id,
                    },
                });
                break;
            case STICKY_CTA_NUDGE_SCENARIO.signedInNoWebAccess:
            default:
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.presentedBuyBookNudgeMessage,
                    sessionId: currentSessionId,
                    payload: {
                        postId: stickyCtaNudgePost.id,
                    },
                });
                break;
            }
        }
    };

    const clearTimeoutAnnotationNudge = (): void => {
        if (
            user
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
        ) {
            getUserUndeletedAnnotationCount({
                test: useStripeTestMode,
            }).then((count) => {
                setUserUndeletedAnnotationCount(count);
            }).catch(() => undefined);
        }
        if (
            scheduledStickyCtaNudgeScenario === STICKY_CTA_NUDGE_SCENARIO.signedInHasWebAccess
            && !completedStickyCtaNudgeScenarios.signedInHasWebAccess
        ) {
            clearTimeoutStickyCtaNudge();
        }
    };

    /**
     * Manages setting zoom level of post based on selection of character
     * @param character type of character selected
     */
    const handleSelectAdventureCharacter = (characterId: ADVENTURE_CHARACTER, icon: string): void => {
        let level: ZOOM_LEVEL;
        switch (characterId) {
        case ADVENTURE_CHARACTER.restlessMinor:
            level = ZOOM_LEVEL.one;
            break;
        case ADVENTURE_CHARACTER.busyInvestor:
            level = ZOOM_LEVEL.two;
            break;
        case ADVENTURE_CHARACTER.committedJournalist:
            level = ZOOM_LEVEL.three;
            break;
        case ADVENTURE_CHARACTER.analyticalPhilosopher:
            level = ZOOM_LEVEL.four;
            break;
        case ADVENTURE_CHARACTER.meticulousGeek:
        default:
            level = ZOOM_LEVEL.five;
        }

        setCurrentZoomLevel({
            level,
            icon,
        });

        // Update user zoom level preference
        if (user) {
            updateUser({
                test: useStripeTestMode,
                zoom: level,
            });
        }

        setCharacterModalIsVisible(false);
        setCheckedForCharacterModal(true);

        // Present tooltip to inform of capability to adjust zoom level
        clearTimeoutShowZoomTooltip();
        timeoutShowZoomTooltip();

        // Play Sound
        if (hasSound && successClip.current) {
            successClip.current.pause();
            successClip.current.currentTime = 0;
            playAudio(successClip.current);
        }

        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.selectAdventureCharacter,
                sessionId: currentSessionId,
                payload: {
                    characterId,
                },
            });
        }
    };

    /**
     * Manages pausing and resuming timeouts based on whether tab or window
     * is focused and caching the amount of time that's elapsed before the
     * pause so resuming timeouts starts from there.
     */
    const handleTabOrWindowChange = (): void => {
        logReaderNudge('focus-change', {
            browserFocused: browserTabIsFocused != null,
            cachedPostFocusedDuration,
            startOrResumeBrowsingTimestamp,
        });
        if (browserTabIsFocused != null) {
            setStartOrResumeBrowsingTimestamp(Date.now());
        } else {
            setCachedPostFocusedDuration(cachedPostFocusedDuration + (Date.now() - startOrResumeBrowsingTimestamp));
        }
    };

    /**
     * Manages recording user has read for some significant duration of time
     */
    const recordReadDuration = (duration: READ_DURATION_TYPE): void => {
        if (
            post
            && currentSessionId
        ) {
            switch (duration) {
            case READ_DURATION_TYPE.hour:
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.readOneHour,
                    sessionId: currentSessionId,
                    payload: {
                        postId: post.id,
                    },
                });
                break;
            case READ_DURATION_TYPE.thirty:
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.readThirtyMinutes,
                    sessionId: currentSessionId,
                    payload: {
                        postId: post.id,
                    },
                });
                break;
            case READ_DURATION_TYPE.ten:
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.readTenMinutes,
                    sessionId: currentSessionId,
                    payload: {
                        postId: post.id,
                    },
                });
                break;
            default:
            }
        }
    };

    /**
     * Records user action of viewing read page
     */
    const recordReadPageUserAction = (): boolean => {
        if (write) return false;
        if (shouldSkipReadModeAuthCalls()) return false;
        // Resolve the currently rendered scope directly from the hydrated read
        // tree so one logical chapter/section route records exactly one backend
        // view update.
        const renderedReadChapter = (
            activeRootPost?.chapters
            && selectedPostValuePath.length > 0
            && selectedPostValuePath[0] != null
            && selectedPostValuePath[0] >= 0
            && selectedPostValuePath[0] < activeRootPost.chapters.length
        ) ? activeRootPost.chapters[selectedPostValuePath[0]] : null;
        const renderedReadSection = (
            renderedReadChapter?.sections
            && selectedPostValuePath.length > 1
            && selectedPostValuePath[1] != null
            && selectedPostValuePath[1] >= 0
            && selectedPostValuePath[1] < renderedReadChapter.sections.length
        ) ? renderedReadChapter.sections[selectedPostValuePath[1]] : null;
        if (!currentSessionId) return false;
        // Record user action
        recordUserAction({
            test: useStripeTestMode,
            type: USER_ACTION_TYPE.viewReadPage,
            sessionId: currentSessionId,
            payload: postId ? { postId } : undefined,
        });
        if (postId) {
            const postActionId = uuidv4();
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.viewPost,
                actionId: postActionId,
                sessionId: currentSessionId,
                payload: {
                    postId,
                    ...(renderedReadChapter?.id ? { chapterId: renderedReadChapter.id } : {}),
                    ...(renderedReadSection?.id ? { sectionId: renderedReadSection.id } : {}),
                },
            });
            if (renderedReadChapter?.id) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.viewPostChapter,
                    actionId: uuidv4(),
                    sessionId: currentSessionId,
                    payload: {
                        postId,
                        chapterId: renderedReadChapter.id,
                    },
                });
            }
            if (
                renderedReadChapter?.id
                && renderedReadSection?.id
            ) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.viewPostChapterSection,
                    actionId: uuidv4(),
                    sessionId: currentSessionId,
                    payload: {
                        postId,
                        chapterId: renderedReadChapter.id,
                        sectionId: renderedReadSection.id,
                    },
                });
            }
            // Keep chapter/section page views centralized here so reloads and
            // path changes only record a single backend view update per route.
            recordPostView({
                test: useStripeTestMode,
                postId,
                chapterId: renderedReadChapter?.id || undefined,
                sectionId: renderedReadSection?.id || undefined,
                requestId: postActionId,
                retry: POST_VIEW_RECORD_RETRY,
            }).catch((error) => {
                if (isTransientMissingUserForAnnotationFetch(error)) {
                    return;
                }
                if (currentSessionId) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            error: (
                                `Read page view update failed: ${(error as Error)?.message || String(error)}`
                            ),
                            source: 'reader-read-page-view-update',
                            postId,
                        },
                    });
                }
            });
        }
        return true;
    };

    /**
     * Invoke email to haggle price of book.
     */
    const handleInitiateHaggleRequest = (): void => {
        const recipientEmail = SALES_EMAIL_ADDRESS;
        const senderEmail = userEmail || '';
        const requestId = uuidv4();
        const subject = `Negotiate Book Price: Request #${requestId}`;
        const senderName = user
            ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Reader'
            : 'Reader';
        // eslint-disable-next-line max-len
        const body = `Hi,%0D%0A%0D%0AI'm interested in purchasing the Learning the Game of Life [Insert book version], but I was wondering if we could discuss the price. Here's my offer and reasoning:%0D%0A%0D%0A[Insert offer (in USD) and reason]%0D%0A%0D%0APlease let me know if this is acceptable or if you'd like to make a counteroffer.%0D%0A%0D%0AThanks,%0D%0A${senderName}`;
        const mailtoLink = `mailto:${recipientEmail}?subject=${subject}&body=${body}${senderEmail ? `&from=${senderEmail}` : ''}`;
        const emailLink = document.createElement('a');
        emailLink.href = mailtoLink;
        emailLink.target = '_blank';
        emailLink.rel = 'noopener noreferrer';
        emailLink.click();

        const canTrackNegotiationRequest = !!currentSessionId;
        if (canTrackNegotiationRequest) {
            createSaleNegotiationRequest({
                test: useStripeTestMode,
                requestId,
                sessionId: currentSessionId,
                status: SALE_NEGOTIATION_ITEM_STATUS_TYPE.initiated,
            }).then((success) => {
                if (!success) {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: `Encountered an error initiating your request. Please reach out to support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        icon: CartIcon,
                        hasFailure: true,
                    });
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.error,
                        sessionId: currentSessionId,
                        payload: {
                            // eslint-disable-next-line max-len
                            error: `Encountered an error initiating your request. Please reach out to support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                        },
                    });
                    return;
                }
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: 'Opened your email app. Please submit an email to us explaining your situation.',
                    icon: CartIcon,
                    hasSuccess: true,
                });
            }).catch((error) => {
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: `Encountered an error initiating your request: ${error.message}. Please reach out to support at: ${SUPPORT_EMAIL_ADDRESS}.`,
                    icon: CartIcon,
                    hasFailure: true,
                });
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        // eslint-disable-next-line max-len
                        error,
                    },
                });
            });
            return;
        }

        setSnackbarData({
            visible: true,
            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
            text: `Encountered an error initiating your request. Please reach out to support at: ${SUPPORT_EMAIL_ADDRESS}.`,
            icon: CartIcon,
            hasFailure: true,
        });
    };

    const revealBuyButtons = (): void => {
        if (!baseShowStickyPurchaseCta) {
            if (stickyPurchaseForceShowResetTimeoutRef.current != null) {
                window.clearTimeout(stickyPurchaseForceShowResetTimeoutRef.current);
                stickyPurchaseForceShowResetTimeoutRef.current = null;
            }
            setForceShowStickyPurchaseCta(true);
        }
        setPurchasePanelExpanded((prev) => {
            if (
                !prev
                && hasSound
                && swooshInClip.current
            ) {
                swooshInClip.current.pause();
                swooshInClip.current.currentTime = 0;
                playAudio(swooshInClip.current);
            }
            if (
                !prev
                && currentSessionId
            ) {
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.toggleStickyCTA,
                    sessionId: currentSessionId,
                    payload: {
                        expanded: true,
                    },
                });
            }
            return true;
        });
    };

    const handleBuyButton = (): void => {
        revealBuyButtons();
    };

    /**
     * Opens the reader banner overview for readers who need a quick sense of
     * what the book is before buying or continuing.
     */
    const handleBookAboutButton = (): void => {
        setBookAboutDialogIsVisible(true);
    };

    /**
     * Closes the reader book overview dialog without letting the press leak to
     * the modal overlay or reader surface beneath it.
     */
    const handleBookAboutDialogCloseButton = useCallback((
        e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent,
    ): void => {
        e?.preventDefault();
        e?.stopPropagation();
        setBookAboutDialogIsVisible(false);
    }, []);

    useEffect(() => {
        if (hasAutoOpenedPurchasePanelRef.current) return;

        if (searchParams.get(READER_PARAMS_TYPE.openBuyOptions) !== 'true') {
            return;
        }

        hasAutoOpenedPurchasePanelRef.current = true;
        revealBuyButtons();
    }, [
        revealBuyButtons,
        searchParams,
    ]);

    const handlePostBannerSignInButton = (): void => {
        setIsUserProfileDialogExpanded(true);
    };

    const handlePaywallBuyButton = (): void => {
        if (
            currentSessionId
            && post
            && post.id
        ) {
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.pressPaywallBuyButton,
                sessionId: currentSessionId,
                payload: {
                    postId: post.id,
                },
            });
        }
        revealBuyButtons();
    };

    const handleBuyBannerButton = (): void => {
        if (
            currentSessionId
            && post
            && post.id
        ) {
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.pressBuyBannerButton,
                sessionId: currentSessionId,
                payload: {
                    postId: post.id,
                },
            });
        }
        revealBuyButtons();
    };

    const toggleStickyPurchasePanel = (): void => {
        setPurchasePanelExpanded((prev) => {
            const next = !prev;
            if (
                next
                && hasSound
                && swooshInClip.current
            ) {
                swooshInClip.current.pause();
                swooshInClip.current.currentTime = 0;
                playAudio(swooshInClip.current);
            } else if (
                !next
                && hasSound
                && swooshOutClip.current
            ) {
                swooshOutClip.current.pause();
                swooshOutClip.current.currentTime = 0;
                playAudio(swooshOutClip.current);
            }

            if (!next && !baseShowStickyPurchaseCta) {
                if (stickyPurchaseForceShowResetTimeoutRef.current != null) {
                    window.clearTimeout(stickyPurchaseForceShowResetTimeoutRef.current);
                }
                stickyPurchaseForceShowResetTimeoutRef.current = window.setTimeout(() => {
                    setForceShowStickyPurchaseCta(false);
                    stickyPurchaseForceShowResetTimeoutRef.current = null;
                }, BUTTON_EXPANSION_DURATION);
            }

            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.toggleStickyCTA,
                    sessionId: currentSessionId,
                    payload: {
                        expanded: next,
                    },
                });
            }

            return next;
        });
    };

    const togglePurchaseMoreOptions = (): void => {
        setPurchaseMoreOptionsExpanded((prev) => {
            const next = !prev;
            if (
                next
                && hasSound
                && swooshInClip.current
            ) {
                swooshInClip.current.pause();
                swooshInClip.current.currentTime = 0;
                playAudio(swooshInClip.current);
            } else if (
                !next
                && hasSound
                && swooshOutClip.current
            ) {
                swooshOutClip.current.pause();
                swooshOutClip.current.currentTime = 0;
                playAudio(swooshOutClip.current);
            }
            if (currentSessionId) {
                // Record user action
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.toggleStickyCTAMoreOptions,
                    sessionId: currentSessionId,
                    payload: {
                        expanded: next,
                    },
                });
            }
            return next;
        });
    };

    const resolveInteractionCenter = (
        event?: React.MouseEvent | React.TouchEvent,
    ): ICoord | null => {
        if (!event) return null;

        if ('touches' in event && event.touches.length > 0) {
            return {
                x: event.touches[0].clientX,
                y: event.touches[0].clientY,
            };
        }

        if ('changedTouches' in event && event.changedTouches.length > 0) {
            return {
                x: event.changedTouches[0].clientX,
                y: event.changedTouches[0].clientY,
            };
        }

        if ('clientX' in event && 'clientY' in event) {
            return {
                x: event.clientX,
                y: event.clientY,
            };
        }

        const sourceElement = event.currentTarget as HTMLElement | null;
        if (sourceElement && sourceElement.getBoundingClientRect) {
            const sourceRect = sourceElement.getBoundingClientRect();
            return {
                x: sourceRect.left + (sourceRect.width / 2),
                y: sourceRect.top + (sourceRect.height / 2),
            };
        }

        return null;
    };

    const handleCompactPurchaseSelect = (
        type?: BOOK_TYPE,
        sourceId?: string,
        event?: React.MouseEvent | React.TouchEvent,
    ): void => {
        if (!type) return;
        if (sourceId) {
            setActiveCartAnimationSourceId(sourceId);
            setActiveCartAnimationSourceCenter(resolveInteractionCenter(event));
        } else {
            setActiveCartAnimationSourceId(null);
            setActiveCartAnimationSourceCenter(null);
        }

        if (queuedCartAddRafRef.current != null) {
            window.cancelAnimationFrame(queuedCartAddRafRef.current);
            queuedCartAddRafRef.current = null;
        }
        queuedCartAddRafRef.current = window.requestAnimationFrame(() => {
            addBookToCart({ type, sourceId });
            queuedCartAddRafRef.current = null;
        });
    };

    useEffect(() => () => {
        if (queuedCartAddRafRef.current != null) {
            window.cancelAnimationFrame(queuedCartAddRafRef.current);
            queuedCartAddRafRef.current = null;
        }
    }, []);

    const resolveAuthLockAutoResumeTarget = useCallback((): {
        postId: string,
        path: number[],
        progress: IReaderChapterProgressItem,
        source: READER_PROGRESS_SOURCE,
    } | null => {
        // Only prepare an auto-resume target while still anonymous and while
        // reader-progress persistence is enabled for the current post context.
        if (
            !auth.currentUser?.isAnonymous
            || !user
            || user.saveReaderProgress === false
            || !currentReadPost?.id
        ) {
            return null;
        }
        const toTarget = (
            savedProgress: IReaderChapterProgressItem | undefined,
            source: READER_PROGRESS_SOURCE,
        ): {
            postId: string,
            path: number[],
            progress: IReaderChapterProgressItem,
            source: READER_PROGRESS_SOURCE,
        } | null => {
            // Normalize persisted progress into a concrete path we can navigate to,
            // preserving source metadata for debug attribution.
            const supportsRootReaderProgress = (currentReadPost.chapters?.length || 0) === 0;
            if (
                !isPersistedReaderProgressItem(savedProgress)
            ) {
                return null;
            }
            if (!savedProgress.chapterId) {
                return supportsRootReaderProgress
                    ? {
                        postId: currentReadPost.id,
                        path: [],
                        progress: savedProgress,
                        source,
                    }
                    : null;
            }
            const path = resolvePostPathFromScopeIds({
                post: currentReadPost,
                chapterId: savedProgress.chapterId,
                sectionId: savedProgress.sectionId,
            });
            if (path.length === 0 && savedProgress.chapterId) {
                return null;
            }
            return {
                postId: currentReadPost.id,
                path,
                progress: savedProgress,
                source,
            };
        };

        if (selectedPostValuePath.length > 0 || (currentReadPost.chapters?.length || 0) === 0) {
            // Prefer the currently selected path first so conversion does not jump away
            // from a chapter/section the reader explicitly chose. For root-only posts,
            // an empty path is the selected scope and should resolve the root key.
            const readerProgressKey = buildReaderProgressKey({
                resolvedPostId: currentReadPost.id,
                path: selectedPostValuePath,
            });
            if (readerProgressKey) {
                const userSelectedPathProgress = user.readerChapterProgress?.[readerProgressKey];
                const localSelectedPathProgress = getLocalReaderProgressByKey(readerProgressKey);
                const selectedPathTarget = isPersistedReaderProgressItem(userSelectedPathProgress)
                    ? toTarget(userSelectedPathProgress, READER_PROGRESS_SOURCE.userReaderProgress)
                    : toTarget(localSelectedPathProgress || undefined, READER_PROGRESS_SOURCE.localReaderProgress);
                if (selectedPathTarget) {
                    return selectedPathTarget;
                }
            }
        }

        // Fallback: choose the most recent progress item for this post across
        // server-backed user progress and local fallback progress.
        const allProgress = [
            ...Object.values(user.readerChapterProgress || {}).map((savedProgress) => ({
                progress: savedProgress,
                source: READER_PROGRESS_SOURCE.userReaderProgress as const,
            })),
            ...getLocalReaderProgressForPost(currentReadPost.id).map((savedProgress) => ({
                progress: savedProgress,
                source: READER_PROGRESS_SOURCE.localReaderProgress as const,
            })),
        ].filter(({ progress: savedProgress }) => (
            isPersistedReaderProgressItem(savedProgress)
            && savedProgress.postId === currentReadPost.id
            && (
                !!savedProgress.chapterId
                || (currentReadPost.chapters?.length || 0) === 0
            )
        ));
        if (allProgress.length === 0) {
            logResumeFlow(READER_RESUME_LOG_EVENT.authLockAutoTargetMissing, {
                reason: 'no-progress-for-post',
                postId: currentReadPost.id,
            });
            return null;
        }

        const mostRecentProgress = allProgress.reduce((latest, current) => (
            current.progress.timestamp > latest.progress.timestamp ? current : latest
        ));
        return toTarget(mostRecentProgress.progress, mostRecentProgress.source);
    }, [
        user,
        currentReadPost,
        selectedPostValuePath,
        buildReaderProgressKey,
        getLocalReaderProgressByKey,
        getLocalReaderProgressForPost,
        isPersistedReaderProgressItem,
        logResumeFlow,
    ]);

    const setAuthLockAutoResumePending = useCallback((pending: boolean): void => {
        // Single reset point for auth-lock auto-resume flags so each new attempt
        // starts from a clean state and stale session targets are cleared.
        clearAuthLockAutoResumeRetryTimeout();
        authLockAutoResumePendingRef.current = pending;
        authLockAutoResumeAwaitingPostRefreshRef.current = pending;
        authLockAutoResumeSawPostRefreshRef.current = false;
        authLockAutoResumeRequestedPostRefreshRef.current = false;
        authLockAutoResumeAuthCompletedRef.current = false;
        authLockAutoResumeAwaitingPathRefreshRef.current = false;
        authLockAutoResumeSawPathRefreshRef.current = false;
        if (!pending) {
            authLockAutoResumeTargetRef.current = null;
            clearAuthLockAutoResumeStoredTarget();
        }
        notifyAuthLockAutoResumeTick();
    }, [
        clearAuthLockAutoResumeStoredTarget,
        clearAuthLockAutoResumeRetryTimeout,
        notifyAuthLockAutoResumeTick,
    ]);

    const handleAuthLockedSocialSignIn = async (type: AUTHENTICATION_TYPE): Promise<void> => {
        if (signingInOut || authLockedEmailSubmitting || authLockedSignInMethod != null) return;
        // Fast scroll + immediate auth click can outrun the debounce writer. Force-sync
        // progress first so auth-lock auto-resume can always read the latest local snapshot.
        clearReaderProgressPersistTimeout();
        persistReaderProgress(true);
        const autoResumeTarget = resolveAuthLockAutoResumeTarget();
        logResumeFlow(READER_RESUME_LOG_EVENT.authLockProviderAutoResumeTarget, {
            provider: type,
            hasTarget: !!autoResumeTarget,
            targetPath: autoResumeTarget?.path,
            targetSource: autoResumeTarget?.source,
        });
        authLockAutoResumeTargetRef.current = autoResumeTarget;
        persistAuthLockAutoResumeTarget(autoResumeTarget);
        setAuthLockAutoResumePending(!!autoResumeTarget);
        setSigningInOut(true);
        setAuthLockedSignInMethod(type);

        const oldUserData: {
            user?: IPartialUserItem,
            email?: string,
            oldUserCart?: ICartItem[],
            userSessions?: string[],
            userUpdateTimestamps?: number[],
        } = {};

        const isAnonymousUser = auth.currentUser?.isAnonymous;
        if (
            user
            && isAnonymousUser
            && type !== AUTHENTICATION_TYPE.email
        ) {
            oldUserData.user = {
                ...user,
            };
            oldUserData.oldUserCart = userCartItems
                ? [...userCartItems]
                : [];
            // Provider auth must start immediately from the click path so redirect
            // mode remains browser-gesture-safe (especially Safari/iOS).
            // Optional email/session context is fetched later in non-provider flows
            // and callable migration now has server-side fallback reads.
        }

        authenticateWithProvider({
            test: useStripeTestMode,
            type,
            hasSound,
            oldUserData,
            onSuccess: () => {
                setAuthLockedSignInMethod(null);
                setSigningInOut(false);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_AUTH_LOCKED_CONTAINER_SUCCESS,
                    icon: SmileyIcon,
                    hasSuccess: true,
                });
            },
            onError: (message) => {
                setAuthLockAutoResumePending(false);
                setAuthLockedSignInMethod(null);
                setSigningInOut(false);
                setSnackbarData({
                    visible: true,
                    duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                    text: message,
                    icon: CautionIcon,
                    hasFailure: true,
                });
            },
        });
    };

    const resolveAuthLockExistingEmailFlow = async ({
        email,
        showError,
    }: {
        email: string,
        showError: (errorMessage: string) => void,
    }): Promise<void> => {
        let methods: string[] = [];
        try {
            methods = await fetchSignInMethodsForEmail(auth, email);
        } catch {
            // Fall through to backend fallback lookup below.
        }

        if (methods.length === 0) {
            try {
                methods = await getEmailSignInMethodsByEmail({
                    email,
                });
            } catch {
                // Fall through to the generic existing-account guidance below.
            }
        }

        try {
            const dedupedMethods = Array.from(new Set(methods));
            const mappedAuthTypes = Array.from(
                new Set(
                    dedupedMethods
                        .map((method) => mapSignInMethodToAuthenticationType(method))
                        .filter((type): type is AUTHENTICATION_TYPE => !!type),
                ),
            );
            const socialAuthTypes = mappedAuthTypes
                .filter((type) => AUTH_LOCK_SOCIAL_PROVIDER_TYPES.has(type));

            if (socialAuthTypes.length === 1) {
                // Allow provider handoff to pass the busy-guard in the shared handler.
                setAuthLockedEmailSubmitting(false);
                await handleAuthLockedSocialSignIn(socialAuthTypes[0]);
                return;
            }

            setIsUserProfileDialogExpanded(true);
        } catch {
            // Fall back to the generic existing-account guidance below.
        }

        showError(SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_IN_USE_ERROR);
    };

    const handleAuthLockedEmailCapture = async (): Promise<void> => {
        if (signingInOut || authLockedEmailSubmitting || authLockedSignInMethod != null) return;
        setAuthLockAutoResumePending(false);

        const showAuthLockedEmailError = (errorMessage: string): void => {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: errorMessage,
                icon: CautionIcon,
                hasFailure: true,
            });
            if (currentSessionId) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.error,
                    sessionId: currentSessionId,
                    payload: {
                        error: errorMessage,
                    },
                });
            }
        };

        const normalizedEmail = authLockedEmail.trim().toLowerCase();
        if (!validateEmail(normalizedEmail)) {
            showAuthLockedEmailError('Please enter a valid email address.');
            return;
        }

        if (!auth.currentUser?.isAnonymous) {
            showAuthLockedEmailError(SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_CONVERSION_ERROR);
            return;
        }

        setAuthLockedEmail('');
        // Same race protection as provider auth: write a synchronous local snapshot
        // before resolving the auth-lock resume target.
        clearReaderProgressPersistTimeout();
        persistReaderProgress(true);
        const autoResumeTarget = resolveAuthLockAutoResumeTarget();
        logResumeFlow(READER_RESUME_LOG_EVENT.authLockEmailAutoResumeTarget, {
            hasTarget: !!autoResumeTarget,
            targetPath: autoResumeTarget?.path,
            targetSource: autoResumeTarget?.source,
        });
        authLockAutoResumeTargetRef.current = autoResumeTarget;
        persistAuthLockAutoResumeTarget(autoResumeTarget);
        setAuthLockAutoResumePending(!!autoResumeTarget);
        setSigningInOut(true);
        let shouldResolveExistingEmailFlow = false;
        try {
            setAuthLockedEmailSubmitting(true);
            const oldUserData: {
                user?: IPartialUserItem,
                email?: string,
                oldUserCart?: ICartItem[],
                userSessions?: string[],
                userUpdateTimestamps?: number[],
            } = {};
            if (user && auth.currentUser?.isAnonymous) {
                oldUserData.user = {
                    ...user,
                };
                oldUserData.oldUserCart = userCartItems
                    ? [...userCartItems]
                    : [];
            }
            const inferredName = inferNameFromEmail(normalizedEmail);
            const resolvedName = resolvePreferredUserName({
                preferredFirstName: oldUserData.user?.firstName,
                preferredLastName: oldUserData.user?.lastName,
                fallbackFirstName: inferredName.firstName,
                fallbackLastName: inferredName.lastName,
            });
            await authenticateWithEmailConversion({
                test: useStripeTestMode,
                hasSound,
                email: normalizedEmail,
                oldUserData: {
                    user: oldUserData.user
                        ? {
                            ...oldUserData.user,
                            firstName: resolvedName.firstName,
                            lastName: resolvedName.lastName,
                        }
                        : undefined,
                    email: oldUserData.email,
                    oldUserCart: oldUserData.oldUserCart,
                    userSessions: oldUserData.userSessions,
                    userUpdateTimestamps: oldUserData.userUpdateTimestamps,
                },
                errorMessages: {
                    fallback: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_CONVERSION_ERROR,
                    emailInUse: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_IN_USE_ERROR,
                    precheckUnavailable: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_PRECHECK_ERROR,
                    invalidEmail: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_ERROR,
                    notAnonymous: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_CONVERSION_ERROR,
                    userNotFound: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_USER_NOT_FOUND_ERROR,
                    unauthenticated: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_UNAUTHENTICATED_ERROR,
                    signInFallback: SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_CONVERSION_ERROR,
                },
            });
            if (
                currentSessionId
                && post
            ) {
                recordUserAction({
                    test: useStripeTestMode,
                    type: USER_ACTION_TYPE.submitShareEmailFromAuthLock,
                    sessionId: currentSessionId,
                    payload: {
                        postId: post.id,
                    },
                });
            }
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_AUTH_LOCKED_CONTAINER_SUCCESS,
                icon: SmileyIcon,
                hasSuccess: true,
            });
        } catch (error) {
            setAuthLockAutoResumePending(false);
            const errorCode = (error as { code?: string } | undefined)?.code;
            if (errorCode && AUTH_LOCK_EXISTING_EMAIL_ERROR_CODES.has(errorCode)) {
                shouldResolveExistingEmailFlow = true;
            }
            const errorMessage = (error as { message?: string } | undefined)?.message
                || SNACKBAR_MESSAGE_AUTH_LOCKED_EMAIL_CONVERSION_ERROR;
            if (!shouldResolveExistingEmailFlow) {
                showAuthLockedEmailError(errorMessage);
            }
        } finally {
            if (!shouldResolveExistingEmailFlow) {
                setAuthLockedEmailSubmitting(false);
            }
            setSigningInOut(false);
        }
        if (shouldResolveExistingEmailFlow) {
            try {
                await resolveAuthLockExistingEmailFlow({
                    email: normalizedEmail,
                    showError: showAuthLockedEmailError,
                });
            } finally {
                setAuthLockedEmailSubmitting(false);
            }
        }
    };

    const handleAuthLockBannerVisibility = useCallback((isVisible: boolean): void => {
        if (
            !isVisible
            || !currentSessionId
            || !post
            || !post.id
        ) return;
        const authLockBannerViewActionKey = `${currentSessionId}-${post.id}`;
        if (viewedAuthLockBannerRef.current.has(authLockBannerViewActionKey)) return;
        viewedAuthLockBannerRef.current.add(authLockBannerViewActionKey);
        recordUserAction({
            test: useStripeTestMode,
            type: USER_ACTION_TYPE.viewAuthLockBanner,
            sessionId: currentSessionId,
            payload: {
                postId: post.id,
            },
        });
    }, [currentSessionId, post]);

    const handlePaywallBannerVisibility = useCallback((isVisible: boolean): void => {
        if (
            !isVisible
            || !currentSessionId
            || !post
            || !post.id
        ) return;
        const paywallBannerViewActionKey = `${currentSessionId}-${post.id}`;
        if (viewedPaywallBannerRef.current.has(paywallBannerViewActionKey)) return;
        viewedPaywallBannerRef.current.add(paywallBannerViewActionKey);
        recordUserAction({
            test: useStripeTestMode,
            type: USER_ACTION_TYPE.viewPaywallBanner,
            sessionId: currentSessionId,
            payload: {
                postId: post.id,
            },
        });
    }, [currentSessionId, post]);

    const handleBuyBannerVisibility = useCallback((isVisible: boolean): void => {
        if (
            !isVisible
            || !currentSessionId
            || !post
            || !post.id
        ) return;
        const buyBannerViewActionKey = `${currentSessionId}-${post.id}`;
        if (viewedBuyBannerRef.current.has(buyBannerViewActionKey)) return;
        viewedBuyBannerRef.current.add(buyBannerViewActionKey);
        recordUserAction({
            test: useStripeTestMode,
            type: USER_ACTION_TYPE.viewBuyBanner,
            sessionId: currentSessionId,
            payload: {
                postId: post.id,
            },
        });
    }, [currentSessionId, post]);

    function scrollToAuthLockedEmailInput(): boolean {
        const emailInput = authLockedEmailInputRef.current;
        if (!emailInput) return false;
        const authLockBanner = authLockBannerRef.current;

        // Once the scroll actually starts, the spinner can give way to motion.
        setAuthLockedStickyCtaButtonLoading(false);

        const easeInOutCubic = (t: number): number => (
            t < 0.5
                ? 4 * t * t * t
                : 1 - (((-2 * t) + 2) ** 3) / 2
        );
        const animateScroll = (
            getStart: () => number,
            setPosition: (value: number) => void,
            target: number,
        ): void => {
            const start = getStart();
            const delta = target - start;
            const startedAt = performance.now();
            const duration = Math.max(AUTH_LOCKED_EMAIL_DURATION, 1);

            const tick = (timestamp: number): void => {
                const elapsed = timestamp - startedAt;
                const progress = Math.min(elapsed / duration, 1);
                const eased = easeInOutCubic(progress);
                setPosition(start + (delta * eased));
                if (progress < 1) {
                    window.requestAnimationFrame(tick);
                    return;
                }

                // Focus after the scroll completes so keyboard users land
                // directly in the email field once the auth-lock CTA is visible.
                if (authLockedEmailInputRef.current) {
                    try {
                        authLockedEmailInputRef.current.focus({ preventScroll: true });
                    } catch (error) {
                        authLockedEmailInputRef.current.focus();
                    }
                }
                // Some browsers still nudge the viewport after focus even when
                // preventScroll is requested, so pin the final position for a
                // couple of frames to keep the full auth-lock CTA in view.
                setPosition(target);
                window.requestAnimationFrame(() => {
                    setPosition(target);
                    window.requestAnimationFrame(() => {
                        setPosition(target);
                    });
                });
                window.setTimeout(() => {
                    setPosition(target);
                }, 150);
            };

            window.requestAnimationFrame(tick);
        };

        const scrollContainer = postContentContainerRef.current;
        const shouldUseContainerScroll = !!scrollContainer
            && !useDocumentScroll
            && scrollContainer.scrollHeight > scrollContainer.clientHeight;
        if (shouldUseContainerScroll) {
            const resolvedScrollContainer = scrollContainer as HTMLDivElement;
            const targetRect = (authLockBanner || emailInput).getBoundingClientRect();
            const containerRect = resolvedScrollContainer.getBoundingClientRect();
            const bannerBottomTop = resolvedScrollContainer.scrollTop
                + (targetRect.bottom - containerRect.top)
                - resolvedScrollContainer.clientHeight;
            const inputRect = emailInput.getBoundingClientRect();
            const centeredInputTop = resolvedScrollContainer.scrollTop
                + (inputRect.top - containerRect.top)
                - ((resolvedScrollContainer.clientHeight - inputRect.height) / 2);
            // When the auth-lock banner is present, readers expect to land with
            // the whole CTA visible, not merely the email input near its top.
            const targetTop = Math.max(
                authLockBanner
                    ? bannerBottomTop
                    : centeredInputTop,
                0,
            );
            animateScroll(
                () => resolvedScrollContainer.scrollTop,
                (value: number) => { resolvedScrollContainer.scrollTop = value; },
                targetTop,
            );
        } else {
            const targetRect = (authLockBanner || emailInput).getBoundingClientRect();
            const bannerBottomTop = window.scrollY + targetRect.bottom - window.innerHeight;
            const inputRect = emailInput.getBoundingClientRect();
            const centeredInputTop = window.scrollY + inputRect.top
                - ((window.innerHeight - inputRect.height) / 2);
            const targetTop = Math.max(
                authLockBanner
                    ? bannerBottomTop
                    : centeredInputTop,
                0,
            );
            animateScroll(
                () => window.scrollY,
                (value: number) => {
                    window.scrollTo({
                        top: value,
                        behavior: 'smooth',
                    });
                },
                targetTop,
            );
        }

        return true;
    }

    function revealAuthLockedContainerFromInternalPostLink(): void {
        setPurchasePanelExpanded(false);
        if (!write && !forceFullReadOnlyRenderForResume) {
            // In progressive read mode the auth-lock UI can already exist at the
            // current truncated bottom, which would only scroll into the next
            // chunk. Force the full render first, then scroll once the auth-lock
            // target has settled into its real final position.
            setAuthLockedStickyCtaButtonLoading(true);
            pendingAuthLockedStickyCtaAfterFullRenderRef.current = true;
            pendingAuthLockedStickyCtaPaintVersionRef.current = postBodyPaintVersion;
            setForceFullReadOnlyRenderForResume(true);
            return;
        }

        if (scrollToAuthLockedEmailInput()) {
            return;
        }

        setAuthLockedStickyCtaButtonLoading(true);
    }

    const handleShareEmailFromStickyCta = (): void => {
        revealAuthLockedContainerFromInternalPostLink();
    };

    const executeQueuedPostUpdate = async (): Promise<void> => {
        if (postUpdateQueueSize > 0) {
            setRunningPostUpdateQueueTask(true);
            let shouldContinue = true;
            for (let i = 0; i < postUpdateQueueSize && shouldContinue; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                const taskSuccess = await runNextPostUpdateWithoutDequeue();
                if (!taskSuccess) {
                    shouldContinue = false;
                } else {
                    dequeuePostUpdate();
                }
            }
            setRunningPostUpdateQueueTask(false);
        }
    };

    const removeAnnotationLocally = (annotationId: string): void => {
        const updatedAnnotations = new Map([
            ...Array.from(annotations),
        ]);
        const annotationToRemove = updatedAnnotations.get(annotationId);
        if (annotationToRemove) {
            const removed = updatedAnnotations.delete(annotationId);
            if (!removed) {
                setSnackbarData({
                    visible: true,
                    duration: EXTENDED_SNACKBAR_VISIBLE_DURATION,
                    text: SNACKBAR_MESSAGE_ANNOTATION_DELETE_ERROR,
                    icon: HighlightIcon,
                    hasFailure: true,
                });
                return;
            }
            setPendingAnnotationCreationIds((prev) => {
                if (!prev.has(annotationId)) return prev;
                const updated = new Set(prev);
                updated.delete(annotationId);
                return updated;
            });
            setPendingAnnotationFallbackTops((prev) => {
                if (!prev.has(annotationId)) return prev;
                const updated = new Map(prev);
                updated.delete(annotationId);
                return updated;
            });
            setPendingAnnotationProvisionalParagraphIds((prev) => {
                if (!prev.has(annotationId)) return prev;
                const updated = new Map(prev);
                updated.delete(annotationId);
                return updated;
            });
            setQueuedAnnotationRetries((prev) => {
                if (!prev.has(annotationId)) return prev;
                const updated = new Map(prev);
                updated.delete(annotationId);
                return updated;
            });
            recentlyAckedAnnotationIdsRef.current.delete(annotationId);
            protectedAnnotationIdsRef.current.delete(annotationId);
            if (!annotationToRemove.comment) {
                decrementContentsTableAnnotationCount({
                    chapterId: annotationToRemove.chapterId,
                    sectionId: annotationToRemove.sectionId,
                });
            }
            setAnnotations(updatedAnnotations);
        }
    };

    /**
     * Determines whethr post user item addition can be executed immediately
     * or whether it must be queued because someone else is currently
     * updating the post
     */
    const addToPostUserItemsLatest = useLatest({
        updatingPostUserItems,
        postUserItems,
    });
    const addToPostUserItems = async ({
        userItems,
    }: {
        userItems: IPartialUserItem[],
    }): Promise<void> => enqueuePostUserItemUpdate(async () => {
        const cur = addToPostUserItemsLatest.current;
        if (!cur.updatingPostUserItems) setUpdatingPostUserItems(true);
        const updatedPostUserItems = new Map([
            ...Array.from(cur.postUserItems),
        ]);
        for (let i = 0; i < userItems.length; i += 1) {
            const userItem = userItems[i];
            const existingUserItem = updatedPostUserItems.get(userItem.id);
            updatedPostUserItems.set(userItem.id, {
                ...existingUserItem,
                ...userItem,
                photoURL: userItem.photoURL || existingUserItem?.photoURL,
                avatarFilePath: userItem.avatarFilePath || existingUserItem?.avatarFilePath,
            });
        }
        setPostUserItems(updatedPostUserItems);
        clearTimeoutUpdatingPostUserItems();
        timeoutUpdatingPostUserItems();
        return true;
    });
    /**
     * Determines whethr annotation audio url addition can be executed immediately
     * or whether it must be queued because someone else is currently
     * updating the post
     */
    const addToAnnotationAudioURLsLatest = useLatest({
        updatingAnnotationAudioURLs,
        annotationAudioURLs,
    });
    const addToAnnotationAudioURLs = async ({
        annotationId,
        url,
    }: {
        annotationId: string,
        url: string,
    }): Promise<void> => enqueueAnnotationAudioURLUpdate(async () => {
        const cur = addToAnnotationAudioURLsLatest.current;
        if (!cur.updatingAnnotationAudioURLs) setUpdatingAnnotationAudioURLs(true);
        const updatedAnnotationAudioURLs = new Map([
            ...Array.from(cur.annotationAudioURLs),
            [annotationId, url],
        ]);
        setAnnotationAudioURLs(updatedAnnotationAudioURLs);
        clearTimeoutUpdatingAnnotationAudioURLs();
        timeoutUpdatingAnnotationAudioURLs();
        return true;
    });

    /**
     * Determines whethr annotation audio url removal can be executed immediately
     * or whether it must be queued because someone else is currently
     * updating the post
     */
    const removeFromAnnotationAudioURLsLatest = useLatest({
        updatingAnnotationAudioURLs,
        annotationAudioURLs,
    });
    const removeFromAnnotationAudioURLs = async ({
        annotationId,
    }: {
        annotationId: string,
    }): Promise<void> => enqueueAnnotationAudioURLUpdate(async () => {
        const cur = removeFromAnnotationAudioURLsLatest.current;
        if (!cur.updatingAnnotationAudioURLs) setUpdatingAnnotationAudioURLs(true);
        const updatedAnnotationAudioURLs = new Map([
            ...Array.from(cur.annotationAudioURLs),
        ]);
        updatedAnnotationAudioURLs.delete(annotationId);
        setAnnotationAudioURLs(updatedAnnotationAudioURLs);
        clearTimeoutUpdatingAnnotationAudioURLs();
        timeoutUpdatingAnnotationAudioURLs();
        return true;
    });

    const executeQueuedPostUserItemUpdate = async (): Promise<void> => {
        if (postUserItemUpdateQueueSize > 0) {
            if (!runningPostUserItemUpdateQueueTask) {
                setRunningPostUserItemUpdateQueueTask(true);
                setClearingPostUserItemUpdateQueue(true);
            }
            let chain = Promise.resolve();
            for (let i = 0; i < postUserItemUpdateQueueSize; i += 1) {
                chain = chain
                    .then(() => runNextPostUserItemUpdateWithoutDequeue())
                    .then((taskSuccess) => {
                        if (taskSuccess) dequeuePostUserItemUpdate();
                    });
            }
            chain.then(() => {
                setRunningPostUserItemUpdateQueueTask(false);
                setClearingPostUserItemUpdateQueue(false);
            });
        }
    };

    const executeQueuedAnnotationAudioURLUpdate = async (): Promise<void> => {
        if (annotationAudioURLUpdateQueueSize > 0) {
            if (!runningAnnotationAudioURLUpdateQueueTask) {
                setRunningAnnotationAudioURLUpdateQueueTask(true);
                setClearingAnnotationAudioURLUpdateQueue(true);
            }
            let chain = Promise.resolve();
            for (let i = 0; i < annotationAudioURLUpdateQueueSize; i += 1) {
                chain = chain
                    .then(() => runNextAnnotationAudioURLUpdateWithoutDequeue())
                    .then((taskSuccess) => {
                        if (taskSuccess) dequeueAnnotationAudioURLUpdate();
                    });
            }
            chain.then(() => {
                setRunningAnnotationAudioURLUpdateQueueTask(false);
                setClearingAnnotationAudioURLUpdateQueue(false);
            });
        }
    };

    // ===== Side Effects =====

    const { quality } = useMediaQuality({ preferHighQuality: false });
    /**
     * Loads all page sound files into audio elements
     */
    /**
     * Fetches and saves store items
     */
    useEffect(() => {
        if (
            user
            && !fetchedStoreItems
            && !fetchingStoreItems
        ) {
            fetchStoreItems();
        }
    }, [
        user,
        fetchedStoreItems,
    ]);

    /**
     * Triggers rerender upon initialization of root components
     */
    useEffect(() => {
        if (!initializationRerender) {
            setInitializationRerender(true);
        }
    }, [containerRef.current, rootRef.current]);

    /**
     * Completes initial render gate as soon as required state is ready.
     * Uses timeout only as fallback if readiness stalls.
     */
    useEffect(() => {
        const readyToComplete = write
            ? (
                fetchedUserAdminStatus
                && currentZoomLevel != null
                && initializationRerender
            )
            : (
                currentZoomLevel != null
                && initializationRerender
                && (
                    post?.id != null
                    || fetchedPost
                    || refreshingPost
                )
            );

        if (pageLoadRendersCompleted) {
            clearTimeoutPageLoadRendersCompleted();
            return;
        }

        if (readyToComplete) {
            setPageLoadRendersCompleted(true);
            clearTimeoutPageLoadRendersCompleted();
            return;
        }

        timeoutPageLoadRendersCompleted();

        return () => {
            clearTimeoutPageLoadRendersCompleted();
        };
    }, [
        write,
        fetchedUserAdminStatus,
        currentZoomLevel,
        initializationRerender,
        post?.id,
        fetchedPost,
        readMetadataOnlyFetchAwaitingUser,
        refreshingPost,
        pageLoadRendersCompleted,
    ]);

    /**
     * Resets initial read layout gate on route/mode changes.
     */
    useEffect(() => {
        setInitialReadLayoutCommitted(write);
    }, [
        postId,
        write,
    ]);

    /**
     * Commits initial render once post payload is ready.
     * Read mode should not block post content on annotations/contents table readiness.
     */
    useEffect(() => {
        const readyToCommit = (
            !!post
            && !!post.id
            && pageLoadRendersCompleted
            && !refreshingPost
        );
        if (!initialReadLayoutCommitted && readyToCommit) {
            setInitialReadLayoutCommitted(true);
        }
    }, [
        initialReadLayoutCommitted,
        post,
        pageLoadRendersCompleted,
        refreshingPost,
    ]);

    useEffect(() => {
        setPostEditorInitialized(false);
    }, [
        postId,
        write,
        selectedPostValuePath,
    ]);

    useEffect(() => {
        setWriteContentsTableRendered(!write);
    }, [
        postId,
        write,
    ]);

    useEffect(() => {
        if (
            write
            && !writeContentsTableRendered
            && !!post?.id
            && pageLoadRendersCompleted
        ) {
            setWriteContentsTableRendered(true);
        }
    }, [
        write,
        writeContentsTableRendered,
        post?.id,
        pageLoadRendersCompleted,
    ]);

    /**
     * Manages revealing buy buttons if receives appropriate prop flag
     */
    useEffect(() => {
        if (!write && queueRevealBuyButtons && pageLoadRendersCompleted) {
            skipPurchasePanelResetOnPathChangeRef.current = true;
            revealBuyButtons();
            setQueueRevealBuyButtons(false);
        }
    }, [
        write,
        queueRevealBuyButtons,
        pageLoadRendersCompleted,
    ]);

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle(
            write ? 'Write' : 'Read',
            title.length > 0 ? title : DEFAULT_POST_TITLE,
        );
    }, [title]);

    /**
     * Records View Read Page User Action
     */
    useEffect(() => {
        if (
            write
            || !currentSessionId
            || recordedViewPageUserActionKey.current === currentReadPageViewRecordKey
            || (
                currentReadPageViewRecordKey != null
                && recordedReadPageViewKeys.has(currentReadPageViewRecordKey)
            )
        ) return;
        const bookChapterResolved = (
            postId !== BOOK_POST_ID
            || (
                activeRootPost?.id === BOOK_POST_ID
                && activeRootPost.chapters
                && selectedPostValuePath.length > 0
                && selectedPostValuePath[0] != null
                && selectedPostValuePath[0] >= 0
                && selectedPostValuePath[0] < activeRootPost.chapters.length
            )
        );
        if (!bookChapterResolved) return;
        if (recordReadPageUserAction()) {
            recordedViewPageUserActionKey.current = currentReadPageViewRecordKey;
            if (currentReadPageViewRecordKey != null) {
                recordedReadPageViewKeys.add(currentReadPageViewRecordKey);
            }
        }
    }, [
        write,
        currentSessionId,
        currentReadPageViewRecordKey,
        postId,
        activeRootPost?.id,
        activeRootPost?.chapters,
        selectedPostValuePath,
        signingInOut,
        user?.id,
    ]);

    /**
     * Manages navigating to specified chapter and/or section
     * and particular zoom level if present
     */
    useEffect(() => {
        // Parse search params and apply settings

        // Apply zoom level
        let zoom: number | null = null;
        if (
            searchParams.has(READER_PARAMS_TYPE.zoom)
            && Number(searchParams.get(READER_PARAMS_TYPE.zoom)) !== currentZoomLevel?.level
        ) {
            zoom = Number(searchParams.get(READER_PARAMS_TYPE.zoom));
            switch (zoom) {
            case ZOOM_LEVEL.one:
                handleZoomLevel(ZOOM_LEVEL.one);
                break;
            case ZOOM_LEVEL.two:
                handleZoomLevel(ZOOM_LEVEL.two);
                break;
            case ZOOM_LEVEL.three:
                handleZoomLevel(ZOOM_LEVEL.three);
                break;
            case ZOOM_LEVEL.four:
                handleZoomLevel(ZOOM_LEVEL.four);
                break;
            case ZOOM_LEVEL.five:
                handleZoomLevel(ZOOM_LEVEL.five);
                break;
            default:
                break;
            }
        }

        const locationState = (location.state || {}) as {
            chapterId?: string,
            sectionId?: string,
        };
        const chapterIdParam = searchParams.get(READER_PARAMS_TYPE.chapterId) || (
            params.annotationId
                && !hasReaderPageParam(searchParams)
                && !searchParams.has(READER_PARAMS_TYPE.section)
                ? locationState.chapterId
                : undefined
        );
        const sectionIdParam = searchParams.get(READER_PARAMS_TYPE.sectionId) || (
            params.annotationId
                && !hasReaderPageParam(searchParams)
                && !searchParams.has(READER_PARAMS_TYPE.section)
                ? locationState.sectionId
                : undefined
        );
        if (
            chapterIdParam
            && (
                zoom === null
                || zoom >= 4
            )
            && selectedPostValuePath.length === 0
        ) {
            const resolvedPath = resolvePostPathFromScopeIds({
                post: write ? post : currentReadPost,
                chapterId: chapterIdParam,
                sectionId: sectionIdParam || undefined,
            });
            if (resolvedPath.length === 2) {
                onChangeSelectedPostValuePath({
                    path: resolvedPath,
                    expandItems: [{
                        chapterIndex: resolvedPath[0],
                        expand: true,
                    }],
                });
                return;
            }
            if (resolvedPath.length === 1) {
                onChangeSelectedPostValuePath({ path: resolvedPath });
                return;
            }
        }

        // Apply chapter and section search parameters to URL
        if (
            hasReaderPageParam(searchParams)
            && (
                zoom === null
                || zoom >= 4
            )
            && selectedPostValuePath.length === 0
        ) {
            const chapter = Number(getReaderPageParam(searchParams));
            if (chapter) {
                if (
                    searchParams.has(READER_PARAMS_TYPE.section)
                    && (
                        zoom === null
                        || zoom === 5
                    )
                ) {
                    const section = Number(searchParams.get(READER_PARAMS_TYPE.section));
                    if (section) {
                        onChangeSelectedPostValuePath({
                            path: [chapter - 1, section - 1],
                            expandItems: [{
                                chapterIndex: chapter - 1,
                                expand: true,
                            }],
                        });
                    }
                } else {
                    onChangeSelectedPostValuePath({ path: [chapter - 1] });
                }
            }
        }
    }, [
        searchParams,
        location.state,
        post,
        currentReadPost,
        write,
        selectedPostValuePath.length,
        currentZoomLevel,
    ]);

    /**
     * Manages timers for recording significant reading durations
     */
    useEffect(() => {
        if (
            !!user
            && post
            && !initiatedReadingTimers
            && !write
        ) {
            timeoutReadTenMinutes();
            timeoutReadThirtyMinutes();
            timeoutReadOneHour();
            setInitiatedReadingTimers(true);
        }
    }, [
        user,
        post,
        initiatedReadingTimers,
    ]);
    /**
     * Returns the current signed-in Firebase auth user, if one is available.
     * We intentionally read from Firebase on demand because anon -> credential
     * linking can mutate the existing auth user in place between render and effect
     * commit. Effects that need the freshest auth state must call this helper.
     */
    const getCurrentSignedInAuthUser = useCallback(() => (
        auth.currentUser && !auth.currentUser.isAnonymous
            ? auth.currentUser
            : null
    ), [auth]);
    // Firebase can upgrade the existing currentUser object in place during anon ->
    // credential linking. Read directly each render instead of memoizing by object
    // reference so auth-lock handoff observes those in-place transitions.
    const currentSignedInAuthUser = getCurrentSignedInAuthUser();
    const hasAlignedReadAuthContext = !!(
        user
        && auth.currentUser
        && (
            user.authenticationType === AUTHENTICATION_TYPE.anonymous
                ? auth.currentUser.isAnonymous === true
                : auth.currentUser.isAnonymous !== true
        )
    );
    const readBodyWaitingForUserHydration = !!(
        !write
        && user == null
    );

    useEffect(() => {
        if (readerModeResolutionPending) {
            if (readFetchAuthFallbackElapsed) {
                setReadFetchAuthFallbackElapsed(false);
            }
        } else if (
            !write
            && user == null
            && auth.currentUser
        ) {
            if (!readFetchAuthFallbackElapsed) {
                const fallbackTimer = window.setTimeout(() => {
                    setReadFetchAuthFallbackElapsed(true);
                }, 0.6 * MILLISECONDS_IN_A_SECOND);
                return () => {
                    window.clearTimeout(fallbackTimer);
                };
            }
        } else if (readFetchAuthFallbackElapsed) {
            setReadFetchAuthFallbackElapsed(false);
        }
        return undefined;
    }, [
        write,
        user?.id,
        auth.currentUser,
        readerModeResolutionPending,
        readFetchAuthFallbackElapsed,
    ]);
    /**
     * Seeds a temporary zoom if Firebase auth is ready but user hydration lags.
     * Naked `/book` depends on a zoom level before read bootstrap can progress,
     * while explicit `?zoom=` URLs already self-seed from the route.
     */
    useEffect(() => {
        const hasExplicitZoomSearchParam = searchParams.has(READER_PARAMS_TYPE.zoom.toString());
        if (readerModeResolutionPending) {
            if (readZoomFallbackElapsed) {
                setReadZoomFallbackElapsed(false);
            }
        } else if (
            !write
            && !params.annotationId
            && !currentZoomLevel
            && !hasExplicitZoomSearchParam
            && user == null
            && auth.currentUser
        ) {
            if (!readZoomFallbackElapsed) {
                const fallbackTimer = window.setTimeout(() => {
                    setReadZoomFallbackElapsed(true);
                }, 0.6 * MILLISECONDS_IN_A_SECOND);
                return () => {
                    window.clearTimeout(fallbackTimer);
                };
            }
        } else if (readZoomFallbackElapsed) {
            setReadZoomFallbackElapsed(false);
        }
        return undefined;
    }, [
        write,
        params.annotationId,
        currentZoomLevel,
        searchParams,
        user?.id,
        auth.currentUser,
        readerModeResolutionPending,
        readZoomFallbackElapsed,
    ]);
    /**
     * Fetches post once required context is available.
     * Read mode can fetch metadata before user hydration for the book shell, but
     * waits for App's hydrated user before fetching user-scoped post content.
     * Write mode only needs resolved admin status to bootstrap the editor payload;
     * session creation can complete in parallel for later analytics and mutations.
     */
    useEffect(() => {
        const hasRequiredFetchContext = hasRequiredReaderPostFetchContext({
            write,
            hasAlignedReadAuthContext,
            fetchedUserAdminStatus,
        });
        const hasRequiredWriteContext = !write || fetchedUserAdminStatus;
        const needsInitialWriteFetch = write && post == null;
        const needsReadScopeHydration = !!(
            !write
            && fetchedPost
            && (
                (
                    selectedPostValuePath.length === 1
                    && readFetchedPostState.chapter == null
                )
                || (
                    selectedPostValuePath.length === 2
                    && readFetchedPostState.chapterSection == null
                )
            )
        );
        const hasReadMetadataFallback = (
            !write
            && readFetchAuthFallbackElapsed
            && !!auth.currentUser
            && user == null
            && !readMetadataOnlyFetchAwaitingUser
        );
        const needsReadContentAfterUserHydration = !!(
            !write
            && readMetadataOnlyFetchAwaitingUser
            && hasAlignedReadAuthContext
        );
        const hasResolvedReaderMode = !readerModeResolutionPending;
        const canStartFetch = !hasPendingSelectedPathFromRouteParams;
        if (
            hasResolvedReaderMode
            && canStartFetch
            && (
                hasRequiredFetchContext
                || hasReadMetadataFallback
            )
            && (
                (
                    !fetchedPost
                    && !refreshingPost
                ) || (
                    needsReadContentAfterUserHydration
                    && !refreshingPost
                ) || (
                    needsReadScopeHydration
                    && !refreshingPost
                ) || (
                    refreshPost
                    && !refreshingPost
                ) || (
                    needsInitialWriteFetch
                    && !refreshingPost
                )
            )
            && hasRequiredWriteContext
            && currentZoomLevel != null
        ) {
            if (!fetchedPost) setFetchedPost(true);
            handleFetchPost();
        }
    }, [
        hasPendingSelectedPathFromRouteParams,
        refreshPost,
        refreshingPost,
        readerModeResolutionPending,
        write,
        readFetchAuthFallbackElapsed,
        readMetadataOnlyFetchAwaitingUser,
        hasAlignedReadAuthContext,
        fetchedUserAdminStatus,
        auth.currentUser,
        queuedPostFetchVersion,
        readFetchedPostState.chapter,
        readFetchedPostState.chapterSection,
        selectedPostValuePath,
        currentZoomLevel,
        post,
        user?.id,
        user?.authenticationType,
    ]);
    /**
     * Sets post title and subtitle
     */
    useEffect(() => {
        if (postBannerRootPost) {
            if (postBannerRootPost.title !== title) {
                setTitle(postBannerRootPost.title);
            }

            if (postBannerRootPost.subtitle !== subtitle) {
                setSubtitle(postBannerRootPost.subtitle);
            }
        }
    }, [postBannerRootPost, title, subtitle]);

    /**
     * When in reading mode, generates contents table of post
     */
    useEffect(() => {
        if (
            !write
            && !!auth.currentUser
            && contentsTable == null
            && !refreshingContentsTable
        ) {
            refreshContentsTable();
        }
    }, [
        write,
        auth.currentUser,
        contentsTable,
        refreshingContentsTable,
    ]);

    useEffect(() => {
        if (
            !write
            && contentsTable != null
            && contentsTable.length === 0
            && characterModalIsVisible
        ) {
            setCharacterModalIsVisible(false);
        }
    }, [
        write,
        contentsTable,
        characterModalIsVisible,
    ]);

    /**
     * Determines the number of zoom levels in post
     */
    useEffect(() => {
        if (
            user
            && !write
            && postId
            && !appliedZoomLevelCount
            && !determiningPostZoomLevelCount
        ) {
            determineNumberAppliedZoomLevels();
        }
    }, [
        user,
        write,
        postId,
        appliedZoomLevelCount,
    ]);

    const postHasChapters = useMemo(() => (
        (
            !write
            && (
                (currentReadPost?.chapters?.length || 0) > 0
                || (contentsTable?.length || 0) > 0
            )
        ) || (
            write
            && post
            && post.chapters != null
        )
    ), [
        write,
        currentReadPost,
        contentsTable,
        post,
    ]);

    const firstChapterHasSections = useMemo(() => (
        (
            !write
            && (
                currentReadPost?.chapters?.[0]?.hasSections === true
                || (
                    contentsTable
                    && contentsTable.length > 0
                    && contentsTable[0]?.sections != null
                )
            )
        ) || (
            write
            && post
            && post.chapters != null
            && post.chapters.length > 0
            && post.chapters[0]?.sections != null
        )
    ), [
        write,
        currentReadPost,
        contentsTable,
        post,
    ]);

    /**
     * Sets post value path to first chapter and/or section
     * if post has chapters and/or sections and annotation hasn't been focused
     * IMPORTANT: The first chapter of the book isn't index zero because of Dedication and Acknowledgments
     */
    useEffect(() => {
        if (
            authLockAutoResumePendingRef.current
            || authLockAutoResumeTargetRef.current != null
        ) {
            return;
        }
        // Read mode auto-enters the first readable chapter/section. In write mode,
        // forcing an immediate path jump triggers a second scoped fetch right after
        // the initial admin load, which can thrash the editor boot sequence.
        const canAutoSelectPath = !write && !!auth.currentUser;
        if (
            canAutoSelectPath
            && !params.annotationId
            && postHasChapters
            && !firstChapterHasSections
            && selectedPostValuePath.length === 0
            && (
                write
                || (
                    currentZoomLevel
                    && currentZoomLevel.level >= ZOOM_LEVEL.four
                )
            )
        ) {
            onChangeSelectedPostValuePath({ path: [2] });
        } else if (
            canAutoSelectPath
            && !params.annotationId
            && postHasChapters
            && firstChapterHasSections
            && selectedPostValuePath.length === 0
            && (
                write
                || (
                    currentZoomLevel
                    && currentZoomLevel.level === ZOOM_LEVEL.five
                )
            )
        ) {
            // Refresh contents table
            // So that we can expand current chapter
            if (!write && auth.currentUser && !refreshingContentsTable) {
                refreshContentsTable();
            }
            onChangeSelectedPostValuePath({
                path: [2, 0],
                expandItems: [{
                    chapterIndex: 2,
                    expand: true,
                }],
            });
        }
    }, [
        write,
        user,
        auth.currentUser,
        postHasChapters,
        firstChapterHasSections,
        params,
        // We leave out currentZoomLevel because
        // it is present before post generally and we don't
        // want side effect to run whenever zoom level
        // changes
    ]);

    /**
     * Sets post value path when zoom level is changed
     */
    useEffect(() => {
        if (
            authLockAutoResumePendingRef.current
            || authLockAutoResumeTargetRef.current != null
        ) {
            return;
        }
        if (!currentZoomLevel) return;
        if (
            selectedPostValuePath.length === 0
            && hasReaderPathScopeSearchParams(searchParams)
        ) {
            return;
        }
        const nextPathname = `${readerBasePath}${params.annotationId ? `/${params.annotationId}` : ''}`;
        if (
            currentZoomLevel.level === ZOOM_LEVEL.five
            && selectedPostValuePath.length === 1
            && currentReadPost
            && currentReadPostChapter
            && currentReadPostChapterSection
        ) {
            // We have switched from a lower zoom level to zoom level 5
            // which is a chapter with sections.
            // We need to set the selectedPostValuePath to the first section of the chapter.
            onChangeSelectedPostValuePath({
                path: [selectedPostValuePath[0], 0],
                expandItems: [{
                    chapterIndex: selectedPostValuePath[0],
                    expand: true,
                }],
            });
        } else if (
            currentZoomLevel.level === ZOOM_LEVEL.five
            && selectedPostValuePath.length > 0
        ) {
            const newSearchParams = buildReaderScopeSearchParams({
                path: selectedPostValuePath,
                zoomLevel: currentZoomLevel.level,
                includeRootZoom: true,
                includePathParams: shouldIncludeReaderPathParamsForPath(selectedPostValuePath),
            });
            navigateReaderIfChanged(nextPathname, newSearchParams);
        } else if (
            currentZoomLevel.level === ZOOM_LEVEL.four
            && selectedPostValuePath.length > 1
            && !write
        ) {
            // We have switched from a zoom level 5 to zoom level 4
            // which is a chapter without sections.
            // We need to set the selectedPostValuePath to the chapter.
            onChangeSelectedPostValuePath({
                path: [selectedPostValuePath[0]],
            });
        } else if (
            currentZoomLevel.level === ZOOM_LEVEL.four
            && selectedPostValuePath.length <= 1
        ) {
            const newSearchParams = buildReaderScopeSearchParams({
                path: selectedPostValuePath,
                zoomLevel: currentZoomLevel.level,
                includeRootZoom: true,
                includePathParams: shouldIncludeReaderPathParamsForPath(selectedPostValuePath),
            });
            navigateReaderIfChanged(nextPathname, newSearchParams);
        } else if (selectedPostValuePath.length > 0) {
            // We have switched from a zoom level greater than three to zoom level 3 or below
            // which is a post without chapters.
            // We need to set the selectedPostValuePath to the post.
            onChangeSelectedPostValuePath({
                path: [],
            });
        } else {
            const newSearchParams = buildReaderScopeSearchParams({
                path: selectedPostValuePath,
                zoomLevel: currentZoomLevel.level,
                includeRootZoom: true,
                includePathParams: shouldIncludeReaderPathParamsForPath(selectedPostValuePath),
            });
            navigateReaderIfChanged(nextPathname, newSearchParams);
        }
    }, [
        // Set after currentZoomLevel sometimes
        // but necessary to test for first conditional
        currentReadPost,
        currentReadPostChapter,
        currentReadPostChapterSection,
        currentZoomLevel,
        buildReaderScopeSearchParams,
        navigateReaderIfChanged,
        params.annotationId,
        shouldIncludeReaderPathParamsForPath,
        readerBasePath,
    ]);

    /**
     * Sets post value path to the chapter and section of a focused annotation
     */
    useEffect(() => {
        if (
            params.annotationId
            && annotations.size > 0
            && currentReadPostChapter
            && !hasReaderPageParam(searchParams)
            && !searchParams.has(READER_PARAMS_TYPE.section)
            && !searchParams.has(READER_PARAMS_TYPE.chapterId)
            && !searchParams.has(READER_PARAMS_TYPE.sectionId)
        ) {
            const annotation = annotations.get(params.annotationId);
            if (
                annotation
                && currentZoomLevel
                && currentZoomLevel.level === ZOOM_LEVEL.five
            ) {
                const targetPath = resolvePostPathFromScopeIds({
                    post: currentReadPost,
                    chapterId: annotation.chapterId,
                    sectionId: annotation.sectionId,
                });
                if (targetPath.length === 2) {
                    onChangeSelectedPostValuePath({
                        path: targetPath,
                        expandItems: [{
                            chapterIndex: targetPath[0],
                            expand: true,
                        }],
                    });
                } else if (targetPath.length === 1) {
                    onChangeSelectedPostValuePath({ path: targetPath });
                }
            }
        }
    }, [
        params,
        annotations,
        currentZoomLevel,
        currentReadPost,
        currentReadPostChapter,
    ]);

    /**
     * Resolves typed annotation URLs that do not carry router state. Shared links
     * can arrive as `/book/:annotationId`, so fetch the annotation by id and use
     * its saved scope to select the matching chapter or section at zoom 5.
     */
    useEffect(() => {
        if (
            write
            || !params.annotationId
            || hasReaderPathScopeSearchParams(searchParams)
            || selectedPostValuePath.length > 0
            || !currentReadPost
            || currentZoomLevel?.level !== ZOOM_LEVEL.five
        ) {
            return;
        }

        const selectAnnotationDeepLinkScope = (annotation: IAnnotationItem): boolean => {
            if (!annotation.chapterId) return false;
            let targetPath = resolvePostPathFromScopeIds({
                post: currentReadPost,
                chapterId: annotation.chapterId,
                sectionId: annotation.sectionId || undefined,
            });
            if (
                targetPath.length === 0
                && contentsTable
            ) {
                const chapterIndex = contentsTable.findIndex((chapter) => chapter.id === annotation.chapterId);
                if (chapterIndex >= 0) {
                    const sectionIndex = annotation.sectionId
                        ? (contentsTable[chapterIndex].sections || []).findIndex((section) => section.id === annotation.sectionId)
                        : -1;
                    targetPath = sectionIndex >= 0
                        ? [chapterIndex, sectionIndex]
                        : [chapterIndex];
                }
            }
            if (targetPath.length === 2) {
                onChangeSelectedPostValuePath({
                    path: targetPath,
                    expandItems: [{
                        chapterIndex: targetPath[0],
                        expand: true,
                    }],
                });
                return true;
            }
            if (targetPath.length === 1) {
                onChangeSelectedPostValuePath({ path: targetPath });
                return true;
            }
            return false;
        };

        const cachedAnnotation = annotations.get(params.annotationId);
        if (
            cachedAnnotation
            && selectAnnotationDeepLinkScope(cachedAnnotation)
        ) {
            return;
        }

        const { annotationId } = params;
        getAnnotationById({
            test: useStripeTestMode,
            annotationId,
        }).then((annotation) => {
            if (latestReaderPathnameRef.current !== `${readerBasePath}/${annotationId}`) return;
            if (!annotation) return;
            selectAnnotationDeepLinkScope(annotation);
        }).catch(() => {
            // Missing/deleted annotations fall back to the regular annotation route behavior.
        });
    }, [
        write,
        params.annotationId,
        searchParams,
        selectedPostValuePath.length,
        currentReadPost,
        contentsTable,
        currentZoomLevel,
        annotations,
        useStripeTestMode,
    ]);

    /**
     * Sets post zoom level to user's preferred level if annotation not focused
     * Otherwise, set zoom level to level 5.
     */
    useEffect(() => {
        if (
            user
            && !params.annotationId
            && !currentZoomLevel
            && !write
        ) {
            readZoomInitializedFromAuthFallbackRef.current = false;
            // We only want to automatically set user zoom level when in read mode
            setCurrentZoomLevel({
                level: user.zoom || ZOOM_LEVEL.five,
                icon: adventureCharacters.get(user.zoom || ZOOM_LEVEL.five)!.icon,
            });

            // Update URL with zoom level
            let newSearchParams = searchParams;
            newSearchParams = new URLSearchParams([
                ...(Array.from(newSearchParams).filter(([key]) => key !== READER_PARAMS_TYPE.zoom.toString())),
                [READER_PARAMS_TYPE.zoom.toString(), (user.zoom || ZOOM_LEVEL.five).toString()],
            ]);
            navigate(
                `${readerBasePath}${params.annotationId ? `/${params.annotationId}` : ''}?${newSearchParams.toString()}`,
                {
                    state: location.state,
                },
            );
        } else if (
            !write
            && !params.annotationId
            && !currentZoomLevel
            && readZoomFallbackElapsed
            && auth.currentUser
        ) {
            readZoomInitializedFromAuthFallbackRef.current = true;
            setCurrentZoomLevel({
                level: ZOOM_LEVEL.five,
                icon: adventureCharacters.get(ZOOM_LEVEL.five)!.icon,
            });
        } else if (
            (
                params.annotationId
                || write
            ) && !currentZoomLevel
        ) {
            readZoomInitializedFromAuthFallbackRef.current = false;
            setCurrentZoomLevel({
                level: ZOOM_LEVEL.five,
                icon: adventureCharacters.get(ZOOM_LEVEL.five)!.icon,
            });
        }
    }, [
        user,
        params,
        readerBasePath,
        currentZoomLevel,
        write,
        readZoomFallbackElapsed,
        auth.currentUser,
        searchParams,
        location.state,
    ]);
    /**
     * Once user hydration catches up, replace any temporary auth-gap bootstrap
     * zoom with the persisted user preference.
     */
    useEffect(() => {
        if (
            write
            || params.annotationId
            || !user
            || !readZoomInitializedFromAuthFallbackRef.current
        ) {
            return;
        }

        const preferredZoomLevel = user.zoom || ZOOM_LEVEL.five;
        if (currentZoomLevel?.level === preferredZoomLevel) {
            readZoomInitializedFromAuthFallbackRef.current = false;
            return;
        }

        readZoomInitializedFromAuthFallbackRef.current = false;
        setCurrentZoomLevel({
            level: preferredZoomLevel,
            icon: adventureCharacters.get(preferredZoomLevel)!.icon,
        });
    }, [
        write,
        params.annotationId,
        user?.id,
        user?.zoom,
        currentZoomLevel?.level,
    ]);

    const postAccessMap = useMemo(() => (
        userPostAccess || new Map()
    ), [userPostAccess]);

    const hasPostAccessItem: IPostAccessItem = useMemo(() => {
        if (activeRootPost && postAccessMap != null) {
            return postAccessMap.get(activeRootPost.id);
        }

        return false;
    }, [
        activeRootPost,
        postAccessMap,
    ]);

    const hasBookAccess: IPostAccessItem = useMemo(() => {
        if (postAccessMap != null) {
            return postAccessMap.get(BOOK_POST_ID);
        }
        return false;
    }, [postAccessMap]);

    /**
     * Manages presenting Character Selector if not an anonymous user
     * and came from landing page
     */
    useEffect(() => {
        const userIsRecognized = (
            !!user
            && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
        );
        if (
            user
            && (userIsRecognized || userIsAdmin)
            && !characterModalIsVisible
            && !checkedForCharacterModal
            && !write
            && appliedZoomLevelCount !== null
            && appliedZoomLevelCount > 0
            && activeRootPost
            && isNotPaywalled
            && location.state
            && (location.state as { prevPath: string}).prevPath === `/${PAGE_ROUTE.landing}`
        ) {
            setCharacterModalIsVisible(true);
        } else if (
            !checkedForCharacterModal
            && user
            && activeRootPost
            && (
                write
                // Anonymous users never see the character modal; unblock buy-button gates.
                || user.authenticationType === AUTHENTICATION_TYPE.anonymous
                || !location.state
                || (location.state as { prevPath: string}).prevPath !== `/${PAGE_ROUTE.landing}`
                || userIsRecognized
                || (appliedZoomLevelCount && appliedZoomLevelCount === 0)
            )
        ) {
            setCheckedForCharacterModal(true);
        }
    }, [
        userIsAdmin,
        activeRootPost,
        hasPostAccessItem,
        appliedZoomLevelCount,
        location.state,
        user?.id,
        user?.authenticationType,
    ]);

    /**
     * Fetches user items of all post authors
     */
    useEffect(() => {
        if (
            postBannerRootPost?.authors
            && postBannerRootPost.authors.length > 0
            && postAuthorIds.length === 0
        ) {
            setPostAuthorIds(postBannerRootPost.authors);
        }
    }, [
        postBannerRootPost?.authors,
        postAuthorIds.length,
    ]);

    useEffect(() => {
        const hasMissingPostAuthors = postAuthorIds.some((authorId) => !postUserItems.has(authorId));
        if (
            postId != null
            && postAuthorIds.length > 0
            && hasMissingPostAuthors
            && !fetchingPostAuthors
            && !signingInOut
            && auth.currentUser != null
        ) {
            handleFetchPostAuthors();
        }
    }, [
        postId,
        postAuthorIds,
        postUserItems,
        fetchingPostAuthors,
        signingInOut,
        auth.currentUser?.uid,
    ]);

    /**
     * Fetches avater URLs of all post authors
     */
    useEffect(() => {
        if (postUserItems.size > 0) {
            const postUsers = Array.from(postUserItems.values());
            const postUserIds: string[] = [];
            const postUserPromises = [];
            for (let i = 0; i < postUsers.length; i += 1) {
                const postUser = postUsers[i];
                if (
                    postUser.avatarFilePath
                    && !postUserAvatarURLs.get(postUser.id)
                ) {
                    const storage = getStorage();
                    const pathParts = postUser.avatarFilePath.split('.');
                    const dynamicImagePath = `${pathParts[0]}_${quality}.${pathParts[1]}`;
                    postUserIds.push(postUser.id);
                    postUserPromises.push(getDownloadURL(ref(storage, dynamicImagePath)));
                }
            }

            Promise.all(postUserPromises).then((imageURLs) => {
                const updatedPostUserAvatarURLs = new Map([
                    ...Array.from(postUserAvatarURLs),
                ]);
                for (let i = 0; i < imageURLs.length; i += 1) {
                    const postUserId = postUserIds[i];
                    const imageURL = imageURLs[i];
                    updatedPostUserAvatarURLs.set(postUserId, imageURL);
                }
                setPostUserAvatarURLs(updatedPostUserAvatarURLs);
            });
        }
    }, [postUserItems]);

    /**
     * Manages fetching URL for Post Image
     */
    useEffect(() => {
        if (
            postBannerRootPost?.imagePath
            && postBannerRootPost.imagePath !== postImagePath
        ) {
            if (postImageURL) {
                // Make sure to revoke the data uris to avoid memory leaks
                // Set when we use temporary URL from blob
                URL.revokeObjectURL(postImageURL);
            }
            setPostImagePath(postBannerRootPost.imagePath);
            const storage = getStorage();
            const pathParts = postBannerRootPost.imagePath.split('.');
            const dynamicImagePath = `${pathParts[0]}_${quality}.${pathParts[1]}`;
            getDownloadURL(ref(storage, dynamicImagePath)).then((imageURL) => {
                setPostImageURL(imageURL);
            }).catch((error) => {
                // We assume cloud function has not yet generated medium image yet
                if (error.code === STORAGE_ERROR_CODE.objectNotFound) {
                    getDownloadURL(ref(storage, postBannerRootPost.imagePath)).then((imageURL) => {
                        setPostImageURL(imageURL);
                    }).catch(() => {
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: SNACKBAR_MESSAGE_FETCH_IMAGE_ERROR,
                            icon: CautionIcon,
                            hasFailure: true,
                        });
                    });
                } else {
                    setSnackbarData({
                        visible: true,
                        duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                        text: SNACKBAR_MESSAGE_FETCH_IMAGE_ERROR,
                        icon: CautionIcon,
                        hasFailure: true,
                    });
                }
            });
        }
    }, [postBannerRootPost?.imagePath, postImagePath, postImageURL, quality]);

    /**
     * Manages checking whether user has bought a book in the past
     */
    useEffect(() => {
        if (
            user
            && post
            && (
                !checkedBookPurchases
                || refreshBookPurchases
            )
            && !checkingBookPurchases
        ) {
            determineBookPurchases();
        }
    }, [
        user,
        post,
        refreshBookPurchases,
    ]);

    /**
     * Set post banner scroll threshold once post banner height is known
     */
    useEffect(() => {
        if (postBannerRef.current) {
            const measuredThreshold = postBannerRef.current.clientHeight - STICKY_POST_BANNER_HEIGHT;
            const useStableMobileThreshold = viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min;
            setStickyBannerScrollThreshold(
                useStableMobileThreshold
                    ? DEFAULT_STICKY_BANNER_THRESHOLD
                    : Math.max(measuredThreshold, 1),
            );
        }
    }, [
        postBannerRef.current,
        viewportDimensions.width,
    ]);

    useEffect(() => {
        stickyPostBannerActiveRef.current = stickyPostBannerActive;
    }, [stickyPostBannerActive]);

    useEffect(() => () => {
        Object.values(pendingPostMetadataUpdateTimeoutsRef.current).forEach((timeoutId) => {
            window.clearTimeout(timeoutId);
        });
        pendingPostMetadataUpdateTimeoutsRef.current = {};
    }, []);

    /**
     * Manages updating scroll top upon scrolling
     */
    useLayoutEffect(() => {
        const viewport = window.visualViewport;
        const handleVisualViewportChange = (): void => {
            trackPostScrollTop();
        };

        if (useDocumentScroll) {
            window.addEventListener('scroll', trackPostScrollTop, { passive: true });
            if (viewport) {
                viewport.addEventListener('resize', handleVisualViewportChange);
                viewport.addEventListener('scroll', handleVisualViewportChange);
            }
            trackPostScrollTop();
        } else if (postContentContainerRef.current) {
            postContentContainerRef.current.addEventListener('scroll', trackPostScrollTop);
        }

        return () => {
            if (scrollWillChangeTimeoutRef.current != null) {
                window.clearTimeout(scrollWillChangeTimeoutRef.current);
                scrollWillChangeTimeoutRef.current = null;
            }
            setScrollWillChange(false);
            clearReaderProgressPersistTimeout();

            if (scrollRafRef.current != null) {
                window.cancelAnimationFrame(scrollRafRef.current);
                scrollRafRef.current = null;
                pendingScrollTopRef.current = null;
            }
            if (annotationEditorScrollRafRef.current != null) {
                window.cancelAnimationFrame(annotationEditorScrollRafRef.current);
                annotationEditorScrollRafRef.current = null;
            }
            if (secondaryScrollRafRef.current != null) {
                window.cancelAnimationFrame(secondaryScrollRafRef.current);
                secondaryScrollRafRef.current = null;
                pendingSecondaryScrollTopRef.current = null;
            }
            if (stickyDeactivateTimeoutRef.current != null) {
                window.clearTimeout(stickyDeactivateTimeoutRef.current);
                stickyDeactivateTimeoutRef.current = null;
            }
            if (stickyLockRecheckTimeoutRef.current != null) {
                window.clearTimeout(stickyLockRecheckTimeoutRef.current);
                stickyLockRecheckTimeoutRef.current = null;
            }
            if (stickyPurchaseForceShowResetTimeoutRef.current != null) {
                window.clearTimeout(stickyPurchaseForceShowResetTimeoutRef.current);
                stickyPurchaseForceShowResetTimeoutRef.current = null;
            }
            lastContentsProgressRef.current = '';
            annotationHighlightRef.current = null;

            if (useDocumentScroll) {
                window.removeEventListener('scroll', trackPostScrollTop);
                if (viewport) {
                    viewport.removeEventListener('resize', handleVisualViewportChange);
                    viewport.removeEventListener('scroll', handleVisualViewportChange);
                }
            } else if (postContentContainerRef.current) {
                postContentContainerRef.current.removeEventListener('scroll', trackPostScrollTop);
            }
        };
    }, [
        postContentContainerRef.current,
        stickyBannerScrollThreshold,
        containerRef.current,
        annotationEditorRef.current,
        rootRef.current,
        contentsTableProgressBarRef.current,
        postBannerRef.current,
        postBannerLeftContainerRef.current,
        postThumbnailRef.current,
        contentsTableRef.current,
        contentsTableProgressBarContainerRef.current,
        contentsTableToggleButtonContainerRef.current,
        // must be present to update state value in method when sticky banner is active
        stickyPostBannerActive,
        selectedPostValuePath,
        useDocumentScroll,
        clearReaderProgressPersistTimeout,
    ]);

    // Unmount-only cleanup for CSS vars managed by scroll pipeline.
    useEffect(() => () => {
        document.documentElement.style.removeProperty('--reader-post-banner-height');
        postBannerLeftContainerRef.current?.style.removeProperty('--reader-post-banner-left-collapse-progress');
        postBannerLeftContainerRef.current?.style.removeProperty('--reader-post-banner-left-height');
        postBannerLeftContainerRef.current?.style.removeProperty('--reader-post-banner-left-padding');
        postThumbnailRef.current?.style.removeProperty('--reader-post-thumbnail-bottom');
        postThumbnailRef.current?.style.removeProperty('--reader-post-thumbnail-width');
        postThumbnailRef.current?.style.removeProperty('--reader-post-thumbnail-border-radius');
        postThumbnailRef.current?.style.removeProperty('--reader-post-thumbnail-height');
        contentsTableRef.current?.style.removeProperty('--reader-contents-table-top');
        contentsTableRef.current?.style.removeProperty('--reader-contents-table-height');
        contentsTableToggleButtonContainerRef.current?.style.removeProperty('--reader-contents-toggle-top');
        contentsTableProgressBarRef.current?.style.removeProperty('--reader-contents-progress-height');
    }, []);

    useEffect(() => {
        // Best-effort flush: persist position when tab hides/unloads so refresh/navigation restores accurately.
        const flushReaderProgress = (): void => {
            clearReaderProgressPersistTimeout();
            persistReaderProgress(true);
        };
        const onVisibilityChange = (): void => {
            if (document.visibilityState === 'hidden') {
                flushReaderProgress();
            }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('beforeunload', flushReaderProgress);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('beforeunload', flushReaderProgress);
            flushReaderProgress();
        };
    }, [
        clearReaderProgressPersistTimeout,
        persistReaderProgress,
    ]);

    /**
     * Remeasures post height so annotation container matches height
     */
    useEffect(() => {
        if (remeasurePostHeight) {
            setRemeasurePostHeight(false);
        }
    }, [remeasurePostHeight]);

    // Keep optimistic annotation visibility aligned with persisted user preference.
    useEffect(() => {
        if (!user) {
            clearHideAnnotationsSyncTimeout();
            clearHideAnnotationsFeedbackTimeout();
            hideAnnotationsWriteInFlightRef.current = false;
            inFlightHideAnnotationsTargetRef.current = null;
            queuedHideAnnotationsRef.current = null;
            setAnnotationsHiddenPendingSync(false);
            setAnnotationsHiddenOverride(null);
            annotationsHiddenValueRef.current = false;
            return;
        }

        if (!annotationsHiddenPendingSync) {
            return;
        }

        if (!hideAnnotationsWriteInFlightRef.current) {
            if (annotationsHiddenOverride == null || remoteAnnotationsHidden !== annotationsHiddenOverride) {
                return;
            }

            setAnnotationsHiddenPendingSync(false);
            setAnnotationsHiddenOverride(null);
            annotationsHiddenValueRef.current = remoteAnnotationsHidden;
            return;
        }

        const inFlightTarget = inFlightHideAnnotationsTargetRef.current;
        if (inFlightTarget == null || remoteAnnotationsHidden !== inFlightTarget) {
            return;
        }

        clearHideAnnotationsSyncTimeout();
        hideAnnotationsWriteInFlightRef.current = false;
        inFlightHideAnnotationsTargetRef.current = null;
        const queuedHide = queuedHideAnnotationsRef.current;
        if (queuedHide != null && queuedHide !== remoteAnnotationsHidden) {
            queuedHideAnnotationsRef.current = null;
            setAnnotationsHiddenOverride(queuedHide);
            annotationsHiddenValueRef.current = queuedHide;
            persistHideAnnotationsPreference(queuedHide);
            return;
        }

        queuedHideAnnotationsRef.current = null;
        setAnnotationsHiddenPendingSync(false);
        setAnnotationsHiddenOverride(null);
        annotationsHiddenValueRef.current = remoteAnnotationsHidden;
    }, [
        user?.id,
        remoteAnnotationsHidden,
        annotationsHiddenPendingSync,
        annotationsHiddenOverride,
        clearHideAnnotationsSyncTimeout,
        clearHideAnnotationsFeedbackTimeout,
        persistHideAnnotationsPreference,
    ]);

    useEffect(() => () => {
        clearHideAnnotationsSyncTimeout();
        clearHideAnnotationsFeedbackTimeout();
    }, [
        clearHideAnnotationsSyncTimeout,
        clearHideAnnotationsFeedbackTimeout,
    ]);

    // Keep bucket layout width aligned with split width outside active steering.
    useEffect(() => {
        if (!isSteering && annotationBucketWidth !== annotationViewWidth) {
            setAnnotationBucketWidth(annotationViewWidth);
        }
    }, [
        isSteering,
        annotationBucketWidth,
        annotationViewWidth,
    ]);

    /**
     * Split-view steering changes post text wrapping in real time.
     * We schedule a post-commit remeasure so annotation pane geometry and
     * bucket anchors are recomputed against the latest wrapped layout.
     */
    useLayoutEffect(() => {
        if (isSteering) return;
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) return;
        const rafId = window.requestAnimationFrame(() => {
            setRemeasurePostHeight(true);
        });
        return () => window.cancelAnimationFrame(rafId);
    }, [
        isSteering,
        annotationBucketWidth,
        viewportDimensions.width,
    ]);

    /**
     * Present annotation detail if small screen and annotation selected
     */
    useEffect(() => {
        if (viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min) {
            return;
        }

        if (!params.annotationId) {
            if (annotationDetailData) setAnnotationDetailData(null);
            return;
        }

        if (annotations.size === 0) {
            return;
        }

        const routeAnnotationId = params.annotationId;
        let annotation = annotations.get(routeAnnotationId);
        if (!annotation) {
            annotation = Array.from(annotations.values()).find((candidate) => (
                (candidate as IAnnotationItem & { canonicalId?: string }).canonicalId === routeAnnotationId
                || candidate.id === routeAnnotationId
            ));
        }

        if (!annotation) {
            return;
        }

        const canViewDetachedAnnotationDetail = (
            !!user
            && (
                userIsAdmin
                || postAuthorIds.includes(user.id)
            )
        );
        if (
            isDetachedAnnotation(annotation)
            && !canViewDetachedAnnotationDetail
        ) {
            if (annotationDetailData) {
                setAnnotationDetailData(null);
            }
            navigate(
                `${readerBasePath}?${searchParams.toString()}`,
                {
                    replace: true,
                    state: location.state,
                },
            );
            return;
        }

        if (
            !annotationDetailData
            || annotationDetailData.id !== annotation.id
            || annotationDetailData.published !== annotation.published
            || annotationDetailData.history.length !== annotation.history.length
        ) {
            setAnnotationDetailData(annotation);
        }
    }, [
        params.annotationId,
        annotations,
        viewportDimensions.width,
        annotationDetailData,
        user,
        userIsAdmin,
        postAuthorIds,
        navigate,
        searchParams,
        location.state,
        readerBasePath,
    ]);

    /**
     * Scroll to highlight and annotation if annotation selected
     */
    useEffect(() => {
        if (!params.annotationId) {
            routeAnnotationHighlightScrollIdRef.current = null;
            return;
        }

        if (
            !annotationHighlightsRendered
            || routeAnnotationHighlightScrollIdRef.current === params.annotationId
        ) {
            return;
        }

        const highlightElement: HTMLElement | null = document
            .querySelector(`[data-${ANNOTATION_DECORATION_PREFIX}${params.annotationId}='${params.annotationId}']`);
        if (!highlightElement) {
            return;
        }

        highlightElement.scrollIntoView({
            behavior: 'smooth',
            inline: 'nearest',
            block: 'center',
        });
        routeAnnotationHighlightScrollIdRef.current = params.annotationId;
    }, [
        params.annotationId,
        annotationHighlightsRendered,
        annotationDomRefreshVersion,
        postBodyPaintVersion,
        selectedPostValuePath.join('-'),
    ]);

    /**
     * Only touch-mobile docked editors should reveal the highlighted text at the
     * top of the reader. Desktop and non-touch layouts keep the current scroll
     * position so highlight creation does not unexpectedly jump the page.
     */
    useEffect(() => {
        if (
            !annotationEditorIsVisible
            || !postQuote
            || !useDocumentScroll
            || !shouldDockAnnotationEditorToViewport(viewportDimensions.width)
        ) {
            handledDockedAnnotationQuoteIdRef.current = null;
            stickyRevealHoldUntilRef.current = 0;
            if (dockedAnnotationRevealRafRef.current != null) {
                window.cancelAnimationFrame(dockedAnnotationRevealRafRef.current);
                dockedAnnotationRevealRafRef.current = null;
            }
            return;
        }

        if (handledDockedAnnotationQuoteIdRef.current === postQuote.id) {
            return;
        }

        let attempts = 0;
        const revealHighlight = (): void => {
            dockedAnnotationRevealRafRef.current = null;
            const highlightQueryRoot = postContentContainerRef.current || rootRef.current || document;
            const highlights = Array.from(highlightQueryRoot.querySelectorAll<HTMLElement>('[data-quote=\'post-quote\']'))
                .filter((highlight) => highlight.isConnected);
            const rect = resolveHighlightSelectionRect(
                highlights.map((highlight) => highlight.getBoundingClientRect()),
            );

            if (!rect) {
                attempts += 1;
                if (attempts < 4) {
                    dockedAnnotationRevealRafRef.current = window.requestAnimationFrame(revealHighlight);
                }
                return;
            }

            handledDockedAnnotationQuoteIdRef.current = postQuote.id;
            const currentScrollTop = getReaderProgressCurrentScrollTop();
            const scrollSurfaceTop = useDocumentScroll
                ? 0
                : postContentContainerRef.current?.getBoundingClientRect().top || 0;
            const topViewportInset = stickyPostBannerActiveRef.current && postBannerRef.current
                ? Math.max(postBannerRef.current.getBoundingClientRect().bottom, 0)
                : 0;
            const nextScrollTop = resolveDockedAnnotationEditorRevealScrollTop({
                currentScrollTop,
                highlightTop: rect.top,
                scrollSurfaceTop,
                topViewportInset,
                visualViewportOffsetTop: window.visualViewport?.offsetTop || 0,
            });

            if (Math.abs(nextScrollTop - currentScrollTop) > 1) {
                /**
                 * Programmatic highlight reveal scroll should not immediately
                 * collapse the sticky banner off-screen. Hold the current
                 * sticky state briefly so only subsequent user scrolling can
                 * decide whether the banner should exit sticky mode.
                 */
                if (stickyPostBannerActiveRef.current) {
                    stickyRevealHoldUntilRef.current = window.performance.now() + 500;
                }
                scrollReaderProgressTo(nextScrollTop, 'auto');
            }
        };

        dockedAnnotationRevealRafRef.current = window.requestAnimationFrame(revealHighlight);

        return () => {
            if (dockedAnnotationRevealRafRef.current != null) {
                window.cancelAnimationFrame(dockedAnnotationRevealRafRef.current);
                dockedAnnotationRevealRafRef.current = null;
            }
        };
    }, [
        annotationEditorIsVisible,
        postQuote,
        viewportDimensions.width,
        getReaderProgressCurrentScrollTop,
        scrollReaderProgressTo,
        useDocumentScroll,
    ]);

    /**
     * Keep the docked touch-mobile editor inside a stable fixed overlay. The
     * root stays inert while the editor owns the keyboard-safe bottom offset,
     * which avoids the old split where both ReaderView and AnnotationEditor
     * tried to react to viewport motion.
     */
    const dockedAnnotationViewportRootRef = useDockedAnnotationViewportRoot({
        enabled: shouldDockAnnotationEditorToViewport(viewportDimensions.width)
            && annotationEditorIsVisible,
    });

    /**
     * Manages triggering snackbar messages upon Dropzone events
     */
    useEffect(() => {
        const nextFeedbackKey = resolveReaderPostFileDragFeedbackKey(postFileDragFeedback);

        if (
            nextFeedbackKey != null
            && activePostFileDragFeedbackKeyRef.current === nextFeedbackKey
        ) {
            return;
        }

        if (postFileDragFeedback.hasSuccess) {
            activePostFileDragFeedbackKeyRef.current = nextFeedbackKey;
            // Show file accept
            setSnackbarData({
                visible: true,
                text: SNACKBAR_MESSAGE_FILE_ACCEPT(postFileDragFeedback.fileCount > 1),
                icon: UploadIcon,
                hasSuccess: true,
                suppressReplay: true,
            });
        } else if (postFileDragFeedback.hasFailure) {
            activePostFileDragFeedbackKeyRef.current = nextFeedbackKey;
            // Show file reject
            setSnackbarData({
                visible: true,
                text: SNACKBAR_MESSAGE_FILE_REJECT(postFileDragFeedback.fileCount > 1),
                icon: CrossIcon,
                hasFailure: true,
                suppressReplay: true,
            });
        } else {
            if (activePostFileDragFeedbackKeyRef.current == null) {
                return;
            }

            activePostFileDragFeedbackKeyRef.current = null;
            // Hide snackbar
            setSnackbarData({
                visible: false,
                text: '',
            });
        }
    }, [
        postFileDragFeedback,
    ]);

    useEffect(() => {
        if (!write) {
            return undefined;
        }

        const clearPostFileDragFeedback = (): void => {
            clearPostFileDragMetadata();
        };
        const clearPostFileDragFeedbackOnWindowLeave = (event: DragEvent): void => {
            if (event.relatedTarget) {
                return;
            }

            clearPostFileDragMetadata();
        };

        window.addEventListener('drop', clearPostFileDragFeedback, true);
        window.addEventListener('dragend', clearPostFileDragFeedback, true);
        window.addEventListener('dragleave', clearPostFileDragFeedbackOnWindowLeave, true);

        return () => {
            window.removeEventListener('drop', clearPostFileDragFeedback, true);
            window.removeEventListener('dragend', clearPostFileDragFeedback, true);
            window.removeEventListener('dragleave', clearPostFileDragFeedbackOnWindowLeave, true);
        };
    }, [
        write,
        clearPostFileDragMetadata,
    ]);

    /**
     * Processes progress updates for uploading post thumbnail
     */
    useEffect(() => {
        if (uploadingThumbnailStateChangeEvent) {
            handleUploadingPostThumbnailStateChange(
                uploadingThumbnailStateChangeEvent.mediaItem,
                uploadingThumbnailStateChangeEvent.snapshot,
                uploadingThumbnailStateChangeEvent.progress,
            );
        }
    }, [uploadingThumbnailStateChangeEvent]);

    /**
     * Processes completion of uploading post thumbnail
     */
    useEffect(() => {
        if (uploadingThumbnailCompleteEvent) {
            handleUploadingPostThumbnailComplete();
        }
    }, [uploadingThumbnailCompleteEvent]);

    useEffect(() => {
        if (skipPurchasePanelResetOnPathChangeRef.current) {
            skipPurchasePanelResetOnPathChangeRef.current = false;
            return;
        }
        setPurchasePanelExpanded(false);
        setPurchaseMoreOptionsExpanded(false);
    }, [selectedPostValuePath]);

    /**
     * Triggers a method that hands tab or window changes
     */
    useEffect(() => {
        handleTabOrWindowChange();
    }, [browserTabIsFocused]);

    /**
     * Empties queue of updates to post
     */
    useEffect(() => {
        if (
            postUpdateQueueSize > 0
            && !updatingPost
            && !runningPostUpdateQueueTask
        ) {
            executeQueuedPostUpdate();
        }
    }, [
        postUpdateQueueSize,
        updatingPost,
        runningPostUpdateQueueTask,
    ]);

    /**
     * Empties queue of updates to post user items
     */
    useEffect(() => {
        if (
            // not currently updating post user items
            !updatingPostUserItems
            // we have a non-empty number of post user item updates that haven't been executed
            && postUserItemUpdateQueueSize > 0
            // we aren't currently executing a post user item update
            && !runningPostUserItemUpdateQueueTask
        ) {
            executeQueuedPostUserItemUpdate();
        }
    }, [
        updatingPostUserItems,
        postUserItemUpdateQueueSize,
        runningPostUserItemUpdateQueueTask,
    ]);

    /**
     * Empties queue of updates to annotation audio urls
     */
    useEffect(() => {
        if (
            // not currently updating annotation audio urls
            !updatingAnnotationAudioURLs
            // we have a non-empty number of annotation audio urls updates that haven't been executed
            && annotationAudioURLUpdateQueueSize > 0
            // we aren't currently executing an annotation audio urls update
            && !runningAnnotationAudioURLUpdateQueueTask
        ) {
            executeQueuedAnnotationAudioURLUpdate();
        }
    }, [
        updatingAnnotationAudioURLs,
        annotationAudioURLUpdateQueueSize,
        runningAnnotationAudioURLUpdateQueueTask,
    ]);

    /**
     * Manages setting flag indicating page load renders have completed
     */
    const {
        start: timeoutPageLoadRendersCompleted,
        clear: clearTimeoutPageLoadRendersCompleted,
    } = useTimeout(() => {
        if (!pageLoadRendersCompleted) setPageLoadRendersCompleted(true);
    }, PAGE_LOAD__RENDERS_COMPLETED_TIMEOUT_DURATION);

    /**
     * Manages showing zoom tooltip for first-time users
     */
    const {
        start: timeoutShowZoomTooltip,
        clear: clearTimeoutShowZoomTooltip,
    } = useTimeout(() => {
        setShowZoomTooltip(true);
        // Reveal zooms
        if (zoomLevelButtonRef.current) {
            zoomLevelButtonRef.current.click();
        }
        clearTimeoutHideZoomTooltip();
        timeoutHideZoomTooltip();
        // Play Sound
        if (tooltipEnterClip.current) {
            tooltipEnterClip.current.pause();
            tooltipEnterClip.current.currentTime = 0;
            playAudio(tooltipEnterClip.current);
        }
    }, SHOW_ZOOM_TOOLTIP_DURATION);

    /**
     * Manages hiding zoom tooltip for first-time users
     */
    const {
        start: timeoutHideZoomTooltip,
        clear: clearTimeoutHideZoomTooltip,
    } = useTimeout(() => {
        setShowZoomTooltip(false);
        setPresentedZoomTooltip(true);
    }, HIDE_ZOOM_TOOLTIP_DURATION);

    /**
     * Manages recording user has read for ten minutes
     */
    const {
        start: timeoutReadTenMinutes,
    } = useTimeout(() => {
        recordReadDuration(READ_DURATION_TYPE.ten);
    }, browserTabIsFocused != null ? TEN_MINUTES_DURATION - cachedPostFocusedDuration : null);

    /**
     * Manages recording user has read for ten minutes
     */
    const {
        start: timeoutReadThirtyMinutes,
    } = useTimeout(() => {
        recordReadDuration(READ_DURATION_TYPE.thirty);
    }, browserTabIsFocused != null ? THIRTY_MINUTES_DURATION - cachedPostFocusedDuration : null);

    /**
     * Manages recording user has read for ten minutes
     */
    const {
        start: timeoutReadOneHour,
    } = useTimeout(() => {
        recordReadDuration(READ_DURATION_TYPE.hour);
    }, browserTabIsFocused != null ? HOUR_DURATION - cachedPostFocusedDuration : null);

    /**
     * Manages waiting some time for batch updates to post user items
     * to transpire before triggering rerender
     */
    const {
        start: timeoutUpdatingPostUserItems,
        clear: clearTimeoutUpdatingPostUserItems,
    } = useTimeout(() => {
        setUpdatingPostUserItems(false);
    }, UPDATING_POST_USER_ITEMS_TIMEOUT_DURATION);

    /**
     * Manages waiting some time for batch updates to annotation audio urls
     * to transpire before triggering rerender
     */
    const {
        start: timeoutUpdatingAnnotationAudioURLs,
        clear: clearTimeoutUpdatingAnnotationAudioURLs,
    } = useTimeout(() => {
        setUpdatingAnnotationAudioURLs(false);
    }, UPDATING_ANNOTATION_AUDIO_URLS_TIMEOUT_DURATION);

    // ===== Memoization =====

    const resizeableMaxConstraintsWidth = useMemo(() => (
        viewportDimensions.width - MIN_POST_SECTION_WIDTH
    ), [
        viewportDimensions,
    ]);

    const showDetailBanner = useMemo(() => (
        !!annotationIsFocused && !!annotationDetailData
    ), [
        annotationIsFocused,
        annotationDetailData,
    ]);

    const titlePlaceholderWidth = useMemo(() => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.small.min) {
            return POST_TITLE_PLACEHOLDER_WIDTH_EXTRA_SMALL;
        }

        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) {
            return POST_TITLE_PLACEHOLDER_WIDTH_SMALL;
        }

        if (viewportDimensions.width < MEDIA_QUERY_SIZE.large.min) {
            return POST_TITLE_PLACEHOLDER_WIDTH_MEDIUM;
        }

        return POST_TITLE_PLACEHOLDER_WIDTH_LARGE;
    }, [viewportDimensions.width]);

    const subtitlePlaceholderWidth = useMemo(() => {
        if (viewportDimensions.width < MEDIA_QUERY_SIZE.small.min) {
            return POST_SUBTITLE_PLACEHOLDER_WIDTH_EXTRA_SMALL;
        }

        if (viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min) {
            return POST_SUBTITLE_PLACEHOLDER_WIDTH_SMALL;
        }

        if (viewportDimensions.width < MEDIA_QUERY_SIZE.large.min) {
            return POST_SUBTITLE_PLACEHOLDER_WIDTH_MEDIUM;
        }

        return POST_SUBTITLE_PLACEHOLDER_WIDTH_LARGE;
    }, [viewportDimensions.width]);

    const informZoomAdjustCapability = useMemo(() => (
        showZoomTooltip
        && !presentedZoomTooltip
    ), [
        showZoomTooltip,
        presentedZoomTooltip,
    ]);

    const postLockedUntil = useMemo(() => {
        if (!currentReadPost) { return undefined; }

        if (
            selectedPostValuePath.length === 0
            && currentReadPost.lockedUntil != null
        ) {
            // post has no chapters or sections
            // is also locked
            return currentReadPost.lockedUntil;
        }

        if (
            selectedPostValuePath.length === 1
            && currentReadPostChapter
            && currentReadPostChapter.lockedUntil != null
        ) {
            // post has chapters but no sections
            // is also locked
            return currentReadPostChapter.lockedUntil;
        }

        if (
            selectedPostValuePath.length === 2
            && currentReadPostChapter
            && currentReadPostChapterSection
            && currentReadPostChapter.lockedUntil != null
        ) {
            // post has chapters and sections
            // is also locked
            // but lock is at chapter-level
            return currentReadPostChapter.lockedUntil;
        }

        if (
            selectedPostValuePath.length === 2
            && currentReadPostChapter
            && currentReadPostChapterSection
            && currentReadPostChapterSection.lockedUntil != null
        ) {
            // post has chapters and sections
            // is also locked
            // but lock is at section-level
            return currentReadPostChapterSection.lockedUntil;
        }

        return undefined;
    }, [
        currentReadPost,
        currentReadPostChapter,
        currentReadPostChapterSection,
        selectedPostValuePath,
    ]);

    const postContentAdjustMarginTop = useMemo(() => {
        if (
            pageLoadRendersCompleted
            && postLockedUntil != null
            && postLockedUntil > Date.now()
            && !userIsAdmin
        ) {
            return LOCKED_CONTAINER_MARGIN;
        }

        if (stickyPostBannerActive) {
            return POST_CONTENT_MARGIN_TOP_WRITE + stickyBannerScrollThreshold + STICKY_POST_BANNER_HEIGHT;
        }

        return POST_CONTENT_MARGIN_TOP_WRITE;
    }, [
        write,
        userIsAdmin,
        pageLoadRendersCompleted,
        postLockedUntil,
        stickyPostBannerActive,
        stickyBannerScrollThreshold,
    ]);

    const postPlaceholderTargetHeight = Math.max(
        viewportDimensions.height - (POST_BANNER_HEIGHT + postContentAdjustMarginTop),
        0,
    );
    const postPlaceholderBoxCount = Math.max(
        POST_PLACEHOLDER_MIN_BOX_COUNT,
        Math.ceil(postPlaceholderTargetHeight / (BODY_FONT_SIZE + POST_PLACEHOLDER_BOX_VERTICAL_MARGIN)),
    );

    const postContentLeftContainerWidth = useMemo(() => {
        if (
            contentsTableVisible
            && !!contentsTable
            && contentsTable.length > 0
            && viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min
        ) {
            return POST_BANNER_LEFT_CONTAINER_WIDTH;
        }

        if (
            postLockedUntil != null
            && postLockedUntil > Date.now() && !userIsAdmin
        ) {
            return ANNOTATION_VIEW_SMALL_VIEWPORT_WIDTH;
        }

        return CONTENTS_TABLE_WIDTH_CONTRACTED + (2 * CONTENTS_TABLE_MARGIN);
    }, [
        userIsAdmin,
        postLockedUntil,
        contentsTableVisible,
        contentsTable,
        viewportDimensions.width,
    ]);

    const hasRenderableAnnotationsForLayout = useMemo(() => {
        if (annotations.size === 0) return false;

        const hasParagraphAnchor = Array.from(annotations.values()).some((annotation) => {
            if (annotation.comment) return false;
            const highlightElement: HTMLElement | null = document
                .querySelector(`[data-${ANNOTATION_DECORATION_PREFIX}${annotation.id}='${annotation.id}']`);
            if (!highlightElement) return false;
            const paragraph = findParentNodeWithClass(
                SLATE_PARAGRAPH_CLASSNAME,
                highlightElement,
                10,
            );
            return !!paragraph?.dataset.paragraphId;
        });
        if (hasParagraphAnchor) return true;

        // Once an annotation has fallback/provisional placement metadata, keep
        // treating it as renderable until real paragraph lookup catches up.
        // Otherwise the canonical annotation can disappear as soon as the short
        // ack-protection window expires, then reappear only after a later rerender.
        return Array.from(annotations.values()).some((annotation) => (
            !annotation.comment
            && (
                pendingAnnotationFallbackTops.has(annotation.id)
                || pendingAnnotationProvisionalParagraphIds.has(annotation.id)
            )
        ));
    }, [
        annotations,
        pendingAnnotationFallbackTops,
        pendingAnnotationProvisionalParagraphIds,
        pendingAnnotationCreationIds,
        annotationHighlightsRendered,
        annotationEngagementItems,
    ]);

    const postContentRightContainerWidth = useMemo(() => {
        if (write) return 0;
        const shouldShowAnnotationLayout = !annotationsHidden
            && postBodyPainted
            && (
                annotations.size > 0
                || hasRenderableAnnotationsForLayout
            );
        if (annotationsHidden) {
            // Preserve a small right gutter in read mode so content does not visually hug the viewport edge.
            if (viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min) {
                return POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER;
            }
            return POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER_SMALL;
        }
        if (!initialReadLayoutCommitted && !shouldShowAnnotationLayout) {
            if (viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min) {
                return POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER;
            }

            return POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER_SMALL;
        }
        if (!shouldShowAnnotationLayout) {
            if (viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min) {
                return POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER;
            }
            return POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER_SMALL;
        }
        if (viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min) {
            return annotationViewWidth;
        }

        return ANNOTATION_VIEW_SMALL_VIEWPORT_WIDTH;
    }, [
        annotationViewWidth,
        viewportDimensions.width,
        annotationsHidden,
        postBodyPainted,
        annotations.size,
        hasRenderableAnnotationsForLayout,
        initialReadLayoutCommitted,
    ]);

    // CSS blends these widths while scrolling so entering sticky mode does not
    // relocate the cover in one step when the contents panel is closed.
    const postBannerLeftContainerWidthContracted = useMemo(() => {
        if (contentsTableVisible) return POST_BANNER_LEFT_CONTAINER_WIDTH;

        const textWidth = Math.min(POST_EDITOR_MAX_WIDTH, viewportDimensions.width - postContentRightContainerWidth);
        const textMarginWidth = ((viewportDimensions.width - postContentRightContainerWidth) - textWidth) / 2;
        const contractedWidth = PAGE_LOGO_WIDTH + textMarginWidth;
        if (!write && !postImageURL) return contractedWidth;

        // Leave room for both the left padding and the contracted cover.
        const minimumCoverWidth = POST_CONTENT_LEFT_CONTAINER_PADDING_LEFT
            + POST_THUMBNAIL_WIDTH_CONTRACTED + POST_BANNER_LEFT_CONTAINER_PADDING_CONTRACTED;
        return Math.max(contractedWidth, minimumCoverWidth);
    }, [
        contentsTableVisible,
        viewportDimensions.width,
        postContentRightContainerWidth,
        write,
        postImageURL,
    ]);
    const postBannerLeftContainerWidth = postScrollTop.current > stickyBannerScrollThreshold
        ? postBannerLeftContainerWidthContracted
        : POST_BANNER_LEFT_CONTAINER_WIDTH;

    const lockedContainerWidth = useMemo(() => (
        viewportDimensions.width - postContentLeftContainerWidth - postContentRightContainerWidth
    ), [
        viewportDimensions.width,
        postContentLeftContainerWidth,
        postContentRightContainerWidth,
    ]);

    const lockedContainerHeight = useMemo(() => (
        Math.max(
            12.5 * BODY_FONT_SIZE,
            (viewportDimensions.height - POST_BANNER_HEIGHT) - 2 * LOCKED_CONTAINER_MARGIN,
        )
    ), [
        viewportDimensions.height,
    ]);

    const userIsSignedIn = useMemo(() => !!(
        user
        && user.authenticationType !== AUTHENTICATION_TYPE.anonymous
    ), [user]);
    /**
     * Distinguishes reader payloads fetched for a prior authenticated user from
     * payloads fetched for the current anonymous/signed-out session.
     */
    const getCurrentReadAuthSessionKey = (): string => {
        if (!auth.currentUser) {
            return 'signed-out';
        }
        if (auth.currentUser.isAnonymous) {
            return `anonymous:${auth.currentUser.uid}`;
        }

        return `authenticated:${auth.currentUser.uid}`;
    };
    const currentReadAuthSessionKey = getCurrentReadAuthSessionKey();
    const showPostBannerSignInButton = useMemo(() => !!(
        user
        && user.authenticationType === AUTHENTICATION_TYPE.anonymous
    ), [user]);
    useEffect(() => {
        if (signingInOut && !previousSigningInOutRef.current) {
            if (
                userIsSignedIn
                && !write
            ) {
                // Invalidate any authenticated read response already in flight and
                // immediately scrub cached full values so sign-out cannot flash
                // stale gated content back into the reader.
                readPostFetchCycleRef.current += 1;
                setReadFetchedPostAuthSessionKey(null);
                setPostEditorInitialized(false);
                setPostEditorReadOnlyChunkingIdle(false);
                setPostEditorReadOnlyFullRenderReady(false);
                setShowPostBodyContentSettled(false);
                setReadFetchedPostState((prev) => downgradeFetchedReadPostForSignOut({
                    post: prev.post || null,
                    chapter: prev.chapter || undefined,
                    chapterSection: prev.chapterSection || undefined,
                }));
            }
            if (signingInOutStartedFromAuthenticated !== userIsSignedIn) {
                setSigningInOutStartedFromAuthenticated(userIsSignedIn);
            }
        } else if (
            !signingInOut
            && signingInOutStartedFromAuthenticated
        ) {
            if (
                readFetchedPostAuthSessionKey != null
                && readFetchedPostAuthSessionKey === currentReadAuthSessionKey
            ) {
                setSigningInOutStartedFromAuthenticated(false);
            }
        }

        previousSigningInOutRef.current = signingInOut;
    }, [
        signingInOut,
        userIsSignedIn,
        write,
        currentReadAuthSessionKey,
        readFetchedPostAuthSessionKey,
        signingInOutStartedFromAuthenticated,
        downgradeFetchedReadPostForSignOut,
    ]);
    const showStickyPostBannerPurchaseButtons = useMemo(() => !!(
        !!postBannerRootPost
        && !!postBannerRootPost.title
        && stickyPostBannerActive
        && !write
        && !characterModalIsVisible
        && checkedForCharacterModal
        && pageLoadRendersCompleted
        && viewportDimensions.width >= PURCHASE_BOOK_BUTTON_THRESHOLD
        && storeItemsByProduct.size > 0
    ), [
        postBannerRootPost,
        stickyPostBannerActive,
        write,
        characterModalIsVisible,
        checkedForCharacterModal,
        pageLoadRendersCompleted,
        viewportDimensions.width,
        storeItemsByProduct.size,
    ]);
    const stickyPostBannerPurchaseButtonCount = useMemo(() => {
        if (!showStickyPostBannerPurchaseButtons) return 0;
        if (showPostBannerSignInButton) return 2;
        return 1;
    }, [
        showStickyPostBannerPurchaseButtons,
        showPostBannerSignInButton,
    ]);
    const postBannerRightContainerWidth = useMemo(() => (
        resolvePostBannerRightContainerWidth({
            annotationsHidden,
            viewportWidth: viewportDimensions.width,
            annotationViewWidth,
            showStickyPurchaseButtons: showStickyPostBannerPurchaseButtons,
            stickyPurchaseButtonCount: stickyPostBannerPurchaseButtonCount,
            stickyPurchaseButtonWidth: WEB_DOWNLOAD_BUTTON_WIDTH_REGULAR,
            stickyPurchaseButtonGap: POST_BANNER_PURCHASE_ACTION_GAP,
            stickyPurchaseButtonRightOffset: 11.5 * BODY_FONT_SIZE,
            hiddenAnnotationsBufferWidth: POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER,
            hiddenAnnotationsBufferWidthSmall: POST_CONTENT_RIGHT_CONTAINER_WIDTH_BUFFER_SMALL,
            smallAnnotationViewWidth: ANNOTATION_VIEW_SMALL_VIEWPORT_WIDTH,
        })
    ), [
        annotationsHidden,
        viewportDimensions.width,
        annotationViewWidth,
        showStickyPostBannerPurchaseButtons,
        stickyPostBannerPurchaseButtonCount,
    ]);

    const currentContentAuthLocked = useMemo(() => {
        if (!currentReadPost) return false;

        if (selectedPostValuePath.length === 2 && currentReadPostChapterSection) {
            return !!currentReadPostChapterSection.authLocked;
        }

        if (selectedPostValuePath.length === 1 && currentReadPostChapter) {
            return !!currentReadPostChapter.authLocked;
        }

        return !!currentReadPost.authLocked;
    }, [
        currentReadPost,
        currentReadPostChapter,
        currentReadPostChapterSection,
        selectedPostValuePath,
    ]);
    internalPostLinkAccessStateRef.current = {
        currentContentAuthLocked,
        userIsSignedIn,
    };
    // Determines whether the current reader scope can legitimately collapse back
    // to a preview shell after access changes, so placeholder handoff only runs
    // for auth-locked/paywalled scopes that have downgraded content to fetch.
    const currentReadScopeCanDowngradeToPreview = useMemo(() => {
        if (write || !currentReadPost) return false;

        const hasReadablePreviewValue = (value?: string, nodes?: Descendant[]): boolean => (
            value != null || nodes != null
        );
        const rootScopeCanDowngrade = (
            hasReadablePreviewValue(
                currentReadPost.previewValue,
                currentReadPost.previewValueNodes,
            )
            || !!currentReadPost.authLocked
            || !!currentReadPost.paywalled
        );
        const chapterScopeCanDowngrade = (
            rootScopeCanDowngrade
            || hasReadablePreviewValue(
                currentReadPostChapter?.previewValue,
                currentReadPostChapter?.previewValueNodes,
            )
            || !!currentReadPostChapter?.authLocked
            || !!currentReadPostChapter?.paywalled
        );
        const sectionScopeCanDowngrade = (
            chapterScopeCanDowngrade
            || hasReadablePreviewValue(
                currentReadPostChapterSection?.previewValue,
                currentReadPostChapterSection?.previewValueNodes,
            )
            || !!currentReadPostChapterSection?.authLocked
            || !!currentReadPostChapterSection?.paywalled
        );

        if (selectedPostValuePath.length === 2) {
            return sectionScopeCanDowngrade;
        }
        if (selectedPostValuePath.length === 1) {
            return chapterScopeCanDowngrade;
        }

        return rootScopeCanDowngrade;
    }, [
        write,
        currentReadPost,
        currentReadPostChapter,
        currentReadPostChapterSection,
        selectedPostValuePath,
    ]);

    /**
     * Re-fetch post once after anonymous -> authenticated transitions so auth-locked
     * content upgrades from previewValue to full value.
     */
    useEffect(() => {
        const previousAuthType = previousUserAuthenticationTypeRef.current;
        let currentAuthType = user?.authenticationType || null;
        const authLockUpgradeHandoffPending = (
            currentContentAuthLocked
            || authLockAutoResumePendingRef.current
            || authLockedEmailSubmitting
            || authLockedSignInMethod != null
        );
        const transitionedFromAnonymousToAuthenticated = (
            previousAuthType === AUTHENTICATION_TYPE.anonymous
            && currentAuthType != null
            && currentAuthType !== AUTHENTICATION_TYPE.anonymous
        );
        const transitionedFromAuthenticatedToAnonymous = (
            previousAuthType != null
            && previousAuthType !== AUTHENTICATION_TYPE.anonymous
            && currentAuthType === AUTHENTICATION_TYPE.anonymous
        );

        if (
            !write
            && transitionedFromAnonymousToAuthenticated
            && authLockUpgradeHandoffPending
            && !refreshingPost
            && !refreshPost
        ) {
            // Auth can complete before the refresh request visibly starts. Mark the
            // upgrade handoff immediately so the UI swaps straight to placeholder
            // instead of flashing the stale auth-lock shell for a frame.
            readAccessUpgradeSawRefreshRef.current = false;
            readAccessUpgradeRequestedRefreshRef.current = false;
            setReadAccessUpgradePending(true);
            setReadAccessDowngradePending(false);
        }
        if (transitionedFromAuthenticatedToAnonymous) {
            readAccessUpgradeRequestedRefreshRef.current = false;
            readAccessUpgradeRefreshRequestIdRef.current += 1;
            if (!write) {
                setReadAccessDowngradePending(currentReadScopeCanDowngradeToPreview);
            }
            // Sign-out can briefly carry over stale local fallback. Clear it and suppress
            // anon fallback until this anon session records fresh manual progress.
            clearLocalReaderProgressCache();
            suppressAnonymousLocalResumeFallbackRef.current = true;
            setResumeReaderProgress(null);
            logReaderAnnotation('annotation-state-cleared', {
                reason: 'auth-transition-to-anonymous',
                selectedPostValuePath,
                currentAnnotationFetchScopeKey,
                currentPostValueSource,
            });
            setAnnotations(new Map());
            setPendingAnnotationCreationIds(new Set());
            setPendingAnnotationFallbackTops(new Map());
            setPendingAnnotationProvisionalParagraphIds(new Map());
            setQueuedAnnotationRetries(new Map());
            protectedAnnotationIdsRef.current.clear();
            recentlyAckedAnnotationIdsRef.current.clear();
            setAnnotationDetailData(null);
            setFetchedAnnotations(false);
            setFetchedAnnotationScopeKey(null);
            setFetchingAnnotations(false);
            setExpandAnnotationStackWithAnnotationId(null);
            clearHideAnnotationsSyncTimeout();
            clearHideAnnotationsFeedbackTimeout();
            hideAnnotationsWriteInFlightRef.current = false;
            inFlightHideAnnotationsTargetRef.current = null;
            queuedHideAnnotationsRef.current = null;
            setAnnotationsHiddenPendingSync(false);
            setAnnotationsHiddenOverride(null);
            annotationsHiddenValueRef.current = true;
            if (
                !write
                && postId
                && currentZoomLevel
            ) {
                setRefreshPost(true);
            }
        }
        if (transitionedFromAnonymousToAuthenticated) {
            suppressAnonymousLocalResumeFallbackRef.current = false;
            setFetchedAnnotations(false);
            setFetchedAnnotationScopeKey(null);
            if (
                !write
                && postId
                && currentZoomLevel
            ) {
                queueFetchAnnotationsPostPaint({
                    resetHighlights: true,
                });
            }
        }

        if (currentAuthType == null) {
            currentAuthType = previousAuthType;
        }
        previousUserAuthenticationTypeRef.current = currentAuthType;
    }, [
        write,
        user?.authenticationType,
        currentContentAuthLocked,
        authLockedEmailSubmitting,
        authLockedSignInMethod,
        authLockAutoResumeTick,
        currentReadScopeCanDowngradeToPreview,
        refreshingPost,
        refreshPost,
        clearLocalReaderProgressCache,
        clearHideAnnotationsSyncTimeout,
        clearHideAnnotationsFeedbackTimeout,
        postId,
        currentZoomLevel,
        queueFetchAnnotationsPostPaint,
    ]);
    useEffect(() => {
        if (
            write
            || !readAccessUpgradePending
            || !userIsSignedIn
            || refreshingPost
            || refreshPost
            || readAccessUpgradeSawRefreshRef.current
        ) {
            return undefined;
        }

        const liveSignedInAuthUser = getCurrentSignedInAuthUser();
        if (liveSignedInAuthUser) {
            scheduleReadAccessUpgradeRefresh(liveSignedInAuthUser);
            return undefined;
        }

        if (typeof auth.onIdTokenChanged !== 'function') {
            return undefined;
        }

        const unsubscribe = auth.onIdTokenChanged((nextAuthUser) => {
            if (!nextAuthUser) return;
            scheduleReadAccessUpgradeRefresh(nextAuthUser);
        });

        return () => {
            unsubscribe();
        };
    }, [
        write,
        readAccessUpgradePending,
        userIsSignedIn,
        refreshingPost,
        refreshPost,
        currentSignedInAuthUser,
        getCurrentSignedInAuthUser,
        scheduleReadAccessUpgradeRefresh,
    ]);

    /**
     * Determines the current post value based on the selected post value path
     * @returns the current post value based on the selected post value path
     */
    const currentPostValueState = useMemo(() => {
        const startedAt = performance.now();
        let result: string | undefined;
        let valueNodes: Descendant[] | undefined;
        let source: 'rootValue' | 'rootPreview' | 'chapterValue' | 'chapterPreview' | 'sectionValue' | 'sectionPreview' | undefined;
        let headerTwoPartCountOverride: number | null = null;
        let trustedValueHash: string | undefined;
        let trustedValueVersion: number | undefined;
        const sourceRootPost = activeRootPost;
        const sourceChapter = activeScopedPostChapter;
        const sourceSection = activeScopedPostChapterSection;
        const hasReadableValue = (value?: string, nodes?: Descendant[]): boolean => (
            value != null || nodes != null
        );
        const hasReadableSectionValue = (value?: string, nodes?: Descendant[]): boolean => (
            nodes != null || value !== ''
        );
        const hasResolvedReadableValue = (): boolean => (
            result !== undefined || valueNodes !== undefined
        );

        // We return undefined because returning a string
        // will cause initializedValue flag to be set to true in PostEditor
        // and prevent updating when actual value is received from database
        if (sourceRootPost) {
            if (
                selectedPostValuePath.length === 1
                && sourceChapter == null
            ) {
                logReaderMemoTiming('currentPostValue', startedAt, {
                    pathLength: selectedPostValuePath.length,
                    hasCurrentReadPost: !!sourceRootPost,
                    valueLength: undefined,
                });
                return {
                    value: undefined,
                    valueNodes: undefined,
                    source: undefined,
                    headerTwoPartCountOverride: null,
                    trustedValueHash: undefined,
                    trustedValueVersion: undefined,
                };
            }

            if (
                selectedPostValuePath.length === 2
                && sourceSection == null
            ) {
                logReaderMemoTiming('currentPostValue', startedAt, {
                    pathLength: selectedPostValuePath.length,
                    hasCurrentReadPost: !!sourceRootPost,
                    valueLength: undefined,
                });
                return {
                    value: undefined,
                    valueNodes: undefined,
                    source: undefined,
                    headerTwoPartCountOverride: null,
                    trustedValueHash: undefined,
                    trustedValueVersion: undefined,
                };
            }

            const shouldForceAuthPreview = (
                !userIsAdmin
                && currentContentAuthLocked
                && !userIsSignedIn
            );

            if (shouldForceAuthPreview) {
                if (
                    selectedPostValuePath.length === 2
                    && sourceSection != null
                    && hasReadableValue(sourceSection?.previewValue, sourceSection?.previewValueNodes)
                ) {
                    result = sourceSection.previewValue;
                    valueNodes = sourceSection.previewValueNodes;
                    source = 'sectionPreview';
                    headerTwoPartCountOverride = sourceSection.previewHeaderTwoPartCount ?? null;
                    trustedValueHash = sourceSection.readOptimizedPreviewValueHash;
                    trustedValueVersion = sourceSection.readOptimizedPreviewValueVersion;
                } else if (
                    selectedPostValuePath.length === 1
                    && sourceChapter != null
                    && hasReadableValue(sourceChapter?.previewValue, sourceChapter?.previewValueNodes)
                ) {
                    result = sourceChapter.previewValue;
                    valueNodes = sourceChapter.previewValueNodes;
                    source = 'chapterPreview';
                    headerTwoPartCountOverride = sourceChapter.previewHeaderTwoPartCount ?? null;
                    trustedValueHash = sourceChapter.readOptimizedPreviewValueHash;
                    trustedValueVersion = sourceChapter.readOptimizedPreviewValueVersion;
                } else if (hasReadableValue(sourceRootPost.previewValue, sourceRootPost.previewValueNodes)) {
                    result = sourceRootPost.previewValue;
                    valueNodes = sourceRootPost.previewValueNodes;
                    source = 'rootPreview';
                    headerTwoPartCountOverride = sourceRootPost.previewHeaderTwoPartCount ?? null;
                    trustedValueHash = sourceRootPost.readOptimizedPreviewValueHash;
                    trustedValueVersion = sourceRootPost.readOptimizedPreviewValueVersion;
                }
            }

            if (
                !hasResolvedReadableValue()
                && !write
                && currentZoomLevel
                && currentZoomLevel.level < ZOOM_LEVEL.four
                && hasReadableValue(sourceRootPost.value, sourceRootPost.valueNodes)
            ) {
                result = sourceRootPost.value;
                valueNodes = sourceRootPost.valueNodes;
                source = 'rootValue';
                trustedValueHash = sourceRootPost.readOptimizedValueHash;
                trustedValueVersion = sourceRootPost.readOptimizedValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && write
                && currentZoomLevel
                && currentZoomLevel.level < ZOOM_LEVEL.four
                && hasReadableValue(sourceRootPost.value, sourceRootPost.valueNodes)
            ) {
                result = sourceRootPost.value;
                valueNodes = sourceRootPost.valueNodes;
                source = 'rootValue';
                trustedValueHash = sourceRootPost.readOptimizedValueHash;
                trustedValueVersion = sourceRootPost.readOptimizedValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && selectedPostValuePath.length === 0
                && hasReadableValue(sourceRootPost.value, sourceRootPost.valueNodes)
            ) {
                result = sourceRootPost.value;
                valueNodes = sourceRootPost.valueNodes;
                source = 'rootValue';
                trustedValueHash = sourceRootPost.readOptimizedValueHash;
                trustedValueVersion = sourceRootPost.readOptimizedValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && selectedPostValuePath.length === 0
                && hasReadableValue(sourceRootPost.previewValue, sourceRootPost.previewValueNodes)
            ) {
                result = sourceRootPost.previewValue;
                valueNodes = sourceRootPost.previewValueNodes;
                source = 'rootPreview';
                headerTwoPartCountOverride = sourceRootPost.previewHeaderTwoPartCount ?? null;
                trustedValueHash = sourceRootPost.readOptimizedPreviewValueHash;
                trustedValueVersion = sourceRootPost.readOptimizedPreviewValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && (
                    selectedPostValuePath.length === 1
                    || (
                        currentZoomLevel
                        && currentZoomLevel.level === ZOOM_LEVEL.four
                    )
                )
                && sourceChapter != null
                && sourceSection == null
                && hasReadableValue(sourceChapter.value, sourceChapter.valueNodes)
            ) {
                result = sourceChapter.value;
                valueNodes = sourceChapter.valueNodes;
                source = 'chapterValue';
                trustedValueHash = sourceChapter.readOptimizedValueHash;
                trustedValueVersion = sourceChapter.readOptimizedValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && selectedPostValuePath.length === 1
                && sourceChapter != null
                && sourceSection == null
                && hasReadableValue(sourceChapter.previewValue, sourceChapter.previewValueNodes)
            ) {
                result = sourceChapter.previewValue;
                valueNodes = sourceChapter.previewValueNodes;
                source = 'chapterPreview';
                headerTwoPartCountOverride = sourceChapter.previewHeaderTwoPartCount ?? null;
                trustedValueHash = sourceChapter.readOptimizedPreviewValueHash;
                trustedValueVersion = sourceChapter.readOptimizedPreviewValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && selectedPostValuePath.length === 2
                && sourceChapter != null
                && sourceSection != null
                && hasReadableSectionValue(sourceSection.value, sourceSection.valueNodes)
            ) {
                result = sourceSection.value;
                valueNodes = sourceSection.valueNodes;
                source = 'sectionValue';
                trustedValueHash = sourceSection.readOptimizedValueHash;
                trustedValueVersion = sourceSection.readOptimizedValueVersion;
            }

            if (
                !hasResolvedReadableValue()
                && selectedPostValuePath.length === 2
                && sourceChapter != null
                && sourceSection != null
                && hasReadableValue(sourceSection.previewValue, sourceSection.previewValueNodes)
            ) {
                result = sourceSection.previewValue;
                valueNodes = sourceSection.previewValueNodes;
                source = 'sectionPreview';
                headerTwoPartCountOverride = sourceSection.previewHeaderTwoPartCount ?? null;
                trustedValueHash = sourceSection.readOptimizedPreviewValueHash;
                trustedValueVersion = sourceSection.readOptimizedPreviewValueVersion;
            }
        }

        logReaderMemoTiming('currentPostValue', startedAt, {
            pathLength: selectedPostValuePath.length,
            hasCurrentReadPost: !!sourceRootPost,
            valueLength: result?.length,
            source,
        });
        return {
            value: result,
            valueNodes,
            source,
            headerTwoPartCountOverride,
            trustedValueHash,
            trustedValueVersion,
        };
    }, [
        activeRootPost,
        activeScopedPostChapter,
        activeScopedPostChapterSection,
        currentZoomLevel,
        selectedPostValuePath,
        currentContentAuthLocked,
        userIsSignedIn,
        userIsAdmin,
        logReaderMemoTiming,
    ]);
    const currentPostValue = currentPostValueState.value;
    const currentPostValueNodes = currentPostValueState.valueNodes;
    const currentPostValueSource = currentPostValueState.source;
    const currentHeaderTwoPartCountOverride = currentPostValueState.headerTwoPartCountOverride;
    const currentPostTrustedValueHash = currentPostValueState.trustedValueHash;
    const currentPostTrustedValueVersion = currentPostValueState.trustedValueVersion;
    const currentPostValueUsesPreviewSource = useMemo(() => (
        currentPostValueSource === 'rootPreview'
        || currentPostValueSource === 'chapterPreview'
        || currentPostValueSource === 'sectionPreview'
    ), [currentPostValueSource]);
    internalPostLinkScopeDebugRef.current = {
        source: currentPostValueSource || null,
        usesPreviewSource: currentPostValueUsesPreviewSource,
        serializedValue: currentPostValue,
        valueNodes: currentPostValueNodes,
    };
    const currentPostHasRenderableValue = currentPostValue !== undefined || currentPostValueNodes !== undefined;
    useEffect(() => {
        if (!currentPostHasRenderableValue) {
            setCurrentPostParagraphCount(0);
        }
    }, [
        currentPostHasRenderableValue,
        selectedPostValuePath,
    ]);
    currentPostParagraphCountRef.current = currentPostParagraphCount;
    const authLockContentHydrated = useMemo(() => {
        if (!currentContentAuthLocked) {
            return true;
        }
        if (userIsAdmin) return true;
        if (!userIsSignedIn) return false;

        return (
            currentPostValueSource === 'rootValue'
            || currentPostValueSource === 'chapterValue'
            || currentPostValueSource === 'sectionValue'
        );
    }, [
        currentContentAuthLocked,
        userIsAdmin,
        userIsSignedIn,
        currentPostValueSource,
    ]);
    const authLockContentHydrationDebug = useMemo(() => ({
        currentPostValueSource,
        rootValueLength: activeRootPost?.value?.length,
        chapterValueLength: activeScopedPostChapter?.value?.length,
        sectionValueLength: activeScopedPostChapterSection?.value?.length,
        currentZoomLevel: currentZoomLevel?.level,
        selectedPathLength: selectedPostValuePath.length,
    }), [
        currentPostValueSource,
        activeRootPost?.value,
        activeScopedPostChapter?.value,
        activeScopedPostChapterSection?.value,
        currentZoomLevel?.level,
        selectedPostValuePath.length,
    ]);
    useEffect(() => {
        logReaderAnnotation('annotation-scope-state', {
            write,
            postId: postId || null,
            activeRootPostId: activeRootPost?.id || null,
            currentReadPostId: currentReadPost?.id || null,
            selectedPostValuePath,
            currentPostValueSource: currentPostValueSource || null,
            currentZoomLevel: currentZoomLevel?.level || null,
            currentAnnotationFetchScopeKey,
            annotationFetchChapterId: currentAnnotationFetchTarget.chapterId || null,
            annotationFetchSectionId: currentAnnotationFetchTarget.sectionId || null,
            currentPostContentTargetDocId: currentPostContentTarget.docId || null,
            currentPostContentTargetChapterId: currentPostContentTarget.chapterId || null,
            currentPostContentTargetSectionId: currentPostContentTarget.sectionId || null,
            currentPostContentTargetResolved: currentPostContentTarget.isResolved,
            canAccessReaderAnnotations,
            annotationsHidden,
            fetchedAnnotations,
            fetchingAnnotations,
            postBodyPainted,
        });
    }, [
        write,
        postId,
        activeRootPost?.id,
        currentReadPost?.id,
        selectedPostValuePath,
        currentPostValueSource,
        currentZoomLevel?.level,
        currentAnnotationFetchScopeKey,
        currentAnnotationFetchTarget.chapterId,
        currentAnnotationFetchTarget.sectionId,
        currentPostContentTarget.docId,
        currentPostContentTarget.chapterId,
        currentPostContentTarget.sectionId,
        currentPostContentTarget.isResolved,
        canAccessReaderAnnotations,
        annotationsHidden,
        fetchedAnnotations,
        fetchingAnnotations,
        postBodyPainted,
        logReaderAnnotation,
    ]);
    useEffect(() => {
        const currentAuthUser = getCurrentSignedInAuthUser();
        const invalidatePendingHydrationRefresh = (): void => {
            authLockContentHydrationRefreshRequestIdRef.current += 1;
        };
        if (authLockContentHydrated) {
            authLockContentHydrationRefreshKeyRef.current = null;
            invalidatePendingHydrationRefresh();
            return;
        }
        if (
            write
            || !user
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
            || userIsAdmin
            || !currentContentAuthLocked
            || !authLockAutoResumePendingRef.current
            || authLockAutoResumeAwaitingPostRefreshRef.current
            || authLockAutoResumeAwaitingPathRefreshRef.current
            || refreshingPost
            || refreshPost
            || !currentAuthUser
        ) {
            invalidatePendingHydrationRefresh();
            return;
        }

        const autoResumeTarget = authLockAutoResumeTargetRef.current;
        const targetPathMatches = !!autoResumeTarget && (
            autoResumeTarget.path.length === selectedPostValuePath.length
            && autoResumeTarget.path.every(
                (pathValue, index) => selectedPostValuePath[index] === pathValue,
            )
        );
        if (
            !authLockAutoResumeAuthCompletedRef.current
            || !targetPathMatches
        ) {
            invalidatePendingHydrationRefresh();
            return;
        }

        const hydrationRefreshKey = [
            currentAuthUser.uid,
            currentReadPost?.id || postId || '',
            selectedPostValuePath.join(':'),
        ].join('::');
        if (authLockContentHydrationRefreshKeyRef.current === hydrationRefreshKey) {
            return;
        }
        authLockContentHydrationRefreshKeyRef.current = hydrationRefreshKey;
        const refreshRequestId = authLockContentHydrationRefreshRequestIdRef.current + 1;
        authLockContentHydrationRefreshRequestIdRef.current = refreshRequestId;

        currentAuthUser.getIdToken(true)
            .catch(() => undefined)
            .then(() => {
                if (readerViewUnmountedRef.current) return;
                if (authLockContentHydrationRefreshRequestIdRef.current !== refreshRequestId) return;
                setRefreshPost(true);
            });
    }, [
        write,
        user,
        user?.authenticationType,
        userIsAdmin,
        currentContentAuthLocked,
        currentReadPost?.id,
        postId,
        selectedPostValuePath,
        currentSignedInAuthUser,
        getCurrentSignedInAuthUser,
        refreshingPost,
        refreshPost,
        authLockContentHydrated,
    ]);

    const isNotPaywalled = useMemo(() => !!(
        currentReadPost
        && (
            userIsAdmin
            || userIsSignedIn
            || !currentContentAuthLocked
        )
        && (
            (
                // post isn't paywalled
                !currentReadPost.paywalled
                // post chapter isn't paywalled
                && (
                    !currentReadPostChapter
                    || (
                        currentReadPostChapter.paywalled != null
                        && !currentReadPostChapter.paywalled
                    )
                )
                // post section isn't paywalled
                && (
                    !currentReadPostChapterSection
                    || (
                        currentReadPostChapterSection.paywalled != null
                        && !currentReadPostChapterSection.paywalled
                    )
                )
            )
        || (
            currentReadPost.paywalled
            && hasPostAccessItem
            && hasPostAccessItem.scope === POST_ACCESS_ITEM_SCOPE.full
            && (
                hasPostAccessItem.expires == null
                || hasPostAccessItem.expires > Date.now()
            )
        )
        || (
            currentReadPost.paywalled
            && currentReadPostChapter
            && currentReadPostChapter.paywalled != null
            && !currentReadPostChapter.paywalled
        )
        || (
            currentReadPost.paywalled
            && currentReadPostChapterSection
            && currentReadPostChapterSection.paywalled != null
            && !currentReadPostChapterSection.paywalled
        )
        || (
            currentReadPost.paywalled
            && hasPostAccessItem
            && hasPostAccessItem.scope === POST_ACCESS_ITEM_SCOPE.limited
            && (
                hasPostAccessItem.expires == null
                || hasPostAccessItem.expires > Date.now()
            )
        ))
    ), [
        currentReadPost,
        currentReadPostChapter,
        currentReadPostChapterSection,
        hasPostAccessItem,
        userIsAdmin,
        userIsSignedIn,
        currentContentAuthLocked,
    ]);

    const showPaywall = useMemo(() => {
        if (!currentReadPost) { return false; }
        if (userIsAdmin) { return false; }
        if (currentContentAuthLocked) {
            // Auth-locked chapters are unlocked by authentication, not by
            // purchase. They should never fall through to the paywall banner
            // during anonymous -> authenticated hydration.
            return false;
        }

        const hasReadableValue = (value?: string, nodes?: Descendant[]): boolean => (
            value != null || nodes != null
        );
        const hasReadableSectionValue = (value?: string, nodes?: Descendant[]): boolean => (
            nodes != null || value !== ''
        );
        if (
            selectedPostValuePath.length === 0
            && !hasReadableValue(currentReadPost.value, currentReadPost.valueNodes)
        ) {
            // post has no chapters or sections
            // doesn't have access to post
            return true;
        }

        if (
            selectedPostValuePath.length === 1
            && currentReadPostChapter != null
            && currentReadPostChapterSection == null
            && !hasReadableValue(currentReadPostChapter.value, currentReadPostChapter.valueNodes)
            && hasReadableValue(
                currentReadPostChapter.previewValue,
                currentReadPostChapter.previewValueNodes,
            )
        ) {
            return true;
        }

        if (
            selectedPostValuePath.length === 2
            && currentReadPostChapter != null
            && currentReadPostChapterSection != null
            && !hasReadableSectionValue(
                currentReadPostChapterSection.value,
                currentReadPostChapterSection.valueNodes,
            )
            && hasReadableValue(
                currentReadPostChapterSection.previewValue,
                currentReadPostChapterSection.previewValueNodes,
            )
        ) {
            return true;
        }

        return false;
    }, [
        userIsAdmin,
        currentReadPost,
        currentReadPostChapter,
        currentReadPostChapterSection,
        selectedPostValuePath,
        currentContentAuthLocked,
        userIsSignedIn,
    ]);

    const showAuthLocked = useMemo(() => (
        !!currentReadPost
        && !userIsAdmin
        && currentContentAuthLocked
        && !userIsSignedIn
    ), [
        currentReadPost,
        userIsAdmin,
        currentContentAuthLocked,
        userIsSignedIn,
    ]);
    /**
     * Reader audio needs the stronger restricted-access signal, not just the
     * current value-lane name. Some paywall/auth-lock renders can still flow
     * through a full-value lane while the surrounding reader state is already
     * enforcing preview-only access.
     */
    const readOnlyAudioUsesRestrictedPreviewSource = useMemo(() => (
        currentPostValueUsesPreviewSource
        || showAuthLocked
        || showPaywall
    ), [
        currentPostValueUsesPreviewSource,
        showAuthLocked,
        showPaywall,
    ]);
    const readOnlyPreviewAudioEndPromptText = useMemo(() => {
        if (!readOnlyAudioUsesRestrictedPreviewSource) {
            return '';
        }

        if (!showAuthLocked && !showPaywall) {
            return '';
        }

        const scope = resolvePreviewAudioEndPromptScope(selectedPostValuePath);
        const accessType = showAuthLocked
            ? PREVIEW_AUDIO_END_PROMPT_ACCESS_TYPE.authLocked
            : PREVIEW_AUDIO_END_PROMPT_ACCESS_TYPE.payLocked;

        return resolvePreviewAudioEndPrompt({
            accessType,
            scope,
        }).text;
    }, [
        readOnlyAudioUsesRestrictedPreviewSource,
        selectedPostValuePath,
        showAuthLocked,
        showPaywall,
    ]);
    // Sign-out flips auth state before the downgrade-pending effect can commit.
    // Detect that first anonymous/null render so the reader swaps straight to the
    // placeholder instead of briefly showing the auth-lock/paywall shell over full content.
    const readAccessDowngradeHandoffPending = useMemo(() => {
        if (
            write
            || currentPostValueUsesPreviewSource
        ) {
            return false;
        }

        const previousAuthType = previousUserAuthenticationTypeRef.current;
        if (
            previousAuthType == null
            || previousAuthType === AUTHENTICATION_TYPE.anonymous
        ) {
            return false;
        }

        const authSessionDowngraded = (
            !auth.currentUser
            || auth.currentUser.isAnonymous
            || user?.authenticationType == null
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
        );
        const readPayloadFetchedForPreviousAuthSession = (
            readFetchedPostAuthSessionKey != null
            && readFetchedPostAuthSessionKey !== currentReadAuthSessionKey
        );

        return authSessionDowngraded || readPayloadFetchedForPreviousAuthSession;
    }, [
        write,
        currentPostValueUsesPreviewSource,
        auth.currentUser,
        user?.authenticationType,
        currentReadAuthSessionKey,
        readFetchedPostAuthSessionKey,
    ]);
    // Only keep the auth-lock shell pinned while the reader is still anonymous.
    // Once auth state upgrades, placeholder/content handoff should continue even
    // if the email/social conversion promise is still finishing in the background.
    const authLockSignInPendingBeforeUpgrade = useMemo(() => (
        !userIsSignedIn
        && (
            authLockedEmailSubmitting
            || authLockedSignInMethod != null
            || signingInOut
        )
    ), [
        userIsSignedIn,
        authLockedEmailSubmitting,
        authLockedSignInMethod,
        signingInOut,
    ]);
    // User-triggered sign-out sets the shared busy flag before Firebase clears
    // the signed-in identity. Keep that downgrade handoff pinned until either
    // the current read payload has been refreshed for the new auth session, so
    // the reader never briefly re-renders the stale signed-in body mid-handoff.
    const readAccessDowngradeSignOutPending = useMemo(() => {
        if (
            write
            || currentPostValueUsesPreviewSource
        ) {
            return false;
        }

        return !!(
            signingInOutStartedFromAuthenticated
            && (
                signingInOut
                || readFetchedPostAuthSessionKey !== currentReadAuthSessionKey
            )
        );
    }, [
        write,
        currentPostValueUsesPreviewSource,
        signingInOut,
        currentReadAuthSessionKey,
        readFetchedPostAuthSessionKey,
        signingInOutStartedFromAuthenticated,
    ]);
    const showAccessDowngradePlaceholder = useMemo(() => (
        !write
        && (
            readAccessDowngradePending
            || readAccessDowngradeSignOutPending
            || readAccessDowngradeHandoffPending
        )
        && (
            refreshingPost
            || refreshPost
            || !currentPostValueUsesPreviewSource
        )
    ), [
        write,
        readAccessDowngradePending,
        readAccessDowngradeSignOutPending,
        readAccessDowngradeHandoffPending,
        refreshingPost,
        refreshPost,
        currentPostValueUsesPreviewSource,
    ]);
    const showAccessUpgradePlaceholder = useMemo(() => (
        !write
        && readAccessUpgradePending
    ), [
        write,
        readAccessUpgradePending,
    ]);
    useEffect(() => {
        if (!readAccessUpgradePending) return;

        // Upgrade handoff clears only after the corresponding refresh has actually
        // started and then settled; otherwise a fast auth/access flip can briefly
        // fall back to stale lock/paywall chrome before the fetch catches up.
        if (
            write
            || !userIsSignedIn
        ) {
            readAccessUpgradeSawRefreshRef.current = false;
            readAccessUpgradeRequestedRefreshRef.current = false;
            setReadAccessUpgradePending(false);
            return;
        }
        if (refreshingPost || refreshPost) {
            readAccessUpgradeSawRefreshRef.current = true;
            return;
        }
        if (!readAccessUpgradeSawRefreshRef.current) return;

        readAccessUpgradeSawRefreshRef.current = false;
        readAccessUpgradeRequestedRefreshRef.current = false;
        setReadAccessUpgradePending(false);
    }, [
        readAccessUpgradePending,
        write,
        userIsSignedIn,
        refreshingPost,
        refreshPost,
    ]);
    useEffect(() => {
        if (!readAccessDowngradePending) return;

        if (
            write
            || user?.authenticationType !== AUTHENTICATION_TYPE.anonymous
        ) {
            setReadAccessDowngradePending(false);
            return;
        }
        if (refreshingPost || refreshPost) return;

        if (
            currentPostValueUsesPreviewSource
            || (
                !showAuthLocked
                && !showPaywall
            )
        ) {
            setReadAccessDowngradePending(false);
        }
    }, [
        readAccessDowngradePending,
        write,
        user?.authenticationType,
        refreshingPost,
        refreshPost,
        currentPostValueUsesPreviewSource,
        showAuthLocked,
        showPaywall,
    ]);
    const authLockedEmailValid = useMemo(() => (
        validateEmail(authLockedEmail.trim().toLowerCase())
    ), [authLockedEmail]);
    const authLockedEmailInvalid = useMemo(() => (
        authLockedEmail.trim().length > 0
        && !authLockedEmailValid
    ), [
        authLockedEmail,
        authLockedEmailValid,
    ]);

    const hasActiveBookLimitedAccess = useMemo(() => !!(
        hasBookAccess
        && hasBookAccess.scope === POST_ACCESS_ITEM_SCOPE.limited
        && (
            hasBookAccess.expires == null
            || hasBookAccess.expires > Date.now()
        )
    ), [hasBookAccess]);

    const hasFullBookAccess = useMemo(() => !!(
        hasBookAccess
        && hasBookAccess.scope === POST_ACCESS_ITEM_SCOPE.full
        && (
            hasBookAccess.expires == null
            || hasBookAccess.expires > Date.now()
        )
    ), [hasBookAccess]);
    const hasActiveWebBookAccess = useMemo(() => (
        hasFullBookAccess || hasActiveBookLimitedAccess
    ), [
        hasFullBookAccess,
        hasActiveBookLimitedAccess,
    ]);
    const currentReaderAccessRefreshKey = useMemo(() => JSON.stringify({
        postAccessScope: hasPostAccessItem?.scope || null,
        postAccessExpires: hasPostAccessItem?.expires ?? null,
        bookAccessScope: hasBookAccess?.scope || null,
        bookAccessExpires: hasBookAccess?.expires ?? null,
    }), [
        hasPostAccessItem?.scope,
        hasPostAccessItem?.expires,
        hasBookAccess?.scope,
        hasBookAccess?.expires,
    ]);
    useEffect(() => {
        if (
            write
            || !postId
            || !currentZoomLevel
        ) {
            previousReaderAccessRefreshKeyRef.current = currentReaderAccessRefreshKey;
            return;
        }

        const previousAccessRefreshKey = previousReaderAccessRefreshKeyRef.current;
        previousReaderAccessRefreshKeyRef.current = currentReaderAccessRefreshKey;

        if (
            previousAccessRefreshKey == null
            || previousAccessRefreshKey === currentReaderAccessRefreshKey
        ) {
            return;
        }

        setFetchedAnnotations(false);
        setFetchedAnnotationScopeKey(null);
        if (
            !refreshingPost
            && !refreshPost
        ) {
            // Access-key changes can land before the upgraded post refresh visibly
            // starts. Queue the same placeholder handoff here so paywall unlocks
            // follow the same no-flash path as auth-lock sign-ins.
            if (
                userIsSignedIn
                && currentReadScopeCanDowngradeToPreview
            ) {
                readAccessUpgradeSawRefreshRef.current = false;
                setReadAccessUpgradePending(true);
            }
            setRefreshPost(true);
        }
    }, [
        write,
        postId,
        currentZoomLevel,
        currentReaderAccessRefreshKey,
        userIsSignedIn,
        currentReadScopeCanDowngradeToPreview,
        refreshingPost,
        refreshPost,
    ]);
    const showBuyBanner = useMemo(() => {
        if (!currentReadPost) {
            return false;
        }
        if (userIsAdmin) {
            return false;
        }
        if (showAuthLocked) {
            return false;
        }
        if (
            auth.currentUser
            && !fetchedUserAdminStatus
        ) {
            return false;
        }
        if (
            userIsSignedIn
            && !bookPurchasesLookupFailed
            && (
                !checkedBookPurchases
                || checkingBookPurchases
            )
        ) {
            return false;
        }
        if (
            hasActiveWebBookAccess
            || purchasedWebBook
            || purchasedDigitalBook
            || purchasedPhysicalBook
            || purchasedWebBookCurrentChapter
        ) {
            return false;
        }

        if (
            isNotPaywalled
            && !hasBookAccess
        ) {
            return true;
        }

        return false;
    }, [
        userIsAdmin,
        currentReadPost,
        isNotPaywalled,
        hasBookAccess,
        showAuthLocked,
        auth.currentUser,
        fetchedUserAdminStatus,
        userIsSignedIn,
        bookPurchasesLookupFailed,
        checkedBookPurchases,
        checkingBookPurchases,
        hasActiveWebBookAccess,
        purchasedWebBook,
        purchasedDigitalBook,
        purchasedPhysicalBook,
        purchasedWebBookCurrentChapter,
    ]);
    const postIsLockedForRelease = useMemo(() => !!(
        postLockedUntil != null
        && postLockedUntil > Date.now()
    ), [postLockedUntil]);
    const stickyCtaNudgePost = activeRootPost || post;

    const baseShowStickyPurchaseCta = useMemo(() => {
        if (write || userIsAdmin || storeItemsByProduct.size === 0) {
            return false;
        }
        if (
            auth.currentUser
            && !fetchedUserAdminStatus
        ) {
            return false;
        }
        if (
            userIsSignedIn
            && (
                !checkedBookPurchases
                || checkingBookPurchases
            )
        ) {
            return false;
        }
        if (
            hasActiveWebBookAccess
            || purchasedWebBook
            || purchasedDigitalBook
            || purchasedPhysicalBook
            || purchasedWebBookCurrentChapter
        ) {
            return false;
        }

        return !hasFullBookAccess || hasActiveBookLimitedAccess;
    }, [
        write,
        userIsAdmin,
        storeItemsByProduct.size,
        auth.currentUser,
        fetchedUserAdminStatus,
        userIsSignedIn,
        checkedBookPurchases,
        checkingBookPurchases,
        hasActiveWebBookAccess,
        purchasedWebBook,
        purchasedDigitalBook,
        purchasedPhysicalBook,
        purchasedWebBookCurrentChapter,
        hasFullBookAccess,
        hasActiveBookLimitedAccess,
    ]);

    /**
     * Small-viewport annotation editors occupy the same visual lane as the
     * sticky purchase CTA. Hide the CTA while the editor is visible so the
     * reader does not show the purchase panel peeking out behind the docked
     * editor on phones or narrow desktop windows.
     */
    const hideStickyPurchaseCtaForSmallViewportAnnotationEditor = annotationEditorIsVisible
        && viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min;
    // Initialization only prepares the editor. Wait for its paint callback so
    // access prompts cannot appear above chapter text that is still loading.
    // A resolved restriction with no preview has no editor to paint; keep its
    // access actions available once the requested scope has finished loading.
    const restrictedScopeWithoutPreviewReady = !currentPostHasRenderableValue
        && (currentContentAuthLocked || postIsLockedForRelease)
        && fetchedPost
        && !refreshPost
        && !refreshingPost
        && !readMetadataOnlyFetchAwaitingUser
        && hasAlignedReadAuthContext
        && !hasPendingSelectedPathFromRouteParams
        && !!activeRootPost?.id
        && (selectedPostValuePath.length < 1 || !!activeScopedPostChapter?.id)
        && (selectedPostValuePath.length < 2 || !!activeScopedPostChapterSection?.id);
    const readerAccessPromptsReady = (postEditorInitialized && postBodyPainted)
        || restrictedScopeWithoutPreviewReady;
    const showStickyPurchaseCta = useMemo(() => (
        !write
        && readerAccessPromptsReady
        && !hideStickyPurchaseCtaForSmallViewportAnnotationEditor
        && (
            baseShowStickyPurchaseCta
            || forceShowStickyPurchaseCta
        )
    ), [
        write,
        readerAccessPromptsReady,
        hideStickyPurchaseCtaForSmallViewportAnnotationEditor,
        baseShowStickyPurchaseCta,
        forceShowStickyPurchaseCta,
    ]);

    useEffect(() => {
        if (baseShowStickyPurchaseCta) return;
        if (stickyPurchaseForceShowResetTimeoutRef.current != null) {
            window.clearTimeout(stickyPurchaseForceShowResetTimeoutRef.current);
            stickyPurchaseForceShowResetTimeoutRef.current = null;
        }
        setForceShowStickyPurchaseCta(false);
        setPurchasePanelExpanded(false);
        setPurchaseMoreOptionsExpanded(false);
    }, [baseShowStickyPurchaseCta]);

    // Resolve the current nudge scenario from auth-lock + access state.
    const stickyCtaNudgeScenario = useMemo<STICKY_CTA_NUDGE_SCENARIO | null>(() => {
        if (
            write
            || !user
            || !stickyCtaNudgePost
        ) {
            return null;
        }

        const userIsAnonymous = user.authenticationType === AUTHENTICATION_TYPE.anonymous;
        if (currentContentAuthLocked && userIsAnonymous) {
            return showStickyPurchaseCta ? STICKY_CTA_NUDGE_SCENARIO.authLockedAnonymous : null;
        }

        if (!userIsSignedIn) {
            return null;
        }

        if (!hasActiveWebBookAccess) {
            return showStickyPurchaseCta ? STICKY_CTA_NUDGE_SCENARIO.signedInNoWebAccess : null;
        }

        if (userUndeletedAnnotationCount === 0) {
            return STICKY_CTA_NUDGE_SCENARIO.signedInHasWebAccess;
        }

        return null;
    }, [
        write,
        showStickyPurchaseCta,
        user,
        stickyCtaNudgePost,
        currentContentAuthLocked,
        userIsSignedIn,
        hasActiveWebBookAccess,
        userUndeletedAnnotationCount,
    ]);
    const anonymousStickyNudgeRenderSlot = useMemo<'shareEmail' | 'buy' | 'none'>(() => {
        if (
            !showStickyPurchaseCta
            || !currentContentAuthLocked
            || userIsSignedIn
            || !stickyCtaNudge.visible
            || stickyCtaNudge.target !== STICKY_CTA_NUDGE_TARGET.shareEmail
        ) {
            return 'none';
        }

        const stickyPurchaseIsExtraSmall = viewportDimensions.width <= MEDIA_QUERY_SIZE.extraSmall.max;
        const stickyPurchaseIsSmall = (
            viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min
            && viewportDimensions.width <= MEDIA_QUERY_SIZE.small.max
        );
        const hideShareEmailHeaderButton = stickyPurchaseIsSmall || stickyPurchaseIsExtraSmall;

        if (!purchasePanelExpanded && !hideShareEmailHeaderButton) {
            return 'shareEmail';
        }

        if (hideShareEmailHeaderButton) {
            return 'buy';
        }

        return 'none';
    }, [
        showStickyPurchaseCta,
        currentContentAuthLocked,
        userIsSignedIn,
        stickyCtaNudge.visible,
        stickyCtaNudge.target,
        viewportDimensions.width,
        purchasePanelExpanded,
    ]);
    // Each scenario has its own focused-reading threshold.
    const stickyCtaNudgeThresholdDuration = useMemo<number | null>(() => {
        switch (stickyCtaNudgeScenario) {
        case STICKY_CTA_NUDGE_SCENARIO.authLockedAnonymous:
            return STICKY_CTA_NUDGE_AUTH_LOCKED_ANONYMOUS_THRESHOLD_DURATION;
        case STICKY_CTA_NUDGE_SCENARIO.signedInNoWebAccess:
            return STICKY_CTA_NUDGE_SIGNED_IN_NO_WEB_ACCESS_THRESHOLD_DURATION;
        case STICKY_CTA_NUDGE_SCENARIO.signedInHasWebAccess:
            return STICKY_CTA_NUDGE_SIGNED_IN_WEB_ACCESS_THRESHOLD_DURATION;
        default:
            return null;
        }
    }, [stickyCtaNudgeScenario]);
    const remainingStickyCtaNudgeDuration = useMemo<number | null>(() => (
        browserTabIsFocused != null
        && stickyCtaNudgeThresholdDuration != null
            ? stickyCtaNudgeThresholdDuration - cachedPostFocusedDuration
            : null
    ), [
        browserTabIsFocused,
        stickyCtaNudgeThresholdDuration,
        cachedPostFocusedDuration,
    ]);
    useEffect(() => {
        logReaderNudge('state', {
            write,
            hasUser: !!user,
            userAuthenticationType: user?.authenticationType || null,
            hasPost: !!stickyCtaNudgePost,
            currentContentAuthLocked,
            showStickyPurchaseCta,
            baseShowStickyPurchaseCta,
            forceShowStickyPurchaseCta,
            hasStoreItems: storeItemsByProduct.size > 0,
            hasActiveWebBookAccess,
            checkedBookPurchases,
            checkingBookPurchases,
            bookPurchasesLookupFailed,
            userUndeletedAnnotationCount,
            browserFocused: browserTabIsFocused != null,
            cachedPostFocusedDuration,
            thresholdMs: stickyCtaNudgeThresholdDuration,
            remainingMs: remainingStickyCtaNudgeDuration,
            scenario: stickyCtaNudgeScenario,
            scheduledScenario: scheduledStickyCtaNudgeScenario,
            completedScenario: stickyCtaNudgeScenario != null
                ? completedStickyCtaNudgeScenarios[stickyCtaNudgeScenario]
                : null,
            nudgeVisible: stickyCtaNudge.visible,
            nudgeTarget: stickyCtaNudge.target,
        });
    }, [
        logReaderNudge,
        write,
        user,
        stickyCtaNudgePost,
        currentContentAuthLocked,
        showStickyPurchaseCta,
        baseShowStickyPurchaseCta,
        forceShowStickyPurchaseCta,
        storeItemsByProduct.size,
        hasActiveWebBookAccess,
        checkedBookPurchases,
        checkingBookPurchases,
        bookPurchasesLookupFailed,
        userUndeletedAnnotationCount,
        browserTabIsFocused,
        cachedPostFocusedDuration,
        stickyCtaNudgeThresholdDuration,
        remainingStickyCtaNudgeDuration,
        stickyCtaNudgeScenario,
        scheduledStickyCtaNudgeScenario,
        completedStickyCtaNudgeScenarios,
        stickyCtaNudge.visible,
        stickyCtaNudge.target,
    ]);

    useEffect(() => {
        if (!showStickyPurchaseCta || !currentContentAuthLocked || userIsSignedIn) return;

        logReaderNudge('anonymous-tooltip-slot', {
            viewportWidth: viewportDimensions.width,
            purchasePanelExpanded,
            nudgeVisible: stickyCtaNudge.visible,
            nudgeTarget: stickyCtaNudge.target,
            renderSlot: anonymousStickyNudgeRenderSlot,
        });
    }, [
        logReaderNudge,
        showStickyPurchaseCta,
        currentContentAuthLocked,
        userIsSignedIn,
        viewportDimensions.width,
        purchasePanelExpanded,
        stickyCtaNudge.visible,
        stickyCtaNudge.target,
        anonymousStickyNudgeRenderSlot,
    ]);

    /**
     * Manages triggering timeout to present sticky CTA nudges
     */
    useEffect(() => {
        if (!user || !stickyCtaNudgePost || write || !stickyCtaNudgeScenario) {
            logReaderNudge('schedule-blocked', {
                hasUser: !!user,
                hasPost: !!stickyCtaNudgePost,
                write,
                scenario: stickyCtaNudgeScenario,
            });
            if (scheduledStickyCtaNudgeScenario != null) {
                clearTimeoutStickyCtaNudge();
                setScheduledStickyCtaNudgeScenario(null);
            }
            return;
        }

        if (completedStickyCtaNudgeScenarios[stickyCtaNudgeScenario]) {
            logReaderNudge('schedule-skipped-completed', {
                scenario: stickyCtaNudgeScenario,
            });
            if (scheduledStickyCtaNudgeScenario != null) {
                clearTimeoutStickyCtaNudge();
                setScheduledStickyCtaNudgeScenario(null);
            }
            return;
        }

        if (scheduledStickyCtaNudgeScenario !== stickyCtaNudgeScenario) {
            logReaderNudge('schedule-start', {
                scenario: stickyCtaNudgeScenario,
                thresholdMs: stickyCtaNudgeThresholdDuration,
                remainingMs: remainingStickyCtaNudgeDuration,
                cachedPostFocusedDuration,
                browserFocused: browserTabIsFocused != null,
            });
            clearTimeoutStickyCtaNudge();
            timeoutStickyCtaNudge(stickyCtaNudgeScenario);
            setScheduledStickyCtaNudgeScenario(stickyCtaNudgeScenario);
        }
    }, [
        user,
        stickyCtaNudgePost,
        write,
        stickyCtaNudgeScenario,
        scheduledStickyCtaNudgeScenario,
        completedStickyCtaNudgeScenarios,
        logReaderNudge,
        stickyCtaNudgeThresholdDuration,
        remainingStickyCtaNudgeDuration,
        cachedPostFocusedDuration,
        browserTabIsFocused,
    ]);

    /**
     * Manages determining if sticky CTA nudge should be presented
     */
    const {
        start: timeoutStickyCtaNudge,
        clear: clearTimeoutStickyCtaNudge,
    } = useTimeout((scenario?: STICKY_CTA_NUDGE_SCENARIO) => {
        if (scenario) {
            logReaderNudge('timer-fired', {
                scenario,
                cachedPostFocusedDuration,
                browserFocused: browserTabIsFocused != null,
            });
            performStickyCtaNudge(scenario);
        }
    }, remainingStickyCtaNudgeDuration);

    /**
     * Manages hiding sticky CTA nudge message
     */
    const {
        start: timeoutHideStickyCtaNudge,
        clear: clearTimeoutHideStickyCtaNudge,
    } = useTimeout(() => {
        setStickyCtaNudge({
            visible: false,
            target: null,
            text: '',
        });
    }, HIDE_STICKY_CTA_NUDGE_TIMEOUT_DURATION);

    const getPriceLabelByProductId = useCallback((productId?: string): string => {
        if (!productId) return '';
        const item = storeItemsByProduct.get(productId);
        if (!item?.amount) return '';
        return `$${roundToNDecimals(item.amount / CENTS_IN_A_DOLLAR, 2)}`;
    }, [storeItemsByProduct]);

    const buildStickyCtaButtonText = useCallback((
        label: string,
        productId?: string,
    ): string => {
        const priceLabel = getPriceLabelByProductId(productId);
        return priceLabel ? `${label} (${priceLabel})` : label;
    }, [getPriceLabelByProductId]);

    /**
     * Determines how many zoom levels are unused in post
     */
    const unusedZoomLevelCount = useMemo(
        () => {
            if (!write && appliedZoomLevelCount) {
                return TOTAL_ZOOM_LEVELS - appliedZoomLevelCount - 1;
            }
            return 0;
        },
        [
            write,
            appliedZoomLevelCount,
        ],
    );

    /**
     * Determines which post chapter the current page should refer when
     * adding a chapter to a user's cart
     */
    const currentPageChapterBookType = useMemo(
        () => {
            if (
                activeRootPost
                && activeRootPost.id === BOOK_POST_ID
                && selectedPostValuePath.length > 0
            ) {
                switch (selectedPostValuePath[0]) {
                case 0:
                    return undefined;
                case 1:
                    return undefined;
                case 2:
                    return undefined;
                case 3:
                    return BOOK_TYPE.webChapterOne;
                case 4:
                    return BOOK_TYPE.webChapterTwo;
                case 5:
                    return BOOK_TYPE.webChapterThree;
                case 6:
                    return BOOK_TYPE.webChapterFour;
                case 7:
                    return BOOK_TYPE.webChapterFive;
                case 8:
                    return BOOK_TYPE.webChapterSix;
                case 9:
                    return BOOK_TYPE.webChapterSeven;
                case 10:
                    return BOOK_TYPE.webChapterEight;
                case 11:
                    return BOOK_TYPE.webChapterNine;
                case 12:
                    return BOOK_TYPE.webChapterTen;
                case 13:
                    return BOOK_TYPE.webChapterEleven;
                case 14:
                    return BOOK_TYPE.webChapterTwelve;
                case 15:
                    return BOOK_TYPE.webChapterThirteen;
                case 16:
                    return BOOK_TYPE.webChapterFourteen;
                case 17:
                    return BOOK_TYPE.webChapterFifteen;
                case 18:
                    return BOOK_TYPE.webChapterSixteen;
                case 19:
                    return BOOK_TYPE.webChapterSeventeen;
                case 20:
                    return BOOK_TYPE.webChapterEighteen;
                case 21:
                    return BOOK_TYPE.webChapterNineteen;
                case 22:
                    return BOOK_TYPE.webAppendixA;
                default:
                    return undefined;
                }
            }
            return undefined;
        },
        [
            activeRootPost,
            selectedPostValuePath,
        ],
    );

    const currentPageChapterProductId = useMemo(
        () => {
            if (currentPageChapterBookType) {
                switch (currentPageChapterBookType) {
                case BOOK_TYPE.webChapterOne:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterTwo:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterThree:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterFour:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterFive:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterSix:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterSeven:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterEight:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterNine:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterTen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterEleven:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterTwelve:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterThirteen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterFourteen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterFifteen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterSixteen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterSeventeen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterEighteen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webChapterNineteen:
                    return useStripeTestMode
                        ? WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_LIVE_API_ID;
                case BOOK_TYPE.webAppendixA:
                    return useStripeTestMode
                        ? WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_TEST_API_ID
                        : WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_LIVE_API_ID;
                default:
                    return undefined;
                }
            }
            return undefined;
        },
        [
            currentPageChapterBookType,
            useStripeTestMode,
        ],
    );

    useEffect(() => {
        if (!bookPurchases) {
            setPurchasedWebBookCurrentChapter(false);
            return;
        }
        setPurchasedWebBookCurrentChapter(getPurchasedCurrentChapterFromPurchases(bookPurchases));
    }, [
        bookPurchases,
        currentPageChapterBookType,
    ]);

    const WEB_BOOK_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_DAY_PASS_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_WEEK_PASS_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_ONE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_TWO_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_THREE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SIX_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_NINE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_TEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_APPENDIX_A_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const DIGITAL_BOOK_PREORDER_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const AUDIO_BOOK_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? AUDIO_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : AUDIO_BOOK_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const PHYSICAL_BOOK_PREORDER_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );

    const getProductIdForBookType = useCallback((type?: BOOK_TYPE): string | undefined => {
        switch (type) {
        case BOOK_TYPE.web:
            return WEB_BOOK_PRODUCT_ID;
        case BOOK_TYPE.webDayPass:
            return WEB_BOOK_DAY_PASS_PRODUCT_ID;
        case BOOK_TYPE.webWeekPass:
            return WEB_BOOK_WEEK_PASS_PRODUCT_ID;
        case BOOK_TYPE.webChapterOne:
            return WEB_BOOK_CHAPTER_ONE_PRODUCT_ID;
        case BOOK_TYPE.webChapterTwo:
            return WEB_BOOK_CHAPTER_TWO_PRODUCT_ID;
        case BOOK_TYPE.webChapterThree:
            return WEB_BOOK_CHAPTER_THREE_PRODUCT_ID;
        case BOOK_TYPE.webChapterFour:
            return WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID;
        case BOOK_TYPE.webChapterFive:
            return WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID;
        case BOOK_TYPE.webChapterSix:
            return WEB_BOOK_CHAPTER_SIX_PRODUCT_ID;
        case BOOK_TYPE.webChapterSeven:
            return WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterEight:
            return WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID;
        case BOOK_TYPE.webChapterNine:
            return WEB_BOOK_CHAPTER_NINE_PRODUCT_ID;
        case BOOK_TYPE.webChapterTen:
            return WEB_BOOK_CHAPTER_TEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterEleven:
            return WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterTwelve:
            return WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID;
        case BOOK_TYPE.webChapterThirteen:
            return WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterFourteen:
            return WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterFifteen:
            return WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterSixteen:
            return WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterSeventeen:
            return WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterEighteen:
            return WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID;
        case BOOK_TYPE.webChapterNineteen:
            return WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID;
        case BOOK_TYPE.webAppendixA:
            return WEB_BOOK_APPENDIX_A_PRODUCT_ID;
        case BOOK_TYPE.digital:
            return DIGITAL_BOOK_PREORDER_PRODUCT_ID;
        case BOOK_TYPE.audio:
            return AUDIO_BOOK_PRODUCT_ID;
        case BOOK_TYPE.physical:
            return PHYSICAL_BOOK_PREORDER_PRODUCT_ID;
        default:
            return undefined;
        }
    }, [
        WEB_BOOK_PRODUCT_ID,
        WEB_BOOK_DAY_PASS_PRODUCT_ID,
        WEB_BOOK_WEEK_PASS_PRODUCT_ID,
        WEB_BOOK_CHAPTER_ONE_PRODUCT_ID,
        WEB_BOOK_CHAPTER_TWO_PRODUCT_ID,
        WEB_BOOK_CHAPTER_THREE_PRODUCT_ID,
        WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID,
        WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID,
        WEB_BOOK_CHAPTER_SIX_PRODUCT_ID,
        WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID,
        WEB_BOOK_CHAPTER_NINE_PRODUCT_ID,
        WEB_BOOK_CHAPTER_TEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID,
        WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID,
        WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID,
        WEB_BOOK_APPENDIX_A_PRODUCT_ID,
        DIGITAL_BOOK_PREORDER_PRODUCT_ID,
        AUDIO_BOOK_PRODUCT_ID,
        PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
    ]);

    // In the paywalled chapter state, keep the chapter unlock first and surface
    // the more durable upgrades before time-based passes.
    const isPaywalledStickyCta = showPaywall && !postIsLockedForRelease;
    const compactCtaOneType = useMemo(
        () => (
            showPaywall && currentPageChapterBookType && !postIsLockedForRelease
                ? currentPageChapterBookType
                : BOOK_TYPE.web
        ),
        [showPaywall, currentPageChapterBookType, postIsLockedForRelease],
    );
    const compactCtaOneIsChapter = useMemo(
        () => (
            currentPageChapterBookType != null
            && compactCtaOneType === currentPageChapterBookType
        ),
        [compactCtaOneType, currentPageChapterBookType],
    );
    const compactCtaTwoType = useMemo(
        () => (
            isPaywalledStickyCta ? BOOK_TYPE.web : BOOK_TYPE.webWeekPass
        ),
        [isPaywalledStickyCta],
    );
    const compactCtaThreeType = BOOK_TYPE.physical;
    const compactCtaOneText = useMemo(
        () => buildStickyCtaButtonText(
            compactCtaOneIsChapter ? 'Unlock This Chapter' : 'Full Web Book',
            getProductIdForBookType(compactCtaOneType),
        ),
        [buildStickyCtaButtonText, compactCtaOneIsChapter, compactCtaOneType, getProductIdForBookType],
    );
    const compactCtaTwoText = useMemo(
        () => buildStickyCtaButtonText(
            isPaywalledStickyCta ? 'Full Web Book' : '7-Day Binge Pass',
            getProductIdForBookType(compactCtaTwoType),
        ),
        [buildStickyCtaButtonText, isPaywalledStickyCta, compactCtaTwoType, getProductIdForBookType],
    );
    const compactCtaThreeText = useMemo(
        () => buildStickyCtaButtonText(
            'Get Physical Edition',
            PHYSICAL_BOOK_PREORDER_PRODUCT_ID,
        ),
        [buildStickyCtaButtonText, PHYSICAL_BOOK_PREORDER_PRODUCT_ID],
    );
    const cartAnimationSourceItemStyle = useMemo((): React.CSSProperties => {
        const CART_ANIMATION_PORTAL_Z_INDEX = CURSOR_Z_INDEX - 1;
        if (!activeCartAnimationSourceCenter) return {};
        return {
            position: 'fixed',
            left: activeCartAnimationSourceCenter.x,
            top: activeCartAnimationSourceCenter.y,
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            marginBottom: 0,
            pointerEvents: 'none',
            zIndex: CART_ANIMATION_PORTAL_Z_INDEX,
        };
    }, [
        activeCartAnimationSourceCenter,
    ]);

    // ===== Renderers =====

    /**
     * Renderer for File Upload Progress
     */
    const FileUploads = useMemo((): React.ReactElement => {
        const uploadingFiles = Array.from(uploadingMedia.values()).filter((uploadingItem) => {
            if (
                uploadingItem.type === MEDIA_TYPE.image
                || uploadingItem.type === MEDIA_TYPE.sketch
                || uploadingItem.type === MEDIA_TYPE.audioNote
            ) {
                return true;
            }
            return false;
        });

        return (
            <Transition
                in={uploadingFiles.length > 0}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {
                    (state) => (
                        <UploadProgressContainer
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'up',
                                    offset: 10,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 10,
                                })[state],
                            }}
                        >
                            {uploadingFiles.map((item, index) => (
                                <Transition
                                    key={item.id}
                                    in={uploadingFiles.length > 0}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION
                                            + UPLOAD_TRANSITION_DELAY,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((uploadingFiles.length - Math.max(index - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                >
                                    {(transitionState) => (
                                        <UploadingMediaItem
                                            compact
                                            currentSessionId={currentSessionId}
                                            progress={item.uploadProgress}
                                            mediaItem={item}
                                            onItemCompletion={removeUploadingMedia}
                                            color={themeObj.verascopeColor.blue200}
                                            style={{
                                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                                    direction: 'up',
                                                    offset: 10,
                                                }),
                                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                                    direction: 'up',
                                                    offset: 10,
                                                    numItems: uploadingFiles.length,
                                                    index,
                                                    enterDelay: UPLOAD_TRANSITION_DELAY,
                                                })[transitionState],
                                            }}
                                        />
                                    )}
                                </Transition>
                            ))}
                        </UploadProgressContainer>
                    )
                }
            </Transition>
        );
    }, [
        uploadingMedia,
        currentSessionId,
    ]);

    /**
     * Renderer for Animate To Cart Items
     */
    const WebBookAnimationItems = useMemo((): React.ReactNode => {
        if (typeof document === 'undefined') return null;
        return createPortal(
            <Transition
                in={animateWebBookToCartButtonItems.size > 0}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {
                    () => (
                        <CartAnimationItemsContainer
                            $portalMode
                            $portalZIndex={CURSOR_Z_INDEX - 1}
                        >
                            {Array.from(animateWebBookToCartButtonItems.values()).map((item, index) => (
                                <Transition
                                    key={item.id}
                                    in={animateWebBookToCartButtonItems.size > 0}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION
                                            + UPLOAD_TRANSITION_DELAY,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((animateWebBookToCartButtonItems.size - Math.max(index - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                >
                                    {() => (
                                        <UploadingMediaItem
                                            compact
                                            iconOnly
                                            currentSessionId={currentSessionId}
                                            progress={item.uploadProgress}
                                            mediaItem={item}
                                            iconImageUrl={item.url}
                                            travelEasing={CART_FLY_TO_BUTTON_EASING}
                                            onItemCompletion={onCartItemAnimationComplete}
                                            color={themeObj.verascopeColor.blue200}
                                            style={cartAnimationSourceItemStyle}
                                        />
                                    )}
                                </Transition>
                            ))}
                        </CartAnimationItemsContainer>
                    )
                }
            </Transition>,
            document.body,
        );
    }, [
        animateWebBookToCartButtonItems,
        currentSessionId,
        cartAnimationSourceItemStyle,
    ]);

    const DigitalBookAnimationItems = useMemo((): React.ReactNode => {
        if (typeof document === 'undefined') return null;
        return createPortal(
            <Transition
                in={animateDigitalBookToCartButtonItems.size > 0}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {
                    () => (
                        <CartAnimationItemsContainer
                            $portalMode
                            $portalZIndex={CURSOR_Z_INDEX - 1}
                        >
                            {Array.from(animateDigitalBookToCartButtonItems.values()).map((item, index) => (
                                <Transition
                                    key={item.id}
                                    in={animateDigitalBookToCartButtonItems.size > 0}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION
                                            + UPLOAD_TRANSITION_DELAY,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((animateDigitalBookToCartButtonItems.size - Math.max(index - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                >
                                    {() => (
                                        <UploadingMediaItem
                                            compact
                                            iconOnly
                                            currentSessionId={currentSessionId}
                                            progress={item.uploadProgress}
                                            mediaItem={item}
                                            iconImageUrl={item.url}
                                            travelEasing={CART_FLY_TO_BUTTON_EASING}
                                            onItemCompletion={onCartItemAnimationComplete}
                                            color={themeObj.verascopeColor.blue200}
                                            style={cartAnimationSourceItemStyle}
                                        />
                                    )}
                                </Transition>
                            ))}
                        </CartAnimationItemsContainer>
                    )
                }
            </Transition>,
            document.body,
        );
    }, [
        animateDigitalBookToCartButtonItems,
        currentSessionId,
        cartAnimationSourceItemStyle,
    ]);

    const PhysicalBookAnimationItems = useMemo((): React.ReactNode => {
        if (typeof document === 'undefined') return null;
        return createPortal(
            <Transition
                in={animatePhysicalBookToCartButtonItems.size > 0}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {
                    () => (
                        <CartAnimationItemsContainer
                            $portalMode
                            $portalZIndex={CURSOR_Z_INDEX - 1}
                        >
                            {Array.from(animatePhysicalBookToCartButtonItems.values()).map((item, index) => (
                                <Transition
                                    key={item.id}
                                    in={animatePhysicalBookToCartButtonItems.size > 0}
                                    timeout={{
                                        enter: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION
                                            + UPLOAD_TRANSITION_DELAY,
                                        ),
                                        exit: Math.min(
                                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                                            ((animatePhysicalBookToCartButtonItems.size - Math.max(index - 1, 0)) * FADE_IN_STAGGER_OFFSET_DURATION)
                                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                                        ),
                                    }}
                                    appear
                                    mountOnEnter
                                >
                                    {() => (
                                        <UploadingMediaItem
                                            compact
                                            iconOnly
                                            currentSessionId={currentSessionId}
                                            progress={item.uploadProgress}
                                            mediaItem={item}
                                            iconImageUrl={item.url}
                                            travelEasing={CART_FLY_TO_BUTTON_EASING}
                                            onItemCompletion={onCartItemAnimationComplete}
                                            color={themeObj.verascopeColor.blue200}
                                            style={cartAnimationSourceItemStyle}
                                        />
                                    )}
                                </Transition>
                            ))}
                        </CartAnimationItemsContainer>
                    )
                }
            </Transition>,
            document.body,
        );
    }, [
        animatePhysicalBookToCartButtonItems,
        currentSessionId,
        cartAnimationSourceItemStyle,
    ]);

    // Native cursor fallback used when custom cursor is disabled.
    const nativeHandlebarCursor = useMemo(() => {
        if (hitDetailMinimum || hitPostMinimum) {
            return 'not-allowed';
        }

        if (isSteering) {
            return 'ew-resize';
        }

        if (handlebarHovered) {
            return 'grab';
        }

        return 'default';
    }, [
        handlebarHovered,
        hitDetailMinimum,
        hitPostMinimum,
        isSteering,
    ]);

    /**
     * Renderer for annotation view handlebar
     */
    const handle = (
        <HandlebarContainer
            width={DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH}
            height="100%"
            customCursorActive={customCursorActive}
            nativeCursor={nativeHandlebarCursor}
            isSteering={isSteering}
            transitionDuration={HANDLEBAR_TRANSITION_DURATION}
        >
            <Handlebar
                className={HOVER_TARGET_CLASSNAME}
                width={DETAIL_VIEW_HANDLEBAR_WIDTH}
                height={DETAIL_VIEW_HANDLEBAR_HEIGHT}
                isSteering={isSteering}
                transitionDuration={HANDLEBAR_TRANSITION_DURATION}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onHandlebarMouseEnter,
                    onMouseLeave: onHandlebarMouseLeave,
                })}
            />
        </HandlebarContainer>
    );

    /**
     * Determines the height of the post editor
     */
    const postHeight = useMemo(() => {
        if (postEditorContainerRef.current) {
            const postEditorElement = postEditorContainerRef.current.children[0] as HTMLElement | undefined;
            // Auth transitions can briefly remount the editor wrapper before its first child exists.
            // Guard this read so conversion/sign-in rerenders cannot crash on Safari timing edges.
            if (!postEditorElement || typeof postEditorElement.getBoundingClientRect !== 'function') {
                return 0;
            }
            const postEditorHeight = postEditorElement.getBoundingClientRect().height + POST_CONTENT_MARGIN_TOP_WRITE;
            return postEditorHeight;
        }
        return 0;
    }, [
        postValueIsSaving,
        title,
        subtitle,
        postEditorContainerRef.current,
        remeasurePostHeight,
        annotationBucketWidth,
        annotationHighlightsRendered,
        post, // Necessary to rerender when change postValue via chapter or section change
    ]);

    const detailViewHeight = useMemo(() => {
        const minimumVisibleHeight = viewportDimensions.height;
        return Math.max(postHeight || 0, minimumVisibleHeight);
    }, [
        postHeight,
        viewportDimensions.height,
    ]);

    /**
     * When the annotation shelf is extended upward to cover the post-content top strip,
     * its own coordinate space shifts upward. Bucket tops are offset by this same amount
     * so annotations remain aligned to their source text.
     */
    const detailViewTopOffset = useMemo(() => (
        viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
            ? POST_CONTENT_MARGIN_TOP_WRITE
            : 0
    ), [
        viewportDimensions.width,
    ]);

    const getAnnotationParagraphTopWithinEditor = useCallback((element: HTMLElement): number => {
        const editorContainer = postEditorContainerRef.current;
        if (!editorContainer) return 0;
        const elementRect = element.getBoundingClientRect();
        const containerRect = editorContainer.getBoundingClientRect();
        return Math.max(elementRect.top - containerRect.top, 0);
    }, []);
    const canManageDetachedAnnotations = useMemo(() => (
        !!user
        && (
            userIsAdmin
            || postAuthorIds.includes(user.id)
        )
    ), [
        user,
        userIsAdmin,
        postAuthorIds,
    ]);
    const detachedAnnotationItems = useMemo(() => (
        Array.from(annotations.values()).filter((annotation) => isDetachedAnnotation(annotation))
    ), [
        annotations,
    ]);
    const detachedAnnotationIds = useMemo(() => (
        new Set(detachedAnnotationItems.map((annotation) => annotation.id))
    ), [
        detachedAnnotationItems,
    ]);
    const readerVisibleAnnotationItems = useMemo(() => (
        Array.from(annotations.values()).filter((annotation) => !shouldHideAnnotationFromReader(
            annotation,
            detachedAnnotationIds,
        ))
    ), [
        annotations,
        detachedAnnotationIds,
    ]);

    /**
     * Generates a list of annotations to render
     */
    const annotationItems = useMemo(() => (
        readerVisibleAnnotationItems
    ), [
        postHeight,
        readerVisibleAnnotationItems,
        annotationHighlightsRendered,
    ]);
    useEffect(() => {
        logReaderAnnotation('annotation-state', {
            annotationCount: annotationItems.length,
            detachedCount: detachedAnnotationItems.length,
            topLevelCount: annotationItems.filter((annotation) => !annotation.comment).length,
            commentCount: annotationItems.filter((annotation) => annotation.comment).length,
            sample: summarizeAnnotationsForDebug(annotationItems, 6),
        });
    }, [
        annotationItems,
        detachedAnnotationItems.length,
        logReaderAnnotation,
    ]);

    /**
     * Find annotation paragraphs
     * UNRESOLVED BUG: It may not be competent in locating deeply nested annotations
     */
    const annotationParagraphs = useMemo(() => {
        if (!postBodyPainted) {
            logReaderMemoTiming('annotationParagraphs', performance.now(), {
                annotationCount: annotationItems.length,
                paragraphCount: 0,
            });
            return [];
        }
        const startedAt = performance.now();
        const highlightElements: (HTMLElement | null)[] = [];
        annotationItems.forEach((annotation) => {
            const highlightElement: HTMLElement | null = document
                .querySelector(`[data-${ANNOTATION_DECORATION_PREFIX}${annotation.id}='${annotation.id}']`);
            if (highlightElement) {
                highlightElements.push(highlightElement);
            } else {
                highlightElements.push(null);
            }
        });

        const result = highlightElements.map((highlightElement: HTMLElement | null) => (highlightElement
            ? findParentNodeWithClass(
                SLATE_PARAGRAPH_CLASSNAME,
                highlightElement,
                10,
            ) : null
        ));
        logReaderMemoTiming('annotationParagraphs', startedAt, {
            annotationCount: annotationItems.length,
            paragraphCount: result.length,
        });
        return result;
    }, [
        annotationItems,
        annotationBucketWidth,
        annotationDomRefreshVersion,
        postBodyPainted,
        postBodyPaintVersion,
        logReaderMemoTiming,
    ]);

    /**
     * Stable DOM-backed paragraph lookup so optimistic annotations can join the
     * same buckets as settled annotations before highlight lookup catches up.
     */
    const renderedAnnotationParagraphMap = useMemo(() => {
        const map: Map<string, {
            index: number,
            element: HTMLElement,
        }> = new Map();
        if (!postBodyPainted) return map;

        const paragraphs = Array.from(
            (postEditorContainerRef.current || document)
                .querySelectorAll(`.${SLATE_PARAGRAPH_CLASSNAME}[data-paragraph-id]`),
        ) as HTMLElement[];

        paragraphs.forEach((paragraph, index) => {
            const paragraphId = `${paragraph.dataset.paragraphId || ''}`.trim();
            if (paragraphId && !map.has(paragraphId)) {
                map.set(paragraphId, {
                    index,
                    element: paragraph,
                });
            }
        });

        return map;
    }, [
        annotationDomRefreshVersion,
        postBodyPainted,
        postBodyPaintVersion,
        annotationBucketWidth,
    ]);

    /**
     * Pending optimistic top-level annotations that are not yet discoverable via
     * highlight/paragraph lookup.
     */
    const pendingRenderableAnnotationItemsWithoutParagraph = useMemo(() => (
        annotationItems.filter((annotation, index) => {
            if (annotation.comment) return false;
            if (!pendingAnnotationFallbackTops.has(annotation.id)) return false;
            if (annotationParagraphs[index]) return false;

            const provisionalParagraphId = pendingAnnotationProvisionalParagraphIds.get(annotation.id);
            if (!provisionalParagraphId) return true;

            // Keep the fallback bucket until the provisional paragraph can be
            // resolved against the currently rendered DOM. Without this, the
            // optimistic row can disappear after ack because we stop using the
            // fallback before the canonical row has a real paragraph anchor.
            return !renderedAnnotationParagraphMap.has(provisionalParagraphId);
        })
    ), [
        annotationItems,
        annotationParagraphs,
        pendingAnnotationFallbackTops,
        pendingAnnotationProvisionalParagraphIds,
        renderedAnnotationParagraphMap,
    ]);

    const hasRenderableAnnotationParagraphs = useMemo(() => (
        annotationParagraphs.some((paragraph) => !!paragraph?.dataset.paragraphId)
        || annotationItems.some((annotation) => (
            !annotation.comment
            && !!pendingAnnotationProvisionalParagraphIds.get(annotation.id)
        ))
    ), [
        annotationItems,
        annotationParagraphs,
        pendingAnnotationProvisionalParagraphIds,
    ]);

    const hasRenderableAnnotations = useMemo(() => (
        hasRenderableAnnotationParagraphs
        || pendingRenderableAnnotationItemsWithoutParagraph.length > 0
    ), [
        hasRenderableAnnotationParagraphs,
        pendingRenderableAnnotationItemsWithoutParagraph.length,
    ]);

    // Drop placement metadata only when the optimistic annotation itself is gone.
    // Keeping the provisional placement while the optimistic row is alive prevents
    // ack/reconcile races from making it disappear until another rerender happens.
    useEffect(() => {
        if (
            pendingAnnotationFallbackTops.size === 0
            && pendingAnnotationProvisionalParagraphIds.size === 0
        ) return;
        const annotationIds = new Set(annotationItems.map((annotation) => annotation.id));
        setPendingAnnotationFallbackTops((prev) => {
            const updated = new Map(prev);
            let changed = false;
            prev.forEach((_, annotationId) => {
                if (!annotationIds.has(annotationId)) {
                    updated.delete(annotationId);
                    changed = true;
                }
            });
            return changed ? updated : prev;
        });
        setPendingAnnotationProvisionalParagraphIds((prev) => {
            const updated = new Map(prev);
            let changed = false;
            prev.forEach((_, annotationId) => {
                if (!annotationIds.has(annotationId)) {
                    updated.delete(annotationId);
                    changed = true;
                }
            });
            return changed ? updated : prev;
        });
    }, [
        annotationItems,
        pendingAnnotationFallbackTops.size,
        pendingAnnotationProvisionalParagraphIds.size,
    ]);

    /**
     * Map of unique paragraphs associated with annotations
     */
    const paragraphMap = useMemo(() => {
        const startedAt = performance.now();
        // Create map of unique paragraphs
        const map: Map<string, {
            index: number,
            element: HTMLElement,
        }> = new Map();

        annotationParagraphs.forEach((paragraph, index) => {
            const paragraphId = paragraph?.dataset.paragraphId;
            if (paragraphId && !map.has(paragraphId)) {
                map.set(
                    paragraphId,
                    renderedAnnotationParagraphMap.get(paragraphId) || {
                        index,
                        element: paragraph,
                    },
                );
            }
        });

        annotationItems.forEach((annotation) => {
            if (annotation.comment) return;
            const provisionalParagraphId = pendingAnnotationProvisionalParagraphIds.get(annotation.id);
            if (!provisionalParagraphId || map.has(provisionalParagraphId)) return;
            const renderedParagraph = renderedAnnotationParagraphMap.get(provisionalParagraphId);
            if (!renderedParagraph) return;
            map.set(provisionalParagraphId, renderedParagraph);
        });

        logReaderMemoTiming('paragraphMap', startedAt, {
            paragraphCount: map.size,
        });
        return map;
    }, [
        annotationItems,
        annotationParagraphs,
        pendingAnnotationProvisionalParagraphIds,
        renderedAnnotationParagraphMap,
        logReaderMemoTiming,
    ]);

    /**
     * Determines the top position of each annotation paragraph
     */
    const paragraphPositionTopMap = useMemo(() => {
        const startedAt = performance.now();
        // Create map of unique paragraphs
        const map: Map<string, number> = new Map();
        Array.from(paragraphMap).forEach(([id, data]) => {
            if (!map.has(id)) {
                // Measure settled annotation buckets relative to the post
                // editor container itself, not the document/scroller.
                map.set(id, getAnnotationParagraphTopWithinEditor(data.element));
            }
        });

        logReaderMemoTiming('paragraphPositionTopMap', startedAt, {
            paragraphCount: map.size,
        });
        return map;
    }, [
        paragraphMap,
        annotationBucketWidth,
        getAnnotationParagraphTopWithinEditor,
        logReaderMemoTiming,
    ]);

    /**
     * Generates annotation paragraph groups
     */
    const paragraphBuckets = useMemo(() => {
        const startedAt = performance.now();
        // Bucket highlights into paragraphs
        const buckets: Map<string, IAnnotationItem[]> = new Map();
        annotationItems.forEach((annotation: IAnnotationItem, index: number) => {
            const paragraph = annotationParagraphs[index];
            const paragraphId = paragraph?.dataset.paragraphId
                || pendingAnnotationProvisionalParagraphIds.get(annotation.id);
            if (!paragraphId) {
                return;
            }

            const existingBucket = buckets.get(paragraphId);
            if (existingBucket) {
                existingBucket.push(annotation);
            } else {
                buckets.set(paragraphId, [annotation]);
            }
        });

        logReaderMemoTiming('paragraphBuckets', startedAt, {
            bucketCount: buckets.size,
            annotationCount: annotationItems.length,
        });
        return buckets;
    }, [
        annotationItems,
        annotationParagraphs,
        pendingAnnotationProvisionalParagraphIds,
        logReaderMemoTiming,
    ]);
    useEffect(() => {
        const missingParagraphAnnotationIds = annotationItems
            .filter((annotation, index) => {
                if (annotation.comment) return false;
                if (annotationParagraphs[index]?.dataset.paragraphId) return false;
                if (pendingAnnotationProvisionalParagraphIds.get(annotation.id)) return false;
                return true;
            })
            .slice(0, 10)
            .map((annotation) => annotation.id);

        logReaderAnnotation('annotation-render-summary', {
            annotationCount: annotationItems.length,
            detachedCount: detachedAnnotationItems.length,
            paragraphMatchCount: annotationParagraphs.filter((paragraph) => !!paragraph).length,
            paragraphIdMatchCount: annotationParagraphs.filter((paragraph) => !!paragraph?.dataset.paragraphId).length,
            renderedParagraphCount: renderedAnnotationParagraphMap.size,
            paragraphMapCount: paragraphMap.size,
            bucketCount: paragraphBuckets.size,
            pendingFallbackCount: pendingRenderableAnnotationItemsWithoutParagraph.length,
            pendingProvisionalCount: pendingAnnotationProvisionalParagraphIds.size,
            hasRenderableAnnotationParagraphs,
            hasRenderableAnnotations,
            annotationHighlightsRendered,
            postBodyPainted,
            missingParagraphAnnotationIds,
        });
    }, [
        annotationItems,
        detachedAnnotationItems.length,
        annotationParagraphs,
        renderedAnnotationParagraphMap,
        paragraphMap,
        paragraphBuckets,
        pendingRenderableAnnotationItemsWithoutParagraph,
        pendingAnnotationProvisionalParagraphIds,
        hasRenderableAnnotationParagraphs,
        hasRenderableAnnotations,
        annotationHighlightsRendered,
        postBodyPainted,
        logReaderAnnotation,
    ]);

    /**
     * Detached annotations no longer map to visible post text, so authors get a
     * dedicated repair lane instead of reusing the paragraph bucket layout.
     */
    const DetachedAnnotationsPanel = useMemo(() => {
        if (
            !canManageDetachedAnnotations
            || detachedAnnotationItems.length === 0
            || viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min
        ) {
            return null;
        }

        const detachedAnnotationWidth = annotationBucketWidth - DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH;
        /**
         * Once a detached annotation enters reattach mode, keep the repair lane
         * pinned in view so the author can scroll the post body without losing
         * the confirm/cancel controls.
         */
        const detachedAnnotationsPanelIsPinned = !!detachedAnnotationReattachTargetId;

        return (
            <DetachedAnnotationsPanelContainer
                ref={detachedAnnotationsPanelRef}
                isReattachActive={detachedAnnotationsPanelIsPinned}
                pinOffsetY={detachedAnnotationsPanelPinOffsetY}
                animateToPinnedCenter={detachedAnnotationsPanelAnimateToPinnedCenter}
                width={detachedAnnotationWidth}
            >
                <DetachedAnnotationsPanelHeader>
                    <DetachedAnnotationsPanelTitle>
                        Detached annotations
                    </DetachedAnnotationsPanelTitle>
                    <DetachedAnnotationsPanelDescription>
                        These annotations are hidden from readers until you reattach them to current post text.
                    </DetachedAnnotationsPanelDescription>
                </DetachedAnnotationsPanelHeader>
                <DetachedAnnotationsList
                    isReattachActive={detachedAnnotationsPanelIsPinned}
                >
                    {detachedAnnotationItems.map((annotation, index) => (
                        <Annotation
                            key={`detached-annotation-${annotation.id}`}
                            customCursorActive={customCursorActive}
                            annotation={annotation}
                            trackViewOnFocus={false}
                            enableRouteFocusSync
                            hasSound={hasSound}
                            user={user}
                            userIsAdmin={userIsAdmin}
                            userEmailVerified={userEmailVerified}
                            post={activeRootPost}
                            bucketWidth={detachedAnnotationWidth}
                            currentSessionId={currentSessionId}
                            annotationStackActive={false}
                            index={index}
                            horizontalMargin={0}
                            zIndex={detachedAnnotationItems.length - index}
                            annotationCount={detachedAnnotationItems.length}
                            hide={false}
                            interactionDisabled={false}
                            annotationStackExpanded={false}
                            bucketHeight={null}
                            viewportDimensions={viewportDimensions}
                            postAuthorIds={postAuthorIds}
                            postUserItems={postUserItems}
                            postUserAvatarURLs={postUserAvatarURLs}
                            annotationAudioURLs={annotationAudioURLs}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            setTargetAnnotationID={setTargetAnnotationID}
                            setCursorSigns={setCursorSigns}
                            showAnnotationDetail={onShowAnnotationDetail}
                            hideAnnotationDetail={onHideAnnotationDetail}
                            setSnackbarData={setSnackbarData}
                            clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                            selectedPostValuePath={selectedPostValuePath}
                            currentZoomLevel={currentZoomLevel}
                            setRefreshNotifications={setRefreshNotifications}
                            setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                            setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                            refreshAnnotation={refreshAnnotation}
                            addToPostUserItems={addToPostUserItems}
                            addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                            removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                            detachedAnnotationReattachTargetId={detachedAnnotationReattachTargetId}
                            detachedAnnotationReattachInFlightId={detachedAnnotationReattachInFlightId}
                            detachedAnnotationPendingReattachQuote={detachedAnnotationPendingQuote}
                            startDetachedAnnotationReattach={startDetachedAnnotationReattach}
                            confirmDetachedAnnotationReattach={confirmDetachedAnnotationReattach}
                            clearDetachedAnnotationPendingQuote={clearDetachedAnnotationPendingQuoteState}
                            cancelDetachedAnnotationReattach={cancelDetachedAnnotationReattach}
                        />
                    ))}
                </DetachedAnnotationsList>
            </DetachedAnnotationsPanelContainer>
        );
    }, [
        canManageDetachedAnnotations,
        detachedAnnotationItems,
        viewportDimensions,
        annotationBucketWidth,
        detachedAnnotationsPanelPinOffsetY,
        detachedAnnotationsPanelAnimateToPinnedCenter,
        customCursorActive,
        hasSound,
        user,
        userIsAdmin,
        userEmailVerified,
        activeRootPost,
        currentSessionId,
        postAuthorIds,
        postUserItems,
        postUserAvatarURLs,
        annotationAudioURLs,
        onCursorEnter,
        onCursorLeave,
        setInputFocused,
        setTargetAnnotationID,
        setCursorSigns,
        selectedPostValuePath,
        currentZoomLevel,
        detachedAnnotationReattachTargetId,
        detachedAnnotationReattachInFlightId,
        detachedAnnotationPendingQuote,
        startDetachedAnnotationReattach,
        confirmDetachedAnnotationReattach,
        clearDetachedAnnotationPendingQuoteState,
        cancelDetachedAnnotationReattach,
        logReaderAnnotation,
    ]);

    /**
     * Renderer for annotations
     * UNRESOLVED BUG: Some orderings of annotations (by when they are created) don't
     * cause annotation buckets to activate
     */
    const Annotations = useMemo(() => {
        const startedAt = performance.now();
        if (!hasRenderableAnnotations) {
            logReaderMemoTiming('Annotations', startedAt, {
                rendered: false,
            });
            return null;
        }

        const buckets = Array.from(paragraphMap.entries()).sort((a, b) => {
            if (a[1].index < b[1].index) return 1;
            if (a[1].index > b[1].index) return -1;
            return 0;
        });
        const postBannerHeightExpanded = POST_THUMBNAIL_HEIGHT_EXPANDED
            + 2 * POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED;
        const postBannerHeightContracted = POST_THUMBNAIL_HEIGHT_CONTRACTED
            + 2 * POST_BANNER_LEFT_CONTAINER_PADDING_CONTRACTED;
        const annotationTopOffset = detailViewTopOffset;

        const result = (
            <>
                {pendingRenderableAnnotationItemsWithoutParagraph.map((annotation) => (
                    <AnnotationBucket
                        key={`pending-annotation-fallback-${annotation.id}`}
                        customCursorActive={customCursorActive}
                        user={user}
                        userEmailVerified={userEmailVerified}
                        post={activeRootPost}
                        hasSound={hasSound}
                        currentSessionId={currentSessionId}
                        top={(pendingAnnotationFallbackTops.get(annotation.id) || 0) + annotationTopOffset}
                        width={viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
                            ? annotationBucketWidth - DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH
                            : ANNOTATION_VIEW_SMALL_VIEWPORT_WIDTH}
                        postBannerHeight={postScrollTop.current > stickyBannerScrollThreshold
                            ? postBannerHeightContracted
                            : (
                                ((postBannerHeightContracted - postBannerHeightExpanded)
                                    / stickyBannerScrollThreshold) * postScrollTop.current
                            ) + postBannerHeightExpanded}
                        nextBucketTop={null}
                        interactionDisabled={false}
                        annotations={[annotation]}
                        viewportDimensions={viewportDimensions}
                        postAuthorIds={postAuthorIds}
                        postUserItems={postUserItems}
                        postUserAvatarURLs={postUserAvatarURLs}
                        annotationAudioURLs={annotationAudioURLs}
                        expandAnnotationStackWithAnnotationId={expandAnnotationStackWithAnnotationId}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        setTargetAnnotationID={setTargetAnnotationID}
                        setCursorSigns={setCursorSigns}
                        showAnnotationDetail={onShowAnnotationDetail}
                        hideAnnotationDetail={onHideAnnotationDetail}
                        setSnackbarData={setSnackbarData}
                        clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                        showWarning={hitDetailMinimum || hitPostMinimum}
                        selectedPostValuePath={selectedPostValuePath}
                        currentZoomLevel={currentZoomLevel}
                        setRefreshNotifications={setRefreshNotifications}
                        setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                        setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                        removeAnnotationLocally={removeAnnotationLocally}
                        refreshAnnotation={refreshAnnotation}
                        enableRouteFocusSync={viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min}
                        addToPostUserItems={addToPostUserItems}
                        addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                        removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                        setExpandAnnotationStackWithAnnotationId={setExpandAnnotationStackWithAnnotationId}
                    />
                ))}
                {buckets.map(([id], index) => {
                    const nextBucketId = index + 1 < buckets.length ? buckets[buckets.length - index - 1][0] : null;
                    const bucketAnnotations = paragraphBuckets.get(id);
                    const bucketTop = paragraphPositionTopMap.get(id);
                    if (!bucketAnnotations || bucketAnnotations.length === 0) {
                        return null;
                    }
                    if (bucketTop == null) {
                        return null;
                    }
                    let nextBucketTop: number | null = null;
                    if (nextBucketId != null) {
                        const nextTop = paragraphPositionTopMap.get(nextBucketId);
                        nextBucketTop = nextTop == null ? null : nextTop + annotationTopOffset;
                    }
                    return (
                        <AnnotationBucket
                            key={id}
                            customCursorActive={customCursorActive}
                            user={user}
                            userEmailVerified={userEmailVerified}
                            post={activeRootPost}
                            hasSound={hasSound}
                            currentSessionId={currentSessionId}
                            top={bucketTop + annotationTopOffset}
                            width={viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min
                                ? annotationBucketWidth - DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH
                                : ANNOTATION_VIEW_SMALL_VIEWPORT_WIDTH}
                            postBannerHeight={postScrollTop.current > stickyBannerScrollThreshold
                                ? postBannerHeightContracted
                                : (
                                    ((postBannerHeightContracted - postBannerHeightExpanded)
                                        / stickyBannerScrollThreshold) * postScrollTop.current
                                ) + postBannerHeightExpanded}
                            nextBucketTop={nextBucketTop}
                            interactionDisabled={false}
                            annotations={bucketAnnotations}
                            viewportDimensions={viewportDimensions}
                            postAuthorIds={postAuthorIds}
                            postUserItems={postUserItems}
                            postUserAvatarURLs={postUserAvatarURLs}
                            annotationAudioURLs={annotationAudioURLs}
                            expandAnnotationStackWithAnnotationId={expandAnnotationStackWithAnnotationId}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            setTargetAnnotationID={setTargetAnnotationID}
                            setCursorSigns={setCursorSigns}
                            showAnnotationDetail={onShowAnnotationDetail}
                            hideAnnotationDetail={onHideAnnotationDetail}
                            setSnackbarData={setSnackbarData}
                            clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                            showWarning={hitDetailMinimum || hitPostMinimum}
                            selectedPostValuePath={selectedPostValuePath}
                            currentZoomLevel={currentZoomLevel}
                            setRefreshNotifications={setRefreshNotifications}
                            setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                            setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                            removeAnnotationLocally={removeAnnotationLocally}
                            refreshAnnotation={refreshAnnotation}
                            enableRouteFocusSync={viewportDimensions.width >= MEDIA_QUERY_SIZE.medium.min}
                            addToPostUserItems={addToPostUserItems}
                            addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                            removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                            setExpandAnnotationStackWithAnnotationId={setExpandAnnotationStackWithAnnotationId}
                        />
                    );
                })}
            </>
        );
        logReaderMemoTiming('Annotations', startedAt, {
            rendered: true,
            bucketCount: buckets.length,
            pendingCount: pendingRenderableAnnotationItemsWithoutParagraph.length,
        });
        return result;
    }, [
        paragraphMap, // Necessary to rerender with new annotations. Causes multiple rerenders
        paragraphBuckets, // Necessary to rerender with new annotations. Causes multiple rerenders
        hasRenderableAnnotations,
        pendingRenderableAnnotationItemsWithoutParagraph,
        pendingAnnotationFallbackTops,
        paragraphPositionTopMap, // Necessary to rerender with new annotations. Causes multiple rerenders
        detailViewTopOffset,
        postScrollTop.current, // Necessary to collapse expanded annotatin buckets when scrolling begins
        user, // Necessary to record user actions in annotation
        userEmailVerified,
        currentSessionId, // Necessary to record user actions in annotation
        annotationBucketWidth, // Necessary to adjust width of annotation buckets when resizing detail view
        viewportDimensions, // Necessary to adjust width of annotation buckets when resizing detail view
        hitDetailMinimum, // Necessary to change color of background when minimum is hit
        hitPostMinimum, // Necessary to change color of background when maximum is hit
        write,
        selectedPostValuePath,
        currentZoomLevel,
        postAuthorIds,
        // Instead of updating when postUserItems changes, we update when this changes to avoid torrent
        // of re-renders for each annotation
        updatingPostUserItems,
        clearingPostUserItemUpdateQueue,
        postUserAvatarURLs,
        // Instead of updating when annotationAudioURLs changes, we update when this changes to avoid torrent
        // of re-renders for each annotation
        updatingAnnotationAudioURLs,
        clearingAnnotationAudioURLUpdateQueue,
        expandAnnotationStackWithAnnotationId,
        customCursorActive,
        logReaderMemoTiming,
    ]);

    /**
     * Renderer for the annotation detail view
     */
    const SheetContent = useMemo(() => {
        const startedAt = performance.now();
        if (annotationDetailData) {
            const result = (
                <Annotation
                    key={annotationDetailData.id}
                    customCursorActive={customCursorActive}
                    annotation={annotationDetailData}
                    trackViewOnFocus={false}
                    enableRouteFocusSync
                    hasSound={hasSound}
                    user={user}
                    userIsAdmin={userIsAdmin}
                    userEmailVerified={userEmailVerified}
                    post={activeRootPost}
                    bucketWidth={viewportDimensions.width}
                    currentSessionId={currentSessionId}
                    annotationStackActive={false}
                    index={0}
                    horizontalMargin={0}
                    zIndex={0}
                    annotationCount={annotations.size}
                    hide={false}
                    interactionDisabled={false}
                    annotationStackExpanded={false}
                    bucketHeight={null}
                    viewportDimensions={viewportDimensions}
                    postAuthorIds={postAuthorIds}
                    postUserItems={postUserItems}
                    postUserAvatarURLs={postUserAvatarURLs}
                    annotationAudioURLs={annotationAudioURLs}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    setCursorSigns={setCursorSigns}
                    showAnnotationDetail={onShowAnnotationDetail}
                    hideAnnotationDetail={onHideAnnotationDetail}
                    setSnackbarData={setSnackbarData}
                    clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                    selectedPostValuePath={selectedPostValuePath}
                    currentZoomLevel={currentZoomLevel}
                    setRefreshNotifications={setRefreshNotifications}
                    setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                    setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                    refreshAnnotation={refreshAnnotation}
                    addToPostUserItems={addToPostUserItems}
                    addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                    removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                    detachedAnnotationReattachTargetId={detachedAnnotationReattachTargetId}
                    detachedAnnotationReattachInFlightId={detachedAnnotationReattachInFlightId}
                    detachedAnnotationPendingReattachQuote={detachedAnnotationPendingQuote}
                    startDetachedAnnotationReattach={startDetachedAnnotationReattach}
                    confirmDetachedAnnotationReattach={confirmDetachedAnnotationReattach}
                    clearDetachedAnnotationPendingQuote={clearDetachedAnnotationPendingQuoteState}
                    cancelDetachedAnnotationReattach={cancelDetachedAnnotationReattach}
                />
            );
            logReaderMemoTiming('SheetContent', startedAt, {
                hasAnnotationDetail: true,
                annotationId: annotationDetailData.id,
            });
            return result;
        }

        if (
            canManageDetachedAnnotations
            && detachedAnnotationItems.length > 0
            && viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min
        ) {
            const result = (
                <DetachedAnnotationsPanelContainer>
                    <DetachedAnnotationsPanelHeader>
                        <DetachedAnnotationsPanelTitle>
                            Detached annotations
                        </DetachedAnnotationsPanelTitle>
                        <DetachedAnnotationsPanelDescription>
                            These annotations are hidden from readers until you reattach them to current post text.
                        </DetachedAnnotationsPanelDescription>
                    </DetachedAnnotationsPanelHeader>
                    <DetachedAnnotationsList>
                        {detachedAnnotationItems.map((annotation, index) => (
                            <Annotation
                                key={`detached-annotation-mobile-${annotation.id}`}
                                customCursorActive={customCursorActive}
                                annotation={annotation}
                                trackViewOnFocus={false}
                                enableRouteFocusSync
                                hasSound={hasSound}
                                user={user}
                                userIsAdmin={userIsAdmin}
                                userEmailVerified={userEmailVerified}
                                post={activeRootPost}
                                bucketWidth={viewportDimensions.width}
                                currentSessionId={currentSessionId}
                                annotationStackActive={false}
                                index={index}
                                horizontalMargin={0}
                                zIndex={detachedAnnotationItems.length - index}
                                annotationCount={detachedAnnotationItems.length}
                                hide={false}
                                interactionDisabled={false}
                                annotationStackExpanded={false}
                                bucketHeight={null}
                                viewportDimensions={viewportDimensions}
                                postAuthorIds={postAuthorIds}
                                postUserItems={postUserItems}
                                postUserAvatarURLs={postUserAvatarURLs}
                                annotationAudioURLs={annotationAudioURLs}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setInputFocused={setInputFocused}
                                setTargetAnnotationID={setTargetAnnotationID}
                                setCursorSigns={setCursorSigns}
                                showAnnotationDetail={onShowAnnotationDetail}
                                hideAnnotationDetail={onHideAnnotationDetail}
                                setSnackbarData={setSnackbarData}
                                clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                                selectedPostValuePath={selectedPostValuePath}
                                currentZoomLevel={currentZoomLevel}
                                setRefreshNotifications={setRefreshNotifications}
                                setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                                setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                                refreshAnnotation={refreshAnnotation}
                                addToPostUserItems={addToPostUserItems}
                                addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                                removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                                detachedAnnotationReattachTargetId={detachedAnnotationReattachTargetId}
                                detachedAnnotationReattachInFlightId={detachedAnnotationReattachInFlightId}
                                detachedAnnotationPendingReattachQuote={detachedAnnotationPendingQuote}
                                startDetachedAnnotationReattach={startDetachedAnnotationReattach}
                                confirmDetachedAnnotationReattach={confirmDetachedAnnotationReattach}
                                clearDetachedAnnotationPendingQuote={clearDetachedAnnotationPendingQuoteState}
                                cancelDetachedAnnotationReattach={cancelDetachedAnnotationReattach}
                            />
                        ))}
                    </DetachedAnnotationsList>
                </DetachedAnnotationsPanelContainer>
            );
            logReaderMemoTiming('SheetContent', startedAt, {
                hasAnnotationDetail: false,
                detachedAnnotationCount: detachedAnnotationItems.length,
            });
            return result;
        }

        logReaderMemoTiming('SheetContent', startedAt, {
            hasAnnotationDetail: false,
        });
        return <div />;
    }, [
        activeRootPost,
        user,
        userIsAdmin,
        userEmailVerified,
        hasSound,
        annotations,
        currentSessionId,
        viewportDimensions,
        write,
        annotationDetailData,
        canManageDetachedAnnotations,
        detachedAnnotationItems,
        detachedAnnotationReattachTargetId,
        detachedAnnotationReattachInFlightId,
        detachedAnnotationPendingQuote,
        startDetachedAnnotationReattach,
        confirmDetachedAnnotationReattach,
        clearDetachedAnnotationPendingQuoteState,
        cancelDetachedAnnotationReattach,
        selectedPostValuePath,
        currentZoomLevel,
        postAuthorIds,
        // Instead of updating when postUserItems changes, we update when this changes to avoid torrent
        // of re-renders for each annotation
        updatingPostUserItems,
        clearingPostUserItemUpdateQueue,
        postUserAvatarURLs,
        // Instead of updating when annotationAudioURLs changes, we update when this changes to avoid torrent
        // of re-renders for each annotation
        updatingAnnotationAudioURLs,
        clearingAnnotationAudioURLUpdateQueue,
        customCursorActive,
        logReaderMemoTiming,
    ]);

    const annotationRenderDependency = useMemo(() => (
        readerVisibleAnnotationItems
            .map((annotation) => `${annotation.id}:${annotation.quoteHistory[0]?.id || ''}`)
            .join('|')
    ), [readerVisibleAnnotationItems]);

    useEffect(() => {
        if (!postBodyPainted) return undefined;
        // Annotation paragraph lookup runs in ReaderView, but the corresponding
        // Slate highlight DOM for a newly acked canonical annotation only exists
        // after PostEditor commits. Re-run the DOM-backed paragraph queries on
        // the next frame so canonical rows can appear immediately without
        // waiting for an unrelated viewport rerender.
        const frame = window.requestAnimationFrame(() => {
            setAnnotationDomRefreshVersion((prev) => prev + 1);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [
        annotationRenderDependency,
        postBodyPainted,
    ]);

    useEffect(() => {
        if (
            !postBodyPainted
            || postEditorRenderedBlockCount <= 0
        ) {
            return undefined;
        }

        /**
         * Progressive read-only loading appends new Slate blocks after the
         * initial annotation fetch. Refresh the DOM-backed paragraph lookup once
         * those blocks commit so newly mounted highlights can immediately join
         * the existing annotation shelf without re-fetching annotations.
         */
        const frame = window.requestAnimationFrame(() => {
            setAnnotationDomRefreshVersion((prev) => prev + 1);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [
        postBodyPainted,
        postEditorRenderedBlockCount,
    ]);

    /**
     * Merge backend contents-table counts with live in-memory annotation updates.
     * During navigation, annotations can be temporarily empty; we preserve last-known
     * chapter/section counts to prevent badge flicker.
     */
    const contentsTableWithLiveAnnotationCounts = useMemo(() => {
        const startedAt = performance.now();
        if (!contentsTable || !activeRootPost) {
            logReaderMemoTiming('contentsTableWithLiveAnnotationCounts', startedAt, {
                hasContentsTable: !!contentsTable,
                hasActiveRootPost: !!activeRootPost,
            });
            return contentsTable;
        }

        const chapterAnnotationCounts = new Map<string, number>();
        const sectionAnnotationCounts = new Map<string, number>();

        readerVisibleAnnotationItems.forEach((annotation) => {
            if (
                isAnnotationComment(annotation)
                || annotation.chapterId == null
            ) {
                return;
            }

            chapterAnnotationCounts.set(
                annotation.chapterId,
                (chapterAnnotationCounts.get(annotation.chapterId) || 0) + 1,
            );

            if (annotation.sectionId != null) {
                sectionAnnotationCounts.set(
                    annotation.sectionId,
                    (sectionAnnotationCounts.get(annotation.sectionId) || 0) + 1,
                );
            }
        });

        const mergedContentsTable = contentsTable.map((chapter) => {
            const chapterEngagement = effectivePostEngagementItems.get(buildPostScopeKey({
                postId: activeRootPost.id,
                scope: POST_CONTENT_SCOPE.chapter,
                scopeId: chapter.id,
            }));
            const chapterBaselineCount = chapterEngagement
                ? Math.max(
                    chapterEngagement.annotationCount
                    - chapterEngagement.commentCount
                    - chapterEngagement.deletedAnnotationCount,
                    0,
                )
                : chapter.annotationCount;
            const hasLiveChapterCount = chapterAnnotationCounts.has(chapter.id);
            const cachedChapterCount = cachedChapterAnnotationCountsRef.current.get(chapter.id);
            const mergedChapterCount = hasLiveChapterCount
                ? (chapterAnnotationCounts.get(chapter.id) || 0)
                : (cachedChapterCount ?? chapterBaselineCount);

            if (hasLiveChapterCount) {
                cachedChapterAnnotationCountsRef.current.set(chapter.id, mergedChapterCount);
            } else if (cachedChapterCount == null) {
                cachedChapterAnnotationCountsRef.current.set(chapter.id, chapterBaselineCount);
            }

            return {
                ...chapter,
                annotationCount: mergedChapterCount,
                ...(chapter.sections
                    ? {
                        sections: chapter.sections.map((section) => {
                            const sectionEngagement = effectivePostEngagementItems.get(buildPostScopeKey({
                                postId: activeRootPost.id,
                                scope: POST_CONTENT_SCOPE.section,
                                scopeId: section.id,
                            }));
                            const sectionBaselineCount = sectionEngagement
                                ? Math.max(
                                    sectionEngagement.annotationCount
                                    - sectionEngagement.commentCount
                                    - sectionEngagement.deletedAnnotationCount,
                                    0,
                                )
                                : section.annotationCount;
                            const hasLiveSectionCount = sectionAnnotationCounts.has(section.id);
                            const cachedSectionCount = cachedSectionAnnotationCountsRef.current.get(section.id);
                            const mergedSectionCount = hasLiveSectionCount
                                ? (sectionAnnotationCounts.get(section.id) || 0)
                                : (cachedSectionCount ?? sectionBaselineCount);

                            if (hasLiveSectionCount) {
                                cachedSectionAnnotationCountsRef.current.set(section.id, mergedSectionCount);
                            } else if (cachedSectionCount == null) {
                                cachedSectionAnnotationCountsRef.current.set(section.id, sectionBaselineCount);
                            }

                            return {
                                ...section,
                                annotationCount: mergedSectionCount,
                            };
                        }),
                    }
                    : {}),
            };
        });

        logReaderMemoTiming('contentsTableWithLiveAnnotationCounts', startedAt, {
            chapterCount: mergedContentsTable.length,
            annotationCount: readerVisibleAnnotationItems.length,
        });
        return mergedContentsTable;
    }, [
        contentsTable,
        activeRootPost,
        readerVisibleAnnotationItems,
        effectivePostEngagementItems,
        logReaderMemoTiming,
    ]);

    const effectiveRootPostEngagement = useMemo(() => {
        if (rootPostEngagement) return rootPostEngagement;
        if (!postId) return null;
        return effectivePostEngagementItems.get(buildPostScopeKey({
            postId,
            scope: POST_CONTENT_SCOPE.root,
            scopeId: postId,
        })) || null;
    }, [
        rootPostEngagement,
        effectivePostEngagementItems,
        postId,
    ]);
    const currentPostBannerScopeKey = useMemo(() => {
        if (!postId) return null;
        return buildPostScopeKey({
            postId,
            scope: POST_CONTENT_SCOPE.root,
            scopeId: postId,
        });
    }, [
        postId,
    ]);
    const rawPostBannerAnnotationCount = useMemo(() => (
        Math.max(
            (effectiveRootPostEngagement?.annotationCount || 0)
            - (effectiveRootPostEngagement?.commentCount || 0)
            - (effectiveRootPostEngagement?.deletedAnnotationCount || 0),
            0,
        )
    ), [
        effectiveRootPostEngagement?.annotationCount,
        effectiveRootPostEngagement?.commentCount,
        effectiveRootPostEngagement?.deletedAnnotationCount,
    ]);
    const postViewCount = useMemo(() => (
        effectiveRootPostEngagement?.viewCount ?? 0
    ), [
        effectiveRootPostEngagement?.viewCount,
    ]);
    const currentScopeUpdatedAt = useMemo(() => (
        resolveDraftStatusUpdatedAt({
            collabDocUpdatedAt: currentWriteCollaborativeDoc?.updatedAt ?? null,
            latestScopeUpdateEventUpdatedAt: latestCurrentScopeUpdateEvent?.updatedAt ?? null,
            engagementUpdatedAt: effectiveCurrentPostEngagement?.updatedAt ?? null,
            localSaveUpdatedAt: (
                currentScopeSaveStatusKey
                    ? localScopeSaveTimestamps[currentScopeSaveStatusKey]
                    : null
            ) ?? null,
        })
    ), [
        currentWriteCollaborativeDoc?.updatedAt,
        latestCurrentScopeUpdateEvent?.updatedAt,
        effectiveCurrentPostEngagement?.updatedAt,
        currentScopeSaveStatusKey,
        localScopeSaveTimestamps,
    ]);
    useEffect(() => {
        if (!write || currentScopeUpdatedAt == null) return undefined;

        const intervalId = window.setInterval(() => {
            setDraftStatusNow(Date.now());
        }, 30 * MILLISECONDS_IN_A_SECOND);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [
        write,
        currentScopeUpdatedAt,
    ]);
    /**
     * Renderer for post update text
     */
    const UpdateText = useMemo(() => {
        let notifText = '';
        if (postValueIsSaving || updatingPost) {
            notifText = 'Saving...';
        } else if (currentScopeUpdatedAt != null) {
            notifText = `Draft saved ${moment.utc(currentScopeUpdatedAt).fromNow()}`;
        }

        return (
            <Transition
                in={postValueIsSaving || currentScopeUpdatedAt != null}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {(state) => (
                    <PostUpdateTextContainer
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'up',
                                offset: 10,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'up',
                                offset: 10,
                            })[state],
                        }}
                    >
                        <EditorUpdateText>
                            {notifText}
                        </EditorUpdateText>
                    </PostUpdateTextContainer>
                )}
            </Transition>
        );
    }, [
        currentScopeUpdatedAt,
        draftStatusNow,
        postValueIsSaving,
        updatingPost,
    ]);

    useEffect(() => {
        // Reset banner cache when switching scopes so transient zeros don't clear a valid count.
        if (cachedPostBannerAnnotationPostIdRef.current !== currentPostBannerScopeKey) {
            cachedPostBannerAnnotationPostIdRef.current = currentPostBannerScopeKey;
            cachedPostBannerAnnotationCountRef.current = 0;
        }
        // Update cache only with non-zero values so transient zeroes don't erase it.
        if (rawPostBannerAnnotationCount > 0) {
            cachedPostBannerAnnotationCountRef.current = rawPostBannerAnnotationCount;
        }
    }, [
        currentPostBannerScopeKey,
        rawPostBannerAnnotationCount,
    ]);

    const postBannerAnnotationCount = useMemo(() => {
        // Use cached non-zero count to suppress transient drop-to-zero flicker.
        if (rawPostBannerAnnotationCount === 0 && cachedPostBannerAnnotationCountRef.current > 0) {
            return cachedPostBannerAnnotationCountRef.current;
        }
        return rawPostBannerAnnotationCount;
    }, [
        rawPostBannerAnnotationCount,
    ]);
    const resolvedPostAuthorIds = useMemo(() => (
        postAuthorIds.filter((authorId) => postUserItems.has(authorId))
    ), [
        postAuthorIds,
        postUserItems,
    ]);
    useEffect(() => {
        if (!detachedAnnotationReattachTargetId) {
            return;
        }

        const targetAnnotation = resolveAnnotationById(detachedAnnotationReattachTargetId);
        if (
            targetAnnotation
            && isDetachedAnnotation(targetAnnotation)
        ) {
            return;
        }

        clearDetachedAnnotationReattachState({ clearQuote: false });
    }, [
        detachedAnnotationReattachTargetId,
        resolveAnnotationById,
        clearDetachedAnnotationReattachState,
    ]);
    const hasVisibleAnnotationsForCurrentScope = useMemo(() => {
        const hasDetachedRepairItems = (
            canManageDetachedAnnotations
            && detachedAnnotationItems.length > 0
        );
        const liveVisibleTopLevelCount = readerVisibleAnnotationItems.filter((annotation) => (
            !isAnnotationComment(annotation)
        )).length;

        if (fetchedAnnotations) {
            return liveVisibleTopLevelCount > 0 || hasDetachedRepairItems;
        }

        return (
            (currentScopeVisibleAnnotationCount ?? 0) > 0
            || liveVisibleTopLevelCount > 0
            || hasDetachedRepairItems
        );
    }, [
        currentScopeVisibleAnnotationCount,
        fetchedAnnotations,
        readerVisibleAnnotationItems,
        canManageDetachedAnnotations,
        detachedAnnotationItems.length,
    ]);
    const currentWriteCollaborativeDocId = useMemo(() => {
        if (!activeRootPost) return undefined;
        if (!shouldUseWriteModeCollabDoc({
            target: currentPostContentTarget,
            currentPost: activeRootPost,
        })) {
            return undefined;
        }
        return currentPostContentTarget.docId;
    }, [
        activeRootPost,
        currentPostContentTarget,
        shouldUseWriteModeCollabDoc,
    ]);
    useEffect(() => {
        if (
            !write
            || !userIsAdmin
            || !currentWriteCollaborativeDocId
        ) {
            setCurrentWriteCollaborativeDoc(null);
            return undefined;
        }

        return subscribeToPostCollabDoc({
            test: useStripeTestMode,
            docId: currentWriteCollaborativeDocId,
            onNext: setCurrentWriteCollaborativeDoc,
        });
    }, [
        write,
        userIsAdmin,
        currentWriteCollaborativeDocId,
        useStripeTestMode,
    ]);
    const sortedCollaborativeParticipants = useMemo(() => {
        const nextParticipants = collaborativeParticipants.filter((participant) => !!participant.name);

        return nextParticipants.sort((participantA, participantB) => {
            const participantAIsCurrentUser = !!user?.id && participantA.userId === user.id;
            const participantBIsCurrentUser = !!user?.id && participantB.userId === user.id;

            if (participantAIsCurrentUser && !participantBIsCurrentUser) return -1;
            if (!participantAIsCurrentUser && participantBIsCurrentUser) return 1;

            return participantA.name.localeCompare(participantB.name);
        });
    }, [
        collaborativeParticipants,
        user?.id,
    ]);
    const CollaborativeParticipantsStrip = useMemo(() => {
        if (
            !write
            || !currentWriteCollaborativeDocId
            || sortedCollaborativeParticipants.length === 0
        ) {
            return null;
        }

        return (
            <CollaborativePresenceContainer>
                <CollaborativePresenceRow>
                    {sortedCollaborativeParticipants.map((participant) => {
                        const participantInitials = participant.name
                            .split(' ')
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((namePart) => namePart[0])
                            .join('');

                        return (
                            <CollaborativePresenceAvatarWrapper
                                key={participant.userId || participant.name}
                            >
                                <Tooltip
                                    text={participant.name}
                                    side={TOOLTIP_TYPE.top}
                                />
                                <CollaborativePresenceAvatarButton>
                                    {participant.photoURL ? (
                                        <UserAvatar
                                            url={participant.photoURL}
                                            length={1.625 * BODY_FONT_SIZE}
                                        />
                                    ) : (
                                        <CollaborativePresenceInitials>
                                            {participantInitials || '?'}
                                        </CollaborativePresenceInitials>
                                    )}
                                </CollaborativePresenceAvatarButton>
                            </CollaborativePresenceAvatarWrapper>
                        );
                    })}
                </CollaborativePresenceRow>
            </CollaborativePresenceContainer>
        );
    }, [
        write,
        currentWriteCollaborativeDocId,
        sortedCollaborativeParticipants,
    ]);
    const PublishingButton = useMemo(() => {
        if (
            !write
            || !userIsAdmin
            || !currentWriteCollaborativeDocId
        ) {
            return null;
        }

        return (
            <VersionHistoryButtonContainer>
                <Button
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    type={BUTTON_TYPE.floating}
                    text="Publishing"
                    background={themeObj.verascopeColor.purple200}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onButtonMouseEnter,
                        onMouseLeave: onButtonMouseLeave,
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: () => setPostPublishingVisible(true),
                        onMouseDown: () => setPostPublishingVisible(true),
                    })}
                />
            </VersionHistoryButtonContainer>
        );
    }, [
        write,
        userIsAdmin,
        currentWriteCollaborativeDocId,
        customCursorActive,
        onButtonMouseEnter,
        onButtonMouseLeave,
    ]);
    const AccessButton = useMemo(() => {
        if (
            !write
            || !userIsAdmin
            || !currentWriteCollaborativeDocId
        ) {
            return null;
        }

        return (
            <VersionHistoryButtonContainer>
                <Button
                    className={HOVER_TARGET_CLASSNAME}
                    customCursorActive={customCursorActive}
                    type={BUTTON_TYPE.floating}
                    text="Access"
                    background={themeObj.verascopeColor.purple200}
                    {...getTouchMouseEventHandlers({
                        onMouseEnter: onButtonMouseEnter,
                        onMouseLeave: onButtonMouseLeave,
                    })}
                    {...getPressEventHandlers({
                        onTouchStart: () => setPostAccessVisible(true),
                        onMouseDown: () => setPostAccessVisible(true),
                    })}
                />
            </VersionHistoryButtonContainer>
        );
    }, [
        write,
        userIsAdmin,
        currentWriteCollaborativeDocId,
        customCursorActive,
        onButtonMouseEnter,
        onButtonMouseLeave,
    ]);
    const PostPublishingDialogComponent = useMemo(() => (
        <PostPublishingDialog
            visible={postPublishingVisible}
            test={useStripeTestMode}
            docId={currentWriteCollaborativeDocId || undefined}
            post={activeRootPost || null}
            currentPostValue={currentPostValue}
            selectedPostValuePath={selectedPostValuePath}
            currentZoomLevel={currentZoomLevel}
            currentSessionId={currentSessionId || undefined}
            authUserId={auth.currentUser?.uid || null}
            user={user}
            userIsAdmin={userIsAdmin}
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            setSnackbarData={setSnackbarData}
            onCursorEnter={onCursorEnter}
            onCursorLeave={onCursorLeave}
            onSyncScopeAccessMetadata={syncLocalScopeAccessMetadata}
            onClose={() => setPostPublishingVisible(false)}
            onRestore={handleRestorePostVersion}
        />
    ), [
        postPublishingVisible,
        useStripeTestMode,
        currentWriteCollaborativeDocId,
        activeRootPost,
        currentPostValue,
        selectedPostValuePath,
        currentZoomLevel,
        currentSessionId,
        user,
        userIsAdmin,
        customCursorActive,
        hasSound,
        setSnackbarData,
        onCursorEnter,
        onCursorLeave,
        syncLocalScopeAccessMetadata,
        handleRestorePostVersion,
    ]);
    const PostAccessDialogComponent = useMemo(() => (
        <PostAccessDialog
            visible={postAccessVisible}
            test={useStripeTestMode}
            docId={currentWriteCollaborativeDocId || undefined}
            post={activeRootPost || null}
            currentPostValue={currentPostValue}
            selectedPostValuePath={selectedPostValuePath}
            currentZoomLevel={currentZoomLevel}
            currentSessionId={currentSessionId || undefined}
            user={user}
            userIsAdmin={userIsAdmin}
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            setSnackbarData={setSnackbarData}
            onCursorEnter={onCursorEnter}
            onCursorLeave={onCursorLeave}
            onSyncScopeAccessMetadata={syncLocalScopeAccessMetadata}
            onClose={() => setPostAccessVisible(false)}
            onRestore={handleRestorePostVersion}
        />
    ), [
        postAccessVisible,
        useStripeTestMode,
        currentWriteCollaborativeDocId,
        activeRootPost,
        currentPostValue,
        selectedPostValuePath,
        currentZoomLevel,
        currentSessionId,
        user,
        userIsAdmin,
        customCursorActive,
        hasSound,
        setSnackbarData,
        onCursorEnter,
        onCursorLeave,
        syncLocalScopeAccessMetadata,
        handleRestorePostVersion,
    ]);
    const BookAboutDialogComponent = useMemo(() => (
        <Modal
            hasContainer
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            width={BOOK_ABOUT_DIALOG_WIDTH}
            isOpen={bookAboutDialogIsVisible}
            color={themeObj.verascopeColor.purple200}
            closeModal={() => setBookAboutDialogIsVisible(false)}
        >
            <ModalContentsContainer data-testid="reader-book-about-dialog">
                <BookAboutDialogCloseButtonContainer data-testid="reader-book-about-dialog-close-button">
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.floating}
                        background={themeObj.verascopeColor.purple300}
                        height={2.5 * BODY_FONT_SIZE}
                        width={2.5 * BODY_FONT_SIZE}
                        icon={CrossIcon}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onModalCloseButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: handleBookAboutDialogCloseButton,
                            onMouseDown: handleBookAboutDialogCloseButton,
                        })}
                    />
                </BookAboutDialogCloseButtonContainer>
                <ModalHeader>
                    <ModalTitle>
                        About the Book
                    </ModalTitle>
                </ModalHeader>
                <BookAboutDialogBody data-testid="reader-book-about-dialog-body">
                    {BOOK_ABOUT_BLURB_PARAGRAPHS.map((paragraph) => (
                        <BookAboutDialogParagraph key={paragraph.id}>
                            {paragraph.content}
                        </BookAboutDialogParagraph>
                    ))}
                </BookAboutDialogBody>
            </ModalContentsContainer>
        </Modal>
    ), [
        BOOK_ABOUT_DIALOG_WIDTH,
        bookAboutDialogIsVisible,
        customCursorActive,
        handleBookAboutDialogCloseButton,
        hasSound,
        onButtonMouseLeave,
        onModalCloseButtonMouseEnter,
    ]);

    useEffect(() => {
        if (!write || !auth.currentUser) {
            if (postCollaborativeAuthToken !== undefined) {
                setPostCollaborativeAuthToken(undefined);
            }
            return;
        }

        let active = true;
        auth.currentUser.getIdToken().then((token) => {
            if (active) {
                setPostCollaborativeAuthToken(token);
            }
        }).catch(() => {
            if (active) {
                setPostCollaborativeAuthToken(undefined);
            }
        });

        return () => {
            active = false;
        };
    }, [
        write,
        auth.currentUser?.uid,
    ]);
    const disableProgressiveReadOnlyForResume = useMemo(() => (
        !write
        && forceFullReadOnlyRenderForResume
    ), [
        write,
        forceFullReadOnlyRenderForResume,
    ]);
    const readerAnnotationEditorRenderState = useMemo(() => {
        const editorRootPost = activeRootPost;
        let hasHydratedReadScope = !!editorRootPost?.id;
        if (selectedPostValuePath.length === 2) {
            hasHydratedReadScope = !!activeScopedPostChapterSection?.id;
        } else if (selectedPostValuePath.length === 1) {
            hasHydratedReadScope = !!activeScopedPostChapter?.id;
        }

        const hasCurrentPostValue = currentPostHasRenderableValue;
        const scopeUnlocked = (
            postLockedUntil == null
            || postLockedUntil < Date.now()
            || userIsAdmin
        );
        const canRenderPostEditor = canRenderReaderPostEditor({
            write,
            hasEditorRootPostId: !!editorRootPost?.id,
            hasHydratedScope: hasHydratedReadScope,
            hasCurrentPostValue,
            scopeUnlocked,
            postEditorDeleteTransitionInProgress,
        });

        return {
            editorRootPostId: editorRootPost?.id || null,
            hasHydratedReadScope,
            hasCurrentPostValue,
            scopeUnlocked,
            canRenderPostEditor,
        };
    }, [
        write,
        userIsAdmin,
        activeRootPost?.id,
        activeScopedPostChapter?.id,
        activeScopedPostChapterSection?.id,
        selectedPostValuePath,
        currentPostHasRenderableValue,
        postLockedUntil,
        postEditorDeleteTransitionInProgress,
    ]);
    useEffect(() => {
        logReaderAnnotation('annotation-editor-render-state', {
            readOnly: !write,
            editorRootPostId: readerAnnotationEditorRenderState.editorRootPostId,
            hasHydratedReadScope: readerAnnotationEditorRenderState.hasHydratedReadScope,
            hasCurrentPostValue: readerAnnotationEditorRenderState.hasCurrentPostValue,
            scopeUnlocked: readerAnnotationEditorRenderState.scopeUnlocked,
            canRenderPostEditor: readerAnnotationEditorRenderState.canRenderPostEditor,
            postEditorInitialized,
            postEditorReadOnlyChunkingIdle,
            postEditorReadOnlyFullRenderReady,
            showPostBodyContentSettled,
            postBodyPainted,
            annotationCount: annotations.size,
            selectedPostValuePath,
            currentPostValueSource: currentPostValueSource || null,
        });
    }, [
        write,
        readerAnnotationEditorRenderState,
        postEditorInitialized,
        postEditorReadOnlyChunkingIdle,
        postEditorReadOnlyFullRenderReady,
        showPostBodyContentSettled,
        postBodyPainted,
        annotations.size,
        selectedPostValuePath,
        currentPostValueSource,
        logReaderAnnotation,
    ]);

    /**
     * Renderer for the post editor
     */
    const PostEditorComponent = useMemo(() => {
        const startedAt = performance.now();
        const editorRootPost = activeRootPost;
        if (readerAnnotationEditorRenderState.canRenderPostEditor && editorRootPost?.id) {
            // Delete-driven scope changes temporarily remount the editor so
            // Slate does not try to restore selection into a removed scope.
            const postEditorKey = write
                ? `${currentWriteCollaborativeDocId || `${editorRootPost.id}:${selectedPostValuePath.join('-')}`}:${postEditorDeleteResetVersion}`
                // Full-render resume/auth-lock flows need a fresh read-only editor
                // instance so we don't carry the truncated progressive tree across
                // the mode switch.
                : `${editorRootPost.id}:${selectedPostValuePath.join('-')}:${user?.id || auth.currentUser?.uid || 'anon'}:${disableProgressiveReadOnlyForResume ? 'full' : 'progressive'}`;
            const result = (
                <PostEditor
                    key={postEditorKey}
                    customCursorActive={customCursorActive}
                    readOnly={!write}
                    readOnlyUsesPreviewSource={readOnlyAudioUsesRestrictedPreviewSource}
                    previewAudioEndPromptText={readOnlyPreviewAudioEndPromptText}
                    readOnlyChapterId={currentPostContentTarget.chapterId || undefined}
                    readOnlySectionId={currentPostContentTarget.sectionId || undefined}
                    user={user}
                    userIsAdmin={userIsAdmin}
                    isAuthor={!!user && !!editorRootPost.authors.includes(user.id)}
                    hasSound={hasSound}
                    currentSessionId={currentSessionId}
                    color={DEFAULT_EDITOR_COLOR}
                    id={editorRootPost.id}
                    type={EDITOR_CONTEXT_TYPE.post} // Used to know which storage bucket to save media
                    postValue={currentPostValue}
                    postValueNodes={currentPostValueNodes}
                    rootPostTitle={editorRootPost.title}
                    rootPostValue={editorRootPost.value}
                    rootPostValueNodes={editorRootPost.valueNodes}
                    rootPostPreviewValueNodes={editorRootPost.previewValueNodes}
                    headerTwoPartCountOverride={currentHeaderTwoPartCountOverride}
                    readOnlyTrustedValueHash={currentPostTrustedValueHash}
                    readOnlyTrustedValueVersion={currentPostTrustedValueVersion}
                    placeholder="Express your mind..."
                    showHeaderTwoPartMarkers
                    bufferHeight={POST_EDITOR_BUFFER_HEIGHT}
                    parentRef={postEditorContainerRef.current}
                    scrollParentRef={useDocumentScroll ? null : postContentContainerRef}
                    acceptedFiles={acceptedFiles}
                    uploadingMedia={uploadingMedia}
                    setUploadingMedia={addUploadingMedia}
                    updateUploadingMedia={updateUploadingMedia}
                    removeUploadingMedia={removeUploadingMedia}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    savePost={handlePostChange}
                    postValueIsSaving={postValueIsSaving}
                    setEditor={setPostEditor}
                    postQuote={postQuote}
                    annotations={Array.from(annotations.values())}
                    setAnnotations={setAnnotations}
                    chapters={editorRootPost.chapters}
                    setAnnotationHighlightsRendered={setAnnotationHighlightsRendered}
                    annotationHighlightsRendered={annotationHighlightsRendered}
                    targetAnnotationID={targetAnnotationID}
                    setRemeasurePostHeight={setRemeasurePostHeight}
                    setSnackbarData={setSnackbarData}
                    currentZoomLevel={currentZoomLevel}
                    hasSuccess={postFileDragFeedback.hasSuccess}
                    hasError={postFileDragFeedback.hasFailure}
                    selectedPostValuePath={selectedPostValuePath}
                    disableProgressiveReadOnly={disableProgressiveReadOnlyForResume}
                    disableMergedWriteRouting={write}
                    collaborativeDocId={currentWriteCollaborativeDocId}
                    collaborativeAuthToken={postCollaborativeAuthToken}
                    collaborativeTestMode={useStripeTestMode}
                    onCollaborativeParticipantsChange={setCollaborativeParticipants}
                    restorePostValueRequest={pendingPostVersionRestore || undefined}
                    onRestorePostValueApplied={handleRestorePostValueApplied}
                    unusedZoomLevelCount={unusedZoomLevelCount}
                    zoomLevelIcons={new Map(Array.from(adventureCharacters.values()).map((character) => [character.level, character.icon]))}
                    resizingAnnotationView={isSteering}
                    onInitializedValueChange={setPostEditorInitialized}
                    onBodyPainted={handlePostBodyPainted}
                    onReadOnlyChunkingIdleChange={setPostEditorReadOnlyChunkingIdle}
                    onReadOnlyRenderedBlockCountChange={setPostEditorRenderedBlockCount}
                    onReadOnlyParagraphCountChange={setCurrentPostParagraphCount}
                    onReadOnlyFullRenderReadyChange={setPostEditorReadOnlyFullRenderReady}
                    onReadOnlyAudioPlaybackStateChange={setReadOnlyAudioPlaybackState}
                    onNavigateInternalPostLink={handleNavigateInternalPostLink}
                    readOnlyRevealParagraphId={!write
                        ? pendingInternalPostLinkNavigation?.paragraphId || null
                        : null}
                    onReadOnlyRevealTargetResolutionChange={handleReadOnlyRevealTargetResolutionChange}
                    activeInternalPostLinkNavigationHref={activeInternalPostLinkNavigationHref}
                />
            );
            logReaderMemoTiming('PostEditorComponent', startedAt, {
                rendered: true,
                readOnly: !write,
                hasCurrentPostValue: readerAnnotationEditorRenderState.hasCurrentPostValue,
                hasHydratedReadScope: readerAnnotationEditorRenderState.hasHydratedReadScope,
                annotationCount: readerVisibleAnnotationItems.length,
            });
            return result;
        }

        logReaderMemoTiming('PostEditorComponent', startedAt, {
            rendered: false,
            readOnly: !write,
            hasCurrentPostValue: readerAnnotationEditorRenderState.hasCurrentPostValue,
            hasHydratedReadScope: readerAnnotationEditorRenderState.hasHydratedReadScope,
        });
        return null;
    }, [
        write,
        user,
        userIsAdmin,
        postId,
        activeRootPost,
        activeScopedPostChapter,
        activeScopedPostChapterSection,
        currentPostValue,
        currentPostValueNodes,
        currentHeaderTwoPartCountOverride,
        currentPostTrustedValueHash,
        currentPostTrustedValueVersion,
        readOnlyPreviewAudioEndPromptText,
        hasSound,
        currentSessionId,
        postEditorContainerRef.current,
        acceptedFiles,
        uploadingMedia,
        postValueIsSaving,
        readerVisibleAnnotationItems.length,
        handlePostBodyPainted,
        annotationRenderDependency,
        // we leave out annotationHighlightsRendered because it'll cause infinite loop
        // because PostEditor changes the value when it renders
        targetAnnotationID,
        params.annotationId,
        currentZoomLevel,
        postFileDragFeedback.hasSuccess,
        postFileDragFeedback.hasFailure,
        hitPostMinimum,
        hitDetailMinimum,
        selectedPostValuePath,
        currentWriteCollaborativeDocId,
        postEditorDeleteResetVersion,
        postEditorDeleteTransitionInProgress,
        postCollaborativeAuthToken,
        useStripeTestMode,
        auth.currentUser?.uid,
        pendingPostVersionRestore,
        handleRestorePostValueApplied,
        postQuote,
        unusedZoomLevelCount,
        isSteering,
        postLockedUntil,
        customCursorActive,
        disableProgressiveReadOnlyForResume,
        readerAnnotationEditorRenderState,
        handleNavigateInternalPostLink,
        handleReadOnlyRevealTargetResolutionChange,
        activeInternalPostLinkNavigationHref,
        pendingInternalPostLinkNavigation?.paragraphId,
        logReaderMemoTiming,
    ]);

    const postBodyReadyForRender = useMemo(() => {
        if (!activeRootPost?.id) {
            return false;
        }

        if (readBodyWaitingForUserHydration) {
            return false;
        }

        if (write) {
            return readerAnnotationEditorRenderState.canRenderPostEditor;
        }

        return (
            (
                currentPostHasRenderableValue
                || PostEditorComponent != null
                || (
                    currentContentAuthLocked
                    && !userIsSignedIn
                )
                || (
                    postLockedUntil != null
                    && postLockedUntil > Date.now()
                    && !userIsAdmin
                )
                || (
                    pageLoadRendersCompleted
                    && initialReadLayoutCommitted
                )
            )
            && (
                PostEditorComponent != null
                || (
                    currentContentAuthLocked
                    && !userIsSignedIn
                )
                || (
                    postLockedUntil != null
                    && postLockedUntil > Date.now()
                    && !userIsAdmin
                )
            )
        );
    }, [
        activeRootPost?.id,
        readBodyWaitingForUserHydration,
        write,
        readerAnnotationEditorRenderState.canRenderPostEditor,
        currentPostHasRenderableValue,
        PostEditorComponent,
        currentContentAuthLocked,
        userIsSignedIn,
        postLockedUntil,
        userIsAdmin,
        pageLoadRendersCompleted,
        initialReadLayoutCommitted,
    ]);

    const showLockedContainer = useMemo(() => (
        !!activeRootPost
        && !!activeRootPost.id
        && !readBodyWaitingForUserHydration
        && pageLoadRendersCompleted
        && postLockedUntil != null
        && postLockedUntil > Date.now()
        && !userIsAdmin
    ), [
        activeRootPost,
        readBodyWaitingForUserHydration,
        pageLoadRendersCompleted,
        postLockedUntil,
        userIsAdmin,
    ]);
    const preserveAuthLockBannerDuringPendingHandoff = useMemo(() => (
        !write
        && currentContentAuthLocked
        && (
            authLockSignInPendingBeforeUpgrade
            || (
                authLockAutoResumePendingRef.current
                && !authLockAutoResumeSawPostRefreshRef.current
            )
        )
        && !refreshingPost
    ), [
        write,
        currentContentAuthLocked,
        authLockSignInPendingBeforeUpgrade,
        refreshingPost,
        authLockAutoResumeTick,
    ]);
    const authLockBannerSuppressedDuringTransition = useMemo(() => (
        !write
        && currentContentAuthLocked
        && authLockAutoResumePendingRef.current
        && authLockAutoResumeSawPostRefreshRef.current
        // Keep the auth-lock banner visible through email validation and the
        // auth-state handoff. Suppress it only once the signed-in post refresh
        // is actually in flight.
        && refreshingPost
    ), [
        write,
        currentContentAuthLocked,
        refreshingPost,
        authLockAutoResumeTick,
    ]);
    const showAuthLockedContainer = useMemo(() => (
        !!activeRootPost
        && !!activeRootPost.id
        && readerAccessPromptsReady
        && !readBodyWaitingForUserHydration
        && !write
        && pageLoadRendersCompleted
        && (
            showAuthLocked
            || preserveAuthLockBannerDuringPendingHandoff
        )
        && !authLockBannerSuppressedDuringTransition
        && !showAccessUpgradePlaceholder
        && !showAccessDowngradePlaceholder
        && !userIsAdmin
        && (
            postLockedUntil == null
            || postLockedUntil < Date.now()
        )
    ), [
        activeRootPost,
        readerAccessPromptsReady,
        readBodyWaitingForUserHydration,
        write,
        pageLoadRendersCompleted,
        showAuthLocked,
        preserveAuthLockBannerDuringPendingHandoff,
        authLockBannerSuppressedDuringTransition,
        showAccessUpgradePlaceholder,
        showAccessDowngradePlaceholder,
        userIsAdmin,
        postLockedUntil,
    ]);
    useEffect(() => {
        // Do not overwrite the reader's saved position with the paywall/auth-lock
        // banner location; auto-resume should restore where they were reading.
        readerProgressPersistenceBlockedRef.current = (
            showAuthLockedContainer
            || showLockedContainer
            || showAccessUpgradePlaceholder
            || showAccessDowngradePlaceholder
            || authLockAutoResumePendingRef.current
        );
    }, [
        showAuthLockedContainer,
        showLockedContainer,
        showAccessUpgradePlaceholder,
        showAccessDowngradePlaceholder,
        authLockAutoResumeTick,
    ]);
    useEffect(() => {
        const node = authLockBannerRef.current;
        if (!node) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            handleAuthLockBannerVisibility(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            handleAuthLockBannerVisibility(!!entries[0]?.isIntersecting);
        }, { threshold: 0 });
        observer.observe(node);
        return () => {
            observer.disconnect();
        };
    }, [
        handleAuthLockBannerVisibility,
        showAuthLockedContainer,
        refreshingPost,
    ]);

    useEffect(() => {
        const node = paywallBannerRef.current;
        if (!node) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            handlePaywallBannerVisibility(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            handlePaywallBannerVisibility(!!entries[0]?.isIntersecting);
        }, { threshold: 0 });
        observer.observe(node);
        return () => {
            observer.disconnect();
        };
    }, [
        handlePaywallBannerVisibility,
        showPaywall,
        readerAccessPromptsReady,
        postBodyReadyForRender,
        refreshingPost,
        userIsAdmin,
    ]);

    useEffect(() => {
        const node = buyBannerRef.current;
        if (!node) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            handleBuyBannerVisibility(true);
            return undefined;
        }
        const observer = new IntersectionObserver((entries) => {
            handleBuyBannerVisibility(!!entries[0]?.isIntersecting);
        }, { threshold: 0 });
        observer.observe(node);
        return () => {
            observer.disconnect();
        };
    }, [
        handleBuyBannerVisibility,
        showBuyBanner,
        readerAccessPromptsReady,
        postBodyReadyForRender,
        refreshingPost,
        userIsAdmin,
    ]);
    const showPostBodyContent = useMemo(() => (
        postBodyReadyForRender
        && !showAccessUpgradePlaceholder
        && !showAccessDowngradePlaceholder
        && (
            !refreshingPost
            || write
        )
        && (
            (write && writeContentsTableRendered)
            || postEditorInitialized
            || showLockedContainer
            || showAuthLockedContainer
        )
    ), [
        postBodyReadyForRender,
        showAccessUpgradePlaceholder,
        showAccessDowngradePlaceholder,
        refreshingPost,
        write,
        writeContentsTableRendered,
        postEditorInitialized,
        showLockedContainer,
        showAuthLockedContainer,
    ]);
    const readPostBodyContentSettled = useMemo(() => (
        showPostBodyContent
        && (
            showLockedContainer
            || showAuthLockedContainer
            || (
                postEditorInitialized
                && postBodyPainted
            )
        )
    ), [
        showPostBodyContent,
        showLockedContainer,
        showAuthLockedContainer,
        postEditorInitialized,
        postBodyPainted,
    ]);
    useEffect(() => {
        const nextSettled = write
            ? showPostBodyContent
            : readPostBodyContentSettled;
        if (showPostBodyContentSettled !== nextSettled) {
            setShowPostBodyContentSettled(nextSettled);
        }
    }, [
        write,
        showPostBodyContent,
        readPostBodyContentSettled,
        showPostBodyContentSettled,
    ]);
    useEffect(() => () => {
        clearResumeReaderProgressEligibilityWait();
        clearResumeReaderProgressActionWait();
        clearResumeReaderProgressCorrectionTimeout();
        clearAuthLockAutoResumeRetryTimeout();
        if (authLockedStickyCtaLayoutWaitCancelRef.current) {
            authLockedStickyCtaLayoutWaitCancelRef.current();
            authLockedStickyCtaLayoutWaitCancelRef.current = null;
        }
        if (authLockedStickyCtaWaitFrameRef.current != null) {
            window.cancelAnimationFrame(authLockedStickyCtaWaitFrameRef.current);
            authLockedStickyCtaWaitFrameRef.current = null;
        }
    }, [
        clearResumeReaderProgressEligibilityWait,
        clearResumeReaderProgressActionWait,
        clearResumeReaderProgressCorrectionTimeout,
        clearAuthLockAutoResumeRetryTimeout,
    ]);

    useEffect(() => {
        const readerProgressPostId = currentReadPost?.id || post?.id || postId || null;
        const supportsRootReaderProgress = selectedPostValuePath.length === 0
            && ((currentReadPost?.chapters?.length || post?.chapters?.length || 0) === 0);
        const authLockAutoResumeInProgress = (
            authLockAutoResumePendingRef.current
            || resumeReaderProgressSourceRef.current === READER_RESUME_SOURCE.authLockAuto
            || pendingResumeAfterFullRenderSourceRef.current === READER_RESUME_SOURCE.authLockAuto
        );
        if (
            write
            || !showPostBodyContentSettled
            || !user
            // Respect profile preference: do not surface or apply restore CTA when disabled.
            || user.saveReaderProgress === false
            || !readerProgressPostId
            || (selectedPostValuePath.length === 0 && !supportsRootReaderProgress)
        ) {
            clearResumeReaderProgressEligibilityWait();
            setResumeReaderProgress(null);
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeSkippedMissingProgress, {
                reason: 'resume-cta-prerequisites-missing',
                write,
                showPostBodyContentSettled,
                hasUser: !!user,
                saveReaderProgress: user?.saveReaderProgress,
                postId: readerProgressPostId,
                selectedPostValuePath,
            });
            return;
        }
        if (authLockAutoResumeInProgress) {
            // Keep the ordinary floating resume CTA from reseeding progress while
            // auth-lock auto-resume is already coordinating the same restore.
            clearResumeReaderProgressEligibilityWait();
            return;
        }

        const key = buildReaderProgressKey({
            resolvedPostId: readerProgressPostId,
            path: selectedPostValuePath,
        });
        if (!key) {
            clearResumeReaderProgressEligibilityWait();
            setResumeReaderProgress(null);
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeSkippedMissingProgress, {
                reason: 'resume-cta-missing-key',
                postId: readerProgressPostId,
                selectedPostValuePath,
            });
            return;
        }

        // Prefer server progress and use local fallback only when not explicitly suppressed.
        const shouldUseLocalFallback = !(
            user.authenticationType === AUTHENTICATION_TYPE.anonymous
            && suppressAnonymousLocalResumeFallbackRef.current
        );
        const userSavedProgress = user.readerChapterProgress?.[key];
        const localSavedProgress = shouldUseLocalFallback ? getLocalReaderProgressByKey(key) : null;
        const savedProgress = isPersistedReaderProgressItem(userSavedProgress)
            ? userSavedProgress
            : localSavedProgress || undefined;
        if (
            !isPersistedReaderProgressItem(savedProgress)
        ) {
            clearResumeReaderProgressEligibilityWait();
            setResumeReaderProgress(null);
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeSkippedMissingProgress, {
                reason: 'resume-cta-missing-saved-progress',
                key,
                hasSavedProgress: !!userSavedProgress || !!localSavedProgress,
                savedScrollProgress: userSavedProgress?.scrollProgress ?? localSavedProgress?.scrollProgress,
            });
            return;
        }

        clearResumeReaderProgressEligibilityWait();
        if (savedProgress.scrollTop < READER_PROGRESS_RESUME_MIN_SCROLL_TOP) {
            setResumeReaderProgress(null);
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeSkippedMissingProgress, {
                reason: 'resume-cta-saved-scroll-too-small',
                key,
                savedScrollTop: savedProgress.scrollTop,
            });
            return;
        }

        const currentScrollTop = getReaderProgressCurrentScrollTop();
        // Once the reader has scrolled away from the top, treat that as an
        // intentional choice not to jump back to the saved position.
        if (currentScrollTop > READER_PROGRESS_RESUME_HIDE_SCROLL_THRESHOLD) {
            setResumeReaderProgress(null);
            logResumeFlow(READER_RESUME_LOG_EVENT.resumeSkippedMissingProgress, {
                reason: 'resume-cta-user-scrolled',
                key,
                currentScrollTop,
                savedScrollTop: savedProgress.scrollTop,
            });
            return;
        }

        setResumeReaderProgress({
            key,
            scrollTop: savedProgress.scrollTop,
            scrollProgress: savedProgress.scrollProgress,
            anchorParagraphId: savedProgress.anchorParagraphId,
            anchorParagraphIndex: savedProgress.anchorParagraphIndex,
            anchorParagraphOffsetProgress: savedProgress.anchorParagraphOffsetProgress,
        });
        logResumeFlow(READER_RESUME_LOG_EVENT.resumeTriggered, {
            source: READER_RESUME_SOURCE.unspecified,
            reason: 'resume-cta-visible',
            key,
            currentScrollTop,
            savedScrollTop: savedProgress.scrollTop,
            selectedPostValuePath,
        });

        return () => {
            clearResumeReaderProgressEligibilityWait();
        };
    }, [
        write,
        showPostBodyContentSettled,
        user?.id,
        user?.saveReaderProgress,
        user?.readerChapterProgress,
        currentReadPost?.id,
        currentReadPost?.chapters?.length,
        post?.id,
        post?.chapters?.length,
        selectedPostValuePath,
        authLockAutoResumeTick,
        buildReaderProgressKey,
        getLocalReaderProgressByKey,
        getReaderProgressCurrentScrollTop,
        isPersistedReaderProgressItem,
        clearResumeReaderProgressEligibilityWait,
        logResumeFlow,
        READER_PROGRESS_RESUME_HIDE_SCROLL_THRESHOLD,
        postId,
    ]);

    // Auth-lock auto-resume coordination:
    // 1) wait for authenticated user, 2) observe post refresh, 3) sync path, 4) trigger resume.
    useEffect(() => {
        if (
            !authLockAutoResumePendingRef.current
            || authLockAutoResumeAuthCompletedRef.current
            || !user
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
        ) {
            return;
        }
        // Start refresh gating only once conversion auth has actually completed.
        authLockAutoResumeAuthCompletedRef.current = true;
        notifyAuthLockAutoResumeTick();
    }, [
        authLockAutoResumeTick,
        user?.id,
        user?.authenticationType,
        notifyAuthLockAutoResumeTick,
    ]);

    // Phase 0a: if auth conversion triggers a post refresh, wait for that cycle
    // to finish. If no refresh ever starts, unblock immediately and let the later
    // content/path gates decide when auto-resume can actually proceed.
    useEffect(() => {
        if (
            !authLockAutoResumePendingRef.current
            || !authLockAutoResumeAwaitingPostRefreshRef.current
            || !authLockAutoResumeAuthCompletedRef.current
        ) {
            return undefined;
        }

        const requestAuthenticatedPostRefresh = (
            currentAuthUser: typeof auth.currentUser,
        ): void => {
            if (
                !currentAuthUser
                || currentAuthUser.isAnonymous
                || authLockAutoResumeRequestedPostRefreshRef.current
            ) {
                return;
            }

            authLockAutoResumeRequestedPostRefreshRef.current = true;
            currentAuthUser.getIdToken(true)
                .catch(() => undefined)
                .then(() => {
                    if (readerViewUnmountedRef.current) return;
                    setRefreshPost(true);
                });
            logResumeFlow('authLockAutoPostRefreshRequested', {
                selectedPostValuePath,
                refreshingPost,
            });
        };

        if (
            !refreshingPost
            && !authLockAutoResumeSawPostRefreshRef.current
            && !authLockAutoResumeRequestedPostRefreshRef.current
        ) {
            const liveSignedInAuthUser = getCurrentSignedInAuthUser();
            if (liveSignedInAuthUser) {
                requestAuthenticatedPostRefresh(liveSignedInAuthUser);
                return undefined;
            }

            if (typeof auth.onIdTokenChanged !== 'function') {
                return undefined;
            }

            const unsubscribe = auth.onIdTokenChanged((nextAuthUser) => {
                requestAuthenticatedPostRefresh(nextAuthUser);
            });
            return () => {
                unsubscribe();
            };
        }
        if (refreshingPost) {
            if (!authLockAutoResumeSawPostRefreshRef.current) {
                authLockAutoResumeSawPostRefreshRef.current = true;
                notifyAuthLockAutoResumeTick();
            }
            return undefined;
        }
        if (!authLockAutoResumeSawPostRefreshRef.current) {
            return undefined;
        }
        authLockAutoResumeAwaitingPostRefreshRef.current = false;
        logResumeFlow('authLockAutoPostRefreshReady', {
            sawRefresh: authLockAutoResumeSawPostRefreshRef.current,
            selectedPostValuePath,
            refreshingPost,
        });
        notifyAuthLockAutoResumeTick();
        return undefined;
    }, [
        authLockAutoResumeTick,
        refreshingPost,
        refreshPost,
        selectedPostValuePath,
        currentSignedInAuthUser,
        getCurrentSignedInAuthUser,
        notifyAuthLockAutoResumeTick,
        logResumeFlow,
    ]);

    // Phase 0b: if path switch triggered a second refresh, wait for that cycle as well.
    useEffect(() => {
        if (
            !authLockAutoResumePendingRef.current
            || !authLockAutoResumeAwaitingPathRefreshRef.current
        ) {
            return;
        }
        if (refreshingPost) {
            if (!authLockAutoResumeSawPathRefreshRef.current) {
                authLockAutoResumeSawPathRefreshRef.current = true;
                notifyAuthLockAutoResumeTick();
            }
            return;
        }
        if (!authLockAutoResumeSawPathRefreshRef.current) {
            return;
        }
        authLockAutoResumeAwaitingPathRefreshRef.current = false;
        notifyAuthLockAutoResumeTick();
        logResumeFlow(READER_RESUME_LOG_EVENT.authLockAutoPathRefreshComplete);
    }, [
        authLockAutoResumeTick,
        refreshingPost,
        notifyAuthLockAutoResumeTick,
        logResumeFlow,
    ]);

    useEffect(() => {
        if (
            authLockAutoResumePendingRef.current
            || !user
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
            || !currentReadPost?.id
        ) {
            return;
        }
        // If auth-lock sign-in crossed a reload/redirect boundary, recover pending target.
        const storedTarget = readAuthLockAutoResumeStoredTarget();
        if (!storedTarget || storedTarget.postId !== currentReadPost.id) {
            return;
        }
        authLockAutoResumeTargetRef.current = storedTarget;
        setAuthLockAutoResumePending(true);
    }, [
        authLockAutoResumeTick,
        user?.id,
        user?.authenticationType,
        currentReadPost?.id,
        readAuthLockAutoResumeStoredTarget,
        setAuthLockAutoResumePending,
    ]);

    // Phase 1: ensure the reader is on the same chapter/section path as the
    // stored auto-resume target before attempting any scroll restoration.
    useEffect(() => {
        if (
            !authLockAutoResumePendingRef.current
            || authLockAutoResumeAwaitingPostRefreshRef.current
            || authLockAutoResumeAwaitingPathRefreshRef.current
            || !user
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
        ) {
            return;
        }
        const autoResumeTarget = authLockAutoResumeTargetRef.current;
        if (!autoResumeTarget) {
            setAuthLockAutoResumePending(false);
            return;
        }
        const targetPathMatches = (
            autoResumeTarget.path.length === selectedPostValuePath.length
            && autoResumeTarget.path.every(
                (pathValue, index) => selectedPostValuePath[index] === pathValue,
            )
        );
        if (targetPathMatches) return;

        logResumeFlow(READER_RESUME_LOG_EVENT.authLockAutoPathSwitch, {
            fromPath: selectedPostValuePath,
            toPath: autoResumeTarget.path,
        });
        // Mark that a path-driven post refresh is expected after this path switch.
        authLockAutoResumeAwaitingPathRefreshRef.current = true;
        authLockAutoResumeSawPathRefreshRef.current = false;
        notifyAuthLockAutoResumeTick();
        onChangeSelectedPostValuePath({
            path: autoResumeTarget.path,
            expandItems: autoResumeTarget.path.length > 1
                ? [{
                    chapterIndex: autoResumeTarget.path[0],
                    expand: true,
                }]
                : undefined,
        });
    }, [
        authLockAutoResumeTick,
        refreshingPost,
        user?.id,
        user?.authenticationType,
        selectedPostValuePath,
        onChangeSelectedPostValuePath,
        setAuthLockAutoResumePending,
        notifyAuthLockAutoResumeTick,
    ]);

    // Phase 2: once path matches and content is available, seed/trigger resume.
    // If resume state is not yet available, seed it from the pending target first.
    useEffect(() => {
        if (
            !authLockAutoResumePendingRef.current
            || authLockAutoResumeAwaitingPostRefreshRef.current
            || !user
            || user.authenticationType === AUTHENTICATION_TYPE.anonymous
        ) {
            return;
        }
        const autoResumeTarget = authLockAutoResumeTargetRef.current;
        if (!autoResumeTarget) {
            setAuthLockAutoResumePending(false);
            return;
        }
        const targetPathMatches = (
            autoResumeTarget.path.length === selectedPostValuePath.length
            && autoResumeTarget.path.every(
                (pathValue, index) => selectedPostValuePath[index] === pathValue,
            )
        );
        const authLockAutoResumeContentReady = (
            showPostBodyContent
            && postEditorInitialized
            && postBodyPainted
            && authLockContentHydrated
        );
        if (
            !targetPathMatches
            || refreshingPost
            || !authLockAutoResumeContentReady
        ) {
            logResumeFlow('authLockAutoWaitingForResumePrereqs', {
                targetPathMatches,
                refreshingPost,
                showPostBodyContent,
                showPostBodyContentSettled,
                postEditorInitialized,
                postEditorReadOnlyChunkingIdle,
                postBodyPainted,
                authLockAutoResumeContentReady,
                authLockContentHydrated,
                hasCurrentPostValue: currentPostHasRenderableValue,
                currentPostValueLength: currentPostValue?.length,
                authLockContentHydrationDebug,
                selectedPostValuePath,
                targetPath: autoResumeTarget.path,
                hasResumeReaderProgress: !!resumeReaderProgress,
            });
            return;
        }
        if (!resumeReaderProgress) {
            // Seed resume state so downstream resume logic can run once UI/geometry stabilizes.
            const targetKey = buildReaderProgressKey({
                resolvedPostId: autoResumeTarget.postId,
                path: autoResumeTarget.path,
            });
            if (!targetKey) {
                setAuthLockAutoResumePending(false);
                return;
            }
            setResumeReaderProgress({
                key: targetKey,
                scrollTop: autoResumeTarget.progress.scrollTop,
                scrollProgress: autoResumeTarget.progress.scrollProgress,
                anchorParagraphId: autoResumeTarget.progress.anchorParagraphId,
                anchorParagraphIndex: autoResumeTarget.progress.anchorParagraphIndex,
                anchorParagraphOffsetProgress: autoResumeTarget.progress.anchorParagraphOffsetProgress,
            });
            logResumeFlow(READER_RESUME_LOG_EVENT.authLockAutoSeededResumeProgress, {
                key: targetKey,
                path: autoResumeTarget.path,
                source: autoResumeTarget.source,
            });
            return;
        }
        // Auth-lock conversion resume: once migrated user progress arrives, jump immediately.
        const fallbackResumeTarget = autoResumeTarget;
        setAuthLockAutoResumePending(false);
        triggerResumeReaderProgress(READER_RESUME_SOURCE.authLockAuto);
        // One bounded retry prevents rare no-op restores when the first trigger lands
        // before late layout work (e.g. image/content hydration) has fully settled.
        const retryAuthLockAutoResume = (attempt = 0): void => {
            authLockAutoResumeRetryTimeoutRef.current = window.requestAnimationFrame(() => {
                authLockAutoResumeRetryTimeoutRef.current = null;
                if (
                    !user
                    || user.authenticationType === AUTHENTICATION_TYPE.anonymous
                    || refreshingPost
                ) {
                    return;
                }
                if (
                    resumeReaderProgressRef.current
                    && resumeReaderProgressSourceRef.current === READER_RESUME_SOURCE.authLockAuto
                    && attempt < 48
                ) {
                    // The first auth-lock attempt has already seeded resume state,
                    // but it has not consumed that state into an actual scroll run yet.
                    // Let that in-flight attempt progress before replaying it.
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                if (
                    pendingResumeAfterFullRenderSourceRef.current === READER_RESUME_SOURCE.authLockAuto
                    && forceFullReadOnlyRenderForResume
                    && attempt < 48
                ) {
                    // The first auth-lock attempt has forced a full read-only render
                    // but has not yet entered its own resume scroll path. Let that
                    // handoff finish before the fallback decides it needs a replay.
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                if (
                    resumeReaderProgressAwaitingStabilityRef.current
                    && attempt < 48
                ) {
                    // Let the first auth-lock attempt finish its initial layout-stability
                    // wait before the fallback decides it needs to replay the restore.
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                if (
                    resumeReaderProgressAwaitingGeometryRef.current
                    && attempt < 48
                ) {
                    // Let the in-flight first attempt keep polling for geometry before
                    // the auth-lock fallback decides it needs to replay the restore.
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                if (
                    (
                        !postEditorReadOnlyChunkingIdle
                        || !postEditorReadOnlyFullRenderReady
                    )
                    && attempt < 48
                ) {
                    // Late read-only chunk release can still materially change geometry
                    // after auth-lock hydration. Let the first attempt wait for the
                    // editor's own readiness signals before a fallback replay judges it.
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                if (
                    authLockAutoResumeAppliedTargetRef.current
                    && resumeReaderProgressCorrectionTimeoutRef.current != null
                    && attempt < 48
                ) {
                    // The first auth-lock attempt has already applied a real target
                    // and queued its own correction pass. Let that pass absorb the
                    // immediate post-refresh layout flash before the fallback judges
                    // whether a second replay is necessary.
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                const fallbackTargetScrollTop = Math.round(
                    resolveSavedReaderProgressScrollTop(fallbackResumeTarget.progress),
                );
                const appliedTarget = authLockAutoResumeAppliedTargetRef.current;
                if (
                    appliedTarget
                    && appliedTarget.targetScrollMax >= READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                    && appliedTarget.targetScrollTop >= READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                ) {
                    // Once the first auth-lock attempt has applied against real scroll
                    // geometry, its own correction pass owns any late reflow fixes.
                    // A second auth-lock replay from the fallback path is redundant.
                    return;
                }
                const geometryUnavailable = (
                    fallbackResumeTarget.progress.scrollTop >= READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                    && fallbackTargetScrollTop < READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                    && getReaderProgressScrollMax() < READER_PROGRESS_RESUME_MIN_SCROLL_TOP
                );
                if (geometryUnavailable && attempt < 24) {
                    retryAuthLockAutoResume(attempt + 1);
                    return;
                }
                if (fallbackTargetScrollTop < READER_PROGRESS_RESUME_MIN_SCROLL_TOP) {
                    return;
                }
                const currentScrollTop = getReaderProgressCurrentScrollTop();
                const appliedScrollTop = authLockAutoResumeAppliedScrollTopRef.current;
                if (
                    appliedScrollTop != null
                    && Math.abs(currentScrollTop - appliedScrollTop)
                        >= READER_PROGRESS_RESUME_CORRECTION_MIN_SCROLL_DELTA
                ) {
                    // A long smooth restore is still progressing; only retry when the
                    // first auth-lock attempt appears to have done effectively nothing.
                    return;
                }
                if (
                    Math.abs(fallbackTargetScrollTop - currentScrollTop)
                    < READER_PROGRESS_RESUME_CORRECTION_MIN_SCROLL_DELTA
                ) {
                    return;
                }
                const fallbackTargetKey = buildReaderProgressKey({
                    resolvedPostId: fallbackResumeTarget.postId,
                    path: fallbackResumeTarget.path,
                });
                if (!fallbackTargetKey) return;
                setResumeReaderProgress({
                    key: fallbackTargetKey,
                    scrollTop: fallbackResumeTarget.progress.scrollTop,
                    scrollProgress: fallbackResumeTarget.progress.scrollProgress,
                    anchorParagraphId: fallbackResumeTarget.progress.anchorParagraphId,
                    anchorParagraphIndex: fallbackResumeTarget.progress.anchorParagraphIndex,
                    anchorParagraphOffsetProgress: fallbackResumeTarget.progress.anchorParagraphOffsetProgress,
                });
                logResumeFlow(READER_RESUME_LOG_EVENT.authLockAutoRetryTriggered, {
                    key: fallbackTargetKey,
                    path: fallbackResumeTarget.path,
                    source: fallbackResumeTarget.source,
                    currentScrollTop,
                    targetScrollTop: fallbackTargetScrollTop,
                });
                triggerResumeReaderProgress(READER_RESUME_SOURCE.authLockAuto);
            });
        };
        retryAuthLockAutoResume();
    }, [
        authLockAutoResumeTick,
        resumeReaderProgress,
        refreshingPost,
        user?.id,
        user?.authenticationType,
        selectedPostValuePath,
        showPostBodyContent,
        showPostBodyContentSettled,
        authLockContentHydrated,
        authLockContentHydrationDebug,
        currentPostValue,
        currentPostHasRenderableValue,
        postEditorInitialized,
        postEditorReadOnlyChunkingIdle,
        postEditorReadOnlyFullRenderReady,
        postBodyPainted,
        triggerResumeReaderProgress,
        getReaderProgressCurrentScrollTop,
        getReaderProgressScrollMax,
        resolveSavedReaderProgressScrollTop,
        READER_PROGRESS_RESUME_MIN_SCROLL_TOP,
        READER_PROGRESS_RESUME_CORRECTION_MIN_SCROLL_DELTA,
        buildReaderProgressKey,
        setAuthLockAutoResumePending,
        logResumeFlow,
    ]);

    const anyCartItemAnimating = useMemo(() => (
        animatingWebBookCartItem
        || animatingDigitalBookCartItem
        || animatingPhysicalBookCartItem
    ), [
        animatingWebBookCartItem,
        animatingDigitalBookCartItem,
        animatingPhysicalBookCartItem,
    ]);
    const readPostBodyContentForRender = !!(
        !showAccessUpgradePlaceholder
        && !showAccessDowngradePlaceholder
        && (
            showAuthLockedContainer
            || showLockedContainer
            || (
                showPostBodyContentSettled
                || (
                    currentPostHasRenderableValue
                    && postEditorInitialized
                    && showPostBodyContent
                )
            )
        )
    );
    const showPostBodyContentForRender = write
        ? showPostBodyContent
        : readPostBodyContentForRender;
    const stickyCtaBuyButtonsPendingPostData = !showPostBodyContentForRender;
    const authLockedStickyCtaLoading = stickyCtaBuyButtonsPendingPostData || authLockedStickyCtaButtonLoading;

    const buttonLoadingFromSource = useCallback((sourceId: string): boolean => (
        stickyCtaBuyButtonsPendingPostData
        || (
            anyCartItemAnimating
            && activeCartAnimationSourceId === sourceId
        )
    ), [
        stickyCtaBuyButtonsPendingPostData,
        anyCartItemAnimating,
        activeCartAnimationSourceId,
    ]);
    const buttonDisabledFromSource = useCallback((sourceId: string): boolean => (
        stickyCtaBuyButtonsPendingPostData
        || (
            anyCartItemAnimating
            && activeCartAnimationSourceId !== sourceId
        )
    ), [
        stickyCtaBuyButtonsPendingPostData,
        anyCartItemAnimating,
        activeCartAnimationSourceId,
    ]);

    const lockedSubheaderText = useMemo(() => {
        if (
            postLockedUntil == null
            || isLockedUntilIndefinite(postLockedUntil)
        ) {
            return '';
        }

        const releaseLabel = formatReleaseTimestamp(postLockedUntil);

        return releaseLabel.isExactDate
            ? `Releases ${releaseLabel.text}`
            : `Releases in ${releaseLabel.text}`;
    }, [
        postLockedUntil,
    ]);

    /**
     * Renderer for the zoom level buttons
     */
    const ZoomLevelButtons = useMemo(() => {
        const buttons: {
            level: ZOOM_LEVEL,
            icon: string,
            text: string,
        }[] = [];
        adventureCharacters.forEach((character) => {
            if (
                write
                || character.level - unusedZoomLevelCount > 0
            ) {
                let duration = '';
                if (character.duration / MILLISECONDS_IN_AN_HOUR > 1) {
                    duration += `${Math.floor(character.duration / MILLISECONDS_IN_AN_HOUR)}hr`;
                } else if (character.duration / MILLISECONDS_IN_A_MINUTE > 1) {
                    duration += `${Math.floor(character.duration / MILLISECONDS_IN_A_MINUTE)}m`;
                } else {
                    duration += `${Math.floor(character.duration / MILLISECONDS_IN_A_SECOND)}s`;
                }
                buttons.push({
                    level: character.level,
                    icon: character.icon,
                    text: `${character.level - unusedZoomLevelCount}.  ${character.name}  (${duration})`,
                });
            }
        });

        if (buttons.length === 0) {
            return <div />;
        }

        return buttons.map((zoomLevel) => (
            <Button
                key={zoomLevel.level}
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                type={currentZoomLevel
                && currentZoomLevel.level === zoomLevel.level
                    ? BUTTON_TYPE.solid
                    : BUTTON_TYPE.secret}
                height={ZOOM_LEVEL_ITEM_HEIGHT}
                width={ZOOM_LEVEL_ITEM_WIDTH}
                icon={zoomLevel.icon}
                text={zoomLevel.text}
                {...(currentZoomLevel
                    && currentZoomLevel.level === zoomLevel.level
                    ? {
                        background: setColorLightness(
                            DEFAULT_EDITOR_COLOR,
                            BUTTON_CONTAINER_LIGHTNESS_VALUE,
                        ),
                    } : {})}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: onButtonMouseEnter,
                    onMouseLeave: onButtonMouseLeave,
                })}
                {...getPressEventHandlers({
                    onTouchStart: () => handleZoomLevel(zoomLevel.level),
                    onMouseDown: () => handleZoomLevel(zoomLevel.level),
                })}
            />
        ));
    }, [
        write,
        currentZoomLevel,
        unusedZoomLevelCount,
        customCursorActive,
    ]);

    const CharacterModal = useMemo(() => (
        <Modal
            hasContainer
            overrideClickOverlay
            customCursorActive={customCursorActive}
            hasSound={hasSound}
            width={CHARACTER_MODAL_WIDTH}
            isOpen={characterModalIsVisible}
            color={themeObj.verascopeColor.purple200}
            closeModal={() => setCharacterModalIsVisible(false)}
        >
            <ModalContentsContainer>
                <ModalHeader>
                    <ModalTitle>
                        Choose Your Own Adventure
                    </ModalTitle>
                    <ModalSubtitle>
                        Customize your reading experience by selecting a character whose
                        desired level of detail matches your own.
                    </ModalSubtitle>
                </ModalHeader>
                <ModalBody>
                    {Array.from(adventureCharacters.values())
                        .filter((character) => character.level - unusedZoomLevelCount > 0)
                        .map((character) => {
                            let duration = 'est. ';
                            if (character.duration / MILLISECONDS_IN_AN_HOUR > 1) {
                                duration += `${Math.floor(character.duration / MILLISECONDS_IN_AN_HOUR)} hours`;
                            } else if (character.duration / MILLISECONDS_IN_A_MINUTE > 1) {
                                duration += `${Math.floor(character.duration / MILLISECONDS_IN_A_MINUTE)} minutes`;
                            } else {
                                duration += `${Math.floor(character.duration / MILLISECONDS_IN_A_SECOND)} seconds`;
                            }
                            return (
                                <AdventureCharacterContainer
                                    key={character.name}
                                    className={HOVER_TARGET_CLASSNAME}
                                    background={character.background}
                                    zoomHoverColor={character.zoomHoverColor}
                                    transitionDuration={ADVENTURE_CHARACTER_TRANSITION_DURATION}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: (e) => {
                                            onButtonMouseLeave();
                                            onButtonMouseEnter(e);

                                            // Play Sound
                                            if (hasSound && buttonHoverClip.current) {
                                                buttonHoverClip.current.pause();
                                                buttonHoverClip.current.currentTime = 0;
                                                playAudio(buttonHoverClip.current);
                                            }
                                        },
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({
                                        onTouchStart: () => handleSelectAdventureCharacter(
                                            character.id,
                                            character.icon,
                                        ),
                                        onMouseDown: () => handleSelectAdventureCharacter(
                                            character.id,
                                            character.icon,
                                        ),
                                    })}
                                >
                                    <AdventureCharacterAvatarContainer
                                        background={character.background}
                                        transitionDuration={ADVENTURE_CHARACTER_TRANSITION_DURATION}
                                    >
                                        <AdventureCharacterAvatar
                                            src={character.icon}
                                        />
                                    </AdventureCharacterAvatarContainer>
                                    <AdventureCharacterName
                                        color={character.nameColor}
                                    >
                                        {character.name}
                                    </AdventureCharacterName>
                                    <AdventureCharacterZoomLevel
                                        color={character.zoomColor}
                                        transitionDuration={ADVENTURE_CHARACTER_TRANSITION_DURATION}
                                    >
                                        {duration}
                                    </AdventureCharacterZoomLevel>
                                </AdventureCharacterContainer>
                            );
                        })}
                </ModalBody>
            </ModalContentsContainer>
        </Modal>
    ), [
        hasSound,
        characterModalIsVisible,
        unusedZoomLevelCount,
        customCursorActive,
    ]);

    useLayoutEffect(() => {
        if (!showStickyPurchaseCta) return;
        const measure = (): void => {
            const collapsedHeight = Math.max(
                stickyPurchaseHeaderRef.current?.scrollHeight || 0,
                1,
            );
            const contentHeight = Math.max(
                stickyPurchaseContentRef.current?.scrollHeight || 0,
                0,
            );
            const expandedHeight = Math.max(
                collapsedHeight + contentHeight,
                collapsedHeight,
            );
            setStickyPurchaseMeasuredHeights((prev) => {
                if (
                    prev.collapsed === collapsedHeight
                    && prev.expanded === expandedHeight
                ) {
                    return prev;
                }
                return {
                    collapsed: collapsedHeight,
                    expanded: expandedHeight,
                };
            });
        };

        measure();
        const rafId = window.requestAnimationFrame(measure);
        return () => window.cancelAnimationFrame(rafId);
    }, [
        showStickyPurchaseCta,
        purchasePanelExpanded,
        purchaseMoreOptionsExpanded,
        viewportDimensions.width,
        post?.title,
    ]);

    const CompactPurchaseRows: {
        key: string,
        node: JSX.Element,
    }[] = [
    ];
    const STICKY_CTA_SOURCE = {
        fullWebAuthAnon: 'sticky-cta-full-web-auth-anon',
        physicalAuthAnon: 'sticky-cta-physical-auth-anon',
        fullWebAuthSignedIn: 'sticky-cta-full-web-auth-signed-in',
        bingeAuthSignedIn: 'sticky-cta-binge-auth-signed-in',
        physicalAuthSignedIn: 'sticky-cta-physical-auth-signed-in',
        primary: 'sticky-cta-primary',
        binge: 'sticky-cta-binge',
        tertiary: 'sticky-cta-tertiary',
        paywalled: 'sticky-cta-paywalled',
        dayPass: 'sticky-cta-day-pass',
        weekPassAuthAnon: 'sticky-cta-week-pass-auth-anon',
        chapter: 'sticky-cta-chapter',
        pdf: 'sticky-cta-pdf',
        audio: 'sticky-cta-audio',
    } as const;
    const compactCtaTwoSourceId = isPaywalledStickyCta
        ? STICKY_CTA_SOURCE.tertiary
        : STICKY_CTA_SOURCE.binge;
    const compactCtaThreeSourceId = isPaywalledStickyCta
        ? STICKY_CTA_SOURCE.paywalled
        : STICKY_CTA_SOURCE.tertiary;
    const isAuthLockedAnonymousStickyState = currentContentAuthLocked && !userIsSignedIn;
    const isAuthLockedStickyState = currentContentAuthLocked;

    if (isAuthLockedStickyState) {
        if (isAuthLockedAnonymousStickyState) {
            CompactPurchaseRows.push({
                key: 'cta-share-email',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={COMPACT_PURCHASE_CTA_HEIGHT}
                            width={compactPurchaseButtonWidth}
                            text="Read Free"
                            background={themeObj.verascopeColor.yellow100}
                            loading={authLockedStickyCtaLoading}
                            disabled={authLockedStickyCtaLoading}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => handleShareEmailFromStickyCta(),
                                onMouseDown: () => handleShareEmailFromStickyCta(),
                            })}
                        />
                        <StickyPurchaseChoiceCopy>
                            Join our mailing list to unlock this full chapter for free.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
            CompactPurchaseRows.push({
                key: 'cta-full-web-book-authlocked-anon',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={COMPACT_PURCHASE_CTA_HEIGHT}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('Full Web Book', WEB_BOOK_PRODUCT_ID)}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.fullWebAuthAnon)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.fullWebAuthAnon)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.web, STICKY_CTA_SOURCE.fullWebAuthAnon, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.web, STICKY_CTA_SOURCE.fullWebAuthAnon, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.fullWebAuthAnon && WebBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            Access forever. Includes all released and upcoming chapters. Consider adding extra copies to gift to others at checkout.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
            CompactPurchaseRows.push({
                key: 'cta-physical-authlocked-anon',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={COMPACT_PURCHASE_CTA_HEIGHT}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('Get Physical Edition', PHYSICAL_BOOK_PREORDER_PRODUCT_ID)}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.physicalAuthAnon)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.physicalAuthAnon)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.physical, STICKY_CTA_SOURCE.physicalAuthAnon, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.physical, STICKY_CTA_SOURCE.physicalAuthAnon, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.physicalAuthAnon && PhysicalBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            {(
                                'Preorder the print book (ships Q4 2026). Includes full web book access. '
                                + 'Buying multiple? Gift complimentary web books to others at checkout.'
                            )}
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
        } else {
            CompactPurchaseRows.push({
                key: 'cta-full-web-book-authlocked-signedin',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={COMPACT_PURCHASE_CTA_HEIGHT}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('Full Web Book', WEB_BOOK_PRODUCT_ID)}
                            background={themeObj.verascopeColor.yellow100}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.fullWebAuthSignedIn)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.fullWebAuthSignedIn)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.web, STICKY_CTA_SOURCE.fullWebAuthSignedIn, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.web, STICKY_CTA_SOURCE.fullWebAuthSignedIn, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.fullWebAuthSignedIn && WebBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            Access forever. Includes all released and upcoming chapters. Consider adding extra copies to gift to others at checkout.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
            CompactPurchaseRows.push({
                key: 'cta-binge-authlocked-signedin',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={COMPACT_PURCHASE_CTA_HEIGHT}
                            width={compactPurchaseButtonWidth}
                            text={compactCtaTwoText}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.bingeAuthSignedIn)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.bingeAuthSignedIn)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(compactCtaTwoType, STICKY_CTA_SOURCE.bingeAuthSignedIn, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(compactCtaTwoType, STICKY_CTA_SOURCE.bingeAuthSignedIn, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.bingeAuthSignedIn && WebBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            Best for catching up: 7-day access to all released chapters.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
            CompactPurchaseRows.push({
                key: 'cta-physical-authlocked-signedin',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={COMPACT_PURCHASE_CTA_HEIGHT}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('Get Physical Edition', PHYSICAL_BOOK_PREORDER_PRODUCT_ID)}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.physicalAuthSignedIn)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.physicalAuthSignedIn)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.physical, STICKY_CTA_SOURCE.physicalAuthSignedIn, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.physical, STICKY_CTA_SOURCE.physicalAuthSignedIn, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.physicalAuthSignedIn && PhysicalBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            {(
                                'Preorder the print book (ships Q4 2026). Includes full web book access. '
                                + 'Buying multiple? Gift complimentary web books to others at checkout.'
                            )}
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
        }
    } else {
        CompactPurchaseRows.push({
            key: 'cta-primary',
            node: (
                <StickyPurchaseChoiceRow>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        center
                        height={COMPACT_PURCHASE_CTA_HEIGHT}
                        width={compactPurchaseButtonWidth}
                        text={compactCtaOneText}
                        background={themeObj.verascopeColor.yellow100}
                        loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.primary)}
                        disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.primary)}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => handleCompactPurchaseSelect(compactCtaOneType, STICKY_CTA_SOURCE.primary, e),
                            onMouseDown: (e) => handleCompactPurchaseSelect(compactCtaOneType, STICKY_CTA_SOURCE.primary, e),
                        })}
                    />
                    {activeCartAnimationSourceId === STICKY_CTA_SOURCE.primary && WebBookAnimationItems}
                    <StickyPurchaseChoiceCopy>
                        {compactCtaOneIsChapter
                            ? 'Permanent access to this chapter only.'
                            : 'Access forever. Includes all released and upcoming chapters. Consider adding extra copies to gift to others at checkout.'}
                    </StickyPurchaseChoiceCopy>
                </StickyPurchaseChoiceRow>
            ),
        });
        CompactPurchaseRows.push({
            key: 'cta-binge',
            node: (
                <StickyPurchaseChoiceRow>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        center
                        height={COMPACT_PURCHASE_CTA_HEIGHT}
                        width={compactPurchaseButtonWidth}
                        text={compactCtaTwoText}
                        background={themeObj.verascopeColor.purple400}
                        loading={buttonLoadingFromSource(compactCtaTwoSourceId)}
                        disabled={buttonDisabledFromSource(compactCtaTwoSourceId)}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => handleCompactPurchaseSelect(compactCtaTwoType, compactCtaTwoSourceId, e),
                            onMouseDown: (e) => handleCompactPurchaseSelect(compactCtaTwoType, compactCtaTwoSourceId, e),
                        })}
                    />
                    {activeCartAnimationSourceId === compactCtaTwoSourceId && WebBookAnimationItems}
                    <StickyPurchaseChoiceCopy>
                        {isPaywalledStickyCta
                            ? 'Access forever. Includes all released and upcoming chapters. Consider adding extra copies to gift to others at checkout.'
                            : 'Best for catching up: 7-day access to all released chapters.'}
                    </StickyPurchaseChoiceCopy>
                </StickyPurchaseChoiceRow>
            ),
        });
        CompactPurchaseRows.push({
            key: 'cta-tertiary',
            node: (
                <StickyPurchaseChoiceRow>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        center
                        height={COMPACT_PURCHASE_CTA_HEIGHT}
                        width={compactPurchaseButtonWidth}
                        text={compactCtaThreeText}
                        background={themeObj.verascopeColor.purple400}
                        loading={buttonLoadingFromSource(compactCtaThreeSourceId)}
                        disabled={buttonDisabledFromSource(compactCtaThreeSourceId)}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => handleCompactPurchaseSelect(compactCtaThreeType, compactCtaThreeSourceId, e),
                            onMouseDown: (e) => handleCompactPurchaseSelect(compactCtaThreeType, compactCtaThreeSourceId, e),
                        })}
                    />
                    {activeCartAnimationSourceId === compactCtaThreeSourceId && PhysicalBookAnimationItems}
                    <StickyPurchaseChoiceCopy>
                        {(
                            'Preorder the print book (ships Q4 2026). Includes full web book access. '
                            + 'Buying multiple? Gift complimentary web books to others at checkout.'
                        )}
                    </StickyPurchaseChoiceCopy>
                </StickyPurchaseChoiceRow>
            ),
        });
    }

    if (purchaseMoreOptionsExpanded) {
        CompactPurchaseRows.push({
            key: 'option-1-day',
            node: (
                <StickyPurchaseChoiceRow>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        center
                        height={1.75 * BODY_FONT_SIZE}
                        width={compactPurchaseButtonWidth}
                        text={buildStickyCtaButtonText('1-Day Pass', WEB_BOOK_DAY_PASS_PRODUCT_ID)}
                        background={themeObj.verascopeColor.purple400}
                        loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.dayPass)}
                        disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.dayPass)}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.webDayPass, STICKY_CTA_SOURCE.dayPass, e),
                            onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.webDayPass, STICKY_CTA_SOURCE.dayPass, e),
                        })}
                    />
                    {activeCartAnimationSourceId === STICKY_CTA_SOURCE.dayPass && WebBookAnimationItems}
                    <StickyPurchaseChoiceCopy>
                        Lowest-cost access to all released chapters for a day.
                    </StickyPurchaseChoiceCopy>
                </StickyPurchaseChoiceRow>
            ),
        });
        if (isAuthLockedAnonymousStickyState) {
            CompactPurchaseRows.push({
                key: 'option-7-day-authlocked-anon',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={1.75 * BODY_FONT_SIZE}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('7-Day Pass', WEB_BOOK_WEEK_PASS_PRODUCT_ID)}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.weekPassAuthAnon)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.weekPassAuthAnon)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.webWeekPass, STICKY_CTA_SOURCE.weekPassAuthAnon, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.webWeekPass, STICKY_CTA_SOURCE.weekPassAuthAnon, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.weekPassAuthAnon && WebBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            Best for catching up: 7-day access to all released chapters.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
        }
        if (isPaywalledStickyCta) {
            CompactPurchaseRows.push({
                key: 'option-7-day',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={1.75 * BODY_FONT_SIZE}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('7-Day Pass', WEB_BOOK_WEEK_PASS_PRODUCT_ID)}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.binge)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.binge)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.webWeekPass, STICKY_CTA_SOURCE.binge, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.webWeekPass, STICKY_CTA_SOURCE.binge, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.binge && WebBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            Best for catching up: 7-day access to all released chapters.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
        }
        if (currentPageChapterBookType && !postIsLockedForRelease && !isPaywalledStickyCta) {
            CompactPurchaseRows.push({
                key: 'option-chapter',
                node: (
                    <StickyPurchaseChoiceRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.solid}
                            center
                            height={1.75 * BODY_FONT_SIZE}
                            width={compactPurchaseButtonWidth}
                            text={buildStickyCtaButtonText('This Chapter', currentPageChapterProductId)}
                            background={themeObj.verascopeColor.purple400}
                            loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.chapter)}
                            disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.chapter)}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: (e) => handleCompactPurchaseSelect(currentPageChapterBookType, STICKY_CTA_SOURCE.chapter, e),
                                onMouseDown: (e) => handleCompactPurchaseSelect(currentPageChapterBookType, STICKY_CTA_SOURCE.chapter, e),
                            })}
                        />
                        {activeCartAnimationSourceId === STICKY_CTA_SOURCE.chapter && WebBookAnimationItems}
                        <StickyPurchaseChoiceCopy>
                            Permanent access to this chapter only.
                        </StickyPurchaseChoiceCopy>
                    </StickyPurchaseChoiceRow>
                ),
            });
        }
        CompactPurchaseRows.push({
            key: 'option-pdf',
            node: (
                <StickyPurchaseChoiceRow>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        center
                        height={1.75 * BODY_FONT_SIZE}
                        width={compactPurchaseButtonWidth}
                        text={buildStickyCtaButtonText('Preorder PDF', DIGITAL_BOOK_PREORDER_PRODUCT_ID)}
                        background={themeObj.verascopeColor.purple400}
                        loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.pdf)}
                        disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.pdf)}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.digital, STICKY_CTA_SOURCE.pdf, e),
                            onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.digital, STICKY_CTA_SOURCE.pdf, e),
                        })}
                    />
                    {activeCartAnimationSourceId === STICKY_CTA_SOURCE.pdf && DigitalBookAnimationItems}
                    <StickyPurchaseChoiceCopy>
                        Preorder PDF edition (ships Q4 2026) with web book access included. Extra copies include complimentary web books you can gift at checkout.
                    </StickyPurchaseChoiceCopy>
                </StickyPurchaseChoiceRow>
            ),
        });
        CompactPurchaseRows.push({
            key: 'option-audio',
            node: (
                <StickyPurchaseChoiceRow>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.solid}
                        center
                        height={1.75 * BODY_FONT_SIZE}
                        width={compactPurchaseButtonWidth}
                        text={buildStickyCtaButtonText('Audiobook', AUDIO_BOOK_PRODUCT_ID)}
                        background={themeObj.verascopeColor.purple400}
                        loading={buttonLoadingFromSource(STICKY_CTA_SOURCE.audio)}
                        disabled={buttonDisabledFromSource(STICKY_CTA_SOURCE.audio)}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: (e) => handleCompactPurchaseSelect(BOOK_TYPE.audio, STICKY_CTA_SOURCE.audio, e),
                            onMouseDown: (e) => handleCompactPurchaseSelect(BOOK_TYPE.audio, STICKY_CTA_SOURCE.audio, e),
                        })}
                    />
                    {activeCartAnimationSourceId === STICKY_CTA_SOURCE.audio && DigitalBookAnimationItems}
                    <StickyPurchaseChoiceCopy>
                        Narrated audio for all released chapters, downloadable to your computer.
                    </StickyPurchaseChoiceCopy>
                </StickyPurchaseChoiceRow>
            ),
        });
    }

    CompactPurchaseRows.push({
        key: 'actions',
        node: (
            <InlineActionRow>
                <StickyPurchaseMoreOptionsButtonContainer
                    width={compactInlineMoreOptionsButtonWidth}
                    height={1.75 * BODY_FONT_SIZE}
                >
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.secret}
                        center
                        height={1.75 * BODY_FONT_SIZE}
                        width={compactInlineMoreOptionsButtonWidth}
                        text={purchaseMoreOptionsExpanded ? 'Less options' : 'More options'}
                        background={themeObj.verascopeColor.purple300}
                        color={themeObj.verascopeColor.purple500}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        style={{
                            color: themeObj.color.white,
                            paddingLeft: stickyPurchaseIsNarrow
                                ? undefined
                                : `${1.35 * BODY_FONT_SIZE}px`,
                        }}
                        {...getPressEventHandlers({
                            onTouchStart: () => togglePurchaseMoreOptions(),
                            onMouseDown: () => togglePurchaseMoreOptions(),
                        })}
                    />
                    {!stickyPurchaseIsNarrow && (
                        <StickyPurchaseMoreOptionsChevronContainer>
                            <StickyPurchaseMoreOptionsChevronIcon
                                expanded={purchaseMoreOptionsExpanded}
                                transitionDuration={BUTTON_EXPANSION_DURATION}
                            >
                                <ReactSVG
                                    src={ChevronIcon}
                                />
                            </StickyPurchaseMoreOptionsChevronIcon>
                        </StickyPurchaseMoreOptionsChevronContainer>
                    )}
                </StickyPurchaseMoreOptionsButtonContainer>
                <HaggleButtonContainer>
                    <Button
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={BUTTON_TYPE.secret}
                        center
                        height={1.75 * BODY_FONT_SIZE}
                        width={compactInlineHaggleButtonWidth}
                        text="Need a student or hardship discount? Apply."
                        background={themeObj.verascopeColor.purple300}
                        color={themeObj.verascopeColor.purple500}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => handleInitiateHaggleRequest(),
                            onMouseDown: () => handleInitiateHaggleRequest(),
                        })}
                    />
                </HaggleButtonContainer>
            </InlineActionRow>
        ),
    });

    const renderCompactPurchaseTable = (animateRows: boolean): JSX.Element => (
        <StickyPurchaseBody>
            {CompactPurchaseRows.map((row, index) => (
                <Transition
                    key={row.key}
                    in={animateRows}
                    timeout={{
                        enter: Math.min(
                            MAX_FADE_IN_STAGGER_TRANSITION_DURATION,
                            (index * FADE_IN_STAGGER_OFFSET_DURATION)
                            + FADE_IN_STAGGER_TRANSITION_DURATION,
                        ),
                        exit: 0,
                    }}
                    appear
                >
                    {(transitionState) => (
                        <StickyPurchaseRowContainer
                            withBottomMargin={stickyPurchaseIsNarrow && index < CompactPurchaseRows.length - 1}
                            marginBottom={0.4 * BODY_FONT_SIZE}
                            style={{
                                ...FADE_IN_STAGGER_DEFAULT_STYLE({
                                    direction: 'down',
                                    offset: 6,
                                    duration: FADE_IN_STAGGER_TRANSITION_DURATION,
                                    easing: themeObj.motion.overshoot,
                                }),
                                ...FADE_IN_STAGGER_TRANSITION_STYLES({
                                    numItems: CompactPurchaseRows.length,
                                    index,
                                    direction: 'down',
                                    offset: 6,
                                    easing: themeObj.motion.overshoot,
                                })[transitionState],
                            }}
                        >
                            {row.node}
                        </StickyPurchaseRowContainer>
                    )}
                </Transition>
            ))}
        </StickyPurchaseBody>
    );

    const showZoomLevelSelector = !!user
        && !!activeRootPost
        && pageLoadRendersCompleted
        && !!currentZoomLevel
        && (
            write
            || (
                appliedZoomLevelCount !== null
                && appliedZoomLevelCount > 0
            )
        )
        && isNotPaywalled;

    const showAnnotationVisibilityButton = !!activeRootPost
        && postBodyPainted
        && hasVisibleAnnotationsForCurrentScope
        && !write
        && canAccessReaderAnnotations
        && !!currentZoomLevel
        && currentZoomLevel.level === ZOOM_LEVEL.five;
    const dockAnnotationEditorToViewport = shouldDockAnnotationEditorToViewport(viewportDimensions.width);
    /**
     * Keep touch-mobile annotation editors mounted in a stable viewport-root
     * body portal so the first highlight does not lose local open state and the
     * docked editor can follow a frozen keyboard-sized viewport frame.
     */
    const portalAnnotationEditorToBody = dockAnnotationEditorToViewport;
    const hideAnnotationVisibilityButtonForDockedEditor = annotationEditorIsVisible
        && dockAnnotationEditorToViewport;
    const showVisibleAnnotationVisibilityButton = showAnnotationVisibilityButton
        && !hideAnnotationVisibilityButtonForDockedEditor;
    const showAnnotationVisibilityRuntimeHarnessControl = isTestMode()
        && !write
        && canAccessReaderAnnotations
        && !!currentZoomLevel
        && currentZoomLevel.level === ZOOM_LEVEL.five;
    const showAnnotationShelf = !annotationsHidden
        && postBodyPainted
        && (
            hasRenderableAnnotations
            || hasVisibleAnnotationsForCurrentScope
        )
        && !write
        && canAccessReaderAnnotations
        && !!activeRootPost
        && !!currentZoomLevel
        && currentZoomLevel.level === ZOOM_LEVEL.five;
    // Default-on behavior keeps existing users unchanged until they opt out in profile.
    const readerProgressEnabled = (
        user?.saveReaderProgress !== false
    );
    const isMediumOrDownViewport = viewportDimensions.width <= MEDIA_QUERY_SIZE.medium.max;
    // Medium-and-down layout: pin the resume CTA above sticky purchase CTA's measured height.
    const resumeReaderProgressBottomOffset = useMemo(() => {
        if (!showStickyPurchaseCta) {
            return RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET;
        }

        const stickyPurchaseOccupiedHeight = purchasePanelExpanded
            ? stickyPurchaseMeasuredHeights.expanded
            : stickyPurchaseMeasuredHeights.collapsed;
        if (
            !isMediumOrDownViewport
            && !showZoomLevelSelector
            && !showVisibleAnnotationVisibilityButton
        ) {
            // With no other floating controls present, align resume CTA to sticky CTA centerline.
            return Math.max(
                0,
                STICKY_PURCHASE_CTA_BOTTOM_OFFSET
                    + (stickyPurchaseOccupiedHeight / 2)
                    - (RESUME_READER_POSITION_BUTTON_HEIGHT / 2),
            );
        }

        if (!isMediumOrDownViewport) {
            return RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET;
        }

        return Math.max(
            RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET,
            STICKY_PURCHASE_CTA_BOTTOM_OFFSET
                + stickyPurchaseOccupiedHeight
                + RESUME_READER_POSITION_BUTTON_EXTRA_SMALL_GAP,
        );
    }, [
        isMediumOrDownViewport,
        showStickyPurchaseCta,
        showZoomLevelSelector,
        showVisibleAnnotationVisibilityButton,
        purchasePanelExpanded,
        stickyPurchaseMeasuredHeights.collapsed,
        stickyPurchaseMeasuredHeights.expanded,
        RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET,
        STICKY_PURCHASE_CTA_BOTTOM_OFFSET,
        RESUME_READER_POSITION_BUTTON_HEIGHT,
        RESUME_READER_POSITION_BUTTON_EXTRA_SMALL_GAP,
    ]);
    const floatingControlsBaseRightOffset = useMemo(() => {
        if (!isMediumOrDownViewport || !showStickyPurchaseCta) {
            return RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET;
        }

        // Match sticky CTA horizontal inset so floating controls align cleanly on medium-and-down.
        const stickyCtaWidth = Math.min(
            STICKY_PURCHASE_CTA_MAX_WIDTH,
            Math.max(viewportDimensions.width - (2 * BODY_FONT_SIZE), 0),
        );
        return Math.max((viewportDimensions.width - stickyCtaWidth) / 2, 0);
    }, [
        isMediumOrDownViewport,
        viewportDimensions.width,
        showStickyPurchaseCta,
        RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET,
        STICKY_PURCHASE_CTA_MAX_WIDTH,
    ]);
    const annotationVisibilityButtonBottomOffset = useMemo(() => {
        if (!isMediumOrDownViewport || !showStickyPurchaseCta) {
            return ANNOTATION_VISIBILITY_BUTTON_DEFAULT_BOTTOM_OFFSET;
        }
        return resumeReaderProgressBottomOffset;
    }, [
        isMediumOrDownViewport,
        showStickyPurchaseCta,
        resumeReaderProgressBottomOffset,
        ANNOTATION_VISIBILITY_BUTTON_DEFAULT_BOTTOM_OFFSET,
    ]);
    const annotationVisibilityButtonRightOffset = useMemo(() => (
        isMediumOrDownViewport
            ? floatingControlsBaseRightOffset
            : ANNOTATION_VISIBILITY_BUTTON_RIGHT_OFFSET
    ), [
        isMediumOrDownViewport,
        floatingControlsBaseRightOffset,
        ANNOTATION_VISIBILITY_BUTTON_RIGHT_OFFSET,
    ]);
    const useCompactAnnotationVisibilityButtonStyle = isMediumOrDownViewport
        && showStickyPurchaseCta;
    let annotationVisibilityButtonBackground = themeObj.verascopeColor.purple400;
    if (useCompactAnnotationVisibilityButtonStyle && annotationsHidden) {
        annotationVisibilityButtonBackground = themeObj.verascopeColor.purple200;
    } else if (annotationsHidden) {
        annotationVisibilityButtonBackground = themeObj.color.cream500;
    }
    let annotationVisibilityButtonType = BUTTON_TYPE.solid;
    if (useCompactAnnotationVisibilityButtonStyle) {
        annotationVisibilityButtonType = BUTTON_TYPE.floating;
    } else if (annotationsHidden) {
        annotationVisibilityButtonType = BUTTON_TYPE.secret;
    }
    const annotationVisibilityButtonContainerBackground = annotationVisibilityButtonBackground;
    const annotationVisibilityButtonButtonBackground = (
        !useCompactAnnotationVisibilityButtonStyle && annotationsHidden
            ? themeObj.verascopeColor.purple400
            : annotationVisibilityButtonBackground
    );
    const annotationVisibilityButtonIconFillColor = (
        !useCompactAnnotationVisibilityButtonStyle && annotationsHidden
            ? themeObj.verascopeColor.purple400
            : themeObj.color.white
    );
    const annotationVisibilityButtonTooltipSide = useCompactAnnotationVisibilityButtonStyle
        ? TOOLTIP_TYPE.top
        : TOOLTIP_TYPE.left;
    const alignResumeReaderButtonLeftOfAnnotationToggle = isMediumOrDownViewport
        && showStickyPurchaseCta
        && showVisibleAnnotationVisibilityButton;
    const resumeReaderProgressRightOffset = useMemo(() => {
        if (!isMediumOrDownViewport) {
            return RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET;
        }
        return floatingControlsBaseRightOffset
            + (
                alignResumeReaderButtonLeftOfAnnotationToggle
                    ? ANNOTATION_VISIBILITY_BUTTON_LENGTH + ANNOTATION_VISIBILITY_BUTTON_GAP
                    : 0
            );
    }, [
        isMediumOrDownViewport,
        floatingControlsBaseRightOffset,
        alignResumeReaderButtonLeftOfAnnotationToggle,
        RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET,
        ANNOTATION_VISIBILITY_BUTTON_LENGTH,
        ANNOTATION_VISIBILITY_BUTTON_GAP,
    ]);
    const resumeReaderProgressTooltipSide = isMediumOrDownViewport
        ? TOOLTIP_TYPE.top
        : TOOLTIP_TYPE.left;
    const showResumeReaderProgressTooltip = !detectTouchDevice(document);
    const showFloatingInternalPostLinkLoadingPill = (
        !write
        && !!pendingInternalPostLinkNavigation
        && !activeInternalPostLinkNavigationHref
    );
    useEffect(() => {
        const queueVisibilityMeasurement = (): void => {
            if (typeof window === 'undefined') {
                return;
            }

            if (activeReadOnlyAudioNoteVisibilityRafRef.current != null) {
                window.cancelAnimationFrame(activeReadOnlyAudioNoteVisibilityRafRef.current);
            }

            activeReadOnlyAudioNoteVisibilityRafRef.current = window.requestAnimationFrame(() => {
                activeReadOnlyAudioNoteVisibilityRafRef.current = null;

                if (!readOnlyAudioPlaybackState?.id) {
                    return;
                }

                const queryRoot = postContentContainerRef.current || document;
                const activeAudioNoteElement = Array.from(
                    queryRoot.querySelectorAll('[data-audio-note-id]'),
                ).find((element) => (
                    element.getAttribute('data-audio-note-id') === readOnlyAudioPlaybackState.id
                ));

                if (!activeAudioNoteElement) {
                    setActiveReadOnlyAudioNoteVisibleInViewport(
                        readOnlyAudioPlaybackState.isVisibleInViewport,
                    );
                    return;
                }

                const noteRect = activeAudioNoteElement.getBoundingClientRect();
                const viewportRect = useDocumentScroll
                    ? null
                    : postContentContainerRef.current?.getBoundingClientRect();
                const visibleTop = Math.max(noteRect.top, viewportRect?.top ?? 0);
                const visibleBottom = Math.min(
                    noteRect.bottom,
                    viewportRect?.bottom ?? window.innerHeight,
                );
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);
                const requiredVisibleHeight = Math.max(
                    READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_PIXELS,
                    noteRect.height * READ_ONLY_AUDIO_NOTE_MIN_VISIBLE_RATIO,
                );
                const nextIsVisibleInViewport = visibleHeight >= requiredVisibleHeight;

                setActiveReadOnlyAudioNoteVisibleInViewport((currentIsVisible) => (
                    currentIsVisible === nextIsVisibleInViewport
                        ? currentIsVisible
                        : nextIsVisibleInViewport
                ));
            });
        };

        if (!readOnlyAudioPlaybackState?.hasStartedPlayback) {
            setActiveReadOnlyAudioNoteVisibleInViewport(true);
            return undefined;
        }

        queueVisibilityMeasurement();

        const scrollTarget = useDocumentScroll ? null : postContentContainerRef.current;
        const measurementTarget: Window | HTMLDivElement = scrollTarget || window;
        measurementTarget.addEventListener('scroll', queueVisibilityMeasurement, {
            passive: true,
        });
        window.addEventListener('resize', queueVisibilityMeasurement, {
            passive: true,
        });

        return () => {
            if (activeReadOnlyAudioNoteVisibilityRafRef.current != null) {
                window.cancelAnimationFrame(activeReadOnlyAudioNoteVisibilityRafRef.current);
                activeReadOnlyAudioNoteVisibilityRafRef.current = null;
            }

            measurementTarget.removeEventListener('scroll', queueVisibilityMeasurement);
            window.removeEventListener('resize', queueVisibilityMeasurement);
        };
    }, [
        readOnlyAudioPlaybackState?.id,
        readOnlyAudioPlaybackState?.hasStartedPlayback,
        readOnlyAudioPlaybackState?.isVisibleInViewport,
        useDocumentScroll,
        postContentContainerRef.current,
    ]);
    const showFloatingAudioMiniPlayer = !write
        && !annotationIsFocused
        && !!readOnlyAudioPlaybackState
        && readOnlyAudioPlaybackState.hasStartedPlayback
        && !activeReadOnlyAudioNoteVisibleInViewport;
    const floatingAudioMiniPlayerTooltipText = useMemo(() => {
        if (readOnlyAudioPlaybackState?.isStartingPlayback) {
            return 'Preparing audio note';
        }

        if (readOnlyAudioPlaybackState?.isPlaying) {
            return 'Pause audio note';
        }

        return 'Resume audio note';
    }, [
        readOnlyAudioPlaybackState?.isPlaying,
        readOnlyAudioPlaybackState?.isStartingPlayback,
    ]);
    /**
     * Keep the floating mini-player transport in sync with the inline
     * audio-note controls by routing skips through the shared seek callback.
     */
    const handleFloatingAudioMiniPlayerSkip = useCallback((direction: 'backward' | 'forward'): void => {
        if (
            !readOnlyAudioPlaybackState?.hasStartedPlayback
            || !readOnlyAudioPlaybackState?.seekToTimeMs
        ) {
            return;
        }

        const skipDurationMs = 15 * MILLISECONDS_IN_A_SECOND;
        const currentTimestampMs = readOnlyAudioPlaybackState.currentTimeMs || 0;
        const maxTimestampMs = Math.max(
            0,
            readOnlyAudioPlaybackState.durationMs || currentTimestampMs,
        );
        const nextTimestampMs = direction === 'forward'
            ? Math.min(currentTimestampMs + skipDurationMs, maxTimestampMs)
            : Math.max(currentTimestampMs - skipDurationMs, 0);

        readOnlyAudioPlaybackState.seekToTimeMs(nextTimestampMs);
    }, [
        readOnlyAudioPlaybackState?.currentTimeMs,
        readOnlyAudioPlaybackState?.durationMs,
        readOnlyAudioPlaybackState?.hasStartedPlayback,
        readOnlyAudioPlaybackState?.seekToTimeMs,
    ]);
    /**
     * Highlight-driven annotation capture should freeze the active read-only
     * note so the selection and draft quote do not keep shifting underneath
     * the reader while playback continues.
     */
    const pauseReadOnlyAudioPlaybackForAnnotationSelection = useCallback((): void => {
        if (
            !readOnlyAudioPlaybackState?.isPlaying
            || !readOnlyAudioPlaybackState?.setPlaying
        ) {
            return;
        }

        readOnlyAudioPlaybackState.setPlaying(false);
    }, [
        readOnlyAudioPlaybackState?.isPlaying,
        readOnlyAudioPlaybackState?.setPlaying,
    ]);
    useEffect(() => {
        if (
            write
            || !readOnlyAudioPlaybackState?.hasStartedPlayback
            || !readOnlyAudioPlaybackState?.setPlaying
            || !!annotationIsFocused
            || !!annotationDetailData
        ) {
            return undefined;
        }

        /**
         * Spacebar should control the active read-only audio note only when the
         * reader is not currently typing into annotation UI or another control.
         */
        const handleReadOnlyAudioSpacebarShortcut = (event: KeyboardEvent): void => {
            if (event.key !== KEYCODE.spacebar) {
                return;
            }

            if (
                event.defaultPrevented
                || event.repeat
                || event.altKey
                || event.ctrlKey
                || event.metaKey
                || event.shiftKey
            ) {
                return;
            }

            if (
                shouldIgnoreReadOnlyAudioSpacebarShortcut(event.target)
                || shouldIgnoreReadOnlyAudioSpacebarShortcut(document.activeElement)
            ) {
                return;
            }

            event.preventDefault();
            readOnlyAudioPlaybackState.setPlaying(!readOnlyAudioPlaybackState.isPlaying);
        };

        window.addEventListener('keydown', handleReadOnlyAudioSpacebarShortcut);

        return () => {
            window.removeEventListener('keydown', handleReadOnlyAudioSpacebarShortcut);
        };
    }, [
        write,
        annotationIsFocused,
        annotationDetailData,
        readOnlyAudioPlaybackState?.hasStartedPlayback,
        readOnlyAudioPlaybackState?.isPlaying,
        readOnlyAudioPlaybackState?.setPlaying,
    ]);
    const floatingAudioMiniPlayerBottomOffset = useMemo(() => {
        let occupiedBottom = 0;

        if (showStickyPurchaseCta) {
            const stickyPurchaseOccupiedHeight = purchasePanelExpanded
                ? stickyPurchaseMeasuredHeights.expanded
                : stickyPurchaseMeasuredHeights.collapsed;
            occupiedBottom = Math.max(
                occupiedBottom,
                STICKY_PURCHASE_CTA_BOTTOM_OFFSET + stickyPurchaseOccupiedHeight,
            );
        }

        if (showZoomLevelSelector) {
            occupiedBottom = Math.max(
                occupiedBottom,
                (0.3125 * BODY_FONT_SIZE) + ZOOM_LEVEL_BUTTON_LENGTH,
            );
        }

        if (showVisibleAnnotationVisibilityButton && !annotationIsFocused) {
            occupiedBottom = Math.max(
                occupiedBottom,
                annotationVisibilityButtonBottomOffset + ANNOTATION_VISIBILITY_BUTTON_LENGTH,
            );
        }

        if (
            !write
            && readerProgressEnabled
            && !annotationIsFocused
            && (
                resumeReaderProgress != null
                || resumeReaderProgressButtonLoading
            )
        ) {
            occupiedBottom = Math.max(
                occupiedBottom,
                resumeReaderProgressBottomOffset + RESUME_READER_POSITION_BUTTON_HEIGHT,
            );
        }

        return occupiedBottom > 0
            ? occupiedBottom + FLOATING_AUDIO_PLAYER_GAP
            : RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET;
    }, [
        write,
        annotationIsFocused,
        readerProgressEnabled,
        resumeReaderProgress,
        resumeReaderProgressButtonLoading,
        resumeReaderProgressBottomOffset,
        showStickyPurchaseCta,
        purchasePanelExpanded,
        stickyPurchaseMeasuredHeights.collapsed,
        stickyPurchaseMeasuredHeights.expanded,
        showZoomLevelSelector,
        showVisibleAnnotationVisibilityButton,
        annotationVisibilityButtonBottomOffset,
        ANNOTATION_VISIBILITY_BUTTON_LENGTH,
        STICKY_PURCHASE_CTA_BOTTOM_OFFSET,
        ZOOM_LEVEL_BUTTON_LENGTH,
        RESUME_READER_POSITION_BUTTON_HEIGHT,
        RESUME_READER_POSITION_BUTTON_DEFAULT_BOTTOM_OFFSET,
        FLOATING_AUDIO_PLAYER_GAP,
    ]);
    const floatingAudioMiniPlayerRightOffset = useMemo(() => (
        isMediumOrDownViewport
            ? floatingControlsBaseRightOffset
            : RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET
    ), [
        isMediumOrDownViewport,
        floatingControlsBaseRightOffset,
        RESUME_READER_POSITION_BUTTON_DEFAULT_RIGHT_OFFSET,
    ]);

    // In read mode, shift zoom selector left when annotation toggle is mounted to avoid overlap.
    const zoomSelectorReadOnlyRightOffset = ANNOTATION_VISIBILITY_BUTTON_RIGHT_OFFSET
        + (
            showVisibleAnnotationVisibilityButton
                ? ANNOTATION_VISIBILITY_BUTTON_LENGTH + ANNOTATION_VISIBILITY_BUTTON_GAP
                : 0
        );

    // ===== Render Behavior ======
    /*
     * 1) remeasurePostHeight from PostEditor will cause a lot of rerenders as the post's media items
     * are loading into the post.
     *
     * 2) Everytime App.tsx rerenders, ReaderView will also rerender.
    */
    const annotationEditorElement = ((!write || shouldRenderDetachedAnnotationQuoteCaptureEditor))
        && activeRootPost
        && activeRootPost.id
        && pageLoadRendersCompleted
        ? (
            <AnnotationEditor
                id="annotation-editor"
                customCursorActive={customCursorActive}
                user={user}
                userIsAdmin={userIsAdmin}
                userEmailVerified={userEmailVerified}
                shouldFocus
                boxShadow
                currentSessionId={currentSessionId}
                currentZoomLevel={currentZoomLevel}
                readOnly={false}
                hasSound={hasSound}
                postId={activeRootPost.id}
                top={annotationEditorPosition.y}
                left={annotationEditorPosition.x}
                setRef={(editorRef) => { annotationEditorRef.current = editorRef; }}
                postEditor={postEditor}
                width={ANNOTATION_EDITOR_WIDTH}
                type={EDITOR_CONTEXT_TYPE.annotation} // Used to know which storage bucket to save media
                color={DEFAULT_EDITOR_COLOR}
                fontMultiplier={ANNOTATION_EDITOR_FONT_MULTIPLIER_LARGE}
                interactionDisabled={false}
                disablePositionTransition={annotationEditorPositionTransitionDisabled}
                submitAnnotation={handleSubmitAnnotation}
                placeholder="Type..."
                onCursorEnter={onCursorEnter}
                onCursorLeave={onCursorLeave}
                setInputFocused={setInputFocused}
                updatePosition={updateAnnotationEditorPosition}
                initialDraftValue={annotationEditorDraftValueRef.current}
                persistDraftValue={persistAnnotationEditorDraftValue}
                postQuote={postQuote}
                setPostQuote={handlePostQuoteChange}
                deferReaderSelectionFocusUntilPostQuotePaint
                captureQuoteWithoutOpeningEditor={!!detachedAnnotationReattachTargetId}
                viewportDimensions={viewportDimensions}
                setEditor={setAnnotationEditor}
                setSnackbarData={setSnackbarData}
                setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                pauseReadOnlyAudioPlayback={pauseReadOnlyAudioPlaybackForAnnotationSelection}
                handleShowAnnotationEditor={handleAnnotationEditorVisibilityChange}
                showAnnotationEditorOverride={annotationEditorIsVisible}
            />
        )
        : null;

    return (
        <Container
            {...(write
                ? {
                    ...getRootProps({
                        role: 'container',
                    }),
                }
                : { ref: containerRef }
            )}
            hasSuccess={postFileDragFeedback.hasSuccess}
            hasError={postFileDragFeedback.hasFailure || hitPostMinimum || hitDetailMinimum}
            isSteering={isSteering}
            useDocumentScroll={useDocumentScroll}
        >
            { write && (<DropzoneInput {...getInputProps()} />)}
            {
            /* We don't want to render PostEditor until we receive
               post data so that it is not written over by empty value
            */
            }
            <PostContentContainer
                ref={postContentContainerRef}
                isSteering={isSteering}
                showWarning={hitPostMinimum || hitDetailMinimum}
                warningBackground={hitPostMinimum
                    ? themeObj.verascopeColor.red200
                    : themeObj.verascopeColor.red300}
                transitionDuration={DETAIL_VIEW_TRANSITION_DURATION}
                useDocumentScroll={useDocumentScroll}
                useReaderSelectionHighlightStyling={!write}
            >
                <PostBanner
                    data-testid="reader-post-banner"
                    ref={postBannerRef}
                    height={POST_BANNER_HEIGHT}
                    stickyPostBannerActive={stickyPostBannerActive}
                    transitionDuration={POST_BANNER_TRANSITION_DURATION}
                >
                    <PostBannerLeftContainer
                        data-testid="reader-post-banner-cover-container"
                        ref={postBannerLeftContainerRef}
                        widthExpanded={POST_BANNER_LEFT_CONTAINER_WIDTH}
                        widthContracted={postBannerLeftContainerWidthContracted}
                        heightExpanded={POST_BANNER_LEFT_CONTAINER_HEIGHT_EXPANDED}
                        paddingExpanded={POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED}
                        transitionDuration={POST_BANNER_TRANSITION_DURATION}
                    >
                        {/* The compact cover follows the compact title's breakpoint. */}
                        <Transition
                            in={write || (
                                !!postImageURL
                                && (!stickyPostBannerActive || viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min)
                            )}
                            timeout={{
                                enter: POST_TITLE_ENTER_DURATION + POST_SUBTITLE_ENTER_DURATION,
                                exit: POST_METADATA_ENTER_DURATION,
                            }}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state) => (
                                <PostThumbnail
                                    data-testid="reader-post-cover"
                                    ref={postThumbnailRef}
                                    url={postImageURL}
                                    stickyPostBannerActive={stickyPostBannerActive}
                                    bottomExpanded={POST_THUMBNAIL_BOTTOM_EXPANDED}
                                    heightExpandedLarge={POST_THUMBNAIL_HEIGHT_EXPANDED}
                                    heightExpandedMedium={POST_THUMBNAIL_MAX_WIDTH_MEDIUM}
                                    heightExpandedSmall={POST_THUMBNAIL_MAX_WIDTH_SMALL}
                                    heightExpandedExtraSmall={POST_THUMBNAIL_MAX_WIDTH_SMALL}
                                    widthExpanded={POST_THUMBNAIL_WIDTH_EXPANDED}
                                    widthContracted={POST_THUMBNAIL_WIDTH_CONTRACTED}
                                    heightContracted={POST_THUMBNAIL_HEIGHT_CONTRACTED}
                                    borderRadiusExpanded={POST_THUMBNAIL_BORDER_RADIUS_EXPANDED}
                                    borderRadiusContracted={POST_THUMBNAIL_BORDER_RADIUS_CONTRACTED}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min ? 'up' : 'left',
                                            offset: 10,
                                            duration: POST_THUMBNAIL_ENTER_DURATION,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min ? 'up' : 'left',
                                            offset: 10,
                                        })[state],
                                    }}
                                >
                                    {!postImageURL
                                    && post
                                    && write
                                    && pageLoadRendersCompleted && (
                                        <AddPostThumbnailContainer
                                            {...getPostThumbnailRootProps({
                                                isDragAccept: isPostThumbnailDragAccept,
                                                isDragReject: isPostThumbnailDragReject,
                                                className: HOVER_TARGET_CLASSNAME,
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                                onClick: selectPostThumbnail,
                                            })}
                                        >
                                            <AddPostThumbnailInput {...getPostThumbnailInputProps()} />
                                            {uploadingPostThumbnail
                                                ? (
                                                    <PostThumbnailUploadProgressBarContainer>
                                                        <PostThumbnailUploadProgressBar
                                                            progress={uploadingPostThumbnail.uploadProgress}
                                                        />
                                                    </PostThumbnailUploadProgressBarContainer>
                                                ) : (
                                                    <AddPostThumbnailIcon
                                                        stickyPostBannerActive={stickyPostBannerActive}
                                                        transitionDuration={POST_THUMBNAIL_TRANSITION_DURATION}
                                                    >
                                                        <ReactSVG
                                                            src={AddImageIcon}
                                                        />
                                                    </AddPostThumbnailIcon>
                                                )}
                                        </AddPostThumbnailContainer>
                                    )}
                                </PostThumbnail>
                            )}
                        </Transition>
                    </PostBannerLeftContainer>
                    <PostBannerCenterContainer
                        height={POST_BANNER_HEIGHT}
                        postBannerLeftContainerWidth={postBannerLeftContainerWidth}
                        postBannerRightContainerWidth={postBannerRightContainerWidth}
                        pageLoadRendersCompleted={pageLoadRendersCompleted}
                    >
                        <PostTitlesContainer>
                            {/* Post loaded and title either present or not */}
                            <Transition
                                in={!!postBannerRootPost && !write}
                                timeout={{
                                    enter: 0,
                                    exit: POST_METADATA_ENTER_DURATION
                                        + POST_THUMBNAIL_ENTER_DURATION
                                        + POST_SUBTITLE_ENTER_DURATION,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <PostTitle
                                        fontMultiplier={POST_TITLE_FONT_MULTIPLIER}
                                        style={{
                                            ...FADE_IN_DEFAULT_STYLE({
                                                direction: 'right',
                                                offset: 10,
                                                duration: POST_TITLE_ENTER_DURATION,
                                            }),
                                            ...FADE_IN_TRANSITION_STYLES({
                                                direction: 'right',
                                                offset: 10,
                                            })[state],
                                        }}
                                    >
                                        {postBannerRootPost?.title || DEFAULT_POST_TITLE}
                                    </PostTitle>
                                )}
                            </Transition>
                            {/* Post not yet loaded */}
                            {!postBannerRootPost
                            && !write
                            && (
                                <PlaceholderBox
                                    width={titlePlaceholderWidth}
                                    height={POST_TITLE_FONT_MULTIPLIER * BODY_FONT_SIZE}
                                    margin={`${POST_TITLE_MARGIN_TOP}px 0px ${
                                        viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min
                                            ? POST_TITLE_MARGIN_BOTTOM
                                            : 0
                                    }px`}
                                />
                            )}
                            {/* Editing post title */}
                            {postBannerRootPost
                            && write
                            && (
                                <PostInput
                                    type="text"
                                    ref={titleInputRef}
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    defaultValue={postBannerRootPost.title}
                                    placeholder="Title"
                                    onKeyUp={checkForEnter}
                                    color={themeObj.verascopeColor.purple100}
                                    fontSize={POST_TITLE_FONT_MULTIPLIER}
                                    fontWeight={TITLE_INPUT_FONT_WEIGHT}
                                    marginTop={TITLE_INPUT_MARGIN_TOP}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onInputMouseEnter,
                                        onMouseLeave: onInputMouseLeave,
                                    })}
                                    onFocus={onInputFocus}
                                    onChange={onTitleInputChange}
                                    onBlur={onTitleInputBlur}
                                />
                            )}
                            {/* Post loaded and subtitle present */}
                            <Transition
                                in={!!postBannerRootPost
                                    && !!postBannerRootPost.subtitle
                                    && !write}
                                timeout={{
                                    enter: POST_TITLE_ENTER_DURATION,
                                    exit: POST_METADATA_ENTER_DURATION
                                        + POST_THUMBNAIL_ENTER_DURATION,
                                }}
                                appear
                                mountOnEnter
                                unmountOnExit
                            >
                                {(state) => (
                                    <PostSubtitle
                                        fontMultiplier={POST_SUBTITLE_FONT_MULTIPLIER}
                                        fixedToViewport={stickyPostBannerActive}
                                        transitionDuration={POST_SUBTITLE_TRANSITION_DURATION}
                                        style={{
                                            ...FADE_IN_DEFAULT_STYLE({
                                                direction: 'down',
                                                offset: 10,
                                                duration: POST_SUBTITLE_ENTER_DURATION,
                                            }),
                                            ...FADE_IN_TRANSITION_STYLES({
                                                direction: 'down',
                                                offset: 10,
                                            })[state],
                                        }}
                                    >
                                        {postBannerRootPost?.subtitle || ''}
                                    </PostSubtitle>
                                )}
                            </Transition>
                            {/* Post not yet loaded */}
                            {!postBannerRootPost
                            && !write
                            && (
                                <PlaceholderBox
                                    width={subtitlePlaceholderWidth}
                                    height={POST_SUBTITLE_FONT_MULTIPLIER * BODY_FONT_SIZE}
                                    margin={POST_SUBTITLE_MARGIN}
                                />
                            )}
                            {/* Editing post subtitle */}
                            {postBannerRootPost
                            && write
                            && (
                                <PostInput
                                    type="text"
                                    ref={subtitleInputRef}
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    defaultValue={postBannerRootPost.subtitle}
                                    placeholder="Subtitle"
                                    onKeyUp={checkForEnter}
                                    color={themeObj.verascopeColor.purple200}
                                    fontSize={POST_SUBTITLE_FONT_MULTIPLIER}
                                    fontWeight={SUBTITLE_INPUT_FONT_WEIGHT}
                                    marginTop={SUBTITLE_INPUT_MARGIN_TOP}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onInputMouseEnter,
                                        onMouseLeave: onInputMouseLeave,
                                    })}
                                    onFocus={onInputFocus}
                                    onChange={onSubtitleInputChange}
                                    onBlur={onSubtitleInputBlur}
                                />
                            )}
                        </PostTitlesContainer>
                        <Transition
                            in
                            timeout={{
                                enter: POST_TITLE_ENTER_DURATION
                                    + POST_SUBTITLE_ENTER_DURATION
                                    + POST_THUMBNAIL_ENTER_DURATION,
                                exit: 0,
                            }}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state) => (
                                <PostMetadataContainer
                                    padding={POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: 'up',
                                            offset: 10,
                                            duration: POST_METADATA_ENTER_DURATION,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: 'up',
                                            offset: 10,
                                        })[state],
                                    }}
                                >
                                    <Transition
                                        in={
                                            !postBannerRootPost
                                            || !postBannerRootPost.title
                                            || !stickyPostBannerActive
                                        }
                                        timeout={{
                                            enter: 0,
                                            exit: 0,
                                        }}
                                        appear
                                        mountOnEnter
                                    >
                                        {(transitionState) => (
                                            <PostMetadataContentContainer
                                                padding={POST_BANNER_LEFT_CONTAINER_PADDING_EXPANDED}
                                                style={{
                                                    ...FADE_IN_DEFAULT_STYLE({
                                                        direction: 'up',
                                                        offset: 20,
                                                        duration: POST_METADATA_TRANSITION_DURATION,
                                                        easing: themeObj.motion.eagerEasing,
                                                    }),
                                                    ...FADE_IN_TRANSITION_STYLES({
                                                        direction: 'up',
                                                        offset: 20,
                                                    })[transitionState],
                                                }}
                                            >
                                                {viewportDimensions.width > POST_AUTHOR_THRESHOLD
                                                && (
                                                    resolvedPostAuthorIds.length > 0
                                                        ? (
                                                            <PostAuthorContainer>
                                                                <Tooltip
                                                                    text={`Author${resolvedPostAuthorIds.length > 1 ? 's' : ''}`}
                                                                    side={TOOLTIP_TYPE.top}
                                                                />
                                                                <AvatarsContainer>
                                                                    {resolvedPostAuthorIds.map((authorId) => {
                                                                        const hasAuthorAvatarImage = !!postUserAvatarURLs.get(authorId)
                                                                            || (
                                                                                resolvedPostAuthorIds.length > 0
                                                                                && !!postUserItems.get(authorId)
                                                                                && !!postUserItems.get(authorId)?.photoURL
                                                                            );
                                                                        return (
                                                                            <AvatarContainer
                                                                                key={authorId}
                                                                                hasAvatarImage={hasAuthorAvatarImage}
                                                                                length={AVATAR_LENGTH}
                                                                            >
                                                                                {hasAuthorAvatarImage
                                                                                    ? (
                                                                                        <UserAvatar
                                                                                            url={
                                                                                                postUserAvatarURLs.get(authorId)
                                                                                                || postUserItems.get(authorId)!.photoURL!
                                                                                            } // Has to be present if avatarURL is false
                                                                                            length={USER_PROFILE_AVATAR_LENGTH_MULTIPLIER * BODY_FONT_SIZE}
                                                                                        />
                                                                                    ) : (
                                                                                        <ReactSVG
                                                                                            src={SmileyIcon}
                                                                                        />
                                                                                    )}
                                                                            </AvatarContainer>
                                                                        );
                                                                    })}
                                                                </AvatarsContainer>
                                                                <PostAuthorName
                                                                    avatarHeight={AVATAR_LENGTH}
                                                                    fontMultiplier={POST_METADATA_FONT_MULTIPLIER}
                                                                >
                                                                    {resolvedPostAuthorIds.reduce((acc, authorId, authorIndex) => {
                                                                        const author = postUserItems.get(authorId);
                                                                        if (
                                                                            author != null
                                                                            && authorIndex === 0
                                                                            && resolvedPostAuthorIds.length === 1
                                                                        ) {
                                                                            return `${author.firstName} ${author.lastName}`;
                                                                        }

                                                                        if (
                                                                            author != null
                                                                            && authorIndex === 0
                                                                            && resolvedPostAuthorIds.length > 1
                                                                        ) {
                                                                            return `${author.firstName}`;
                                                                        }

                                                                        if (
                                                                            author != null
                                                                            && authorIndex !== 0
                                                                            && resolvedPostAuthorIds.length > 1
                                                                        ) {
                                                                            return `${acc}, ${author.firstName}`;
                                                                        }

                                                                        if (author != null) {
                                                                            return `${acc}, ${author.firstName} ${author.lastName}`;
                                                                        }

                                                                        return acc;
                                                                    }, '')}
                                                                </PostAuthorName>
                                                            </PostAuthorContainer>
                                                        ) : (
                                                            <PlaceholderBox
                                                                width={POST_AUTHOR_NAME_PLACEHOLDER_WIDTH}
                                                                height={POST_METADATA_FONT_MULTIPLIER * BODY_FONT_SIZE}
                                                            />
                                                        )
                                                )}
                                                <PostDetailsContainer>
                                                    {viewportDimensions.width > POST_PUBLISHED_THRESHOLD
                                                    && (
                                                        <PostDetail
                                                            center
                                                        >
                                                            <Tooltip
                                                                text="Published"
                                                                side={TOOLTIP_TYPE.top}
                                                            />
                                                            <PostDetailIcon>
                                                                <ReactSVG
                                                                    src={ClockIcon}
                                                                />
                                                            </PostDetailIcon>
                                                            {postBannerRootPost?.published
                                                                ? (
                                                                    <PostDetailText
                                                                        avatarHeight={AVATAR_LENGTH}
                                                                        fontMultiplier={POST_METADATA_FONT_MULTIPLIER}
                                                                    >
                                                                        {moment.utc(postBannerRootPost.published).format('YYYY')}
                                                                    </PostDetailText>
                                                                ) : (
                                                                    <PlaceholderBox
                                                                        width={POST_PUBLISHED_PLACEHOLDER_WIDTH}
                                                                        height={POST_METADATA_FONT_MULTIPLIER * BODY_FONT_SIZE}

                                                                    />
                                                                )}
                                                        </PostDetail>
                                                    )}
                                                    {viewportDimensions.width > POST_VIEWS_THRESHOLD
                                                    && (
                                                        postViewCount >= POST_BANNER_MINIMUM_VIEWS_TO_DISPLAY
                                                        || !postBannerRootPost
                                                    )
                                                    && (
                                                        <PostDetail
                                                            center
                                                        >
                                                            <Tooltip
                                                                text="Views"
                                                                side={TOOLTIP_TYPE.top}
                                                            />
                                                            <PostDetailIcon>
                                                                <ReactSVG
                                                                    src={EyeIcon}
                                                                />
                                                            </PostDetailIcon>
                                                            {effectiveRootPostEngagement
                                                                ? (
                                                                    <PostDetailText
                                                                        avatarHeight={AVATAR_LENGTH}
                                                                        fontMultiplier={POST_METADATA_FONT_MULTIPLIER}
                                                                    >
                                                                        {formatNumber(postViewCount)}
                                                                    </PostDetailText>
                                                                ) : (
                                                                    <PlaceholderBox
                                                                        width={POST_VIEWS_PLACEHOLDER_WIDTH}
                                                                        height={POST_METADATA_FONT_MULTIPLIER * BODY_FONT_SIZE}
                                                                        margin={`0px 0px 0px ${POST_DETAIL_TEXT_MARGIN_LEFT}px`}
                                                                    />
                                                                )}
                                                        </PostDetail>
                                                    )}
                                                    {viewportDimensions.width > POST_ANNOTATIONS_THRESHOLD
                                                    && postBannerAnnotationCount > 0
                                                    && (
                                                        <PostDetail
                                                            center
                                                        >
                                                            <Tooltip
                                                                text="Annotations"
                                                                side={TOOLTIP_TYPE.top}
                                                            />
                                                            <PostDetailIcon>
                                                                <ReactSVG
                                                                    src={HighlightIcon}
                                                                />
                                                            </PostDetailIcon>
                                                            <PostDetailText
                                                                avatarHeight={AVATAR_LENGTH}
                                                                fontMultiplier={POST_METADATA_FONT_MULTIPLIER}
                                                            >
                                                                {formatNumber(postBannerAnnotationCount)}
                                                            </PostDetailText>
                                                        </PostDetail>
                                                    )}
                                                    {!write
                                                    && !characterModalIsVisible
                                                    && checkedForCharacterModal
                                                    && pageLoadRendersCompleted
                                                    && storeItemsByProduct.size > 0
                                                    && (
                                                        <PostDetail
                                                            isPurchaseBookButtons
                                                        >
                                                            <PostBannerPurchaseActions>
                                                                <Button
                                                                    className={HOVER_TARGET_CLASSNAME}
                                                                    customCursorActive={customCursorActive}
                                                                    type={BUTTON_TYPE.line}
                                                                    center
                                                                    height={DOWNLOAD_BUTTON_HEIGHT_SMALL}
                                                                    width={postBannerAboutButtonWidthSmall}
                                                                    text="About the Book"
                                                                    color={BOOK_ABOUT_BUTTON_COLOR}
                                                                    lineButtonTextColor={themeObj.color.white}
                                                                    useColorForLineButtonChrome
                                                                    {...getTouchMouseEventHandlers({
                                                                        onMouseEnter: onButtonMouseEnter,
                                                                        onMouseLeave: onButtonMouseLeave,
                                                                    })}
                                                                    {...getPressEventHandlers({
                                                                        onTouchStart: () => handleBookAboutButton(),
                                                                        onMouseDown: () => handleBookAboutButton(),
                                                                    })}
                                                                />
                                                                {showPostBannerSignInButton && (
                                                                    <Button
                                                                        className={HOVER_TARGET_CLASSNAME}
                                                                        customCursorActive={customCursorActive}
                                                                        type={BUTTON_TYPE.line}
                                                                        center
                                                                        height={DOWNLOAD_BUTTON_HEIGHT_SMALL}
                                                                        width={postBannerPurchaseButtonWidthSmall}
                                                                        text="Sign In"
                                                                        color={BOOK_ABOUT_BUTTON_COLOR}
                                                                        lineButtonTextColor={themeObj.color.white}
                                                                        useColorForLineButtonChrome
                                                                        {...getTouchMouseEventHandlers({
                                                                            onMouseEnter: onButtonMouseEnter,
                                                                            onMouseLeave: onButtonMouseLeave,
                                                                        })}
                                                                        {...getPressEventHandlers({
                                                                            onTouchStart: () => handlePostBannerSignInButton(),
                                                                            onMouseDown: () => handlePostBannerSignInButton(),
                                                                        })}
                                                                    />
                                                                )}
                                                                <Button
                                                                    className={HOVER_TARGET_CLASSNAME}
                                                                    customCursorActive={customCursorActive}
                                                                    type={BUTTON_TYPE.solid}
                                                                    center
                                                                    height={DOWNLOAD_BUTTON_HEIGHT_SMALL}
                                                                    width={postBannerPurchaseButtonWidthSmall}
                                                                    text="Buy"
                                                                    icon={CartIcon}
                                                                    background={themeObj.verascopeColor.yellow100}
                                                                    {...getTouchMouseEventHandlers({
                                                                        onMouseEnter: onButtonMouseEnter,
                                                                        onMouseLeave: onButtonMouseLeave,
                                                                    })}
                                                                    {...getPressEventHandlers({
                                                                        onTouchStart: () => handleBuyButton(),
                                                                        onMouseDown: () => handleBuyButton(),
                                                                    })}
                                                                />
                                                            </PostBannerPurchaseActions>
                                                        </PostDetail>
                                                    )}
                                                </PostDetailsContainer>
                                            </PostMetadataContentContainer>
                                        )}
                                    </Transition>
                                </PostMetadataContainer>
                            )}
                        </Transition>
                        <Transition
                            in={
                                !!postBannerRootPost
                                && !!postBannerRootPost.title
                                && stickyPostBannerActive
                                && viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min
                            }
                            timeout={{
                                enter: POST_METADATA_TRANSITION_DURATION,
                                exit: 0,
                            }}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state) => (
                                <CollapsedPostTitle
                                    height={STICKY_POST_BANNER_HEIGHT}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: 'up',
                                            offset: 20,
                                            duration: COLLAPSED_POST_TITLE_TRANSITION_DURATION,
                                            easing: themeObj.motion.eagerEasing,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: 'up',
                                            offset: 20,
                                        })[state],
                                    }}
                                >
                                    {postBannerRootPost?.title || ''}
                                </CollapsedPostTitle>
                            )}
                        </Transition>
                    </PostBannerCenterContainer>
                    <PostBannerRightContainer
                        annotationViewWidth={postBannerRightContainerWidth}
                    >
                        <Transition
                            in={showStickyPostBannerPurchaseButtons}
                            timeout={{
                                enter: POST_METADATA_TRANSITION_DURATION,
                                exit: 0,
                            }}
                            appear
                            mountOnEnter
                            unmountOnExit
                        >
                            {(state) => (
                                <PurchaseBookButtonContainer
                                    buttonHeight={DOWNLOAD_BUTTON_HEIGHT_REGULAR}
                                    style={{
                                        ...FADE_IN_DEFAULT_STYLE({
                                            direction: 'up',
                                            offset: 20,
                                            duration: COLLAPSED_PURCHASE_BUTTONS_TRANSITION_DURATION,
                                            easing: themeObj.motion.eagerEasing,
                                        }),
                                        ...FADE_IN_TRANSITION_STYLES({
                                            direction: 'up',
                                            offset: 20,
                                        })[state],
                                    }}
                                >
                                    <PostBannerPurchaseActions>
                                        {showPostBannerSignInButton && (
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                center
                                                height={DOWNLOAD_BUTTON_HEIGHT_REGULAR}
                                                width={postBannerPurchaseButtonWidthRegular}
                                                text="Sign In"
                                                icon={UserIcon}
                                                background={themeObj.color.cream400}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({
                                                    onTouchStart: () => handlePostBannerSignInButton(),
                                                    onMouseDown: () => handlePostBannerSignInButton(),
                                                })}
                                            />
                                        )}
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.solid}
                                            center
                                            height={DOWNLOAD_BUTTON_HEIGHT_REGULAR}
                                            width={postBannerPurchaseButtonWidthRegular}
                                            text="Buy"
                                            icon={CartIcon}
                                            background={themeObj.color.cream400}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({
                                                onTouchStart: () => handleBuyButton(),
                                                onMouseDown: () => handleBuyButton(),
                                            })}
                                        />
                                    </PostBannerPurchaseActions>
                                </PurchaseBookButtonContainer>
                            )}
                        </Transition>
                    </PostBannerRightContainer>
                </PostBanner>
                <PostContent
                    adjustMarginTop={postContentAdjustMarginTop}
                >
                    <PostContentLeftContainer
                        width={postContentLeftContainerWidth}
                    >
                        {activeRootPost
                        && pageLoadRendersCompleted
                        && (
                            contentsTable
                            || write
                        ) && (
                            <ReaderContentsTableOutliner
                                customCursorActive={customCursorActive}
                                visible={contentsTableVisible}
                                hide={characterModalIsVisible}
                                hasSound={hasSound}
                                user={user}
                                userIsAdmin={userIsAdmin}
                                userPostAccess={userPostAccess}
                                currentSessionId={currentSessionId}
                                viewportDimensions={viewportDimensions}
                                write={write}
                                post={post}
                                currentZoomLevel={currentZoomLevel}
                                contentsTable={contentsTableWithLiveAnnotationCounts}
                                selectedPostValuePath={selectedPostValuePath}
                                pageLoadRendersCompleted={pageLoadRendersCompleted}
                                refreshPost={refreshPost}
                                hasError={postFileDragFeedback.hasFailure || hitPostMinimum || hitDetailMinimum}
                                hitDetailMinimum={hitDetailMinimum}
                                isSteering={isSteering}
                                notifyUserToSignUpAfterAnnotationCreationAttempt={notifyUserToSignUpAfterAnnotationCreationAttempt}
                                interactionDisabled={refreshingContentsTable}
                                setContentsTableVisible={setContentsTableVisible}
                                handleUpdatePostOutline={handleUpdatePostOutline}
                                onChangeSelectedPostValuePath={onChangeSelectedPostValuePath}
                                onSetExpandContentsTableChapter={onSetExpandContentsTableChapter}
                                setCursorSigns={setCursorSigns}
                                onCursorEnter={onCursorEnter}
                                onCursorLeave={onCursorLeave}
                                setSnackbarData={setSnackbarData}
                                contentsTableRef={contentsTableRef}
                                contentsTableProgressBarContainerRef={contentsTableProgressBarContainerRef}
                                contentsTableProgressBarRef={contentsTableProgressBarRef}
                                contentsTableToggleButtonContainerRef={contentsTableToggleButtonContainerRef}
                                refreshContentsTable={refreshContentsTable}
                                onOutlineChaptersChange={write ? setWriteOutlineChapters : undefined}
                            />
                        )}
                    </PostContentLeftContainer>
                    <PostContentCenterContainer
                        ref={postEditorContainerRef}
                        readOnly={!write}
                        postContentLeftContainerWidth={postContentLeftContainerWidth}
                        postContentRightContainerWidth={postContentRightContainerWidth}
                    >
                        {postBodyReadyForRender
                        && !showAccessUpgradePlaceholder
                        && !showAccessDowngradePlaceholder
                        && (
                            !write
                            || writeContentsTableRendered
                        )
                        && (
                            !refreshingPost
                            || !postEditorInitialized
                            || write
                        ) && PostEditorComponent}
                        {!showPostBodyContentForRender && (
                            <PostPlaceholderContainer
                                data-testid="reader-post-placeholder"
                                postBannerHeight={POST_BANNER_HEIGHT}
                                postContentMarginTop={postContentAdjustMarginTop}
                            >
                                {Array.from({ length: postPlaceholderBoxCount }, (_, index) => (index + 1)).map((placeholderNumber) => {
                                    const isFirstBox = placeholderNumber === 1;
                                    const isBlockStart = !isFirstBox
                                        && (placeholderNumber - 1) % POST_PLACEHOLDER_BOXES_PER_BLOCK === 0;
                                    let margin = `${POST_PLACEHOLDER_BOX_VERTICAL_MARGIN}px ${POST_PLACEHOLDER_BOX_HORIZTONAL_MARGIN}px`;

                                    if (isFirstBox) {
                                        margin = `0px ${POST_PLACEHOLDER_BOX_HORIZTONAL_MARGIN}px`;
                                    } else if (isBlockStart) {
                                        margin = `${
                                            POST_PLACEHOLDER_BLOCK_VERTICAL_MARGIN
                                        }px ${
                                            POST_PLACEHOLDER_BOX_HORIZTONAL_MARGIN
                                        }px ${
                                            POST_PLACEHOLDER_BOX_VERTICAL_MARGIN
                                        }px ${
                                            POST_PLACEHOLDER_BOX_HORIZTONAL_MARGIN
                                        }px`;
                                    }

                                    return (
                                        <PlaceholderBox
                                            key={`post-placeholder-box-${placeholderNumber}`}
                                            width={`calc(100% - ${2 * POST_PLACEHOLDER_BOX_HORIZTONAL_MARGIN}px)`}
                                            maxWidth={POST_PLACEHOLDER_BOX_MAX_WIDTH}
                                            height={BODY_FONT_SIZE}
                                            margin={margin}
                                            background={themeObj.color.cream400}
                                            shimmerLightColor="#d8d3cb"
                                            shimmerDarkColor="#c6c1ba"
                                        />
                                    );
                                })}
                            </PostPlaceholderContainer>
                        )}
                        {showLockedContainer && (
                            <LockedContainer
                                width={lockedContainerWidth}
                                height={lockedContainerHeight}
                            >
                                <LockedIcon>
                                    <ReactSVG
                                        src={LockIcon}
                                    />
                                </LockedIcon>
                                <LockedHeader>
                                    Not yet available
                                </LockedHeader>
                                <LockedSubheader>
                                    {lockedSubheaderText}
                                </LockedSubheader>
                            </LockedContainer>
                        )}
                        {showAuthLockedContainer && (
                            <AuthLockedContainer
                                data-testid="reader-auth-lock-banner"
                                ref={authLockBannerRef}
                                width={lockedContainerWidth}
                            >
                                <PaywallOverhang
                                    extraOverhang={POST_EDITOR_BUFFER_HEIGHT}
                                />
                                <PaywallContent>
                                    <PaywallHeader>
                                            Continue this chapter
                                    </PaywallHeader>
                                    <PaywallSubheader>
                                            You&apos;re only seeing part of this chapter. Sign in or join our mailing list to read the rest.
                                    </PaywallSubheader>
                                    <AuthLockedActionsContainer>
                                        <AuthLockedEmailRow>
                                            <PostInput
                                                ref={authLockedEmailInputRef}
                                                type="email"
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                value={authLockedEmail}
                                                placeholder="Email address"
                                                color={themeObj.color.neutral1100}
                                                placeholderColor={themeObj.color.neutral500}
                                                placeholderFontSize={0.75 * BODY_FONT_SIZE}
                                                marginTop={0}
                                                fontWeight={500}
                                                fontSize={0.9}
                                                isInvalid={authLockedEmailInvalid}
                                                style={{
                                                    boxShadow: themeObj.color.insetBoxShadow200,
                                                    background: authLockedEmailInvalid
                                                        ? themeObj.verascopeColor.red100
                                                        : themeObj.color.neutral200,
                                                    border: authLockedEmailInvalid
                                                        ? `${0.075 * BODY_FONT_SIZE}px solid ${themeObj.verascopeColor.red200}`
                                                        : 'none',
                                                    width: `${13 * BODY_FONT_SIZE}px`,
                                                    maxWidth: `${13 * BODY_FONT_SIZE}px`,
                                                    height: `${1.875 * BODY_FONT_SIZE}px`,
                                                    borderRadius: `${(1.875 * BODY_FONT_SIZE) / 2}px`,
                                                }}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onAuthLockedEmailInputMouseEnter,
                                                    onMouseLeave: onInputMouseLeave,
                                                })}
                                                onFocus={onAuthLockedEmailInputFocus}
                                                onKeyDown={onAuthLockedEmailInputKeyDown}
                                                onChange={(e) => setAuthLockedEmail(e.currentTarget.value)}
                                            />
                                            {(authLockedEmailValid || authLockedEmailSubmitting) && (
                                                <AuthLockedButtonContainer
                                                    buttonHeight={1.875 * BODY_FONT_SIZE}
                                                    $loading={authLockedEmailSubmitting}
                                                >
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.floating}
                                                        center
                                                        height={1.875 * BODY_FONT_SIZE}
                                                        width={13 * BODY_FONT_SIZE}
                                                        icon={EnvelopeIcon}
                                                        text="Continue with Email"
                                                        background={themeObj.verascopeColor.yellow100}
                                                        disabled={signingInOut || authLockedEmailSubmitting || authLockedSignInMethod != null}
                                                        loading={authLockedEmailSubmitting}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => handleAuthLockedEmailCapture(),
                                                            onMouseDown: () => handleAuthLockedEmailCapture(),
                                                        })}
                                                    />
                                                </AuthLockedButtonContainer>
                                            )}
                                        </AuthLockedEmailRow>
                                        <AuthLockedButtonContainer
                                            buttonHeight={1.875 * BODY_FONT_SIZE}
                                            $loading={authLockedSignInMethod === AUTHENTICATION_TYPE.google}
                                        >
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                center
                                                height={1.875 * BODY_FONT_SIZE}
                                                width={13 * BODY_FONT_SIZE}
                                                icon={GoogleIcon}
                                                background="#EA4335"
                                                text="Continue with Google"
                                                loading={authLockedSignInMethod === AUTHENTICATION_TYPE.google}
                                                disabled={signingInOut || authLockedEmailSubmitting || (
                                                    authLockedSignInMethod != null
                                                    && authLockedSignInMethod !== AUTHENTICATION_TYPE.google
                                                )}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({
                                                    onTouchStart: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.google),
                                                    onMouseDown: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.google),
                                                })}
                                            />
                                        </AuthLockedButtonContainer>
                                        <AuthLockedButtonContainer
                                            buttonHeight={1.875 * BODY_FONT_SIZE}
                                            $loading={authLockedSignInMethod === AUTHENTICATION_TYPE.facebook}
                                        >
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                center
                                                height={1.875 * BODY_FONT_SIZE}
                                                width={13 * BODY_FONT_SIZE}
                                                icon={FacebookIcon}
                                                background="#1877F2"
                                                text="Continue with Facebook"
                                                loading={authLockedSignInMethod === AUTHENTICATION_TYPE.facebook}
                                                disabled={signingInOut || authLockedEmailSubmitting || (
                                                    authLockedSignInMethod != null
                                                    && authLockedSignInMethod !== AUTHENTICATION_TYPE.facebook
                                                )}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({
                                                    onTouchStart: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.facebook),
                                                    onMouseDown: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.facebook),
                                                })}
                                            />
                                        </AuthLockedButtonContainer>
                                        <AuthLockedButtonContainer
                                            buttonHeight={1.875 * BODY_FONT_SIZE}
                                            $loading={authLockedSignInMethod === AUTHENTICATION_TYPE.github}
                                        >
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                center
                                                height={1.875 * BODY_FONT_SIZE}
                                                width={13 * BODY_FONT_SIZE}
                                                icon={GithubIcon}
                                                background="#6E7681"
                                                text="Continue with GitHub"
                                                loading={authLockedSignInMethod === AUTHENTICATION_TYPE.github}
                                                disabled={signingInOut || authLockedEmailSubmitting || (
                                                    authLockedSignInMethod != null
                                                    && authLockedSignInMethod !== AUTHENTICATION_TYPE.github
                                                )}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({
                                                    onTouchStart: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.github),
                                                    onMouseDown: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.github),
                                                })}
                                            />
                                        </AuthLockedButtonContainer>
                                        <AuthLockedButtonContainer
                                            buttonHeight={1.875 * BODY_FONT_SIZE}
                                            $loading={authLockedSignInMethod === AUTHENTICATION_TYPE.xTwitter}
                                        >
                                            <Button
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                type={BUTTON_TYPE.solid}
                                                center
                                                height={1.875 * BODY_FONT_SIZE}
                                                width={13 * BODY_FONT_SIZE}
                                                icon={XTwitterIcon}
                                                background={themeObj.color.neutral1100}
                                                text="Continue with X"
                                                loading={authLockedSignInMethod === AUTHENTICATION_TYPE.xTwitter}
                                                disabled={signingInOut || authLockedEmailSubmitting || (
                                                    authLockedSignInMethod != null
                                                    && authLockedSignInMethod !== AUTHENTICATION_TYPE.xTwitter
                                                )}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onButtonMouseEnter,
                                                    onMouseLeave: onButtonMouseLeave,
                                                })}
                                                {...getPressEventHandlers({
                                                    onTouchStart: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.xTwitter),
                                                    onMouseDown: () => handleAuthLockedSocialSignIn(AUTHENTICATION_TYPE.xTwitter),
                                                })}
                                            />
                                        </AuthLockedButtonContainer>
                                    </AuthLockedActionsContainer>
                                </PaywallContent>
                            </AuthLockedContainer>
                        )}
                        {activeRootPost
                        && activeRootPost.id
                        && postBodyReadyForRender
                        && readerAccessPromptsReady
                        && !write
                        && (write || postEditorInitialized)
                        && !refreshingPost
                        && !showAccessUpgradePlaceholder
                        && !showAccessDowngradePlaceholder
                        && showPaywall
                        && !userIsAdmin
                        && (
                            postLockedUntil == null
                            || postLockedUntil < Date.now()
                        ) && (
                            <PaywallContainer
                                ref={paywallBannerRef}
                                width={lockedContainerWidth}
                            >
                                <PaywallOverhang
                                    extraOverhang={(POST_EDITOR_BUFFER_HEIGHT - POST_PAYWALL_OVERHANG_HEIGHT)}
                                />
                                <PaywallContent>
                                    <PaywallHeader>
                                        {'You\'ve hit the wall.'}
                                    </PaywallHeader>
                                    <PaywallSubheader>
                                        {'Buy '}
                                        {activeRootPost.title
                                            ? `"${activeRootPost.title}"`
                                            : 'the featured text'}
                                        {' to continue reading.'}
                                    </PaywallSubheader>
                                    <PaywallButtonContainer>
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.floating}
                                            center
                                            height={PAYWALL_ACTION_BUTTON_HEIGHT}
                                            width={PAYWALL_ACTION_BUTTON_WIDTH}
                                            text="Buy"
                                            background={themeObj.verascopeColor.yellow100}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({
                                                onTouchStart: () => handlePaywallBuyButton(),
                                                onMouseDown: () => handlePaywallBuyButton(),
                                            })}
                                        />
                                    </PaywallButtonContainer>
                                </PaywallContent>
                            </PaywallContainer>
                        )}
                        {activeRootPost
                        && activeRootPost.id
                        && postBodyReadyForRender
                        && readerAccessPromptsReady
                        && !write
                        && (write || postEditorInitialized)
                        && !refreshingPost
                        && showBuyBanner
                        && !userIsAdmin
                        && (
                            <PaywallContainer
                                ref={buyBannerRef}
                                width={lockedContainerWidth}
                            >
                                <PaywallOverhang
                                    extraOverhang={(POST_EDITOR_BUFFER_HEIGHT - POST_PAYWALL_OVERHANG_HEIGHT)}
                                />
                                <PaywallContent>
                                    <PaywallHeader>
                                        {'This chapter\'s on the house.'}
                                    </PaywallHeader>
                                    <PaywallSubheader>
                                        {'Buy '}
                                        {activeRootPost.title
                                            ? `"${activeRootPost.title}"`
                                            : 'the featured text'}
                                        {' to gain full-access to the others.'}
                                    </PaywallSubheader>
                                    <PaywallButtonContainer>
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.floating}
                                            center
                                            height={PAYWALL_ACTION_BUTTON_HEIGHT}
                                            width={PAYWALL_ACTION_BUTTON_WIDTH}
                                            text="Buy book"
                                            background={themeObj.verascopeColor.yellow100}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            {...getPressEventHandlers({
                                                onTouchStart: () => handleBuyBannerButton(),
                                                onMouseDown: () => handleBuyBannerButton(),
                                            })}
                                        />
                                    </PaywallButtonContainer>
                                </PaywallContent>
                            </PaywallContainer>
                        )}
                    </PostContentCenterContainer>
                    {showAnnotationShelf && (
                        <DetailView
                            ref={detailViewRef}
                            isVisible
                            zIndex={DETAIL_VIEW_Z_INDEX - 1} // We want to ensure UpdateText and UploadProgress are above this
                            width={postContentRightContainerWidth}
                            height={detailViewHeight}
                            // Keep the annotation shelf continuous with the post top strip.
                            topOffset={detailViewTopOffset}
                            isNotPaywalled={isNotPaywalled}
                            isSteering={isSteering}
                            showWarning={hitDetailMinimum || hitPostMinimum}
                            warningBackground={hitPostMinimum
                                ? themeObj.verascopeColor.red300
                                : themeObj.verascopeColor.red200}
                            background={DETAIL_VIEW_BACKGROUND}
                            transitionDuration={DETAIL_VIEW_TRANSITION_DURATION}
                        >
                            <Resizable
                                width={postContentRightContainerWidth}
                                height={detailViewHeight}
                                handle={handle}
                                axis="x"
                                onResizeStart={onResizeStart}
                                onResizeStop={onResizeStop}
                                onResize={onResize}
                                minConstraints={[MIN_ANNOTATION_SECTION_WIDTH, detailViewHeight]}
                                maxConstraints={[resizeableMaxConstraintsWidth, detailViewHeight]}
                                resizeHandles={['w']}
                            >
                                <DetachedAnnotationShelfLayout>
                                    {DetachedAnnotationsPanel}
                                    <AnnotationContainer>
                                        {Annotations}
                                    </AnnotationContainer>
                                </DetachedAnnotationShelfLayout>
                            </Resizable>
                        </DetailView>
                    )}
                </PostContent>
                {showStickyPurchaseCta && (
                    <Transition
                        in={purchasePanelExpanded}
                        timeout={{
                            enter: BUTTON_EXPANSION_DURATION,
                            exit: BUTTON_EXPANSION_DURATION,
                        }}
                    >
                        {(state) => {
                            const collapsedHeight = stickyPurchaseMeasuredHeights.collapsed;
                            const expandedHeight = stickyPurchaseMeasuredHeights.expanded;
                            const opening = state === 'entering';
                            const expanded = opening || state === 'entered';
                            const stickyPurchaseIsExtraSmall = viewportDimensions.width <= MEDIA_QUERY_SIZE.extraSmall.max;
                            const stickyPurchaseIsSmall = (
                                viewportDimensions.width >= MEDIA_QUERY_SIZE.small.min
                                && viewportDimensions.width <= MEDIA_QUERY_SIZE.small.max
                            );
                            const hideShareEmailHeaderButton = stickyPurchaseIsSmall || stickyPurchaseIsExtraSmall;
                            let stickyHeaderShareEmailButtonHeight = COMPACT_PURCHASE_CTA_HEIGHT;
                            let stickyHeaderBuyButtonHeight = purchasePanelExpanded
                                ? 1.75 * BODY_FONT_SIZE
                                : COMPACT_PURCHASE_CTA_HEIGHT;
                            if (stickyPurchaseIsExtraSmall) {
                                stickyHeaderShareEmailButtonHeight = DOWNLOAD_BUTTON_HEIGHT_SMALL;
                                stickyHeaderBuyButtonHeight = DOWNLOAD_BUTTON_HEIGHT_SMALL;
                            }
                            const openingOvershootBuffer = 0.6 * BODY_FONT_SIZE;
                            let animatedHeight = collapsedHeight;
                            if (opening) {
                                animatedHeight = expandedHeight + openingOvershootBuffer;
                            } else if (expanded) {
                                animatedHeight = expandedHeight;
                            }
                            let stickyCtaPrimaryButtonWidth = compactPurchaseButtonWidth;
                            let stickyCtaPrimaryButtonBackground = themeObj.verascopeColor.yellow100;
                            if (purchasePanelExpanded) {
                                stickyCtaPrimaryButtonWidth = compactPurchaseCloseButtonWidth;
                                stickyCtaPrimaryButtonBackground = themeObj.verascopeColor.purple300;
                            }
                            return (
                                <StickyPurchaseCtaContainer
                                    data-testid="reader-sticky-purchase-cta"
                                    ref={stickyPurchaseCtaRef}
                                    animatedHeight={animatedHeight}
                                    transitionDuration={BUTTON_EXPANSION_DURATION}
                                >
                                    <StickyPurchaseHeader
                                        ref={stickyPurchaseHeaderRef}
                                        centerContent={stickyPurchaseIsSmall || (purchasePanelExpanded && stickyPurchaseIsNarrow)}
                                    >
                                        <StickyPurchaseHeaderButtonContainer
                                            marginBottom={
                                                purchasePanelExpanded
                                                && stickyPurchaseIsNarrow
                                                    ? 1 * BODY_FONT_SIZE
                                                    : 0
                                            }
                                        >
                                            {!purchasePanelExpanded
                                            && isAuthLockedAnonymousStickyState
                                            && !hideShareEmailHeaderButton && (
                                                <ButtonContainer>
                                                    {stickyCtaNudge.visible
                                                    && stickyCtaNudge.target === STICKY_CTA_NUDGE_TARGET.shareEmail && (
                                                        <Tooltip
                                                            permanent
                                                            text={stickyCtaNudge.text}
                                                            side={TOOLTIP_TYPE.top}
                                                        />
                                                    )}
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        center
                                                        height={stickyHeaderShareEmailButtonHeight}
                                                        width={stickyPurchaseIsExtraSmall
                                                            ? compactPurchaseButtonWidth
                                                            : compactAuthLockedCollapsedButtonWidth}
                                                        text="Read Free"
                                                        icon={EnvelopeIcon}
                                                        background={themeObj.verascopeColor.yellow100}
                                                        loading={authLockedStickyCtaLoading}
                                                        disabled={authLockedStickyCtaLoading}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        style={{
                                                            marginRight: stickyPurchaseIsExtraSmall
                                                                ? '0px'
                                                                : `${0.375 * BODY_FONT_SIZE}px`,
                                                        }}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => handleShareEmailFromStickyCta(),
                                                            onMouseDown: () => handleShareEmailFromStickyCta(),
                                                        })}
                                                    />
                                                </ButtonContainer>
                                            )}
                                            {(
                                                purchasePanelExpanded
                                                || !isAuthLockedAnonymousStickyState
                                                || hideShareEmailHeaderButton
                                            ) && (
                                                <ButtonContainer>
                                                    {stickyCtaNudge.visible
                                                    && (
                                                        stickyCtaNudge.target === STICKY_CTA_NUDGE_TARGET.buy
                                                        || (
                                                            hideShareEmailHeaderButton
                                                            && stickyCtaNudge.target === STICKY_CTA_NUDGE_TARGET.shareEmail
                                                        )
                                                    ) && (
                                                        <Tooltip
                                                            permanent
                                                            text={stickyCtaNudge.text}
                                                            side={TOOLTIP_TYPE.top}
                                                        />
                                                    )}
                                                    <Button
                                                        className={HOVER_TARGET_CLASSNAME}
                                                        customCursorActive={customCursorActive}
                                                        type={BUTTON_TYPE.solid}
                                                        center
                                                        height={stickyHeaderBuyButtonHeight}
                                                        width={stickyCtaPrimaryButtonWidth}
                                                        text={purchasePanelExpanded ? 'Close' : 'Buy'}
                                                        icon={purchasePanelExpanded ? CrossIcon : CartIcon}
                                                        background={stickyCtaPrimaryButtonBackground}
                                                        loading={!purchasePanelExpanded && stickyCtaBuyButtonsPendingPostData}
                                                        disabled={!purchasePanelExpanded && stickyCtaBuyButtonsPendingPostData}
                                                        style={{
                                                            transition: `width 200ms ${themeObj.motion.overshoot}`,
                                                        }}
                                                        {...getTouchMouseEventHandlers({
                                                            onMouseEnter: onButtonMouseEnter,
                                                            onMouseLeave: onButtonMouseLeave,
                                                        })}
                                                        {...getPressEventHandlers({
                                                            onTouchStart: () => toggleStickyPurchasePanel(),
                                                            onMouseDown: () => toggleStickyPurchasePanel(),
                                                        })}
                                                    />
                                                </ButtonContainer>
                                            )}
                                        </StickyPurchaseHeaderButtonContainer>
                                        <StickyPurchaseHeaderCopy
                                            hidden={
                                                stickyCtaBuyButtonsPendingPostData
                                                || stickyPurchaseIsSmall
                                                || (purchasePanelExpanded && !stickyPurchaseIsSmall)
                                            }
                                            transitionDuration={BUTTON_EXPANSION_DURATION}
                                        >
                                            <StickyPurchaseHeaderTitle>
                                                {currentContentAuthLocked && !userIsSignedIn
                                                    ? 'This chapter is on the house'
                                                    : (() => {
                                                        if (
                                                            viewportDimensions.width >= MEDIA_QUERY_SIZE.large.min
                                                            && activeRootPost?.title
                                                        ) {
                                                            return (
                                                                <>
                                                                    To read more of
                                                                    <StickyPurchaseHeaderTitleEmphasis>
                                                                        {activeRootPost.title}
                                                                    </StickyPurchaseHeaderTitleEmphasis>
                                                                    ...
                                                                </>
                                                            );
                                                        }
                                                        return 'To read more...';
                                                    })()}
                                            </StickyPurchaseHeaderTitle>
                                            {currentContentAuthLocked && !userIsSignedIn && (
                                                <StickyPurchaseHeaderSubtitle>
                                                    Join our mailing list to access the full chapter for free.
                                                </StickyPurchaseHeaderSubtitle>
                                            )}
                                        </StickyPurchaseHeaderCopy>
                                    </StickyPurchaseHeader>
                                    <StickyPurchaseContentContainer
                                        ref={stickyPurchaseContentRef}
                                        visible={purchasePanelExpanded}
                                    >
                                        {renderCompactPurchaseTable(state === 'entered')}
                                    </StickyPurchaseContentContainer>
                                    <StickyPurchaseChevronAnchor
                                        expanded={purchasePanelExpanded}
                                    >
                                        <Button
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            type={BUTTON_TYPE.secret}
                                            center
                                            height={1.875 * BODY_FONT_SIZE}
                                            width={1.875 * BODY_FONT_SIZE}
                                            icon={ChevronIcon}
                                            background={themeObj.verascopeColor.purple200}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: onButtonMouseEnter,
                                                onMouseLeave: onButtonMouseLeave,
                                            })}
                                            style={{
                                                borderRadius: '50%',
                                                transform: purchasePanelExpanded ? 'rotate(90deg)' : 'rotate(-90deg)',
                                                transition: 'transform 160ms ease',
                                            }}
                                            {...getPressEventHandlers({
                                                onTouchStart: () => toggleStickyPurchasePanel(),
                                                onMouseDown: () => toggleStickyPurchasePanel(),
                                            })}
                                        />
                                    </StickyPurchaseChevronAnchor>
                                </StickyPurchaseCtaContainer>
                            );
                        }}
                    </Transition>
                )}
            </PostContentContainer>
            {write && FileUploads}
            {write && (
                <EditorStatusRowContainer>
                    {CollaborativeParticipantsStrip}
                    <EditorStatusControlsRow>
                        {UpdateText}
                        {PublishingButton}
                        {AccessButton}
                    </EditorStatusControlsRow>
                </EditorStatusRowContainer>
            )}
            {PostPublishingDialogComponent}
            {PostAccessDialogComponent}
            {BookAboutDialogComponent}
            {annotationEditorElement && (
                portalAnnotationEditorToBody
                    ? createPortal(
                        (
                            <DockedAnnotationEditorViewportRoot
                                data-testid="docked-annotation-editor-viewport-root"
                                ref={dockedAnnotationViewportRootRef}
                            >
                                {annotationEditorElement}
                            </DockedAnnotationEditorViewportRoot>
                        ),
                        document.body,
                    )
                    : annotationEditorElement
            )}
            <Transition
                in={
                    !annotationsHidden
                    && userIsSignedIn
                    && viewportDimensions.width < MEDIA_QUERY_SIZE.medium.min
                    && annotations.size > 0
                    && !write
                    && showDetailBanner
                }
                timeout={{
                    enter: DETAIL_SHEET_ENTER_TRANSITION_DURATION,
                    exit: DETAIL_SHEET_EXIT_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <MobileDetailSheetRoot
                        className={DETAIL_SHEET_CLASSNAME}
                    >
                        <MobileDetailSheetBackdrop
                            {...getPressEventHandlers({
                                onTouchStart: () => onHideAnnotationDetail(),
                                onMouseDown: () => onHideAnnotationDetail(),
                            })}
                        />
                        <MobileDetailSheetPanel
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'up',
                                    offset: 20,
                                    duration: DETAIL_SHEET_ENTER_TRANSITION_DURATION,
                                    easing: themeObj.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 20,
                                })[state],
                            }}
                        >
                            <ModalSheetCloseButtonContainer>
                                <Button
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    type={BUTTON_TYPE.solid}
                                    background={MODAL_SHEET_CLOSE_BUTTON_COLOR}
                                    height={MODAL_SHEET_CLOSE_BUTTON_LENGTH}
                                    width={MODAL_SHEET_CLOSE_BUTTON_LENGTH}
                                    icon={CrossIcon}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({ onMouseDown: () => onHideAnnotationDetail() })}
                                />
                            </ModalSheetCloseButtonContainer>
                            {SheetContent}
                        </MobileDetailSheetPanel>
                    </MobileDetailSheetRoot>
                )}
            </Transition>
            <PageLogo
                dark={stickyPostBannerActive}
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            {showZoomLevelSelector && (
                <ZoomLevelSelectorContainer
                    readOnly={!write}
                    selectorWidth={ZOOM_LEVEL_BUTTON_LENGTH}
                    readOnlyRightOffset={zoomSelectorReadOnlyRightOffset}
                >
                    {informZoomAdjustCapability
                    && !write
                    && (
                        <Tooltip
                            permanent={showZoomTooltip}
                            text="Too little or too much detail? Adjust the zoom level."
                            side={TOOLTIP_TYPE.left}
                        />
                    )}
                    <OptionsMenu
                        center={!write}
                        revealAbove
                        setRef={(resRef) => { zoomLevelButtonRef.current = resRef; }}
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        buttonIcon={MagnifyingGlassIcon}
                        buttonColor={themeObj.verascopeColor.purple400}
                        buttonHeight={ZOOM_LEVEL_BUTTON_LENGTH}
                        childItemHeight={ZOOM_LEVEL_ITEM_HEIGHT}
                        childItemWidth={ZOOM_LEVEL_ITEM_WIDTH}
                        parentRef={rootRef.current || containerRef.current}
                        tooltipText="Zoom Level"
                        tooltipSideType={!write ? TOOLTIP_TYPE.left : TOOLTIP_TYPE.right}
                        onHoverEnter={onButtonMouseEnter}
                        onHoverLeave={onButtonMouseLeave}
                        onToggle={(visible) => handleToggleZoomLevelButton(visible)}
                    >
                        {ZoomLevelButtons}
                    </OptionsMenu>
                    {currentZoomLevel.icon && (
                        <CurrentZoomLevelIcon
                            src={currentZoomLevel.icon}
                        />
                    )}
                    {!currentZoomLevel.icon && (
                        <CurrentZoomLevelText
                            currentZoomDigit={currentZoomLevel.level - unusedZoomLevelCount}
                        >
                            {/* In write mode, unusedZoomCount is zero */}
                            {currentZoomLevel.level - unusedZoomLevelCount}
                        </CurrentZoomLevelText>
                    )}
                </ZoomLevelSelectorContainer>
            )}
            {/* Transition keeps the resume CTA from disappearing abruptly when scroll/state hides it. */}
            <Transition
                in={!showFloatingInternalPostLinkLoadingPill && !write && readerProgressEnabled && !annotationIsFocused && (
                    resumeReaderProgress != null
                    || resumeReaderProgressButtonLoading
                )}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <ResumeReaderPositionButtonContainer
                        bottom={resumeReaderProgressBottomOffset}
                        right={resumeReaderProgressRightOffset}
                        transitionDuration={RESUME_READING_DURATION}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'down',
                                offset: 8,
                                duration: FADE_IN_TRANSITION_DURATION,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'down',
                                offset: 8,
                            })[state],
                        }}
                    >
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.floating}
                            center
                            height={RESUME_READER_POSITION_BUTTON_HEIGHT}
                            width={RESUME_READER_POSITION_BUTTON_WIDTH}
                            text="Resume reading"
                            icon={BookmarkIcon}
                            loading={resumeReaderProgressButtonLoading}
                            background={themeObj.verascopeColor.purple200}
                            tooltip={{
                                active: !resumeReaderProgressButtonLoading && showResumeReaderProgressTooltip,
                                text: resumeReaderProgressButtonLoading
                                    ? 'Preparing your saved position'
                                    : 'Jump to where you left off',
                                side: resumeReaderProgressTooltipSide,
                            }}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => triggerResumeReaderProgress(READER_RESUME_SOURCE.resumeButton),
                                onMouseDown: () => triggerResumeReaderProgress(READER_RESUME_SOURCE.resumeButton),
                            })}
                        />
                    </ResumeReaderPositionButtonContainer>
                )}
            </Transition>
            <Transition
                in={showFloatingInternalPostLinkLoadingPill}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <ResumeReaderPositionButtonContainer
                        bottom={resumeReaderProgressBottomOffset}
                        right={resumeReaderProgressRightOffset}
                        transitionDuration={RESUME_READING_DURATION}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'down',
                                offset: 8,
                                duration: FADE_IN_TRANSITION_DURATION,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'down',
                                offset: 8,
                            })[state],
                        }}
                    >
                        <InternalPostLinkFloatingStatusPill
                            data-testid="reader-internal-post-link-loading-pill"
                        >
                            <InternalPostLinkFloatingStatusSpinner
                                src={SpinnerIcon}
                                alt=""
                            />
                            <InternalPostLinkFloatingStatusLabel>
                                Jumping to section
                            </InternalPostLinkFloatingStatusLabel>
                        </InternalPostLinkFloatingStatusPill>
                    </ResumeReaderPositionButtonContainer>
                )}
            </Transition>
            <Transition
                in={showFloatingAudioMiniPlayer}
                timeout={{
                    enter: FADE_IN_TRANSITION_DURATION,
                    exit: FADE_IN_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <FloatingAudioPlayerContainer
                        data-testid="reader-floating-audio-player"
                        bottom={floatingAudioMiniPlayerBottomOffset}
                        right={floatingAudioMiniPlayerRightOffset}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'down',
                                offset: 8,
                                duration: FADE_IN_TRANSITION_DURATION,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'down',
                                offset: 8,
                            })[state],
                        }}
                    >
                        <FloatingAudioPlayerTransportRow>
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.floating}
                                center
                                height={1.375 * BODY_FONT_SIZE}
                                width={1.375 * BODY_FONT_SIZE}
                                icon={SkipBackwardIcon}
                                background={themeObj.color.white}
                                iconFillColor={themeObj.color.neutral600}
                                tooltip={{
                                    active: true,
                                    text: 'Back 15 seconds',
                                    side: TOOLTIP_TYPE.left,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: () => handleFloatingAudioMiniPlayerSkip('backward'),
                                    onMouseDown: () => handleFloatingAudioMiniPlayerSkip('backward'),
                                })}
                            />
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.floating}
                                center
                                height={2 * BODY_FONT_SIZE}
                                width={2 * BODY_FONT_SIZE}
                                icon={readOnlyAudioPlaybackState?.isPlaying ? PauseIcon : PlayIcon}
                                loading={!!readOnlyAudioPlaybackState?.isStartingPlayback}
                                background={themeObj.verascopeColor.purple200}
                                tooltip={{
                                    active: true,
                                    text: floatingAudioMiniPlayerTooltipText,
                                    side: TOOLTIP_TYPE.left,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: () => {
                                        if (readOnlyAudioPlaybackState?.isStartingPlayback) {
                                            return;
                                        }

                                        readOnlyAudioPlaybackState?.setPlaying(!readOnlyAudioPlaybackState?.isPlaying);
                                    },
                                    onMouseDown: () => {
                                        if (readOnlyAudioPlaybackState?.isStartingPlayback) {
                                            return;
                                        }

                                        readOnlyAudioPlaybackState?.setPlaying(!readOnlyAudioPlaybackState?.isPlaying);
                                    },
                                })}
                            />
                            <Button
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.floating}
                                center
                                height={1.375 * BODY_FONT_SIZE}
                                width={1.375 * BODY_FONT_SIZE}
                                icon={SkipForwardIcon}
                                background={themeObj.color.white}
                                iconFillColor={themeObj.color.neutral600}
                                tooltip={{
                                    active: true,
                                    text: 'Forward 15 seconds',
                                    side: TOOLTIP_TYPE.left,
                                }}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({
                                    onTouchStart: () => handleFloatingAudioMiniPlayerSkip('forward'),
                                    onMouseDown: () => handleFloatingAudioMiniPlayerSkip('forward'),
                                })}
                            />
                        </FloatingAudioPlayerTransportRow>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            customCursorActive={customCursorActive}
                            type={BUTTON_TYPE.floating}
                            center
                            height={1.375 * BODY_FONT_SIZE}
                            width={2.5 * BODY_FONT_SIZE}
                            textNode={<PlaybackSpeedButtonLabel speed={readOnlyAudioPlaybackState?.playbackSpeed || 1} />}
                            background={themeObj.verascopeColor.purple400}
                            tooltip={{
                                active: true,
                                text: 'Cycle playback speed',
                                side: TOOLTIP_TYPE.left,
                            }}
                            {...getTouchMouseEventHandlers({
                                onMouseEnter: onButtonMouseEnter,
                                onMouseLeave: onButtonMouseLeave,
                            })}
                            {...getPressEventHandlers({
                                onTouchStart: () => readOnlyAudioPlaybackState?.setPlaybackSpeed(
                                    getNextAudioMiniPlayerPlaybackSpeed(
                                        readOnlyAudioPlaybackState?.playbackSpeed || 1,
                                    ),
                                ),
                                onMouseDown: () => readOnlyAudioPlaybackState?.setPlaybackSpeed(
                                    getNextAudioMiniPlayerPlaybackSpeed(
                                        readOnlyAudioPlaybackState?.playbackSpeed || 1,
                                    ),
                                ),
                            })}
                        />
                        <FloatingAudioPlayerTime>
                            {`${formatAudioMiniPlayerTimestamp({
                                timestampMs: readOnlyAudioPlaybackState?.currentTimeMs || 0,
                                clampNegativeToZero: true,
                            })} / ${
                                formatAudioMiniPlayerTimestamp({
                                    timestampMs: readOnlyAudioPlaybackState?.durationMs || 0,
                                    clampNegativeToZero: true,
                                })
                            }`}
                        </FloatingAudioPlayerTime>
                    </FloatingAudioPlayerContainer>
                )}
            </Transition>
            {showVisibleAnnotationVisibilityButton
            && !annotationIsFocused
            && (
                <AnnotationVisibilityButtonContainer
                    bottom={annotationVisibilityButtonBottomOffset}
                    right={annotationVisibilityButtonRightOffset}
                    background={annotationVisibilityButtonContainerBackground}
                >
                    <Button
                        data-testid="reader-annotation-visibility-toggle"
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        type={annotationVisibilityButtonType}
                        center
                        height={ANNOTATION_VISIBILITY_BUTTON_LENGTH}
                        width={ANNOTATION_VISIBILITY_BUTTON_LENGTH}
                        icon={HighlightIcon}
                        iconFillColor={annotationVisibilityButtonIconFillColor}
                        background={annotationVisibilityButtonButtonBackground}
                        tooltip={{
                            active: true,
                            text: annotationsHidden ? 'Show annotations' : 'Hide annotations',
                            side: annotationVisibilityButtonTooltipSide,
                        }}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onAnnotationVisibilityButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => handleToggleAnnotationVisibility(),
                            onMouseDown: () => handleToggleAnnotationVisibility(),
                        })}
                    />
                </AnnotationVisibilityButtonContainer>
            )}
            {showAnnotationVisibilityRuntimeHarnessControl && (
                <button
                    type="button"
                    data-testid="reader-annotation-visibility-toggle-harness"
                    data-toggle-visible={showVisibleAnnotationVisibilityButton ? 'true' : 'false'}
                    data-annotations-hidden={annotationsHidden ? 'true' : 'false'}
                    onClick={() => handleToggleAnnotationVisibility()}
                    style={{ display: 'none' }}
                    aria-hidden="true"
                />
            )}
            <Transition
                in={!write && notifyUserToSignUpAfterAnnotationCreationAttempt}
                timeout={{
                    enter: OVERLAY_TRANSITION_DURATION,
                    exit: OVERLAY_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
                unmountOnExit
            >
                {(state) => (
                    <Overlay
                        className={HOVER_TARGET_CLASSNAME}
                        customCursorActive={customCursorActive}
                        zIndex={READER_VIEW_OVERLAY_Z_INDEX}
                        {...getTouchMouseEventHandlers({
                            onMouseEnter: onButtonMouseEnter,
                            onMouseLeave: onButtonMouseLeave,
                        })}
                        {...getPressEventHandlers({
                            onTouchStart: () => setNotifyUserToSignUpAfterAnnotationCreationAttempt(false),
                            onMouseDown: () => setNotifyUserToSignUpAfterAnnotationCreationAttempt(false),
                        })}
                        style={{
                            ...FADE_IN_DEFAULT_STYLE({
                                duration: OVERLAY_TRANSITION_DURATION,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({})[state],
                        }}
                    />
                )}
            </Transition>
            {CharacterModal}
        </Container>
    );
}

export default ReaderView;
